/**
 * AllJourneys — the Appointment Page Designer (rail "All Journeys", route `alljourneys`).
 *
 * Ported 1:1 from the Gold Master prototype `Appointment Page Designer.dc.html`
 * (2026-09-03, operator: "exactly the same way I designed it"). Two mechanical steps,
 * so there is nothing here that was re-drawn by hand:
 *  • the prototype's template was TRANSPILED to the JSX in render() — every inline
 *    style literal is the prototype's; every `<x-import>` is the same design-system
 *    component from @v0y4ge/jrni-ui;
 *  • the prototype's logic class is carried over VERBATIM as the class body below
 *    (React state/setState/lifecycle were already its contract).
 *
 * What is REAL here:
 *  • Save / Publish go through alljourneys-api — one record per journey per tenant,
 *    published from the first save (no draft state, 2026-09-08) and a public slug per record, registered on publish
 *    (local stub until CD-API-REQUEST-page-designer-2026-09-03 lands).
 *  • Kiosk check-in looks a code up with lookupPublicCheckIn from the typed api-client
 *    (read-only) and shows back only what it returned. This renderer is the designer
 *    preview, so it never posts performPublicCheckIn; the arrived transition (decision 94,
 *    channel "kiosk" — decision-59 value pending, decision 178) belongs to a live kiosk mount.
 *  • A kiosk learns its location from /kiosk?location={external_key}, never from the
 *    record and never from a seed id.
 *
 * Sixth tab (2026-09-04): OneStop — delegated to onestop-tab.tsx, which edits the tenant's
 * One-Stop EXPERIENCE DOCUMENT (onestop-page-api), not a page-designer record.
 * Five journeys: Appointments · Walk-Ins · Event List Page · Event Landing Page · Kiosk.
 * Legacy Journey and Journey Builder rail items are untouched (operator: add, don't replace).
 */
/* eslint-disable */
// @ts-nocheck — the class body is the prototype's JavaScript carried over verbatim; typing it
// would mean editing it, and the ruling is that it comes in exactly as designed.
import React from "react";
import * as UI from "@v0y4ge/jrni-ui";
import {
  Button, SegmentedControl, SectionLabel, EditorSectionNav, Section, Field, Dropdown, Input, ToggleRow,
  SelectableTile, Checkbox, ImageDropField, Banner, MediaRow, MultiSelect, Toggle, Textarea, AddRowButton,
  CopyableUrl, DevicePreviewToggle, resolveDeviceWidths, Pill, Avatar, MeterBar, DatePicker, ChipToggleGroup, InlineEmptyState,
  Pagination, Chip, ChoiceTile, SlotPicker, TimezoneSelect, FileUpload, ConfirmDialog,
} from "@v0y4ge/jrni-ui";
import { QrCode } from "../../shell/QrCode";
import { AppointmentFooter } from "./AppointmentFooter";
import { serviceCategoryOptions, serviceFilterId, serviceMatchesCategory } from "./appointment-service-filters";
import { Screen } from "../../shell/chrome";
import { useTenant } from "../../shell/access";
import { lookupPublicCheckIn } from "@v0y4ge/api-client";
import { listPageDesigner, putPageDesigner, publishPageDesigner } from "./alljourneys-api";
import { loadPreviewCatalog } from "./alljourneys-catalog";
import { ErrorBoundary } from "../../shell/ErrorBoundary";
import {
  resolveAppointmentJourney, loadAppointmentJourney, designerFromJourney,
  createAppointmentJourney, saveAppointmentSettings, publishAppointmentJourney,
  publicLinkFor, unmappedKeys, notYetLiveKeys, inertTextKeys,
} from "./alljourneys-appointments-seam";
import {
  EMPTY_AVAILABILITY, buildCatalog, loadAvailability, loadPublicCatalog,
  loadPublishedJourney, partOfDay, submitBooking,
} from "./appointment-data";
import {
  WALKIN_DEFAULT_SLUG, createWalkInJourney, designerFromWalkInJourney,
  listWalkInQueueOptions, loadWalkInJourney, loadWalkInQueues,
  publishWalkInJourney, resolveWalkInJourney, saveWalkInSettings, serviceOptionsOf,
  walkInBrandFrom, walkInConfigFromDesigner,
} from "./alljourneys-walkins";
import { WalkInJourney } from "./WalkInJourney";
import { LocationsMap } from "../locations/LocationsMap";

import { OneStopTab } from "./onestop-tab";
import { tzContext } from "../../lib/datetime";
import { toast } from "../../shell/toast";
import { publicHost, ensurePublicConfig } from "../../lib/public-url";
import { getTenantVocab, useTenantVocab } from "../../lib/tenant-vocab";
import {
  loadPublicEventsList, loadPublicEventDetail, submitEventRegistration as submitEventRegistrationOp,
  loadPublicEventsPage, loadEventsDocument, saveEventsDocument, publishEventsDocument,
  loadLandingDocument, createLandingDocument, updateLandingDocument, publishLandingDocument,
  loadEventPaymentConfig, downloadEventCalendar,
  EVENT_LANDING_DEFAULT_KEY, EVENTS_BLOCK_SEMANTIC_MAP, NEUTRAL_EVENTS_BLOCK_CONFIG, EVENTS_PAGE_SLUG,
  neutralLandingPresentationDefaults, projectPublicEventToDesignerRow,
  designerFieldsFromEventsBlockConfig, formatSpotsRemaining, formatEventPrice, newIdempotencyKey, civilDateInZone,
  type EventsBlockConfig, type EventsDocumentDraft, type LandingDraft, type DesignerEventRow,
} from "../../lib/events-public-data";
import type { EventListPresentation, EventLandingPresentation } from "@v0y4ge/api-contracts";

interface DesignerProps {
  tenant: string; tenantName?: string; brandColor?: string; publicHost?: string;
  vocab?: TenantWords; initialSection?: string;
  /** "public" mounts the customer page standalone: no editor, no Studio auth,
   *  published settings for `slug` rather than the tab's draft. */
  mode?: "preview" | "public";
  slug?: string;
  /** Which public journey this standalone mount renders. Defaults to
   *  "appointment" — the existing `PublicAppointment` behavior is unchanged
   *  when this prop is omitted. "events" is the events list route (`/events`);
   *  "landing" is one event's detail route (`/events/{eventId}`), which reads
   *  `eventId` below rather than `slug`. */
  journey?: "appointment" | "events" | "landing";
  /** The opaque public event id for `journey="landing"` (e.g. `cu-event-04`).
   *  Not a slug and never UUID-validated. */
  eventId?: string;
  /** Width the customer page should lay itself out for. A standalone page takes
   *  the browser's own width (the default); the Preview dialog passes the device
   *  the operator picked, so one mount can be shown at all three. */
  device?: "desktop" | "tablet" | "mobile";
}

/**
 * CD PREVIEW ONLY — same arrangement as `LEGACY_PREVIEW` in shell/screens.tsx.
 *
 * Deployed, Preview opens the journey's public link in a new tab, which is what
 * an operator wants and what the platform's `book` entry serves. CD's harness
 * cannot reach `book.<tenant>.jrni.ai`, so the tab would open on nothing. When
 * the harness sets `window.__CD_PREVIEW_DIALOG__ = true` the same customer page
 * is mounted in a dialog instead, at the three device widths.
 *
 * Read per click rather than at module load so the harness can set it late, and
 * never set in a production build — a deployed Studio keeps the new tab.
 */
function previewInDialog() {
  try { return typeof window !== "undefined" && (window as { __CD_PREVIEW_DIALOG__?: boolean }).__CD_PREVIEW_DIALOG__ === true; } catch (err) { return false; }
}
/** Nouns the header sub-lines and kiosk copy use.
 *
 *  TENANT-AGNOSTIC (2026-09-08). This file previously carried a `TENANT_WORDS`
 *  table and a `PUBLIC_HOST` table both keyed by REAL TENANT SLUGS, plus
 *  higher-ed defaults ("student", "campus", "adviser"). Three things wrong with
 *  that, and the platform's lint is red on all of them: a tenant that is not in
 *  the table gets another tenant's vocabulary; onboarding a tenant becomes a
 *  code change in a designer screen; and the defaults name one vertical, so
 *  every unlisted tenant reads as a university.
 *
 *  Nouns now come from the served vocabulary (lib/tenant-vocab) and hosts from
 *  lib/public-url, which resolves the tenant's real public config. The defaults
 *  below are deliberately GENERIC — the neutral word for the thing, not a guess
 *  at the industry. */
interface TenantWords { customer: string; customers: string; location: string; locations: string; staff: string; advising: string; events: string; }
/* Kiosk still stores designer settings only in the browser — do not announce a
   customer-facing save until its own contract lands. Appointments, Event List
   (`events`), Event Landing (`landing`) and now Walk-Ins (`queue`, through the
   typed admin journey door in alljourneys-walkins.ts) all write through real
   server contracts. */
const PD_STUB_ONLY: Record<string, true> = { kiosk: true };
const PD_STUB_REASON = "Publishing is not available for this page type yet. Changes here will not appear on your customer pages.";

/** `EventJourneyCommonPresentationShape` keys — shared by the list and
 *  landing presentation schemas (`packages/api-contracts/src/
 *  event-journey-presentation.ts`). One list, both pickers below. */
const EVENT_JOURNEY_COMMON_KEYS = [
  "progressType", "askEmail", "emailOptional", "askPhone", "phoneOptional",
  "askAddress", "addressOptional", "countryFlags", "customCountries",
  "ticketQuantity", "readOnly", "takePayment", "showQr", "addToCalendar",
  "allowCancelReg", "showResource", "showStaff", "embedWidthUnit",
  "embedWidth", "embedHeight", "embedResize",
];
const EVENT_LIST_PRESENTATION_KEYS = [
  ...EVENT_JOURNEY_COMMON_KEYS,
  "eventPaging", "eventDisplay", "cardCols", "eventImages", "showEventDate",
  "showEventTime", "showEventPrice", "showTimezone", "filterBar",
  "filterLocation", "filterType", "filterDates", "eventFilters", "notifPref",
  "notifDefault", "fileUpload", "maxAttachments", "skipSingleEventSlot",
  "customText",
];
const EVENT_LANDING_PRESENTATION_KEYS = [
  ...EVENT_JOURNEY_COMMON_KEYS,
  "showHeader", "showFooter", "landingTemplate", "heroHeight", "heroTreatment",
  "stickyBar", "coverImage", "blockOrder", "blocksOn", "soldOutMode",
  "waitlist", "relatedRule", "relatedCount", "relatedManual", "lpDescText",
  "lpAgendaRows", "lpSpeakerRows", "lpIncludedRows", "lpFaqRows",
  "lpSponsorRows", "sponsorTiers", "lpShareChannels", "customText",
];
/** Pick exactly the declared keys of a `.strict()` presentation schema out of
 *  the Designer's wider `s` state — the schema rejects unknown keys, and `s`
 *  carries every journey's fields on one object. */
function pickPresentationFields(s: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  keys.forEach((k) => { if (k in s) out[k] = s[k]; });
  return out;
}
/** Parses an event's stored bound (`start_at`/`end_at`) into a comparable
 *  number of minutes WITHOUT going through `new Date(string)`. These bounds
 *  are civil (wall-clock) date/times in the tenant's own display zone — the
 *  same zone `civilDateInZone`/`tzContext` work in — so handing an offsetless
 *  string to the Date constructor would reinterpret it in the VISITOR's
 *  browser zone, and on engines that reject the `YYYY-MM-DD HH:MM` spelling
 *  outright would make a perfectly valid schedule parse as NaN and be
 *  rejected here. An explicit `Z`/`±HH:MM` offset, when one IS present, is
 *  honoured so both bounds are normalised onto the same line before they are
 *  compared. */
function eventBoundMinutes(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*(Z|z|[+-]\d{2}:?\d{2})?$/.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
  const hour = Number(m[4]), minute = Number(m[5]), second = Number(m[6] || "0");
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;
  const utc = Date.UTC(year, month - 1, day, hour, minute, second);
  if (!isFinite(utc)) return null;
  // Round-trip check: rejects real-calendar impossibilities (31 February,
  // 29 February in a common year) that the range tests above let through.
  const back = new Date(utc);
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) return null;
  let offsetMinutes = 0;
  const zone = m[7];
  if (zone && zone !== "Z" && zone !== "z") {
    const digits = zone.slice(1).replace(":", "");
    const zoneHour = Number(digits.slice(0, 2)), zoneMinute = Number(digits.slice(2, 4));
    if (zoneHour > 23 || zoneMinute > 59) return null;
    offsetMinutes = (zone.charAt(0) === "-" ? -1 : 1) * (zoneHour * 60 + zoneMinute);
  }
  return utc / 60000 - offsetMinutes;
}
/** True only when the authoritative event detail carries exactly ONE valid
 *  one-off schedule: the contract's `event_type` is the literal `one_off`,
 *  both stored bounds are non-empty strings that actually parse as a
 *  date/time, and the end is strictly after the start — an inverted or
 *  zero-length range is not a schedule anyone can be registered into.
 *  Deliberately NOT derived from ticket-tier count, capacity, the number of
 *  rows on the list, or any synthetic slot — none of those is a schedule,
 *  and a multi-tier or sold-out event still has exactly one date and time. */
function hasSingleOneOffSchedule(detail: { event_type?: unknown; start_at?: unknown; end_at?: unknown } | null | undefined): boolean {
  if (!detail) return false;
  if (detail.event_type !== "one_off") return false;
  const startAt = detail.start_at;
  const endAt = detail.end_at;
  if (typeof startAt !== "string" || startAt.trim() === "") return false;
  if (typeof endAt !== "string" || endAt.trim() === "") return false;
  const start = eventBoundMinutes(startAt);
  const end = eventBoundMinutes(endAt);
  if (start === null || end === null) return false;
  return end > start;
}
/** Designer field name → registered `events` block config key (see
 *  `EVENTS_BLOCK_SEMANTIC_MAP` in events-public-data.ts). */
function eventsBlockConfigFromDesignerFields(s: Record<string, any>): EventsBlockConfig {
  const mapped: Record<string, unknown> = { ...NEUTRAL_EVENTS_BLOCK_CONFIG };
  Object.keys(EVENTS_BLOCK_SEMANTIC_MAP).forEach((designerKey) => {
    const blockKey = (EVENTS_BLOCK_SEMANTIC_MAP as Record<string, string>)[designerKey];
    if (designerKey in s) mapped[blockKey] = s[designerKey];
  });
  return mapped as unknown as EventsBlockConfig;
}
const VOCAB_DEFAULTS: TenantWords = { customer: "customer", customers: "customers", location: "location", locations: "locations", staff: "team member", advising: "appointments", events: "Events" };
/** Served nouns → the words this screen uses. Only what the vocabulary actually
 *  carries is taken; the rest stays generic rather than being invented. */
function wordsFromVocab(v: ReturnType<typeof getTenantVocab>): TenantWords {
  const c = v?.customers ?? null;
  return {
    ...VOCAB_DEFAULTS,
    ...(c?.entity ? { customer: c.entity } : {}),
    ...(c?.entityPlural ? { customers: c.entityPlural } : {}),
  };
}
/* TENANT_WORDS removed 2026-09-08 — see the note above. A
   `Record<string, …>` keyed by real tenant slugs is the exact shape the
   tenant-agnosticism lint refuses; nouns come from the served vocabulary. */

/* Prototype helmet CSS — the only rules that cannot be inline. */
const APD_CSS = `
    html, body { margin: 0; padding: 0; height: 100%; background: var(--jrni-color-surface-canvas); }
    a { color: var(--jrni-color-primary-1); text-decoration: none; }
    a:hover { color: var(--jrni-color-primary-1); text-decoration: underline; }
    button { font-family: inherit; }
    /* The zone's display name moves up beside the label, so the component's own line is redundant. */
    [data-jrni-component="timezone-select"] > div[id$="-selected"] { display: none; }
    @keyframes apd-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.8); } }
    .apd-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
    .apd-scroll::-webkit-scrollbar-thumb { background: var(--jrni-color-neutral-4); border-radius: 999px; border: 3px solid transparent; background-clip: content-box; }
    /* Events filter row — SCREEN-SCOPED geometry only, never a change to the
       shared design system. DatePicker is the one control in that row that
       cannot be aligned from the call site: it exposes no \`style\` prop and
       PAINTS a 4px trigger radius INLINE (DatePicker.tsx:256), while every
       sibling (Input, Dropdown) reads --jrni-radius-default (6px).

       \`!important\` is load-bearing and is the reason this is CSS at all: an
       inline style outranks every selector, however specific, so the first cut
       of these rules without it changed nothing — measured in the rendered
       page as a 4px DatePicker beside five 6px controls. This is the narrow
       case the keyword exists for, not a specificity shortcut.

       Both rules are keyed on the row's own class, so they reach nothing else
       on the screen and nothing outside it. The DURABLE fix is a radius token
       inside DatePicker — a CD change to jrni-ui, which is a docket, not a
       hand-edit of a CD-owned surface. */
    .apd-evt-filters [data-jrni-component="date-picker"] > button[aria-haspopup="dialog"] { border-radius: var(--jrni-radius-default) !important; }
    /* DatePicker stacks label+control at an inline gap of 4; Field (which wraps
       the Input and the Dropdown beside it) uses 6. Matching it lines the
       LABELS up as well as the control bottoms the row's align-items already
       handles. Same inline-style reason for the keyword. */
    .apd-evt-filters [data-jrni-component="date-picker"] { gap: 6px !important; }
    /* Browser QA: the Dropdown trigger draws a black inline focus outline while
       its siblings show the purple focus token. Screen-scoped correction; same
       inline-style reason for the keyword. */
    .apd-evt-filters button:focus-visible { outline: 1px solid var(--jrni-color-utility-focus) !important; outline-offset: 1px !important; }
    /* Row layout responds to the PREVIEW FRAME, not the viewport: the frame is a
       resizable device canvas inside the page, so a media query would read the
       wrong box. The frame declares container-type: inline-size. */
    .apd-evt-filters { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 12px; align-items: end; }
    .apd-evt-filters > * { min-width: 0; }
    .apd-evt-search { grid-column: 1 / -1; width: 100%; }
    @container journey-preview (min-width: 1100px) {
      .apd-evt-filters { grid-template-columns: minmax(0, 1.2fr) repeat(3, minmax(0, 1fr)) auto; }
      .apd-evt-search { grid-column: auto; width: auto; }
    }
    @container journey-preview (max-width: 540px) {
      .apd-evt-filters { grid-template-columns: minmax(0, 1fr); }
    }
  `;

const NS = () => UI as unknown as Record<string, any>;

/** The route this Attend mount is showing, parsed from a pathname.
 *
 *  Attend is a dedicated domain, so its ROOT is the event list and the first
 *  path segment is an event's opaque public id:
 *
 *    /                 -> list
 *    /{eventId}        -> that event's landing + registration
 *    /events           -> list        (compatibility alias, still served)
 *    /events/{eventId} -> that event  (compatibility alias, still served)
 *
 *  Anything deeper is `invalid` — NOT silently the list and NOT some other
 *  event, so a mistyped or truncated link renders the honest not-found state.
 *  `events` is the one reserved first segment this parser knows, because it is
 *  the alias above; every other reserved name (assets, embed, api…) never
 *  reaches this mount — the attend worker routes those itself, and inventing a
 *  second list of them here would be a policy guess that silently disagrees
 *  with the router's.
 *
 *  ONE definition, exported: `public-events.tsx` (the standalone mount) and
 *  `Designer.handlePublicEventsPopState` (back/forward inside the mount) both
 *  read it. Two copies of a route parser drifting apart is exactly how a link
 *  starts resolving differently on first load than on a back button. */
export interface PublicEventsRoute {
  /** "" for the list route; a non-empty opaque id for the detail route. */
  eventId: string;
  /** Too many segments, or an id segment that is not a decodable percent-escape. */
  invalid: boolean;
}

export function parsePublicEventsPath(pathname: string): PublicEventsRoute {
  const raw = String(pathname || "").split("/").filter(Boolean);
  let parts = raw;
  // Strip the compatibility alias prefix, so `/events/x` and `/x` are one case
  // below. A bare `/events` becomes `[]` — the list — rather than being read as
  // an event whose id happens to be the reserved word.
  if (raw.length > 0) {
    let first = "";
    try { first = decodeURIComponent(raw[0]); } catch { return { eventId: "", invalid: true }; }
    if (first === EVENTS_PAGE_SLUG) parts = raw.slice(1);
  }
  if (parts.length === 0) return { eventId: "", invalid: false };
  if (parts.length > 1) return { eventId: "", invalid: true };
  try {
    const eventId = decodeURIComponent(parts[0]);
    return eventId ? { eventId, invalid: false } : { eventId: "", invalid: true };
  } catch {
    // Malformed percent-escape (e.g. a truncated `%E0`) — a decode failure here
    // must not fall back to the list route or to whatever the raw, still-escaped
    // segment happens to collide with.
    return { eventId: "", invalid: true };
  }
}

/** The canonical Attend path for one event — `/{eventId}`.
 *
 *  Accepts whatever authority the server declared: a canonical `/{id}`, the
 *  legacy `/events/{id}` a pre-cutover row still carries, or a bare id. The
 *  server's `public_path` stays the authority for WHICH event; this only
 *  normalises the alias prefix off the front so the link we PRINT is the
 *  canonical one. Idempotent, so it is a no-op once rows carry `/{id}`. */
function canonicalEventPath(publicPathOrId: string): string {
  const parts = String(publicPathOrId || "").split("/").filter(Boolean);
  if (parts.length === 0) return "";
  if (parts[0] === EVENTS_PAGE_SLUG) return parts.length > 1 ? "/" + parts.slice(1).join("/") : "";
  return "/" + parts.join("/");
}

/** Split a configured public authority into scheme (if it declared one) and
 *  host — PORT INCLUDED. The port is the whole point: a local Attend runs on
 *  `localhost:18811`, and the first cut printed `http://localhost/events`
 *  because the authority was truncated to a bare hostname somewhere between
 *  config and link. Nothing here invents a port: if the configured authority
 *  carries none, none is printed, because a port cannot be inferred from
 *  Studio's own origin (Studio and Attend are different ports on the same
 *  local hostname — inheriting Studio's would produce a confidently wrong
 *  link rather than an obviously incomplete one). */
function splitPublicAuthority(configured: string): { scheme: string; host: string } {
  const raw = String(configured || "").trim();
  if (!raw) return { scheme: "", host: "" };
  const m = raw.match(/^(https?:)\/\/(.*)$/i);
  const scheme = m ? m[1].toLowerCase() : "";
  const rest = m ? m[2] : raw.replace(/^\/\//, "");
  // Authority only — drop any path/query/hash the config happened to carry.
  const host = rest.split(/[/?#]/)[0];
  return { scheme, host };
}

const DEFAULTS = {
  multiService: false, firstStep: "service",
  skipService: true, skipLocation: true, showHeader: true, showFooter: false,
  staffSelect: false, reschedule: "same", errBookStaff: true, errOtherServices: true,
  progressType: "titles",
  showCategories: false, showServiceImages: true, serviceDisplay: "list", showDuration: true, showPrice: true,
  nextAvailable: true, quickbook: false, extraLocationInfo: false, loadMore: false,
  initialCount: 2, radius: 0, radiusUnit: "auto", showMap: true, mapHeight: 190,
  calendarLayout: "month", slotGrouping: "partofday", showTimezone: true, defaultDate: "today",
  askEmail: true, emailOptional: false, askPhone: true, phoneOptional: false, askAddress: false, addressOptional: true,
  countryFlags: true, customCountries: ["US", "MX"],
  notifPref: true, notifDefault: "both", attendees: true, attendeeFields: ["first", "last", "email"],
  fileUpload: true, maxAttachments: 5, requiredDocs: [], readOnly: true,
  showStaff: true, showResource: true, addToCalendar: true, allowReschedule: true, allowCancel: true,
  refundMode: "minusFee",
  showQr: false, takePayment: false, partySize: false, maxParty: 6, allowRescheduleReg: false,
  slug: "advising", embedWidthUnit: "auto", embedWidth: 960, embedHeight: 760,
  embedResize: "fixed",
  brandColor: "#5142C7", radiusScale: 6, logo: "",
  language: "en-US",
};

const SECTION_KEYS = {
  general: ["firstStep", "skipService", "skipLocation", "showHeader", "showFooter", "staffSelect", "reschedule", "errBookStaff", "errOtherServices"],
  progress: ["progressType"],
  service: ["showCategories", "showServiceImages", "serviceDisplay", "showDuration", "showPrice"],
  location: ["nextAvailable", "quickbook", "extraLocationInfo", "loadMore", "initialCount", "radius", "radiusUnit", "showMap", "mapHeight"],
  datetime: ["calendarLayout", "slotGrouping", "showTimezone", "defaultDate"],
  details: ["askEmail", "emailOptional", "askPhone", "phoneOptional", "askAddress", "addressOptional", "countryFlags", "customCountries", "notifPref", "notifDefault", "attendees", "attendeeFields", "maxParty", "requiredDocs", "fileUpload", "maxAttachments", "readOnly", "takePayment"],
  confirmation: ["showStaff", "showResource", "addToCalendar", "allowReschedule", "allowCancel", "refundMode", "showQr"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedHeight", "embedResize"],
};

const PANELS = {
  general: ["General", ""],
  progress: ["Progress bar", ""],
  service: ["Service step", ""],
  location: ["Location step", ""],
  datetime: ["Date & time step", ""],
  details: ["Details step", ""],
  confirmation: ["Confirmation step", ""],
  customtext: ["Custom text", ""],
  launch: ["Launch & embed", ""],
};

const PLACES = [
  { main: "48 Warwick St", secondary: "Lakeview, TX 78701, USA" },
  { main: "480 Warwick Crossing", secondary: "Lakeview, TX 78701, USA" },
  { main: "1200 Grayson Rd", secondary: "Lakeview, TX 78704, USA" },
  { main: "1208 Grayson Ct", secondary: "Lakeview, TX 78704, USA" },
  { main: "36 Ellery Ave", secondary: "Lakeview, TX 78702, USA" },
  { main: "914 Bramley Dr", secondary: "Lakeview, TX 78729, USA" },
  { main: "5 Kestrel Way", secondary: "Lakeview, TX 78741, USA" },
  { main: "1801 Pennard St", secondary: "Lakeview, TX 78748", },
  { main: "1408 Coronado Ave NW", secondary: "Albuquerque, NM 87104, USA" },
  { main: "77 Marlow Rd NE", secondary: "Atlanta, GA 30303, USA" },
  { main: "2250 Guadalupe St", secondary: "Austin, TX 78705, USA" },
  { main: "700 Congress Ave", secondary: "Austin, TX 78701, USA" },
];

const DIAL = [
  { iso: "US", dial: "1", flag: "\uD83C\uDDFA\uD83C\uDDF8", name: "United States", min: 10, max: 10 },
  { iso: "CA", dial: "1", flag: "\uD83C\uDDE8\uD83C\uDDE6", name: "Canada", min: 10, max: 10 },
  { iso: "MX", dial: "52", flag: "\uD83C\uDDF2\uD83C\uDDFD", name: "Mexico", min: 10, max: 10 },
  { iso: "GB", dial: "44", flag: "\uD83C\uDDEC\uD83C\uDDE7", name: "United Kingdom", min: 10, max: 10 },
  { iso: "DE", dial: "49", flag: "\uD83C\uDDE9\uD83C\uDDEA", name: "Germany", min: 10, max: 11 },
  { iso: "FR", dial: "33", flag: "\uD83C\uDDEB\uD83C\uDDF7", name: "France", min: 9, max: 9 },
  { iso: "IN", dial: "91", flag: "\uD83C\uDDEE\uD83C\uDDF3", name: "India", min: 10, max: 10 },
  { iso: "CN", dial: "86", flag: "\uD83C\uDDE8\uD83C\uDDF3", name: "China", min: 11, max: 11 },
  { iso: "BR", dial: "55", flag: "\uD83C\uDDE7\uD83C\uDDF7", name: "Brazil", min: 10, max: 11 },
  { iso: "AU", dial: "61", flag: "\uD83C\uDDE6\uD83C\uDDFA", name: "Australia", min: 9, max: 9 },
  { iso: "NG", dial: "234", flag: "\uD83C\uDDF3\uD83C\uDDEC", name: "Nigeria", min: 10, max: 10 },
  { iso: "JP", dial: "81", flag: "\uD83C\uDDEF\uD83C\uDDF5", name: "Japan", min: 10, max: 10 },
];

const CANCEL_CUTOFF_HRS = 24;
const CANCEL_FEE = "$15";

const ZONES = {
  eastern: { label: "Eastern Time (GMT\u221204:00)", short: "Eastern Time" },
  central: { label: "Central Time (GMT\u221205:00)", short: "Central Time" },
  mountain: { label: "Mountain Time (GMT\u221206:00)", short: "Mountain Time" },
  pacific: { label: "Pacific Time (GMT\u221207:00)", short: "Pacific Time" },
  alaska: { label: "Alaska Time (GMT\u221208:00)", short: "Alaska Time" },
  hawaii: { label: "Hawaii Time (GMT\u221210:00)", short: "Hawaii Time" },
};
const ZONE_ORDER = ["eastern", "central", "mountain", "pacific", "alaska", "hawaii"];

/* An empty catalogue is a REAL state, not a failure: a tenant that offers
 * nothing must look like it, and the public mount starts with every binding
 * emptied on purpose. `renderVals` reads the current row's fields
 * unconditionally, so each list has a row-shaped blank to fall back to —
 * otherwise the first render before the catalogue read lands throws on
 * `undefined.name` and the boundary eats the whole screen. */
const EMPTY_SERVICE = { id: "", name: "", desc: "", cat: "general", dur: "", price: "", window: 60, locs: [], questions: [] };
const EMPTY_LOCATION = { id: "", name: "", addr: "", dist: "", tz: "eastern", next: "", hours: "", phone: "" };
const EMPTY_STAFF = { id: "", name: "" };
const EMPTY_DURATION = { id: "", label: "", sub: "" };
const EMPTY_EVENT = { id: "", name: "", desc: "", addr: "", date: "", time: "", when: "", venue: "", cat: "", left: 0, cap: 0, maxPer: 1, price: "Free", hue: "" };

/* THE CATALOGUE IS THE TENANT'S OR IT IS NOTHING (2026-09-09, CD item 1).
 *
 * These four lists were higher-ed FIXTURES — six services, nine branches, three
 * advisers, two durations. They were designer-only until the release that serves
 * this screen's bundle on the PUBLIC booking host, at which point the literals
 * compiled into a bundle a customer downloads, and three of the six service names
 * happened to match the test tenant's real ones. That is what made the fixture
 * look like tenant data: the designer preview rendered "Course planning and a
 * degree audit with your assigned adviser" while the live booking page rendered
 * "Meet with an academic adviser to plan your courses" — two sources, not one
 * source twice. Renaming the three obviously-invented rows would have left the
 * other three lying more convincingly.
 *
 * So they start EMPTY, in every mode, and the only thing that fills them is a read
 * of this tenant: `loadPreviewCatalog` in the designer (services · locations ·
 * staff · durations, from the same choke points the tenant's own surfaces read)
 * and `loadPublicCatalog` on the published page. A tenant that offers nothing, or
 * a read that is refused, now draws nothing — the honest state, and the one the
 * EMPTY_* row-blanks above exist to make renderable on the first frame.
 *
 * Do not reintroduce sample rows here. A designer canvas with no catalogue is a
 * tenant-shaped problem to fix upstream, not a gap to fill with invented copy;
 * if the local preview needs something to draw, it belongs in the harness seed
 * layer (which never ships) and not in this module. */
/* `any[]` and not `PreviewService[]` etc: two reads fill these and they disagree
   on row type — `loadPreviewCatalog` returns the typed `Preview*` rows, while
   `loadPublicCatalog` returns `PublicCatalogRead`, whose lists are `Dict[]`. An
   empty literal on its own would infer `never[]` and refuse both. This is the same
   looseness the fixtures had; narrowing it means one row type across both reads,
   which is a seam change and not this pass. */
let SERVICES: any[] = [];
let DURATIONS: any[] = [];
let LOCATIONS: any[] = [];
let STAFF: any[] = [];


const SLOTS = {
  morning: ["9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:20 AM"],
  afternoon: ["1:00 PM", "1:30 PM", "2:20 PM", "3:00 PM", "3:40 PM"],
  evening: ["5:00 PM", "5:40 PM"],
};


const CT_GROUPS = [
  { id: "progress", step: "service", label: "Progress bar", fields: [
    { id: "svc", label: "Service step", def: "What" },
    { id: "loc", label: "Location step", def: "Where" },
    { id: "dt", label: "Date & time step", def: "When" },
    { id: "pd", label: "Personal details step", def: "Your info" },
    { id: "conf", label: "Confirmation step", def: "Confirmation" },
    { id: "cta", label: "Continue button", def: "Continue" },
    { id: "sum", label: "Summary header", def: "Your appointment" },
    { id: "page", label: "Page title in the header", def: "Book an appointment" },
  ] },
  { id: "service", step: "service", label: "Service step", fields: [
    { id: "cat", label: "Category header", def: "Select a category" },
    { id: "hdr", label: "Service header", def: "Select a service" },
    { id: "info", label: "Service info text", def: "Not shown unless you add text" },
    { id: "dur", label: "Duration header", def: "Select a duration" },
  ] },
  { id: "location", step: "location", label: "Location step", fields: [
    { id: "hdr", label: "Location search header", def: "Select location" },
    { id: "hours", label: "Opening hours label", def: "Branch hours" },
    { id: "nosrv", label: "Not serviceable message", def: "This area is not currently serviceable." },
  ] },
  { id: "datetime", step: "datetime", label: "Date & time step", fields: [
    { id: "date", label: "Date selection header", def: "Select a date" },
    { id: "time", label: "Timeslot selection header", def: "Select a time" },
  ] },
  { id: "details", step: "details", label: "Personal details step", fields: [
    { id: "hdr", label: "Page header", def: "Enter your details" },
    { id: "upl", label: "Document upload header", def: "Document upload" },
    { id: "uplin", label: "Document upload instructions", def: "PNG, JPG, DOC and PDF supported. Max file size 20 MB." },
    { id: "chdr", label: "Consent header", def: "Data protection policy" },
    { id: "ctext", label: "Consent text", def: "I consent to my data being used for the purposes of this booking." },
    { id: "phdr", label: "Payment header", def: "Payment" },
    { id: "pinfo", label: "Payment info text", def: "Not shown unless you add text" },
  ] },
  { id: "confirmation", step: "confirmation", label: "Confirmation step", fields: [
    { id: "msg", label: "Confirmation message", def: "Thanks Jordan! Your appointment is confirmed" },
    { id: "flaghdr", label: "Info-flag header", def: "Appointment confirmed" },
    { id: "flagtxt", label: "Info-flag text", def: "You will receive a confirmation e-mail shortly and may close this window" },
  ] },
  { id: "error", step: "confirmation", label: "Link error page", fields: [
    { id: "ttl", label: "Heading", def: "This booking link is no longer available" },
    { id: "body", label: "Body text", def: "The adviser or service behind this link is not taking appointments at the moment." },
    { id: "staffttl", label: "Another-adviser option", def: "Book with another adviser" },
    { id: "staffsub", label: "Another-adviser detail", def: "Same service, next available adviser" },
    { id: "svcttl", label: "Other-services option", def: "Explore other services" },
    { id: "svcsub", label: "Other-services detail", def: "Services open for booking today" },
    { id: "start", label: "Start-again button", def: "Start again from the beginning" },
  ] },
  { id: "cancel", step: "confirmation", label: "Cancellation", fields: [
    { id: "dlgttl", label: "Confirm dialog title", def: "Cancel this appointment?" },
    { id: "keep", label: "Keep button", def: "Keep appointment" },
    { id: "confirm", label: "Confirm button", def: "Cancel appointment" },
    { id: "msg", label: "Cancelled message", def: "This appointment is cancelled" },
    { id: "flaghdr", label: "Cancelled flag header", def: "Appointment cancelled" },
    { id: "flagtxt", label: "Cancelled flag text", def: "A cancellation e-mail is on its way. Nothing else is needed from you." },
    { id: "again", label: "Book again button", def: "Book a new appointment" },
  ] },
];

const QUEUE_ONLY = {
  queueLocationStep: false,
  capacity: 40, closeWhenFull: true,
  remoteJoin: true, arrivalWindow: 10,
  liveWaitPerService: true,
  showTicketNumber: true, showPosition: true, waitDisplay: "range",
  notifyWhenClose: true, notifyAhead: 2,
  allowLeaveQueue: true, showDesk: true,
  showQr: true, partySize: true, maxParty: 6,
  slug: "walk-in",
};

const QUEUE_DEFAULTS = Object.assign({}, DEFAULTS, {
  showCategories: true, showDuration: false, showPrice: false,
  askEmail: false, askPhone: true, notifDefault: "sms", notifPref: false,
  attendees: false, fileUpload: false, readOnly: false,
  showFooter: false,
}, QUEUE_ONLY);

const ALL_DEFAULTS = Object.assign({}, DEFAULTS, QUEUE_ONLY, {
  eventsPerPage: 6, eventPaging: "pages", eventDisplay: "grid", cardCols: 3,
  eventImages: true, showEventDate: true, showEventTime: true, showEventDesc: true,
  showEventVenue: true, showRemaining: false, showEventPrice: false,
  filterBar: true, filterLocation: true, filterType: true, filterDates: true,
  eventFilters: false, sortOrder: "date", showPastEvents: false,
  skipSingleEventSlot: false,
  showSoldOut: true, waitlist: true,
  ticketQuantity: true, maxTickets: 4, attendeeNames: false,
  showQr: true, allowCancelReg: true, allowRescheduleReg: true,
  takePayment: false, partySize: false, maxParty: 6,
  // Kiosk-only keys — KIOSK_DEFAULTS is declared further down, so the setter list needs them here.
  kioskAppts: true, kioskWalkin: true, kioskEvents: true, kioskCheckin: true,
  kioskWaitTime: true, kioskNextSlot: true, kioskHours: true,
  kioskBigText: true, kioskGreeting: "time", kioskTileSize: "large",
  kioskLocation: "l1",
  ckQr: true, ckWallet: true, ckNfc: true, ckRef: true, ckName: true, ckDefault: "scan",
  ckDoneDwell: 6, kioskResetAfter: 45,
  ckShowQueuePos: true, kioskLocation: "l1",
});

const QUEUE_SECTION_KEYS = {
  general: ["showHeader", "showFooter"],
  progress: ["progressType"],
  service: ["showCategories", "showServiceImages", "serviceDisplay", "liveWaitPerService", "queueLocationStep"],
  location: ["showDesk"],
  datetime: [],
  details: ["askEmail", "emailOptional", "askPhone", "phoneOptional", "askAddress", "addressOptional", "countryFlags", "customCountries", "partySize", "maxParty", "readOnly", "takePayment"],
  confirmation: ["showTicketNumber", "showPosition", "waitDisplay", "notifyWhenClose", "notifyAhead", "allowLeaveQueue", "showDesk", "showQr"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedHeight", "embedResize"],
};

const QUEUE_PANELS = {
  general: ["General", ""],
  service: ["What they need", ""],
  location: ["Service point", ""],
  details: ["Details step", ""],
  confirmation: ["Place in line", ""],
};

const QUEUE_CT_GROUPS = [
  { id: "progress", label: "Progress bar", fields: [
    { id: "svc", label: "Service step", def: "What" },
    { id: "loc", label: "Service point step", def: "Where" },
    { id: "pd", label: "Personal details step", def: "Your info" },
    { id: "conf", label: "Queue step", def: "Your place in line" },
    { id: "cta", label: "Continue button", def: "Continue" },
  ] },
  { id: "service", label: "Service step", fields: [
    { id: "hdr", label: "Service header", def: "What do you need help with?" },
    { id: "info", label: "Service info text", def: "Not shown unless you add text" },
    { id: "wait", label: "Wait time prefix", def: "Wait about" },
  ] },
  { id: "location", label: "Service point step", fields: [
    { id: "hdr", label: "Service point header", def: "Choose a service point" },
  ] },
  { id: "details", label: "Personal details step", fields: [
    { id: "hdr", label: "Page header", def: "Enter your details" },
    { id: "cta", label: "Join button", def: "Join the queue" },
    { id: "chdr", label: "Consent header", def: "Data protection policy" },
    { id: "ctext", label: "Consent text", def: "I consent to my data being used for the purposes of this visit." },
  ] },
  { id: "queue", label: "Place in line", fields: [
    { id: "hdr", label: "Queue confirmation message", def: "You're in the queue" },
    { id: "pos", label: "Position label", def: "Position in line" },
    { id: "wait", label: "Estimated wait label", def: "Estimated wait" },
    { id: "notify", label: "Notification note", def: "We'll text you when you're next to be seen." },
    { id: "leave", label: "Leave queue button", def: "Leave the queue" },
    { id: "closed", label: "Queue full message", def: "The line is full for today. Please come back tomorrow." },
  ] },
];

const QUEUE_CT_PREFILL = {
  "queue.hdr": "You're in line — welcome to Lakeview",
  "details.cta": "Join the walk-in line",
};

const QUEUE_DESKS_ON_DUTY = 3;
const QUEUE_AVG_MIN = 6;

const LANDING_BLOCKS = [
  { id: "desc", label: "Description and agenda" },
  { id: "speakers", label: "Speakers and hosts" },
  { id: "included", label: "What's included" },
  { id: "tickets", label: "Ticket types and prices" },
  { id: "spaces", label: "Spaces remaining" },
  { id: "map", label: "Map and directions" },
  { id: "faq", label: "FAQ" },
  { id: "related", label: "Related events" },
  { id: "sponsors", label: "Sponsor logos" },
  { id: "share", label: "Share buttons" },
];

const LANDING_INCLUDED = ["Campus tour with a student host", "Lunch in the quad", "Resource fair access", "Parking in Lot C"];

const LANDING_FAQ = [
  { q: "Can I bring family?", a: "Yes \u2014 up to two guests per student, added as extra places during registration." },
  { q: "Is parking included?", a: "Lot C is free for the day. Bring your registration e-mail or the QR code." },
  { q: "What if I need to cancel?", a: "Cancel from the confirmation e-mail any time before the event starts." },
];










const SPONSOR_TIERS = ["Gold sponsor", "Silver sponsor", "Bronze sponsor", "Supporter"];

const SHARE_CHANNELS = [
  { id: "copy", label: "Copy link" },
  { id: "email", label: "E-mail" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "x", label: "X" },
  { id: "whatsapp", label: "WhatsApp" },
];

const LANDING_ONLY = {
  landingTemplate: "banner",
  heroHeight: 280, heroTreatment: "image",
  stickyBar: true, coverImage: "",
  blockOrder: ["desc", "speakers", "included", "tickets", "spaces", "map", "faq", "related", "sponsors", "share"],
  // Authored fixture content removed 2026-09-10; empty until real event data or a tenant author fills it.
  lpDescText: "",
  lpAgendaRows: [],
  lpSpeakerRows: [],
  lpIncludedRows: [],
  lpFaqRows: [],
  lpSponsorRows: [],
  sponsorTiers: true,
  lpShareChannels: ["copy", "email", "linkedin"],
  relatedRule: "category", relatedCount: 3, relatedManual: [],
  blocksOn: ["desc", "speakers", "included", "tickets", "spaces", "map", "faq", "related", "share"],
  soldOutMode: "waitlist",
};

// The setter factory is built from ALL_DEFAULTS, so landing keys must be in it.

const SERVICE_WAITS = { advising: "12–18 min", aid: "25 min", career: "8 min", tour: "—", registrar: "5 min", wellness: "20 min" };

const EVENT_ONLY = {
  eventsPerPage: 6, eventPaging: "pages", eventDisplay: "grid", cardCols: 3,
  eventImages: true, showEventDate: true, showEventTime: true, showEventDesc: true,
  showEventVenue: true, showRemaining: false, showEventPrice: false,
  filterBar: true, filterLocation: true, filterType: true, filterDates: true,
  eventFilters: false, sortOrder: "date", showPastEvents: false,
  skipSingleEventSlot: false,
  showSoldOut: true, waitlist: true,
  ticketQuantity: true, maxTickets: 4, attendeeNames: false, attendees: false,
  showQr: true, allowCancelReg: true, allowRescheduleReg: true, showStaff: true,
  fileUpload: false, maxAttachments: 3, showTimezone: true,
  slug: "events", embedHeight: 900,
};

const EVENT_DEFAULTS = Object.assign({}, DEFAULTS, {
  askEmail: true, askPhone: true, phoneOptional: true, readOnly: false,
  attendees: false, fileUpload: false, notifDefault: "email",
  showFooter: true,
}, EVENT_ONLY);

const EVENT_SECTION_KEYS = {
  general: ["sortOrder", "showPastEvents", "skipSingleEventSlot", "showHeader", "showFooter"],
  progress: ["progressType"],
  events: ["eventsPerPage", "eventPaging", "eventDisplay", "cardCols", "eventImages", "showEventDesc", "showEventDate", "showEventTime", "showEventVenue", "showRemaining", "showEventPrice", "showSoldOut", "waitlist", "filterBar", "filterLocation", "filterType", "filterDates", "showTimezone", "eventFilters"],
  details: ["askEmail", "emailOptional", "askPhone", "phoneOptional", "askAddress", "addressOptional", "countryFlags", "customCountries", "notifPref", "notifDefault", "ticketQuantity", "readOnly", "fileUpload", "maxAttachments", "takePayment"],
  confirmation: ["showQr", "addToCalendar", "allowCancelReg", "showResource", "showStaff"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedHeight", "embedResize"],
};



Object.assign(ALL_DEFAULTS, LANDING_ONLY);



const LANDING_DEFAULTS = Object.assign({}, DEFAULTS, {
  askEmail: true, askPhone: true, phoneOptional: true, readOnly: false,
  attendees: false, fileUpload: false, notifDefault: "email",
  showFooter: true, allowCancelReg: true, allowRescheduleReg: false, showQr: true,
  slug: "orientation", embedHeight: 1200,
}, EVENT_ONLY, LANDING_ONLY);

const LANDING_SECTION_KEYS = {
  general: ["landingTemplate", "heroHeight", "heroTreatment", "stickyBar", "coverImage", "showHeader", "showFooter"],
  landing: ["blockOrder", "blocksOn", "soldOutMode", "waitlist", "relatedRule", "relatedCount", "relatedManual"],
  details: ["askEmail", "emailOptional", "askPhone", "phoneOptional", "askAddress", "addressOptional", "countryFlags", "customCountries", "ticketQuantity", "readOnly", "takePayment"],
  confirmation: ["showQr", "addToCalendar", "allowCancelReg", "showResource", "showStaff"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedResize", "embedHeight"],
};

const LANDING_PANELS = {
  general: ["Page layout", ""],
  landing: ["Content blocks", ""],
  details: ["Your details", ""],
  confirmation: ["Confirmation", ""],
};

const EVENT_PANELS = {
  general: ["General", ""],
  events: ["Events list", ""],
  details: ["Your details", ""],
  confirmation: ["Confirmation", ""],
};

const EVENT_CT_GROUPS = [
  { id: "progress", label: "Progress bar", fields: [
    { id: "svc", label: "Events step", def: "Events" },
    { id: "pd", label: "Personal details step", def: "Your info" },
    { id: "conf", label: "Confirmation step", def: "Confirmation" },
    { id: "cta", label: "Continue button", def: "Continue" },
  ] },
  { id: "events", label: "Events list", fields: [
    { id: "hdr", label: "List header", def: "Select an event" },
    { id: "info", label: "List info text", def: "Not shown unless you add text" },
    { id: "remaining", label: "Spaces remaining label", def: "spaces left" },
    { id: "soldout", label: "Fully booked label", def: "Fully booked" },
    { id: "waitlist", label: "Waiting list button", def: "Join the waiting list" },
    { id: "empty", label: "No events message", def: "There are no events to show right now." },
  ] },
  { id: "details", label: "Personal details step", fields: [
    { id: "hdr", label: "Page header", def: "Enter your details" },
    { id: "cta", label: "Register button", def: "Complete registration" },
    { id: "tickets", label: "Ticket quantity label", def: "How many places?" },
    { id: "upl", label: "Document upload header", def: "Document upload" },
    { id: "uplin", label: "Document upload instructions", def: "PNG, JPG, DOC and PDF supported. Max file size 20 MB." },
    { id: "phdr", label: "Payment header", def: "Payment" },
    { id: "pinfo", label: "Payment info text", def: "Not shown unless you add text" },
    { id: "chdr", label: "Consent header", def: "Data protection policy" },
    { id: "ctext", label: "Consent text", def: "I consent to my data being used for the purposes of this registration." },
  ] },
  { id: "confirmation", label: "Confirmation step", fields: [
    { id: "msg", label: "Confirmation message", def: "Thanks Jordan! You're registered" },
    { id: "flaghdr", label: "Info-flag header", def: "Registration confirmed" },
    { id: "flagtxt", label: "Info-flag text", def: "Your ticket is below and a copy is on its way by email" },
    { id: "cnclreg", label: "Cancel button", def: "Cancel registration" },
    { id: "keepreg", label: "Keep-my-place button", def: "Keep my place" },
    { id: "cnclttl", label: "Cancel dialog title", def: "Cancel this registration?" },
    { id: "cnclmsg", label: "Cancelled message", def: "This registration is cancelled" },
    { id: "cnclhdr", label: "Cancelled flag header", def: "Registration cancelled" },
    { id: "cncltxt", label: "Cancelled flag text", def: "Your place has been released. A confirmation e-mail is on its way." },
    { id: "backev", label: "Back-to-events button", def: "Back to the events list" },
  ] },
];

const EVENT_CT_PREFILL = {
  "events.hdr": "Upcoming events",
  "details.cta": "Reserve my place",
};

/* ═══ Kiosk — lobby entry page on a wall-mounted tablet, landscape ════════════════
   The entry screen IS the resting state: there is no separate attract loop, so anything
   personal has to be cleared on a timer rather than waiting for the next visitor to notice. */
const KIOSK_DEFAULTS = Object.assign({}, DEFAULTS, {
  showHeader: true, showFooter: false, progressType: "none",
  kioskAppts: true, kioskWalkin: true, kioskEvents: true, kioskCheckin: true,
  kioskWaitTime: true, kioskNextSlot: true, kioskHours: true,
  kioskBigText: true,
  kioskGreeting: "time", kioskTileSize: "large",
  // A kiosk is bolted to one wall, so the location is a property of the unit, not a choice
  // the visitor makes. Everything it hands off to inherits it.
  kioskLocation: "l1",
  ckQr: true, ckWallet: true, ckNfc: true, ckRef: true, ckName: true,
  ckDefault: "scan",
  ckDoneDwell: 6, kioskResetAfter: 45,
  ckShowQueuePos: true,
  slug: "lobby-kiosk", embedWidthUnit: "fixed", embedWidth: 1280, embedHeight: 800,
});

const KIOSK_SECTION_KEYS = {
  general: ["showHeader", "showFooter", "kioskLocation", "kioskGreeting", "kioskResetAfter", "kioskBigText"],
  kioskentry: ["kioskAppts", "kioskWalkin", "kioskEvents", "kioskCheckin", "kioskTileSize", "kioskWaitTime", "kioskNextSlot", "kioskHours"],
  kioskcheckin: ["ckQr", "ckWallet", "ckNfc", "ckRef", "ckName", "ckDefault", "ckShowQueuePos", "ckDoneDwell"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedHeight", "embedResize"],
};

const KIOSK_PANELS = {
  general: ["General", "How the kiosk behaves between visitors, and who it has to serve."],
  kioskentry: ["Entry screen", "The four destinations and the live information around them."],
  kioskcheckin: ["Check in", "How someone with a booking identifies themselves, and what they see afterwards."],
  customtext: ["Custom text", ""],
  launch: ["Launch & embed", ""],
};

const KIOSK_CT_GROUPS = [
  { id: "entry", label: "Entry screen", fields: [
    { id: "greet", label: "Greeting", def: "Good morning" },
    { id: "sub", label: "Line under the greeting", def: "What do you need today?" },
    { id: "appts", label: "Appointments tile", def: "Book an appointment" },
    { id: "apptsSub", label: "Appointments tile description", def: "Advising, financial aid, careers and more" },
    { id: "walkin", label: "Walk-in tile", def: "See someone today" },
    { id: "walkinSub", label: "Walk-in tile description", def: "Join the walk-in line at this campus" },
    { id: "events", label: "Events tile", def: "What's on at Lakeview" },
    { id: "eventsSub", label: "Events tile description", def: "Orientation, fairs and workshops" },
    { id: "ck", label: "Check-in tile", def: "I'm here for my appointment" },
    { id: "ckSub", label: "Check-in tile description", def: "Scan your pass or find your booking" },
    { id: "page", label: "Title in the purple header", def: "Welcome" },
  ] },
  { id: "checkin", label: "Check-in screen", fields: [
    { id: "hdr", label: "Check-in heading", def: "Let's find your booking" },
    { id: "scan", label: "Scan instruction", def: "Hold your QR code or wallet pass up to the reader" },
    { id: "refHdr", label: "Booking reference heading", def: "Enter your booking reference" },
    { id: "nameHdr", label: "Name search heading", def: "Find your booking by name" },
    { id: "done", label: "Checked-in message", def: "You're checked in" },
    { id: "doneSub", label: "Line under the checked-in message", def: "Please take a seat — someone will call your name." },
  ] },
  { id: "problems", label: "When something goes wrong", fields: [
    { id: "noMatch", label: "Scan not recognised", def: "We couldn't read that pass" },
    { id: "noMatchSub", label: "Scan not recognised — what to do", def: "Try holding it flatter and a little closer, or find your booking by name instead." },
    { id: "already", label: "Already checked in", def: "You're already checked in" },
    { id: "cancelled", label: "Booking cancelled", def: "This booking was cancelled" },
    { id: "noCamera", label: "Reader unavailable", def: "The reader isn't working" },
    { id: "offline", label: "Offline", def: "This kiosk is offline" },
    { id: "offlineSub", label: "Offline — what to do", def: "Please check in at the welcome desk. Your appointment is still booked." },
  ] },
];

const KIOSK_CT_PREFILL = {
  "entry.sub": "What do you need today?",
};

/* The kiosk shows back only what the check-in lookup returned for a typed code
   (lookupPublicCheckIn, already camelized by the client). There is no sample
   booking: with no lookup result the kiosk stays neutral. */
const KIOSK_ARRIVED_STATUSES = ["checked_in", "arrived", "completed"];
const KIOSK_CANCELLED_STATUSES = ["cancelled", "canceled"];
function kioskBookingFromLookup(code, found) {
  const d = found || {};
  const type = d.resultType || d.result_type || "";
  if (type === "booking_found") {
    const b = d.booking || {};
    const start = b.startUtc ? new Date(String(b.startUtc)) : null;
    return {
      ref: code, kind: "appointment", pos: 0,
      name: String(b.customerName || ""), svc: String(b.serviceName || ""), staff: String(b.staffName || ""),
      when: start && !isNaN(start.getTime()) ? start.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }) : "",
      status: String(b.status || ""),
    };
  }
  if (type === "queue_found") {
    const e = d.entry || {};
    // A walk-in has no slot, so the line position is the only thing it can be told.
    return {
      ref: code, kind: "walkin", pos: Number(e.position) || 0,
      name: String(e.customerName || ""), svc: String(e.queueName || ""), staff: "", when: "",
      status: String(e.status || ""),
    };
  }
  return null;
}

export class Designer extends React.Component<DesignerProps, any> {
  kioskLocationKey = "";
  /** Bumped whenever the kiosk's lookup result stops applying (reference edited, kiosk reset). */
  ckLookupGen = 0;
  dwellTimer: any; qTimer: any; _onResize: any; _onPopState: any; _catalogAbort: any; _gone = false;
  /** Which read populated the wizard's catalogue: the public one always wins. */
  catalogSource: "admin" | "public" = "admin";
  /** The authored `events` block config for the current public Events Page
   *  read (or the neutral defaults) — set once per `bootstrapPublicEventsList`. */
  eventsPageBlock: EventsBlockConfig | null = null;
  eventsPagePresentation: unknown = null;
  /** The authored `skipSingleEventSlot` captured straight off the public
   *  page's `eventJourney`, NOT read back off `state.s` at click time: the
   *  selected-event load merges the event's own `EventLandingPresentation`
   *  into `s`, and a landing document is free to carry its own value for a
   *  key of the same name. The list's authored setting is the one that
   *  governs a list entry, so it is held here where nothing can overwrite it. */
  publicListSkipSingleSlot = false;
  /** Whether the CURRENT public event selection actually skipped its Event
   *  page. Reset per `selectPublicEvent` and set only once the authoritative
   *  detail has resolved, so the public rail never claims a step count the
   *  navigation has not settled on yet. */
  publicLandingStepSkipped = false;
  /** Whether the CURRENT public event selection was entered THROUGH the
   *  events list. A directly opened `/events/{eventId}` URL has no list
   *  behind it, so its rail must be the same three steps the Studio Event
   *  Landing Page tab shows — the `events` step only exists for a selection
   *  that actually came from the list (or a history entry that recorded it). */
  publicEventFromList = false;
  /** The canonical resolver's tenant display zone
   *  (`PortalAnonymousDocumentResponse.timeZone`) — relative `dateRange`/
   *  `pastHorizonDays` horizons resolve in THIS zone server-side
   *  (`workers/portal/src/resolve.ts` `windowFor`/`eventsWindowFor`), never
   *  the visitor's browser zone. */
  eventsPageTimeZone = "UTC";
  /** The current event's resolved landing presentation and configured
   *  registration questions — set once per `bootstrapPublicEventDetail`. */
  landingPresentation: unknown = null;
  landingIsConfigured = false;
  /** Whether the real attend public config has resolved — gates the
   *  events/landing launch/embed/share actions in STUDIO so they never act
   *  on `publicHost()`'s pre-config fallback host. Irrelevant to the public
   *  mount, which uses `window.location.origin` directly. */
  attendConfigResolved = false;
  landingRegistrationQuestions: unknown[] = [];
  /** `null` = no payment provider configured (gate paid tiers up front); a
   *  refused READ (e.g. 403) is a distinct error, kept separate below. */
  eventPaymentConfig: unknown = null;
  _evtListRequestId = 0;
  /** The `includePast` window the admin preview pool was last fetched under,
   *  so a later draft/toggle change can tell whether a re-read is needed. */
  _previewIncludedPast = false;
  _previewGeneration = 0;
  _evtQueryDebounce: ReturnType<typeof setTimeout> | null = null;
  _scrollSentinelObserver: IntersectionObserver | null = null;
  _scrollSentinelEl: Element | null = null;
  _eventRegIdemKey: string | null = null;
  _eventRegIdemFingerprint: string | null = null;
  /** The events catalogue this MOUNT is showing — an instance field, not a
   *  module global, so one Designer instance (e.g. an admin preview) cannot
   *  clobber another's (e.g. a concurrently-mounted public page) rows. Empty
   *  until a real read populates it; never fixture data. */
  eventsRows: DesignerEventRow[] = [];
  /** The Studio Events/Landing tabs' last-loaded baseline documents — kept so
   *  a save can preserve every field this Designer does not author (other
   *  blocks, header, locale, theme, branding, globalCss…) instead of
   *  replacing the whole document with a freshly-guessed one. `null` until
   *  loaded, or when nothing has been authored yet. */
  eventsDocBaseline: EventsDocumentDraft | null = null;
  landingDocBaseline: LandingDraft | null = null;
  eventsTabLoadState: "loading" | "ready" | "error" = "loading";
  landingTabLoadState: "loading" | "ready" | "error" = "loading";
  eventsTabSaving = false;
  landingTabSaving = false;
  set: Record<string, (v: any) => void> = {};
  actions: Record<string, any> = {};
  constructor(props: DesignerProps) {
    super(props);
    this.state = {
      journey: "appointment",
      stash: {
        queue: { s: { ...QUEUE_DEFAULTS }, ct: { ...QUEUE_CT_PREFILL } },
        kiosk: { s: { ...KIOSK_DEFAULTS }, ct: { ...KIOSK_CT_PREFILL } },
        events: { s: { ...EVENT_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } },
        landing: { s: { ...LANDING_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } },
      },
      evt: "e1", evtPage: 1, evtCat: "all", tickets: 1, evtQuery: "", evtPicked: false, evtFrom: "", evtTo: "",
      evtDetailState: "idle", evtListState: "idle", evtPaymentState: "idle", selectedTicketId: null, registeredEventId: null,
      s: { ...DEFAULTS },
      ct: {},
      section: props.initialSection || "general",
      previewStep: "service",
      device: props.device || "desktop",
      // Preview dialog (harness only): whether it is open, and the width it draws at.
      pvOpen: false,
      pvDevice: "desktop",
      formOpen: true,
      ctFilter: "all",
      ckMethod: "scan",
      ckRefInput: "",
      ckNameInput: "",
      ckFound: "", ckBooking: null, ckLooking: false, ckLookedUp: "",
      ckKeys: false,
      dwellLeft: null,
      ckProblem: "",
      pinnedLoc: "",
      bigText: false,
      openGroups: { service: true },
      svc: "aid",
      dur: "d1",
      loc: "l1",
      desk: "", party: 1,
      day: this.iso(this.today()),
      calMonth: { y: this.today().getFullYear(), m: this.today().getMonth() },
      slot: "",
      staff: "priya",
      cat: "all",
      consent: false,
      openHours: {},
      geoState: "idle", locQuery: "", locShown: 0, stripWeek: 0, custTz: "",
      email: "", emailTouched: false,
      phone: "", phoneTouched: false, phoneCountry: "US", countryMax: false,
      first: "", firstTouched: false, last: "", lastTouched: false,
      answers: {}, answersTouched: false,
      addr: "", addrPicked: false,
      attRows: [], attSeq: 0,
      upFiles: [], upError: "", calMenu: false, calPicked: "",
      cancelAsking: false, cancelled: false,
      qPos: 4, qFree: 1, qServed: 0, leaveAsking: false, leftQueue: false, soldOutPreview: false, blockOpen: {}, faqOpen: {}, wlDone: false, wlName: "", wlEmail: "",
      dirty: false, saved: {}, published: {}, saveNote: "", previewOnly: false,
      // Appointments seam (drop 1): the admin journey this tab writes to, and the
      // server's typed refusal when it says no. Never cleared by a later local edit
      // — only by the next save attempt, so the refusal cannot go quiet on its own.
      apptJourneyId: null, apptJourney: null, apptBaseline: null, refusedFields: [], saveNotices: [],
      // Walk-Ins seam: the queue journey this tab writes to, the author's own
      // queue selection, the tenant's real queues to pick from, and the
      // canonical public read of the selected ones (what the preview draws).
      // Every one of these starts EMPTY — the preview shows an empty state
      // until a real read lands, never a fixture desk.
      wiJourneyId: null, wiJourney: null, wiBaseline: null,
      wiQueueIds: [], wiQueueOptions: [], wiQueues: [], wiQueuesLoading: false, wiPreviewStep: "service",
    };

    // A ?preview=<journey> tab is a customer view, so it is resolved before the first render.
    let previewJourney = "";
    // Kiosk location — a property of the physical unit, arriving as /kiosk?location={external_key}
    // (operator, 2026-09-03: the record holds no location). Never a seed id.
    try { this.kioskLocationKey = new URLSearchParams(window.location.search).get("location") || ""; } catch (err) { this.kioskLocationKey = ""; }
    try { previewJourney = new URLSearchParams(window.location.search).get("preview") || ""; } catch (err) {}
    if (previewJourney && ["appointment", "queue", "events", "landing"].indexOf(previewJourney) > -1) {
      const seed = previewJourney === "queue" ? { s: { ...QUEUE_DEFAULTS }, ct: { ...QUEUE_CT_PREFILL } }
        : previewJourney === "events" ? { s: { ...EVENT_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } }
        : previewJourney === "landing" ? { s: { ...LANDING_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } }
        : { s: { ...DEFAULTS }, ct: {} };
      this.state = Object.assign({}, this.state, {
        previewOnly: true, formOpen: false, journey: previewJourney,
        s: seed.s, ct: seed.ct,
        previewStep: previewJourney === "landing" ? "landing" : previewJourney === "events" ? "events" : "service",
      });
    }
    if (this.props.mode === "public") {
      // Before the published config arrives there is nothing true to draw, and
      // the prototype's arrays are what would otherwise be drawn. A customer
      // must never see another tenant's services, even for one frame.
      SERVICES = []; LOCATIONS = []; STAFF = []; DURATIONS = []; this.eventsRows = [];
      // Default preserved exactly: an omitted `journey` prop still renders the
      // appointment page, unchanged from before this prop existed.
      const publicJourney = this.props.journey || "appointment";
      this.state = Object.assign({}, this.state, {
        previewOnly: true, formOpen: false, journey: publicJourney,
        previewStep: publicJourney === "landing" ? "landing" : publicJourney === "events" ? "events" : "service",
      });
    }
    this.set = {};
    Object.keys(ALL_DEFAULTS).forEach((k) => {
      this.set[k] = (v) => {
        let val = v;
        if (val && typeof val === "object" && val.target) val = val.target.value;
        if (typeof ALL_DEFAULTS[k] === "number") {
          const n = parseInt(val, 10);
          val = isNaN(n) ? 0 : n;
        }
        this.setState((st) => {
          const next = { s: { ...st.s, [k]: val }, dirty: true };
          if (k === "initialCount" || k === "loadMore") next.locShown = 0;
          return next;
        });
      };
    });
    this.actions = {
      noop: () => {},
      stripPrev: () => this.setState((prev) => ({ stripWeek: Math.max(0, prev.stripWeek - 1) })),
      stripNext: () => this.setState((prev) => ({ stripWeek: prev.stripWeek + 1 })),
      moreLocations: () => this.setState((prev) => {
        const batch = Math.max(1, prev.s.initialCount || 1);
        const total = prev.s.firstStep === "service" ? this.locsForService(prev.svc).length : LOCATIONS.length;
        return { locShown: Math.min(total, Math.max(1, prev.locShown || batch) + batch) };
      }),
      setLocQuery: (v) => this.setState({ locQuery: v && v.target ? v.target.value : v }),
      useMyLocation: () => {
        // The events/landing static host's Permissions-Policy sets
        // `geolocation=()` — the browser refuses this call outright there, so
        // the affordance is disabled rather than offered and left to always
        // fail as "denied".
        if (this.state.journey === "events" || this.state.journey === "landing") { this.setState({ geoState: "unsupported" }); return; }
        if (!navigator.geolocation) { this.setState({ geoState: "unsupported" }); return; }
        this.setState({ geoState: "locating" });
        navigator.geolocation.getCurrentPosition(
          () => this.setState({ geoState: "done", locQuery: "Current location" }),
          () => this.setState({ geoState: "denied" }),
          { timeout: 8000 }
        );
      },
      setJourney: (j, pinFrom) => this.setState((st) => {
        if (j === st.journey) return null;
        // OneStop (2026-09-04) edits the tenant's One-Stop experience document through its own
        // tab (onestop-tab.tsx), not a page-designer record — so it has no s/ct to stash, and
        // leaving it must not stash the previous journey's settings under its name.
        // Unsaved changes are session-scoped to the journey you are on: leaving this tab
        // discards them (product decision 2026-09-08 — no draft, no version to manage), so what
        // gets stashed is the last SAVED copy, never the edited one.
        const kept = st.dirty ? (st.saved[st.journey] || this.journeyDefaults(st.journey)) : { s: st.s, ct: st.ct };
        const keep = { s: kept.s, ct: kept.ct };
        const stash = st.journey === "onestop" ? st.stash : { ...st.stash, [st.journey]: keep };
        if (j === "onestop") return { journey: j, cat: "all", stash, dirty: false, saveNote: "", saveError: "" };
        const next = stash[j] || this.journeyDefaults(j);
        const first = j === "kiosk" ? "kioskEntry" : j === "landing" ? "landing" : j === "events" ? "events" : (j === "appointment" && next.s.firstStep === "location" ? "location" : "service");
        const sec = j === "kiosk" ? "kioskentry" : "general";
        // A pin only exists when a kiosk tile handed us here. Guarded to a location id, because
        // the tab bar's SegmentedControl may pass an event object as its second argument.
        const pinned = typeof pinFrom === "string" && /^l\d+$/.test(pinFrom) ? pinFrom : "";
        const loc = pinned || st.loc;
        return { journey: j, cat: "all", stash, s: next.s, ct: next.ct, section: sec, previewStep: first, wiPreviewStep: "service", cancelled: false, leftQueue: false, ckProblem: "", pinnedLoc: pinned, loc, dirty: false, saveNote: "", saveError: "", refusedFields: [] };
      }),
      copyEmbed: () => {
        // Events/landing embed/share/launch must not act on `publicHost()`'s
        // pre-config fallback host — suppressed until the real attend
        // config has actually resolved.
        if (!this.isPublic() && (this.state.journey === "events" || this.state.journey === "landing") && !this.attendConfigResolved) return;
        const cs = this.state.s;
        const code = this.embedSnippet(cs, this.embedTitle());
        if (!code) return; // No public address yet: nothing to copy, no "Copied" flash.
        try { if (navigator.clipboard) navigator.clipboard.writeText(code); } catch (err) {}
        this.setState({ copiedEmbed: true });
        setTimeout(() => this.setState({ copiedEmbed: false }), 1600);
      },
      evList: () => this.set.eventDisplay("list"),
      evGrid: () => this.set.eventDisplay("grid"),
      pagePages: () => this.set.eventPaging("pages"),
      pageLoadMore: () => this.set.eventPaging("loadmore"),
      pageScroll: () => this.set.eventPaging("scroll"),
      setEvtPage: (p) => { if (this.isPublic()) this.goToPublicEventsPage(p); else this.setState({ evtPage: p }); },
      setEvtFrom: (v) => {
        const value = v || "";
        if (this.isPublic()) this.refreshPublicEventsListFromFilters({ evtFrom: value });
        else this.setState({ evtFrom: value, evtPage: 1 });
      },
      setEvtTo: (v) => {
        const value = v || "";
        if (this.isPublic()) this.refreshPublicEventsListFromFilters({ evtTo: value });
        else this.setState({ evtTo: value, evtPage: 1 });
      },
      setEvtQuery: (v) => {
        const value = v && v.target ? v.target.value : v;
        if (this.isPublic()) {
          this.setState({ evtQuery: value, evtPicked: false });
          this.debounceEvtQuery(value);
          return;
        }
        this.setState({ evtQuery: value, evtPicked: false, evtPage: 1 });
      },
      pickEvent: (id, name) => {
        this.setState({ evtQuery: name, evtPicked: true, evtPage: 1 });
        if (this.isPublic()) this.selectPublicEvent(id, { fromList: true });
        else {
          // Same landing/details routing as a card click, off the row's own
          // raw record; with no matching row there is nothing to decide on,
          // so the selection alone stands.
          const found = this.eventsRows.filter((row) => row.id === id)[0];
          this.setState(found ? this.eventsRailStepForSelection(found) : { evt: id });
        }
      },
      setEvtCat: (c) => {
        if (this.isPublic()) this.refreshPublicEventsListFromFilters({ evtCat: c });
        else this.setState({ evtCat: c, evtPage: 1 });
      },
      setEvtLocationFilter: (locationId) => {
        if (this.isPublic()) this.refreshPublicEventsListFromFilters({ evtLocationFilter: locationId });
        else this.setState({ evtLocationFilter: locationId, evtPage: 1 });
      },
      // The category chips filter the tenant's own service catalogue, so the
      // selection has to survive a filter change: keep the current service when it
      // still matches, otherwise fall to the first service the new category offers,
      // and re-derive the location (and clear the slot) from that service.
      setServiceCategory: (category) => this.setState((prev) => {
        const selected = serviceFilterId(category);
        const current = SERVICES.filter((service) => service.id === prev.svc)[0];
        const nextService = current && (selected === "all" || serviceMatchesCategory(current, selected))
          ? current
          : SERVICES.filter((service) => serviceMatchesCategory(service, selected))[0];
        if (!nextService) return { cat: selected || "all", svc: "", slot: "" };
        const offeredLocations = this.locsForService(nextService.id);
        const locationStillOffered = offeredLocations.some((location) => location.id === prev.loc);
        return {
          cat: selected || "all",
          svc: nextService.id,
          loc: locationStillOffered ? prev.loc : (offeredLocations.length === 1 ? offeredLocations[0].id : ""),
          slot: "",
        };
      }),
      moreEvents: () => { if (this.isPublic()) this.loadMorePublicEvents(); else this.setState((st) => ({ evtPage: st.evtPage + 1 })); },
      reschedule: () => this.setState({ previewStep: "datetime", section: "datetime", slot: "", calMenu: false }),
      errStaff: () => this.setState({ previewStep: "datetime", section: "datetime", slot: "" }),
      errServices: () => this.setState({ previewStep: "service", section: "service" }),
      errStart: () => this.setState((prev) => ({ previewStep: this.steps()[0], section: this.sectionForStep(this.steps()[0]) })),
      lpRegister: () => this.setState({ previewStep: "details", section: "details" }),
      // Feeds the SAME shared first/last/email the registration submit reads
      // (`joinWaitlist` → `submitEventRegistration`), not a second, separate
      // set of fields the submit never looks at. A one-word name sets only
      // `first`; `last` stays blank rather than being invented as a copy of
      // it, so the shared "first/last/email required" validation asks for
      // the rest instead of silently duplicating a name.
      setWlName: (v) => {
        const value = v && v.target ? v.target.value : v;
        const trimmed = String(value || "").trim();
        const spaceAt = trimmed.indexOf(" ");
        this.setState({
          wlName: value,
          first: spaceAt > -1 ? trimmed.slice(0, spaceAt) : trimmed,
          last: spaceAt > -1 ? trimmed.slice(spaceAt + 1).trim() : "",
        });
      },
      setWlEmail: (v) => {
        const value = v && v.target ? v.target.value : v;
        this.setState({ wlEmail: value, email: value });
      },
      joinWaitlist: () => this.joinWaitlist(),
      toggleFaq: (i) => this.setState((prev) => ({ faqOpen: { ...prev.faqOpen, [i]: !prev.faqOpen[i] } })),
      removeRow: (key, i) => this.setState((prev) => {
        const rows = (prev.s[key] || []).slice();
        rows.splice(i, 1);
        return { s: { ...prev.s, [key]: rows }, dirty: true };
      }),
      addRow: (key, blank) => this.setState((prev) => ({ s: { ...prev.s, [key]: (prev.s[key] || []).concat([blank]) }, dirty: true })),
      addDoc: () => this.actions.addRow("requiredDocs", ""),
      setDoc: (i, v) => this.setState((prev) => {
        const rows = (prev.s.requiredDocs || []).slice();
        rows[i] = v;
        return { s: { ...prev.s, requiredDocs: rows }, dirty: true };
      }),
      removeDoc: (i) => this.setState((prev) => ({
        s: { ...prev.s, requiredDocs: (prev.s.requiredDocs || []).filter((_, k) => k !== i) }, dirty: true,
      })),
      addAgenda: () => this.actions.addRow("lpAgendaRows", { time: "", what: "" }),
      addSpeaker: () => this.actions.addRow("lpSpeakerRows", { name: "", role: "", photo: "" }),
      addIncluded: () => this.actions.addRow("lpIncludedRows", ""),
      addFaq: () => this.actions.addRow("lpFaqRows", { q: "", a: "" }),
      addSponsor: () => this.actions.addRow("lpSponsorRows", { name: "", tier: "Supporter", logo: "" }),
      setKey: (k, v) => this.setState((prev) => ({ s: { ...prev.s, [k]: v }, dirty: true })),
      tplBanner: () => this.actions.setKey("landingTemplate", "banner"),
      tplHero: () => this.actions.setKey("landingTemplate", "hero"),
      tplSplit: () => this.actions.setKey("landingTemplate", "split"),
      tplLong: () => this.actions.setKey("landingTemplate", "long"),
      setHeroHeight: (v) => this.actions.setKey("heroHeight", v),
      setHeroTreatment: (v) => this.actions.setKey("heroTreatment", v),
      setStickyBar: (v) => this.actions.setKey("stickyBar", !!v),
      setCoverImage: (v) => this.actions.setKey("coverImage", v),
      soldWaitlist: () => this.actions.setKey("soldOutMode", "waitlist"),
      soldRelated: () => this.actions.setKey("soldOutMode", "related"),
      soldBoth: () => this.actions.setKey("soldOutMode", "both"),
      setSoldOutPreview: (on) => this.setState({ soldOutPreview: !!on }),
      // "Keep past events on the list" changes the WINDOW the rows are read
      // under, not a client-side filter — so it has to re-read, exactly
      // like the other window-changing controls do.
      setShowPastEvents: (on) => this.setState(
        (prev) => ({ s: { ...prev.s, showPastEvents: !!on }, dirty: true }),
        () => {
          if (!this.isPublic()) { this.refreshPublicEventsPreview(); return; }
          // Public reads take the window off the resolved block, not `state.s`
          // — keep the two in step so the re-read is a genuine one.
          this.eventsPageBlock = { ...(this.eventsPageBlock || NEUTRAL_EVENTS_BLOCK_CONFIG), includePast: !!on };
          this.refreshPublicEventsList(1);
        },
      ),
      togglePane: () => this.setState((prev) => ({ formOpen: !prev.formOpen })),
      setDescText: (v) => this.actions.setKey("lpDescText", v && v.target ? v.target.value : v),
      setShareChannels: (vals) => this.actions.setKey("lpShareChannels", vals || []),
      setSponsorTiers: (on) => this.actions.setKey("sponsorTiers", !!on),
      setRelatedRule: (v) => this.actions.setKey("relatedRule", v),
      setRelatedCount: (v) => this.actions.setKey("relatedCount", v),
      setRelatedManual: (vals) => this.actions.setKey("relatedManual", vals || []),
      moveBlock: (id, dir) => this.setState((prev) => {
        const order = (prev.s.blockOrder || []).slice();
        const i = order.indexOf(id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= order.length) return null;
        order[i] = order[j]; order[j] = id;
        return { s: { ...prev.s, blockOrder: order }, dirty: true };
      }),
      toggleBlock: (id) => this.setState((prev) => {
        const on = (prev.s.blocksOn || []).slice();
        const i = on.indexOf(id);
        if (i > -1) on.splice(i, 1); else on.push(id);
        return { s: { ...prev.s, blocksOn: on }, dirty: true };
      }),
      // Walk-Ins preview step nav. Controlled here so the designer's own step
      // list stays in charge of what is on screen; the customer page owns its
      // own step in the public mount.
      setWalkInStep: (step) => this.setState({ wiPreviewStep: step }),
      toggleWalkInQueue: (id) => this.toggleWalkInQueue(id),
      askLeave: () => this.setState({ leaveAsking: true }),
      dismissLeave: () => this.setState({ leaveAsking: false }),
      doLeave: () => this.setState({ leaveAsking: false, leftQueue: true }),
      rejoinQueue: () => this.setState({ leftQueue: false, qPos: 6, qFree: 1, previewStep: this.steps()[0] }),
      bookInstead: () => this.setState((prev) => ({
        leftQueue: false, journey: "appointment",
        stash: { ...prev.stash, queue: { s: prev.s, ct: prev.ct } },
        s: (prev.stash.appointment && prev.stash.appointment.s) || { ...DEFAULTS },
        ct: (prev.stash.appointment && prev.stash.appointment.ct) || {},
        section: "general", previewStep: "service",
      })),
      backToEvents: () => {
        // Invalidate any in-flight detail/related load: returning to the list
        // must not be overwritten later by a stale detail response (or its
        // history marker) for the event we just left.
        this._evtDetailRequestId = (this._evtDetailRequestId || 0) + 1;
        this.publicLandingStepSkipped = false;
        this.publicEventFromList = false;
        if (this.isPublic()) {
          this.pushPublicEventsListHistory();
          // Direct-detail entry mounted with `journey: "landing"` — this
          // must actually SWITCH to the list journey (not just flip
          // `previewStep`) and re-bootstrap it, or `isEvents()` stays false,
          // `usingServerEvents` in `renderVals` stays false, and the render
          // falls back to client-side filtering over whatever detail/
          // related rows are still sitting in `eventsRows`.
          this.setState({ cancelled: false, journey: "events", previewStep: "events", section: "events", evtQuery: "", evtPicked: false }, () => this.bootstrapPublicEventsList());
          return;
        }
        this.setState({ cancelled: false, previewStep: "events", section: "events", evtQuery: "", evtPicked: false });
      },
      // The control reads "Show event details step"; the wire field it
      // persists is still `skipSingleEventSlot` (SKIP), so the value is
      // inverted here rather than renaming the stored contract. Turning the
      // step off while it is the previewed step would leave the Studio
      // sidebar selection pointing at a step that no longer exists, so the
      // preview moves on to Your info in the same update.
      setShowEventDetailsStep: (v) => {
        let val: any = v;
        if (val && typeof val === "object" && val.target) val = val.target.checked;
        const show = Boolean(val);
        this.setState((st) => {
          const next: any = { s: { ...st.s, skipSingleEventSlot: !show }, dirty: true };
          if (!show && st.previewStep === "landing" && this.isEvents()) { next.previewStep = "details"; next.section = "details"; }
          return next;
        });
      },
      askCancel: () => this.setState({ cancelAsking: true, calMenu: false }),
      dismissCancel: () => this.setState({ cancelAsking: false }),
      // Appointments cancel through the real appointment cancellation path
      // this dialog already wires elsewhere. Events/Landing have NO public
      // cancellation operation (the JSON registration contract declares
      // none) — this must not locally fabricate a cancelled state for those.
      doCancel: () => {
        if (this.state.journey === "events" || this.state.journey === "landing") {
          this.setState({ cancelAsking: false, cancelUnavailable: true });
          return;
        }
        this.setState({ cancelAsking: false, cancelled: true });
      },
      bookAgain: () => this.setState((prev) => ({
        cancelled: false, previewStep: this.steps()[0], slot: prev.slot, calPicked: "",
      })),
      toggleCal: () => this.setState((prev) => ({ calMenu: !prev.calMenu })),
      pickCal: (id) => this.setState({ calMenu: false, calPicked: id }),
      addFiles: (list) => this.setState((prev) => {
        const max = prev.s.maxAttachments || 5;
        const room = Math.max(0, max - (prev.upFiles || []).length);
        const OK = /\.(png|jpe?g|docx?|pdf|gdoc)$/i;
        const incoming = Array.from(list || []);
        const good = incoming.filter((f) => OK.test(f.name) || f.type === "application/vnd.google-apps.document");
        const rejected = incoming.length - good.length;
        const taken = good.slice(0, room);
        const over = good.length - taken.length;
        const notes = [];
        if (rejected > 0) notes.push(rejected + (rejected === 1 ? " file was" : " files were") + " not a supported type.");
        if (over > 0) notes.push("Only " + max + " files can be attached.");
        return {
          upFiles: (prev.upFiles || []).concat(taken.map((f) => ({ id: f.name + f.size + f.lastModified, file: f, progress: 1 }))),
          upError: notes.join(" "),
        };
      }),
      removeFile: (id) => this.setState((prev) => ({ upFiles: (prev.upFiles || []).filter((f) => f.id !== id), upError: "" })),
      addAttendee: () => this.setState((prev) => {
        const id = (prev.attSeq || 0) + 1;
        return { attSeq: id, attRows: (prev.attRows || []).concat([{ id, first: "", last: "", email: "", phone: "", country: "US", touched: {} }]) };
      }),
      attSet: (id, key, v) => this.setState((prev) => ({
        attRows: (prev.attRows || []).map((r) => (r.id === id ? Object.assign({}, r, { [key]: v && v.target ? v.target.value : v }) : r)),
      })),
      attBlur: (id, key) => this.setState((prev) => ({
        attRows: (prev.attRows || []).map((r) => (r.id === id ? Object.assign({}, r, { touched: Object.assign({}, r.touched, { [key]: true }) }) : r)),
      })),
      attRemove: (id) => this.setState((prev) => ({ attRows: (prev.attRows || []).filter((r) => r.id !== id) })),
      setParty: (v) => {
        const raw = v && typeof v === "object" && v.target ? v.target.value : v;
        const n = parseInt(raw, 10);
        this.setState((st) => ({ party: Math.max(1, Math.min(st.s.maxParty || 1, isNaN(n) ? 1 : n)) }));
      },
      setTickets: (v) => {
        // Multiple places is Coming soon on the event journeys — pin the count
        // at one there, whatever the input or a stale saved setting says.
        if (this.isEvents() || this.isLanding()) { this.setState({ tickets: 1 }); return; }
        const raw = v && typeof v === "object" && v.target ? v.target.value : v;
        const n = parseInt(raw, 10);
        this.setState((st) => {
          const e = this.eventsRows.filter((x) => x.id === st.evt)[0] || this.eventsRows[0] || EMPTY_EVENT;
          const cap = e.availability && e.availability.soldOut ? 0 : (e.maxPer || 1);
          return { tickets: Math.max(1, Math.min(cap, isNaN(n) ? 1 : n)) };
        });
      },
      setSelectedTicketId: (v) => this.setState({ selectedTicketId: v && v.target ? v.target.value : v, bookingError: "" }),
      waitExact: () => this.set.waitDisplay("exact"),
      waitRange: () => this.set.waitDisplay("range"),
      waitPosition: () => this.set.waitDisplay("position"),
      goSection: (id) => this.goSection(id),
      save: () => {
        const st = this.state;
        if (PD_STUB_ONLY[st.journey]) {
          this.setState({ saveNote: "error", saveError: PD_STUB_REASON });
          return;
        }
        // The Appointments tab writes through the CONTRACT (whitelist-only patch to
        // updateAdminJourney). Events and Landing now write through their own
        // real contracts (below); Walk-Ins and Kiosk stay on the page-designer
        // stub until their contracts land.
        if (st.journey === "appointment") { this.saveAppointments(false); return; }
        if (st.journey === "queue") { this.saveWalkIns(false); return; }
        if (st.journey === "events") { this.saveEventsTab(false); return; }
        if (st.journey === "landing") { this.saveLandingTab(false); return; }
        putPageDesigner(this.props.tenant, st.journey, { s: st.s, ct: st.ct, slug: String(st.s.slug || st.journey) })
          .then(() => this.setState((prev) => ({
            dirty: false,
            saved: { ...prev.saved, [prev.journey]: { s: prev.s, ct: prev.ct, at: new Date() } },
            saveNote: "saved", saveError: "",
          })))
          .then(() => this.saveToast(true, false))
          .catch((err) => this.failSave(err, false));
      },
      publish: () => {
        const st = this.state;
        const j = st.journey;
        if (PD_STUB_ONLY[j]) {
          this.setState({ saveNote: "error", saveError: PD_STUB_REASON });
          return;
        }
        if (j === "appointment") { this.saveAppointments(true); return; }
        if (j === "queue") { this.saveWalkIns(true); return; }
        if (j === "events") { this.saveEventsTab(true); return; }
        if (j === "landing") { this.saveLandingTab(true); return; }
        putPageDesigner(this.props.tenant, j, { s: st.s, ct: st.ct, slug: String(st.s.slug || j) })
          .then(() => publishPageDesigner(this.props.tenant, j))
          .then(() => this.setState((prev) => ({
            dirty: false,
            saved: { ...prev.saved, [prev.journey]: { s: prev.s, ct: prev.ct, at: new Date() } },
            published: { ...prev.published, [prev.journey]: true },
            saveNote: "published", saveError: "",
          })))
          .then(() => this.saveToast(true, true))
          .catch((err) => this.failSave(err, true));
      },
      openPreview: () => {
        // In the harness the public host is unreachable, so the same page opens
        // in a dialog. Everywhere else Preview is a new tab, unchanged.
        if (previewInDialog()) { this.setState({ pvOpen: true }); return; }
        if ((this.state.journey === "events" || this.state.journey === "landing") && !this.attendConfigResolved) return;
        try {
          // The one journey you are editing, at its own public link.
          const url = this.publicLink();
          if (url) window.open(url, "_blank");
        } catch (err) {}
      },
      closePreviewDialog: () => this.setState({ pvOpen: false }),
      setPvDevice: (d) => this.setState({ pvDevice: d }),
      openPreviewTab: () => {
        if ((this.state.journey === "events" || this.state.journey === "landing") && !this.attendConfigResolved) return;
        try { const url = this.publicLink(); if (url) window.open(url, "_blank"); } catch (err) {}
      },
      toggleForm: () => this.setState((st) => ({ formOpen: !st.formOpen })),
      setDevice: (d) => this.setState({ device: d }),
      setCtFilter: (v) => this.setState({ ctFilter: v }),
      setCkMethod: (v) => this.setState({ ckMethod: v, ckProblem: "" }),
      setCkRef: (e) => this.setState({ ckRefInput: (e && e.target ? e.target.value : e || "").toUpperCase(), ...this.clearedCkLookup() }),
      setCkName: (e) => this.setState({ ckNameInput: e && e.target ? e.target.value : e || "" }),
      openKeys: () => this.setState({ ckKeys: true }),
      closeKeys: () => this.setState({ ckKeys: false }),
      keyPress: (ch) => this.setState((st) => (st.ckMethod === "name"
        ? { ckNameInput: (st.ckNameInput || "") + ch }
        : { ckRefInput: ((st.ckRefInput || "") + ch).toUpperCase(), ...this.clearedCkLookup() })),
      keyBack: () => this.setState((st) => (st.ckMethod === "name"
        ? { ckNameInput: (st.ckNameInput || "").slice(0, -1) }
        : { ckRefInput: (st.ckRefInput || "").slice(0, -1), ...this.clearedCkLookup() })),
      keyClear: () => this.setState((st) => (st.ckMethod === "name" ? { ckNameInput: "" } : { ckRefInput: "", ...this.clearedCkLookup() })),
      keySpace: () => this.setState((st) => (st.ckMethod === "name" ? { ckNameInput: (st.ckNameInput || "") + " " } : null)),
      setBigText: (v) => this.setState({ bigText: !!v }),
      kioskGo: (dest) => {
        if (dest === "checkin") { this.setState({ journey: "kiosk", previewStep: "kioskCheckin", section: "kioskcheckin", ckProblem: "", formOpen: true }); return; }
        // The three booking destinations hand off to the journey that owns them.
        const j = dest === "walkin" ? "queue" : dest === "events" ? "events" : "appointment";
        const byKey = this.kioskLocationKey ? (LOCATIONS.filter((l) => (l.external_key || l.externalCode || "") === this.kioskLocationKey)[0] || null) : null;
        this.actions.setJourney(j, (byKey && byKey.id) || this.state.s.kioskLocation || "l1");
      },
      kioskLookupRef: () => {
        // Read-only: the lookup never changes a booking. It shows back exactly what the
        // check-in service returned for the typed code, and nothing when it returned nothing.
        const code = String(this.state.ckRefInput || "").trim().toUpperCase();
        if (!code) return;
        // A response is applied only while it is still the latest lookup AND the field still
        // holds the code it was made for; editing the reference or resetting drops it.
        const gen = (this.ckLookupGen += 1);
        const current = () => !this._gone && gen === this.ckLookupGen
          && String(this.state.ckRefInput || "").trim().toUpperCase() === code;
        this.setState({ ckLooking: true, ckLookedUp: "", ckBooking: null, ckFound: "", ckProblem: "" });
        lookupPublicCheckIn(code)
          .then((found) => {
            if (!current()) return;
            const booking = kioskBookingFromLookup(code, found);
            this.setState({ ckLooking: false, ckLookedUp: code, ckBooking: booking, ckFound: booking ? code : "" });
          })
          .catch((err) => {
            if (!current()) return;
            const errCode = String(err && (err.code || (err.body && err.body.error && err.body.error.code) || "") || "");
            const msg = String(err && err.message || "");
            const status = err && (err.status || (err.response && err.response.status)) || 0;
            // No seam at all (network down / route unbound) is the kiosk's OFFLINE state.
            const offline = /network|failed to fetch|offline|route_unbound/i.test(errCode + msg) || status === 501;
            // Anything else — an invalid or unknown code included — is simply no match.
            this.setState({ ckLooking: false, ckLookedUp: code, ckBooking: null, ckFound: "", ckProblem: offline ? "offline" : "" });
          });
      },
      kioskCheckInNow: () => {
        // This renderer is only ever the designer's preview (nothing mounts a live kiosk
        // from it), so it NEVER performs the check-in transition: an arrival posted from a
        // preview would be indistinguishable from a real one in the tenant's data. The
        // outcome shown follows the looked-up booking's own status, when there is one.
        const b = this.state.ckBooking;
        if (b && KIOSK_CANCELLED_STATUSES.indexOf(b.status) > -1) { this.setState({ ckProblem: "cancelled" }); return; }
        if (b && KIOSK_ARRIVED_STATUSES.indexOf(b.status) > -1) { this.setState({ ckProblem: "already" }); return; }
        this.setState({ previewStep: "kioskDone", ckProblem: "", ckKeys: false, dwellLeft: this.state.s.ckDoneDwell || 6 });
      },
      kioskPick: (ref) => this.setState({ ckFound: ref, ckProblem: "" }),
      kioskProblem: (p) => {
        // Each failure state previews on its own. A booking is shown back under it only when
        // a real lookup found one — never a sample customer.
        this.setState({ previewStep: "kioskCheckin", section: "kioskcheckin", ckProblem: p, formOpen: true });
      },
      kioskReset: () => this.setState({ previewStep: "kioskEntry", section: "kioskentry", ckProblem: "", ckRefInput: "", ckNameInput: "", ...this.clearedCkLookup(), ckMethod: this.state.s.ckDefault || "scan", formOpen: true }),
      toggleBigText: () => this.setState((st) => ({ bigText: !st.bigText })),
      kioskScanFail: () => this.setState({ ckProblem: "noMatch" }),
      kioskTryName: () => this.setState({ ckProblem: "", ckMethod: this.state.s.ckName ? "name" : "ref" }),
      prevStep: () => this.moveStep(-1),
      nextStep: () => {
        if (this.invalidAppointmentServiceSelection()) return;
        const steps = this.steps();
        const leavingDetails = this.state.previewStep === "details"
          && steps[Math.min(steps.length - 1, steps.indexOf("details") + 1)] === "confirmation";
        // Preview NEVER posts. Its confirmation step renders the settings under
        // edit; a booking created from it would be indistinguishable from a
        // customer's in the tenant's own data.
        if (leavingDetails && this.isPublic()) {
          if (this.state.journey === "events" || this.state.journey === "landing") { this.submitEventRegistration(); return; }
          this.submitAppointment();
          return;
        }
        this.moveStep(1);
      },
      goStepIndex: (i) => { const l = this.steps(); this.setState({ previewStep: l[Math.max(0, Math.min(l.length - 1, i))] }); },
      serviceFirst: () => this.setState((st) => ({ s: { ...st.s, firstStep: "service" }, dirty: true })),
      locationFirst: () => this.setState((st) => ({ s: { ...st.s, firstStep: "location" }, dirty: true })),
      mapFirst: () => this.setState((st) => ({
        s: { ...st.s, firstStep: "map", showMap: true, mapHeight: st.s.mapHeight === 190 ? 340 : st.s.mapHeight },
        previewStep: "location", section: "general", dirty: true,
      })),
      startMapResize: (e) => {
        if (e.preventDefault) e.preventDefault();
        const startY = e.clientY;
        const startH = this.state.s.mapHeight;
        const move = (ev) => {
          const h = Math.max(120, Math.min(560, startH + (ev.clientY - startY)));
          this.setState((st) => ({ s: { ...st.s, mapHeight: h }, dirty: true }));
        };
        const up = () => {
          window.removeEventListener("pointermove", move);
          window.removeEventListener("pointerup", up);
          this.setState({ resizingMap: false });
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
        this.setState({ resizingMap: true });
      },
      pTitles: () => this.set.progressType("titles"),
      pNumbers: () => this.set.progressType("numbers"),
      pBar: () => this.set.progressType("bar"),
      pNone: () => this.set.progressType("none"),
      dayStrip: () => this.set.calendarLayout("daystrip"),
      monthCal: () => this.set.calendarLayout("month"),
      dateToday: () => this.setState((st) => ({ s: { ...st.s, defaultDate: "today" }, day: this.iso(this.today()), calMonth: { y: this.today().getFullYear(), m: this.today().getMonth() }, dirty: true })),
      dateFirstAvail: () => this.setState((st) => {
        const iso = this.firstAvailable(this.bookWindow());
        const d = this.fromIso(iso);
        return { s: { ...st.s, defaultDate: "firstAvailable" }, day: iso, calMonth: { y: d.getFullYear(), m: d.getMonth() }, dirty: true };
      }),
      calPrev: () => this.setState((st) => {
        const m = st.calMonth.m - 1;
        return { calMonth: m < 0 ? { y: st.calMonth.y - 1, m: 11 } : { y: st.calMonth.y, m } };
      }),
      calNext: () => this.setState((st) => {
        const m = st.calMonth.m + 1;
        return { calMonth: m > 11 ? { y: st.calMonth.y + 1, m: 0 } : { y: st.calMonth.y, m } };
      }),
      setGrouped: (on) => this.set.slotGrouping(on ? "partofday" : "flat"),
      setDay: (d) => this.setState({ day: d, slot: this.firstSlotFor(d) }),
      setCustTz: (z) => this.setState({ custTz: z }),
      setEmail: (v) => this.setState({ email: v && v.target ? v.target.value : v }),
      blurEmail: () => this.setState({ emailTouched: true }),
      setAddr: (v) => this.setState({ addr: v && v.target ? v.target.value : v, addrPicked: false }),
      pickAddr: (line) => this.setState({ addr: line, addrPicked: true }),
      setFirst: (v) => this.setState({ first: v && v.target ? v.target.value : v }),
      setAnswer: (qid, v) => this.setState((prev) => ({ answers: { ...(prev.answers || {}), [qid]: v && v.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v } })),
      blurFirst: () => this.setState({ firstTouched: true }),
      setLast: (v) => this.setState({ last: v && v.target ? v.target.value : v }),
      blurLast: () => this.setState({ lastTouched: true }),
      setCountries: (vals) => {
        const list = vals || [];
        if (list.length > 10) { this.setState({ countryMax: true }); return; }
        this.setState({ countryMax: false });
        this.set.customCountries(list);
      },
      setPhoneCountry: (iso) => this.setState({ phoneCountry: iso }),
      setPhone: (v) => this.setState({ phone: v && v.target ? v.target.value : v }),
      blurPhone: () => this.setState({ phoneTouched: true }),
      setSlot: (t) => this.setState({ slot: t, reconfirmFrom: null }),
      setDateValue: () => {},
      setConsent: (v) => this.setState({ consent: v }),
    };
  }

  componentDidMount() {
    // StrictMode (and any mount/unmount/remount cycle) reuses this SAME
    // instance: `componentWillUnmount` set `_gone = true` on the first
    // unmount, and without resetting it here every async completion from
    // the real, currently-mounted instance would be discarded as if it were
    // stale. Reset it, and bump the request-generation counters so any
    // completion still in flight from the FIRST mount (which raced past this
    // reset) is still recognized as stale against the new generation, rather
    // than racing the fresh requests this mount is about to issue.
    this._gone = false;
    this._evtListRequestId = (this._evtListRequestId || 0) + 1;
    if (this.isPublic()) {
      if (this.state.journey === "events") this.bootstrapPublicEventsList();
      else if (this.state.journey === "landing") this.bootstrapPublicEventDetail();
      else this.bootstrapPublic();
      if (this.state.journey === "events" || this.state.journey === "landing") {
        this._onPopState = () => this.handlePublicEventsPopState();
        try { window.addEventListener("popstate", this._onPopState); } catch {}
      }
      this.initResize();
      return;
    }
    // Records — one per journey per tenant. The saved draft seeds each journey's stash so
    // switching tabs shows what was last saved; published flags light the status pill.
    listPageDesigner(this.props.tenant).then((env) => {
      this.setState((prev) => {
        const stash = { ...prev.stash };
        const published = { ...prev.published };
        // Baseline for the discard-on-leave rule: what the server last saved for each journey.
        const saved = { ...prev.saved };
        let s = prev.s, ct = prev.ct;
        for (const j of Object.keys(env.records)) {
          const r = env.records[j];
          if (!r) continue;
          // The appointment record is a localStorage stub; the server's journey is the
          // truth for this tab and hydrates it below. Never seed the form from the stub.
          // Events and Landing are ALSO real contracts now (`loadEventsTabDraft`/
          // `loadLandingTabDraft`, called separately below) — this stub read/write
          // loop must not race them or overwrite their config/publication flags.
          //
          // WALK-INS joins them (2026-09-11). `queue` now writes through the typed
          // admin journey door (`hydrateWalkIns` -> `adoptWalkInJourney`), and both
          // reads land asynchronously: whichever resolved LAST won, so a stale
          // browser-local stub from an earlier session could overwrite the settings
          // the server had just returned — an initial-load race with a visibly
          // wrong form. The stub is no longer read for it, and no longer written
          // (see `save`/`publish`), so the server row is the only source.
          if (j === "appointment" || j === "queue" || j === "events" || j === "landing") continue;
          if (j === prev.journey) { s = { ...s, ...r.s }; ct = { ...ct, ...r.ct }; }
          else stash[j] = { s: { ...(this.journeyDefaults(j).s), ...r.s }, ct: { ...(this.journeyDefaults(j).ct), ...r.ct } };
          saved[j] = { s: { ...(this.journeyDefaults(j).s), ...r.s }, ct: { ...(this.journeyDefaults(j).ct), ...r.ct }, at: r.updated_at ? new Date(r.updated_at) : new Date() };
          if (r.published_at && !r.active_to) published[j] = true;
        }
        return { stash, published, saved, s, ct };
      });
    }).catch(() => { /* first run — nothing saved yet */ });
    // The preview describes THIS tenant: services, locations and staff come from
    // the same choke points their own surfaces read. Whatever a read does not
    // return keeps its placeholder, so the preview always draws.
    this._catalogAbort = typeof AbortController === "function" ? new AbortController() : null;
    loadPreviewCatalog(this._catalogAbort ? this._catalogAbort.signal : undefined).then((cat) => {
      if (this._gone) return;
      // The public catalogue is what a customer is offered, so once it has been
      // adopted the admin read must not overwrite it -- otherwise the preview
      // shows rows the customer page does not, decided by whichever request
      // happened to finish last.
      if (this.catalogSource === "public") return;
      // Assigned WHOLESALE, including empty. These used to be applied only when
      // non-empty, so a refused or unauthenticated read left the prototype's
      // Lakeview services, branches and advisers on screen looking like this
      // tenant's own data.
      LOCATIONS = cat.locations;
      SERVICES = cat.services;
      STAFF = cat.staff;
      DURATIONS = cat.durations;
      // A selection naming a row this tenant does not have would render blank.
      this.setState((prev) => ({
        svc: SERVICES.filter((x) => x.id === prev.svc)[0] ? prev.svc : (SERVICES[0] && SERVICES[0].id) || prev.svc,
        loc: LOCATIONS.filter((x) => x.id === prev.loc)[0] ? prev.loc : (LOCATIONS[0] && LOCATIONS[0].id) || prev.loc,
        staff: STAFF.filter((x) => x.id === prev.staff)[0] ? prev.staff : (STAFF[0] && STAFF[0].id) || prev.staff,
        dur: DURATIONS.filter((x) => x.id === prev.dur)[0] ? prev.dur : (DURATIONS[0] && DURATIONS[0].id) || prev.dur,
        // A category filter naming a category this catalogue no longer returns would hide every service.
        cat: prev.journey === "appointment" ? (serviceCategoryOptions(SERVICES).some((option) => option.id === serviceFilterId(prev.cat)) ? serviceFilterId(prev.cat) : "all") : prev.cat,
      }));
    });

    // Which admin journey the Appointments tab patches. Resolved from the list op,
    // never a baked id; a failure here only means the tab reports it on first save.
    const remembered = this.rememberedJourneyId();
    (remembered
      ? loadAppointmentJourney(remembered).then((journey) => ({ row: null, journey })).catch(() => null)
      : Promise.resolve(null)
    ).then((known) => known || resolveAppointmentJourney(String(this.state.s.slug || ""))
      .then((row) => (row && this.ownsJourney(row)
        ? loadAppointmentJourney(String(row.id)).then((journey) => ({ row, journey }))
        : null)))
      .then((got) => { if (got) this.adoptJourney(got.journey, true, got.row); })
      .catch((err) => this.failSave(err, false));
    // Which admin journey the Walk-Ins tab patches, and the tenant's real
    // queues to attach. Same shape as the appointment resolve above: from the
    // list op, never a baked id, and a failure only means the tab reports it
    // on first save.
    this.hydrateWalkIns();
    // WALK-INS NO LONGER RUNS A TIMER. The old preview decremented the queue
    // position on a four-second interval with `Math.random()`, which is a
    // designer preview inventing a live queue: the number moved while nobody
    // was served. The Walk-Ins preview now draws a clearly-labelled sample
    // ticket that does not move, and a real customer's position comes from
    // `getPublicQueuePosition`.
    // The checked-in screen returns to the entry screen on its own, so the count is real:
    // it ticks down and actually navigates, rather than describing a delay that never happens.
    this.dwellTimer = setInterval(() => {
      if (!this.isKiosk() || this.state.previewStep !== "kioskDone") return;
      this.setState((prev) => {
        const left = (prev.dwellLeft == null ? (prev.s.ckDoneDwell || 6) : prev.dwellLeft) - 1;
        if (left <= 0) {
          return { previewStep: "kioskEntry", section: "kioskentry", dwellLeft: null,
            ckProblem: "", ckRefInput: "", ckNameInput: "", ckKeys: false };
        }
        return { dwellLeft: left };
      });
    }, 1000);
    this.loadEventsTabDraft();
    this.loadLandingTabDraft();
    this.refreshPublicEventsPreview();
    ensurePublicConfig().then((cfg) => { if (!this._gone) { this.attendConfigResolved = Boolean(cfg); this.setState({}); } });
    this.initResize();
    this.initDay();
  }

  /**
   * Admin-preview rows for the Events List/Landing tabs — the tenant's OWN
   * real, live catalogue (`getPublicEvents`), never the removed fixtures and
   * never left empty. Loaded once on mount (like `loadPreviewCatalog` for the
   * Appointments tab) and used by both tabs' preview panes regardless of
   * which one is currently active, so switching tabs never re-shows stale or
   * fixture rows.
   */
  refreshPublicEventsPreview() {
    if (this.isPublic()) return;
    // "Keep past events on the list" (`showPastEvents` ↔ the block's
    // `includePast`) decides whether the preview pool may contain completed
    // events at all — `upcoming: true` strips them server-side, so a
    // hard-coded `true` here meant the toggle could never show anything.
    const includePast = Boolean((this.state.journey === "events" ? this.state.s : this.state.stash?.events?.s)?.showPastEvents);
    // Request identity: a later refresh (past-events toggled, a filter
    // changed, a draft hydrated) always wins. An older in-flight walk that
    // resolves afterwards is dropped whole rather than overwriting the newer
    // rows/facets — and `_previewIncludedPast` is only committed by the walk
    // that actually lands, so the flag can never describe rows it did not
    // produce.
    const generation = (this._previewGeneration = (this._previewGeneration || 0) + 1);
    const stale = () => this._gone || generation !== this._previewGeneration;
    // The preview's own pickers, Location facet, related-events pool and
    // "N events are live" count all read `eventsRows`, so an arbitrary first
    // page is not a catalogue — walk every page the response says exists.
    // `getPublicEvents` returns `pagination: { page, limit, total, total_pages,
    // hasMore }`, the same shape `refreshPublicEventsList` already paginates
    // on, so the terminator is the server's own fact, not a guess.
    const PREVIEW_PAGE_LIMIT = 100;
    const PREVIEW_MAX_PAGES = 20;
    const walk = (page, rowsSoFar, firstFacets) => loadPublicEventsList({
      page, limit: PREVIEW_PAGE_LIMIT, upcoming: !includePast, includeSoldOut: true, includeNonBookable: true,
    }).then((result) => {
      if (stale()) return null;
      const rows = rowsSoFar.concat(result.data.map((row) => projectPublicEventToDesignerRow(row, this.props.brandColor || "")));
      const facets = firstFacets || result.facets;
      const pg = result.pagination;
      const totalPages = pg && pg.total_pages ? Math.max(1, pg.total_pages) : page;
      const more = pg ? (pg.hasMore === true || page < totalPages) : false;
      if (more && result.data.length > 0 && page < PREVIEW_MAX_PAGES) return walk(page + 1, rows, facets);
      return { rows, facets };
    });

    walk(1, [], null)
      .then((loaded) => {
        if (!loaded || stale()) return;
        this._previewIncludedPast = includePast;
        this.eventsRows = loaded.rows;
        // Same read door, same facet payload: the preview's Location filter is
        // populated from the server's real {id,name} catalogue, exactly as the
        // public list is, and the Category chips now read the same facets.
        this.setState((prev) => ({ evt: this.eventsRows.find((e) => e.id === prev.evt) ? prev.evt : (this.eventsRows[0]?.id || prev.evt), evtFacets: loaded.facets }));
      })
      .catch(() => { /* the tab's own empty-state renders honestly; no fixture fallback */ });
  }

  componentWillUnmount() {
    this._gone = true;
    if (this._catalogAbort) this._catalogAbort.abort();
    if (this.qTimer) clearInterval(this.qTimer);
    if (this.dwellTimer) clearInterval(this.dwellTimer);
    if (this._onResize) window.removeEventListener("resize", this._onResize);
    if (this._onPopState) { try { window.removeEventListener("popstate", this._onPopState); } catch {} }
    if (this._evtQueryDebounce) clearTimeout(this._evtQueryDebounce);
    if (this._scrollSentinelObserver) { this._scrollSentinelObserver.disconnect(); this._scrollSentinelObserver = null; }
  }

  /** The port carried TWO componentDidMount / componentWillUnmount / initDay trios;
   *  the later definitions shadowed the earlier ones, so the resize listener was never
   *  attached and the intervals were never cleared. One of each now, and the two
   *  distinct initDay bodies live under their own names. */
  initResize() {
    this._onResize = () => {
      const narrow = window.innerWidth < 1100;
      const automaticDevice = this.isPublic() && !this.props.device
        ? (window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop")
        : null;
      this.setState((st) => {
        const patch = {};
        if (st.narrow !== narrow) Object.assign(patch, { narrow, formOpen: !narrow });
        if (automaticDevice && st.device !== automaticDevice) Object.assign(patch, { device: automaticDevice });
        return Object.keys(patch).length ? patch : null;
      });
    };
    this._onResize();
    window.addEventListener("resize", this._onResize);
  }

  isPublic() { return this.props.mode === "public"; }

  /** Clears the kiosk's lookup result and invalidates any lookup still in flight, so a booking
   *  found for one reference can never be checked in under another. */
  clearedCkLookup() {
    this.ckLookupGen += 1;
    return { ckBooking: null, ckFound: "", ckLookedUp: "", ckLooking: false };
  }

  /** The selected event's own ticket tiers from its read — never a sample list. */
  eventTicketRows(evt) {
    return ((evt && evt.raw && evt.raw.ticketTiers) || []).map((t) => ({
      key: t.id, name: t.name, price: formatEventPrice({ amountMinor: t.priceMinor, currency: t.currency }), note: t.desc || "",
    }));
  }

  /**
   * The preview draws the catalogue a CUSTOMER would be offered, from the same
   * public read the standalone page uses.
   *
   * The tab still loads the tenant's full admin catalogue for its own pickers;
   * what changed is that the wizard no longer renders from it. Two surfaces
   * reading the same journey from two different routes is how they drift while
   * both look right -- the operator's whole complaint. `catalogSource` makes the
   * outcome independent of which read resolves first: once the public list has
   * been adopted, the admin one never overwrites it.
   */
  refreshPublicCatalog() {
    const slug = this.bookingSlug();
    if (!slug || this.state.journey !== "appointment") return;
    loadPublicCatalog(slug, this.isPublic() ? "public" : "preview").then((catalog) => {
      if (this._gone) return;
      // null means there was nothing to read. An EMPTY catalogue is a real
      // answer and is adopted: a tenant that offers nothing must look like it.
      if (!catalog) return;
      this.adoptCatalog(buildCatalog(this.state.apptJourney, catalog.services, catalog.locations, (this.state.avail || EMPTY_AVAILABILITY).staff));
    }).catch(() => { /* the admin catalogue already drew; a refused public read changes nothing */ });
  }

  /**
   * Availability belongs to a (service, staff) pair, so a change to either
   * invalidates the loaded window. Reloading here rather than at each call site
   * is what keeps the preview and the public page identical.
   */
  componentDidUpdate(prevProps, prevState) {
    // The dialog re-renders one mount at three widths, so the device the operator
    // picked arrives as a prop and the page's own layout state follows it.
    if (this.props.device && prevProps.device !== this.props.device && this.state.device !== this.props.device) {
      this.setState({ device: this.props.device });
    }
    if (this.state.journey !== "appointment") return;
    const staffChanged = !!this.state.s.staffSelect && prevState.staff !== this.state.staff;
    if (prevState.svc !== this.state.svc || staffChanged) this.refreshAvailability();
  }

  /**
   * Create the booking the customer actually chose.
   *
   * The slot object is carried through whole — its own staffId, startUtc and
   * endUtc — so the instant booked is the instant offered. Nothing here parses a
   * displayed time back into a moment.
   *
   * Confirmation is shown only once the backend has confirmed. A failure stays
   * on the details step with the real message, because a wizard that advances
   * on a refusal has told the customer they have an appointment they do not.
   */
  submitAppointment() {
    const st = this.state;
    if (st.submitting) return;
    const slot = this.slotObjFor(st.day, st.slot);
    if (!slot) {
      this.setState({ bookingError: "That time is no longer available. Please choose another." });
      this.refreshAvailability();
      return;
    }
    const email = String(st.email || "").trim();
    if (!email) { this.setState({ bookingError: "An email address is required to confirm a booking." }); return; }
    const missingQ = this.serviceQuestions().filter((q) => q.required && this.answerEmpty((st.answers || {})[q.id]));
    if (missingQ.length) { this.setState({ answersTouched: true, bookingError: "Please answer: " + missingQ.map((q) => q.label).join(", ") + "." }); return; }
    this.setState({ submitting: true, bookingError: "" });
    submitBooking({
      slug: this.bookingSlug(),
      serviceId: st.svc,
      locationId: st.loc || undefined,
      slot,
      guest: {
        firstName: String(st.first || "").trim() || undefined,
        lastName: String(st.last || "").trim() || undefined,
        email,
        phone: String(st.phone || "").trim() || undefined,
      },
      // ONLY a choice the customer actually made. api-contracts'
      // notification-channel-policy is explicit that a RECORDED preference outranks
      // the tenant's configured channels, and that absence deliberately fails open to
      // that configuration -- "absence is not an opt-out; only a recorded choice is".
      // Falling back to the designer's `notifDefault` therefore did not supply a
      // default, it fabricated consent: every booking recorded a preference the
      // customer never expressed (shipped default "both") and overrode the tenant's
      // channel config with it.
      notificationPreference: st.s.notifPref ? (String(st.notifChoice || "").trim() || undefined) : undefined,
      answers: st.answers || {},
    }).then((response) => {
      if (this._gone) return;
      const payload = (response && (response.data || response)) || {};
      this.setState({ submitting: false, booking: payload, bookingError: "", previewStep: "confirmation" });
    }).catch((err) => {
      if (this._gone) return;
      const message = String((err && (err.message || err.detail)) || err);
      this.setState({ submitting: false, bookingError: message });
      // A refused slot is usually a slot someone else took between the read and
      // the submit, so re-read rather than leaving a stale calendar on screen.
      this.refreshAvailability();
    });
  }

  /** The journey whose slug this page or preview is showing. */
  bookingSlug() {
    if (this.isPublic()) return String(this.props.slug || "");
    const journey = this.state.apptJourney || {};
    return String(journey.slug || this.state.s.slug || "");
  }

  /**
   * The published journey, its settings and its catalogue — one read, no admin
   * route, and the same shapes the designer hydrates from. A slug that resolves
   * to nothing lands on the existing link-error step, which names no tenant.
   */
  bootstrapPublic() {
    const slug = this.bookingSlug();
    // Two reads, both public and both existing: the published config, and the
    // service catalogue. The config alone is not enough — the public surface
    // normalises away the hydrated `custom` block the admin row carries, which
    // is where a first attempt at this wrongly looked for services.
    Promise.all([
      loadPublishedJourney(slug, "public"),
      loadPublicCatalog(slug, "public"),
    ]).then((results) => {
      if (this._gone) return;
      const surface = results[0];
      const catalog = results[1];
      const config = surface && (surface.configJson || surface.config_json);
      if (!surface || !config) { this.setState({ previewStep: "linkError" }); return; }
      const hydrated = designerFromJourney(config);
      // A published journey whose catalogue read came back missing is an empty
      // wizard, not an invented one.
      this.adoptCatalog(buildCatalog(config, (catalog && catalog.services) || [], (catalog && catalog.locations) || [], []));
      const order = Array.isArray(config.order) ? config.order : [];
      this.setState((prev) => ({
        s: { ...DEFAULTS, ...hydrated.s },
        ct: { ...hydrated.ct },
        apptJourney: config,
        previewStep: (order[0] === "location" || config.flowOrder === "location-first") ? "location" : "service",
      }), () => this.startPublicAppointment());
    }).catch(() => { if (!this._gone) this.setState({ previewStep: "linkError" }); });
  }

  /** Start on the first step that remains after the published singleton rules
   * have been applied to the now-hydrated settings and public catalog. */
  startPublicAppointment() {
    const first = this.steps()[0] || "service";
    this.setState({ previewStep: first, section: this.sectionForStep(first) }, () => this.refreshAvailability());
  }

  /**
   * The standalone Event List Page (`/events`). Reads the live Events Page
   * settings (presentation + the authored `events` block, or the neutral
   * platform defaults when the tenant has authored nothing) and the first
   * real page of the catalogue with those settings mapped onto the wire
   * query. Filtering/sorting/paging beyond the first page happen through
   * `refreshPublicEventsList` below, never by re-slicing this page client-side.
   */
  /**
   * Whether the authored `events` block's catalogue-scoping (`scope.events`
   * — curated ids; `scope.orgNodes`; more than one `scope.categories`) can
   * actually be expressed through `getPublicEvents`'s query params (a single
   * `category` + `locationId`, no curated-id list, no org-node scope). When
   * it cannot, the read must not silently fall back to the tenant's WHOLE
   * catalogue and pretend that was the configured scope — the caller shows
   * an explicit unsupported-config state instead.
   */
  publicListScopeIsFulfillable(block: EventsBlockConfig): boolean {
    const scope = (block.scope || {}) as { events?: string[]; categories?: string[]; orgNodes?: string[] };
    if ((scope.events || []).length > 0) return false;
    if ((scope.orgNodes || []).length > 0) return false;
    if ((scope.categories || []).length > 1) return false;
    // The read door's `sort` has no companion `order` param — a `desc`
    // authored order cannot be expressed and rendering `asc` results under
    // it would silently misrepresent the configured order.
    if (block.order === "desc") return false;
    return true;
  }

  /** The single authored scope category, when the block scopes to exactly
   *  one — the one scope dimension the read door CAN express (`category`).
   *  ALWAYS wins over a visitor's own category chip: a fixed authored scope
   *  cannot be broadened or overridden from the public side. */
  publicListScopeCategory(block: EventsBlockConfig): string | undefined {
    const scope = (block.scope || {}) as { categories?: string[] };
    return (scope.categories || [])[0];
  }

  /** The authored forward window (`dateRange`) and lookback
   *  (`includePast`+`pastHorizonDays`) as `{from, to}` civil dates — the
   *  bounds `getPublicEvents`'s typed `from`/`to` can actually express.
   *  Mirrors `workers/portal/src/resolve.ts`'s `windowFor`/`eventsWindowFor`
   *  exactly, rather than guessed semantics:
   *    - an explicit `dateRange: {from, to}` is left ALONE — "an author who
   *      named both edges of the window has already said where their past
   *      ends"; `includePast`/`pastHorizonDays` never touch it.
   *    - otherwise the window is relative: `[now, now + horizonDays]`
   *      (default 7), widened backward to `now - pastHorizonDays` (default
   *      30) only when `includePast` is on. "Now" and the resulting civil
   *      day both resolve in the canonical resolver's OWN zone
   *      (`this.eventsPageTimeZone`, threaded from `page.timeZone`), never
   *      the visitor's browser zone. */
  publicListScopeDateBounds(block: EventsBlockConfig): { from?: string; to?: string } {
    const dateRange = block.dateRange as { from?: string; to?: string; horizonDays?: number } | undefined;
    if (typeof dateRange?.from === "string" && typeof dateRange?.to === "string") {
      return { from: dateRange.from, to: dateRange.to };
    }
    const timeZone = this.eventsPageTimeZone || "UTC";
    const nowMs = Date.now();
    const days = typeof dateRange?.horizonDays === "number" ? dateRange.horizonDays : 7;
    let fromMs = nowMs;
    const toMs = nowMs + days * 86_400_000;
    if (block.includePast) {
      const pastDays = typeof block.pastHorizonDays === "number" ? block.pastHorizonDays : 30;
      fromMs = nowMs - pastDays * 86_400_000;
    }
    return { from: civilDateInZone(fromMs, timeZone), to: civilDateInZone(toMs, timeZone) };
  }

  /** Intersects the authored bound with the visitor's own chosen bound —
   *  never the wider of the two (that would let a visitor filter reach
   *  outside the authored window). `undefined` means "no bound on this
   *  side"; the intersection keeps whichever bound is actually present and
   *  more restrictive. */
  intersectDateBound(authored: string | undefined, visitor: string | undefined, keepLater: boolean): string | undefined {
    if (!authored) return visitor || undefined;
    if (!visitor) return authored;
    return keepLater ? (visitor > authored ? visitor : authored) : (visitor < authored ? visitor : authored);
  }

  bootstrapPublicEventsList() {
    loadPublicEventsPage().then((page) => {
      if (this._gone) return;
      this.eventsPageBlock = page.block;
      this.eventsPagePresentation = page.eventJourney;
      this.publicListSkipSingleSlot = (page.eventJourney as { skipSingleEventSlot?: boolean }).skipSingleEventSlot === true;
      this.eventsPageTimeZone = page.timeZone || "UTC";
      // Hydrate the ACTUAL renderer state from the live config — the
      // header/footer chrome and every visual EventListPresentation field
      // this render tree already reads off `state.s` (showHeader/showFooter
      // are page.presentation's own authoritative fields, not part of
      // eventJourney, so they are set explicitly alongside it).
      this.setState((prev) => ({
        s: { ...prev.s, ...page.eventJourney, ...designerFieldsFromEventsBlockConfig(page.block), showHeader: page.showHeader, showFooter: page.showFooter },
        ct: { ...prev.ct, ...(page.eventJourney.customText || {}) },
        evtPage: 1,
        evtScopeUnsupported: !this.publicListScopeIsFulfillable(page.block),
      }), () => this.refreshPublicEventsList(1));
    }).catch(() => { if (!this._gone) this.setState({ previewStep: "linkError" }); });
  }

  /**
   * Load one server page of the public events catalogue using the authored
   * `events` block's semantic settings (sort/order/includePast/includeSoldOut)
   * plus whatever filters the visitor has chosen (category/location/date
   * range), and adopt it wholesale. Never pages or re-sorts the array the
   * server already paged — a new page replaces `this.eventsRows`, it does not append
   * to or re-slice it, except in "loadmore"/"scroll" presentation modes,
   * which the caller is responsible for concatenating explicitly.
   */
  refreshPublicEventsList(page: number, opts: { append?: boolean } = {}) {
    const st = this.state;
    const block: EventsBlockConfig = this.eventsPageBlock || NEUTRAL_EVENTS_BLOCK_CONFIG;
    const presentation = this.eventsPagePresentation;
    // "loadmore"/"scroll" accumulate pages; "pages" (and the default, when
    // config is unresolved yet) always replaces with exactly the requested
    // page — never both paged AND re-sliced client-side.
    const append = Boolean(opts.append) && presentation?.eventPaging !== "pages";
    // "curated"/"name" have no server sort=curated lane; curated has no
    // manual-order source on this read door, so it degrades to the door's
    // own default (date) rather than silently reinterpreting "curated".
    const sort = block.sort === "popularity" ? "popularity" : block.sort === "name" ? "name" : "date";
    this.setState({ evtListState: "loading", evtListError: "" });
    const requestId = (this._evtListRequestId = (this._evtListRequestId || 0) + 1);
    const authoredBounds = this.publicListScopeDateBounds(block);
    // Authored scope wins: a fixed category cannot be broadened by a
    // visitor's chip, and the effective date window is the INTERSECTION of
    // the authored bounds and whatever the visitor picked, never the wider
    // of the two.
    const scopeCategory = this.publicListScopeCategory(block);
    const hasExplicitDateRange = (() => {
      const dr = block.dateRange as { from?: string; to?: string } | undefined;
      return typeof dr?.from === "string" && typeof dr?.to === "string";
    })();
    // An explicit authored range already names its own edges — `upcoming`
    // (which filters to events that have not yet started) would otherwise
    // have the read door strip out exactly the history the range asked for,
    // even when `includePast` itself is off.
    const upcoming = hasExplicitDateRange ? false : !block.includePast;
    loadPublicEventsList({
      page,
      limit: block.pageSize,
      sort,
      includeNonBookable: true,
      includeSoldOut: block.includeSoldOut,
      upcoming,
      locationId: st.evtLocationFilter || undefined,
      category: scopeCategory || ((st.evtCat && st.evtCat !== "all") ? st.evtCat : undefined),
      from: this.intersectDateBound(authoredBounds.from, st.evtFrom || undefined, true),
      to: this.intersectDateBound(authoredBounds.to, st.evtTo || undefined, false),
      query: (st.evtQuery || "").trim() || undefined,
    }).then((result) => {
      if (this._gone || requestId !== this._evtListRequestId) return;
      const rows = result.data.map((row) => projectPublicEventToDesignerRow(row, this.props.brandColor || ""));
      this.eventsRows = append ? this.eventsRows.concat(rows) : rows;
      this.setState({
        evtPage: page,
        evtPagination: result.pagination,
        evtFacets: result.facets,
        evtListState: this.eventsRows.length === 0 ? "empty" : "ready",
        previewStep: "events",
      });
    }).catch((err) => {
      if (this._gone || requestId !== this._evtListRequestId) return;
      this.setState({ evtListState: "error", evtListError: String((err && err.message) || err) });
    });
  }

  /** Reset paging and re-read page 1 whenever a list filter changes (category,
   *  location, date range, search query) — never re-filters the page already
   *  on screen. Every filter-changing control must call this, not mutate
   *  `evtPage`/`evtCat`/etc. and leave the old server page on screen. */
  refreshPublicEventsListFromFilters(patch: Record<string, unknown>) {
    this.setState({ ...patch, evtPage: 1 }, () => this.refreshPublicEventsList(1));
  }

  /** Load the next server page. For "pages" presentation this REPLACES the
   *  current page (a genuine page-2 read, never an empty re-slice of page 1);
   *  for "loadmore"/"scroll" it appends. */
  loadMorePublicEvents() {
    const pagination = this.state.evtPagination;
    if (!pagination || !pagination.hasMore) return;
    this.refreshPublicEventsList((this.state.evtPage || 1) + 1, { append: true });
  }

  /** Jump directly to an explicit page ("pages" presentation's pager). */
  goToPublicEventsPage(page: number) {
    if (page < 1) return;
    this.refreshPublicEventsList(page, { append: false });
  }

  /** "scroll" presentation's actual sentinel: observes the mounted element
   *  and fetches the next real server page on intersection — pending/end
   *  guarded by `evtListState`/`evtPagination.hasMore`, same as the manual
   *  "Load more" button. Re-attaches on remount (the sentinel unmounts once
   *  `hasMore` goes false) and disconnects any prior observer first, so a
   *  re-render never accumulates duplicate observers on the same instance. */
  attachScrollSentinel(el: Element | null) {
    if (this._scrollSentinelObserver) { this._scrollSentinelObserver.disconnect(); this._scrollSentinelObserver = null; }
    this._scrollSentinelEl = el;
    if (!el || typeof IntersectionObserver !== "function") return;
    this._scrollSentinelObserver = new IntersectionObserver((entries) => {
      if (this._gone) return;
      const pagination = this.state.evtPagination;
      if (!entries.some((e) => e.isIntersecting)) return;
      if (this.state.evtListState === "loading" || !pagination?.hasMore) return;
      this.loadMorePublicEvents();
    }, { rootMargin: "200px" });
    this._scrollSentinelObserver.observe(el);
  }

  /**
   * One event's Landing Page (`/events/{eventId}`). Reads the single event's
   * real projection, including its resolved `landingDesign` (per-event, else
   * tenant default, else `null` — resolved server-side, never guessed here)
   * and its actual configured `registrationQuestions`.
   */
  bootstrapPublicEventDetail() {
    // Direct-load, no dependency on the list ever having run — this is the
    // whole reason `/events/{eventId}` works without visiting `/events`.
    const eventId = String(this.props.eventId || "");
    if (!eventId) { this.setState({ previewStep: "linkError" }); return; }
    this.selectPublicEvent(eventId, { isRoute: true });
  }

  /**
   * THE ONE selected-event loader — every path that puts a visitor on one
   * event's landing page (the direct `/events/{eventId}` route, a card click
   * from the list, or a related-event pick) goes through this, so none of
   * them can land on stale detail/questions/design/payment from whatever
   * event was previously selected.
   */
  selectPublicEvent(eventId: string, opts: { isRoute?: boolean; fromPopstate?: boolean; fromList?: boolean } = {}) {
    if (!eventId) { this.setState({ previewStep: "linkError" }); return; }
    // The direct-route mount and a popstate restore both reflect a URL that
    // is ALREADY correct — pushing again here would create a duplicate
    // history entry for the same navigation. A user-initiated selection
    // (card/related click) is the only case that adds one.
    // Origin is resolved BEFORE the push, so the entry this navigation
    // creates records the origin it actually has.
    const freshFromList = Boolean(opts.fromList) && !opts.isRoute && !opts.fromPopstate;
    // Restored only from the entry the browser navigated to — never from a
    // stale instance field, which would describe the previous selection.
    let restoredSkipped = false;
    if (opts.isRoute) {
      // A directly opened URL has no list behind it, whatever any history
      // marker says.
      this.publicEventFromList = false;
    } else if (opts.fromPopstate) {
      let marker: { fromList?: unknown; skipped?: unknown } | null = null;
      try { marker = ((window.history.state || {}) as { attendEventJourney?: { fromList?: unknown; skipped?: unknown } }).attendEventJourney || null; } catch {}
      this.publicEventFromList = Boolean(marker) && marker!.fromList === true;
      restoredSkipped = this.publicEventFromList && Boolean(marker) && marker!.skipped === true;
    } else if (freshFromList) {
      this.publicEventFromList = true;
    }
    // else: a related-event pick keeps the origin of the journey it is made
    // from — related-from-direct stays direct.
    const originFromList = this.publicEventFromList;
    if (this.isPublic() && !opts.isRoute && !opts.fromPopstate) this.pushPublicEventsHistory(eventId);
    this.publicLandingStepSkipped = false;
    this._eventRegIdemKey = null;
    this._eventRegIdemFingerprint = null;
    this.eventPaymentConfig = null;
    this.landingPresentation = null;
    this.landingRegistrationQuestions = [];
    // A generation guard, not just an id comparison: a stale completion from
    // an earlier mount (StrictMode's discarded first mount, or a previous
    // selection of this SAME event id) must not land after a newer
    // selection has started, even though `this.state.evt` would still equal
    // this `eventId` by the time it resolves.
    const requestId = (this._evtDetailRequestId = (this._evtDetailRequestId || 0) + 1);
    // Read the authored list setting NOW, at click time, and hold it in a
    // local: `this.publicListSkipSingleSlot` is mutable instance state that a
    // concurrent list re-bootstrap is free to rewrite while the detail
    // request is still in flight, which would settle this navigation on a
    // setting that was not the one in force when the visitor tapped the card.
    // The `fromList` intent is captured alongside it for the same reason.
    const fromList = freshFromList;
    const listSkipSingleSlot = this.publicListSkipSingleSlot;
    this.setState({
      evt: eventId,
      // Consistent with `backToEvents`/popstate: a real journey switch, not
      // just a `previewStep` flip, whichever direction the navigation is.
      journey: "landing",
      evtDetailState: "loading",
      evtPaymentState: "loading",
      // Event-specific form/registration state must not leak from whichever
      // event was selected before.
      selectedTicketId: null,
      answers: {},
      answersTouched: false,
      bookingError: "",
      booking: null,
      submitting: false,
      previewStep: opts.isRoute ? "landing" : "landing",
      section: "general",
    });
    loadPublicEventDetail(eventId).then((detail) => {
      if (this._gone || requestId !== this._evtDetailRequestId) return;
      this.eventsRows = [projectPublicEventToDesignerRow(detail, this.props.brandColor || ""), ...this.eventsRows.slice(1).filter((e) => e.id !== detail.id)];
      const presentation = detail.landingDesign ? detail.landingDesign.presentation : neutralLandingPresentationDefaults();
      this.landingPresentation = presentation;
      // Distinguishes "a landing document exists, and this field's value IS
      // the empty string the author explicitly left it" from "no document
      // exists at all, so there is nothing authored to show" — only the
      // second case may fall back to the event's own real facts.
      this.landingIsConfigured = Boolean(detail.landingDesign);
      this.landingRegistrationQuestions = detail.registrationQuestions || [];
      // "Skip event details for a single date/time" — an authored shortcut for
      // list entries ONLY, and only once the authoritative detail has both
      // loaded and confirmed the event is bookable with exactly one valid
      // one-off schedule. It moves the step AFTER every hydration above
      // (presentation, questions, related, payment config all still run), so
      // ticket tiers, paid-provider gating and validation are untouched; a
      // direct URL, a popstate restore or a related-event pick never carries
      // the intent and so always lands on Event details.
      const gate = detail as unknown as { bookable?: unknown; registrationOpen?: unknown; soldOut?: unknown };
      const skipToRegistration = (
        (fromList && listSkipSingleSlot)
        // A back/forward restore replays the shortcut the recorded entry
        // already took — still only if the event is genuinely eligible now.
        || restoredSkipped
      )
        && gate.bookable === true
        // The server's own registration gate and sold-out flag, read as the
        // literal booleans the contract declares — never `!soldOut` (which
        // would let an undisclosed `null` count as "places left") and never
        // derived from `remaining`.
        && gate.registrationOpen === true
        && gate.soldOut === false
        && hasSingleOneOffSchedule(detail);
      // Before the state flip, so the very render that lands on `details`
      // already computes the matching (landing-less) public rail.
      this.publicLandingStepSkipped = skipToRegistration;
      // Record the settled shape of THIS navigation on its own existing
      // history entry (no new entry, no URL change), so a later back/forward
      // returning here restores the same 3- or 4-step journey.
      try {
        const prevState = (window.history.state || {}) as Record<string, unknown>;
        window.history.replaceState({ ...prevState, attendEventJourney: { fromList: originFromList, skipped: skipToRegistration } }, "");
      } catch {}
      this.setState((prev) => ({
        evt: detail.id,
        s: { ...prev.s, ...presentation },
        ct: { ...prev.ct, ...(presentation.customText || {}) },
        // `landingDesign.blocks`/`.branding` are NOT rendered — this renderer
        // only understands the declarative `blockOrder`/`blocksOn` + `lp*`
        // row arrays already hydrated above from `presentation`. Freeform
        // block/branding data has no existing renderer here, so it is
        // deliberately not stored as state implying it does something.
        previewStep: skipToRegistration ? "details" : "landing",
        section: skipToRegistration ? "details" : prev.section,
        evtDetailState: "ready",
      }));
      this.loadRelatedPublicEvents(detail, requestId);
      // A refused READ (e.g. the known 403 on this endpoint) is a distinct
      // error — NOT the same fact as "no provider configured" — so paid
      // tiers stay gated (disabled) on either outcome, but the reason shown
      // to an author/operator differs. Never let this failure retroactively
      // block the free-tier path already rendering above.
      loadEventPaymentConfig(eventId).then((cfg) => {
        if (this._gone || requestId !== this._evtDetailRequestId) return;
        this.eventPaymentConfig = cfg;
        this.setState({ evtPaymentState: "ready" });
      }).catch((err) => {
        if (this._gone || requestId !== this._evtDetailRequestId) return;
        this.eventPaymentConfig = null;
        this.setState({ evtPaymentState: "error", evtPaymentError: String((err && err.message) || err) });
      });
    }).catch(() => { if (!this._gone && requestId === this._evtDetailRequestId) this.setState({ previewStep: "linkError", evtDetailState: "error" }); });
  }

  /** `{provider: null}` is a REAL, truthy response object meaning "no
   *  provider configured" — checking the object's own truthiness (as opposed
   *  to its `provider` field) would treat every resolved config, including
   *  this one, as usable. A config read that errored is NOT "no provider"
   *  either; both cases return false here, and the caller distinguishes them
   *  via `evtPaymentState` for the message it shows. */
  hasUsablePaymentProvider(): boolean {
    const cfg = this.eventPaymentConfig as { provider?: string | null; verified?: boolean } | null;
    return Boolean(cfg && cfg.provider && cfg.verified);
  }

  /** Debounced live search — mints one server request per pause in typing,
   *  not one per keystroke, still through the same server-filtered path. */
  debounceEvtQuery(value: string) {
    if (this._evtQueryDebounce) clearTimeout(this._evtQueryDebounce);
    this._evtQueryDebounce = setTimeout(() => {
      if (this._gone) return;
      this.refreshPublicEventsListFromFilters({ evtQuery: value });
    }, 300);
  }

  /** Real related-event choices for the landing page's "you might also like"
   *  picker — the server's own category catalogue, never the synthetic
   *  `e4`/`e5`/`e7` ids the old fixtures used. Falls back to the upcoming
   *  catalogue (no category filter) when the event has none. */
  loadRelatedPublicEvents(detail: PublicEventDetail, requestId: number) {
    loadPublicEventsList({ category: detail.category || undefined, limit: 5, upcoming: true })
      .then((result) => {
        if (this._gone || requestId !== this._evtDetailRequestId) return;
        const related = result.data
          .filter((row) => row.id !== detail.id)
          .map((row) => projectPublicEventToDesignerRow(row, this.props.brandColor || ""));
        this.eventsRows = [this.eventsRows[0], ...related].filter(Boolean);
        this.setState({ evtRelatedLoaded: true });
      })
      .catch(() => { /* related choices staying empty is honest; the primary event already loaded */ });
  }

  /**
   * Submit a public event registration. First/last/email are mandatory in the
   * current JSON contract regardless of the authored optional toggle, so this
   * refuses locally with the same message a server refusal would carry rather
   * than sending a request the contract will reject anyway. Ticket/payment
   * fields the current registration API does not accept (quantity-as-count,
   * address, notification preference) are never sent.
   */
  /** The "join the waitlist" CTA is the SAME details-form submission, with
   *  `waitlist: true` — never a second, bypassing API call. It reads the
   *  same first/last/email/answers/consent the visitor already entered in
   *  this event's own details form; a single-word name is never invented
   *  into both first and last (the shared validation below refuses it
   *  honestly, the same way it refuses any other incomplete name). */
  joinWaitlist() {
    this.submitEventRegistration({ waitlist: true });
  }

  submitEventRegistration(intent: { waitlist?: boolean } = {}) {
    // Preview NEVER posts — enforced HERE, at the single shared submission
    // method, so EVERY entrypoint is covered: the details-step submit from
    // `nextStep`, the sold-out landing's "join the waiting list" CTA
    // (`actions.joinWaitlist` → `joinWaitlist`), and any later caller. Real
    // contact details typed into a Studio preview must not create a
    // registration that would be indistinguishable from a customer's in the
    // tenant's own data. A no-op is the honest outcome: nothing is written
    // into `booking`/`registeredEventId` to imply a registration happened.
    if (!this.isPublic()) return;
    const st = this.state;
    const eventId = String(st.evt || "");
    // Disable-concurrent-clicks AND the post-success no-retry guard, the
    // latter keyed to the EVENT the successful result belongs to (not
    // `previewStep`, which back/details navigation can change without
    // undoing the registration) — navigating away and back must not be able
    // to silently create a second registration for the same event.
    if (st.submitting || st.registeredEventId === eventId) return;
    const first = String(st.first || "").trim();
    const last = String(st.last || "").trim();
    const email = String(st.email || "").trim();
    if (!first || !last || !email) {
      this.setState({ bookingError: "First name, last name and email are required." });
      return;
    }
    const questions = (this.landingRegistrationQuestions || []) as { id: string; label: string; required?: boolean }[];
    const answersMap = st.answers || {};
    const missingRequired = questions.filter((q) => q.required && answersMap[q.id] === undefined);
    if (missingRequired.length) {
      this.setState({ answersTouched: true, bookingError: `Please answer: ${missingRequired.map((q) => q.label).join(", ")}.` });
      return;
    }
    const eventRow = this.eventsRows.find((e) => e.id === eventId);
    const tiers = (eventRow?.raw?.ticketTiers || []) as { id: string; priceMinor: number }[];
    // Never silently pick a tier the visitor did not choose: with more than
    // one real tier, an explicit `selectedTicketId` is required; with
    // exactly one (or none — a tierless free event) the single/absent choice
    // is unambiguous.
    if (tiers.length > 1 && !st.selectedTicketId) {
      this.setState({ bookingError: "Choose a ticket type." });
      return;
    }
    const ticketId = tiers.length > 1 ? st.selectedTicketId : (st.selectedTicketId || tiers[0]?.id);
    const selectedTier = tiers.find((t) => t.id === ticketId);
    // A priced SELECTION — the actual tier's real `priceMinor`, never the
    // event-level priceFrom summary (can understate a specific tier) and
    // never the first tier when a different one was chosen — with no usable
    // payment provider cannot accept a paid registration. A free tier stays
    // registerable even on an otherwise-paid (mixed) event.
    const requiresPayment = Boolean(selectedTier && selectedTier.priceMinor > 0);
    if (requiresPayment && !this.hasUsablePaymentProvider()) {
      this.setState({ bookingError: this.state.evtPaymentState === "error" ? "Payment could not be verified for this event right now. Try again shortly." : "This event requires payment, and no payment method is configured for it." });
      return;
    }
    const answers = questions
      .map((q) => ({ questionId: q.id, value: answersMap[q.id] }))
      .filter((a) => a.value !== undefined);

    // Fresh idempotency key whenever the actual payload (event, ticket,
    // answers, contact) changes since the last attempt — same key across a
    // retry of the IDENTICAL payload, so a duplicate click after a network
    // failure cannot create a second registration. The key itself carries no
    // PII: it is `newIdempotencyKey()`'s random token, keyed off a
    // fingerprint that is compared, never transmitted.
    const waitlist = Boolean(intent.waitlist);
    const fingerprint = JSON.stringify({ eventId, ticketId, first, last, email, phone: st.phone, answers, consent: st.consent, waitlist });
    if (this._eventRegIdemFingerprint !== fingerprint) {
      this._eventRegIdemKey = newIdempotencyKey();
      this._eventRegIdemFingerprint = fingerprint;
    }
    this.setState({ submitting: true, bookingError: "" });
    submitEventRegistrationOp({
      eventId,
      ticketId,
      firstName: first,
      lastName: last,
      email,
      phone: String(st.phone || "").trim() || undefined,
      answers,
      agreedToTerms: Boolean(st.consent),
      waitlist,
    }, { idempotencyKey: this._eventRegIdemKey || undefined }).then((result) => {
      if (this._gone) return;
      this._eventRegIdemKey = null;
      this._eventRegIdemFingerprint = null;
      this.setState({ submitting: false, booking: result, bookingError: "", previewStep: "confirmation", registeredEventId: eventId });
    }).catch((err) => {
      if (this._gone) return;
      this.setState({ submitting: false, bookingError: String((err && err.message) || err) });
    });
  }

  /**
   * Studio Events tab load — the real draft for the reserved `events` slug,
   * or the neutral platform defaults when the tenant has authored nothing
   * yet (never a localStorage fallback). Called once from the admin branch of
   * `componentDidMount`, alongside the appointment/OneStop record loads.
   */
  loadEventsTabDraft() {
    this.eventsTabLoadState = "loading";
    loadEventsDocument().then((doc) => {
      if (this._gone) return;
      this.eventsDocBaseline = doc;
      this.eventsTabLoadState = "ready";
      const hydratedS = { ...doc.eventJourney, ...designerFieldsFromEventsBlockConfig(doc.block), showHeader: doc.showHeader, showFooter: doc.showFooter, slug: EVENTS_PAGE_SLUG };
      const hydratedCt = { ...(doc.eventJourney.customText || {}) };
      this.setState((prev) => ({
        s: prev.journey === "events" ? { ...prev.s, ...hydratedS } : prev.s,
        ct: prev.journey === "events" ? { ...prev.ct, ...hydratedCt } : prev.ct,
        stash: { ...prev.stash, events: { s: { ...prev.stash.events.s, ...hydratedS }, ct: { ...prev.stash.events.ct, ...hydratedCt } } },
        published: { ...prev.published, events: doc.isPublished },
        saved: { ...prev.saved, events: { s: hydratedS, ct: hydratedCt, at: new Date() } },
      }), () => {
        // The preview pool was fetched on mount, before this draft was known.
        // If the authored setting disagrees with the window it was fetched
        // under, re-read it rather than leaving past events unobtainable.
        if (Boolean(hydratedS.showPastEvents) !== Boolean(this._previewIncludedPast)) this.refreshPublicEventsPreview();
      });
    }).catch((err) => {
      if (this._gone) return;
      this.eventsTabLoadState = "error";
      this.setState({ eventsTabLoadError: String((err && err.message) || err) });
    });
  }

  /** Studio Landing tab load — the tenant-default landing document
   *  (`EVENT_LANDING_DEFAULT_KEY`). Per-event overrides are authored from the
   *  event's own record, not from this tenant-wide tab. */
  loadLandingTabDraft() {
    this.landingTabLoadState = "loading";
    loadLandingDocument(EVENT_LANDING_DEFAULT_KEY).then((doc) => {
      if (this._gone) return;
      this.landingDocBaseline = doc;
      this.landingTabLoadState = "ready";
      const hydratedS = { ...doc.presentation };
      const hydratedCt = { ...(doc.presentation.customText || {}) };
      this.setState((prev) => ({
        s: prev.journey === "landing" ? { ...prev.s, ...hydratedS } : prev.s,
        ct: prev.journey === "landing" ? { ...prev.ct, ...hydratedCt } : prev.ct,
        stash: { ...prev.stash, landing: { s: { ...prev.stash.landing.s, ...hydratedS }, ct: { ...prev.stash.landing.ct, ...hydratedCt } } },
        published: { ...prev.published, landing: doc.isPublished },
        saved: { ...prev.saved, landing: { s: hydratedS, ct: hydratedCt, at: new Date() } },
      }));
    }).catch((err) => {
      if (this._gone) return;
      this.landingTabLoadState = "error";
      this.setState({ landingTabLoadError: String((err && err.message) || err) });
    });
  }

  /**
   * Save (and optionally publish) the Events tab. PUT preserves the full
   * loaded baseline (title/header/locale/other blocks) and touches only
   * showHeader/showFooter/eventJourney and the `events` block's
   * semantic-mapped fields (`saveEventsDocument` merges the rest). Refuses to
   * save if the initial load errored (a failed read must not let a save
   * overwrite real settings with this tab's local defaults) or if a save is
   * already in flight. `customText` is read from `state.ct`, not `state.s`.
   */
  saveEventsTab(publish: boolean) {
    if (this.eventsTabSaving) return;
    if (this.eventsTabLoadState !== "ready") {
      this.setState({ saveNote: "error", saveError: this.eventsTabLoadState === "loading" ? "The Events page is still loading. Try again in a moment." : "The Events page could not be loaded, so saving now could overwrite real settings. Reload and try again." });
      return;
    }
    const st = this.state;
    const snapshotS = st.s, snapshotCt = st.ct;
    const presentation = { ...pickPresentationFields(snapshotS, EVENT_LIST_PRESENTATION_KEYS), customText: { ...(snapshotCt || {}) } } as unknown as EventListPresentation;
    const blockPatch = eventsBlockConfigFromDesignerFields(snapshotS);
    const baseline: EventsDocumentDraft = this.eventsDocBaseline || {
      raw: null, showHeader: true, showFooter: true, eventJourney: presentation, block: { ...NEUTRAL_EVENTS_BLOCK_CONFIG },
    };
    this.eventsTabSaving = true;
    saveEventsDocument(baseline, {
      showHeader: Boolean(snapshotS.showHeader ?? true),
      showFooter: Boolean(snapshotS.showFooter ?? true),
      eventJourney: presentation,
      blockPatch,
      timeZone: tzContext().zone,
    }).then((res) => publish ? publishEventsDocument().then(() => res) : Promise.resolve(res))
      .then((res) => {
        this.eventsTabSaving = false;
        this.eventsDocBaseline = { raw: res.data, showHeader: res.data.settings.showHeader, showFooter: res.data.settings.showFooter, eventJourney: presentation, block: { ...NEUTRAL_EVENTS_BLOCK_CONFIG, ...blockPatch, maxItems: null }, isPublished: publish || res.data.published };
        this.setState((prev) => ({
          // Only clear `dirty` if nothing changed while the request was in
          // flight — an edit made mid-save must not be reported as saved.
          dirty: prev.s === snapshotS && prev.ct === snapshotCt ? false : prev.dirty,
          saved: { ...prev.saved, events: { s: snapshotS, ct: snapshotCt, at: new Date() } },
          published: { ...prev.published, events: publish ? true : prev.published.events },
          saveNote: publish ? "published" : "saved", saveError: "",
        }));
      })
      .then(() => this.saveToast(true, publish))
      .catch((err) => { this.eventsTabSaving = false; this.failSave(err, publish); });
  }

  /** Save (and optionally publish) the tenant-default Landing document. Every
   *  authored `lp*` field and explicit empty value round-trips verbatim, read
   *  from `state.s` (visual fields) and `state.ct` (`customText`) — this is a
   *  PRESENTATION-ONLY patch: blocks/theme/branding/globalCss are never sent,
   *  so the server's partial-update semantics leave them exactly as they
   *  were. Refuses to save if the initial load errored, or while a save is
   *  already in flight. */
  saveLandingTab(publish: boolean) {
    if (this.landingTabSaving) return;
    if (this.landingTabLoadState !== "ready") {
      this.setState({ saveNote: "error", saveError: this.landingTabLoadState === "loading" ? "The Landing page is still loading. Try again in a moment." : "The Landing page could not be loaded, so saving now could overwrite real settings. Reload and try again." });
      return;
    }
    const st = this.state;
    const snapshotS = st.s, snapshotCt = st.ct;
    const presentation = { ...pickPresentationFields(snapshotS, EVENT_LANDING_PRESENTATION_KEYS), customText: { ...(snapshotCt || {}) } } as unknown as EventLandingPresentation;
    const hasExisting = Boolean(this.landingDocBaseline?.raw);
    this.landingTabSaving = true;
    const write = hasExisting
      ? updateLandingDocument(EVENT_LANDING_DEFAULT_KEY, presentation)
      : createLandingDocument(EVENT_LANDING_DEFAULT_KEY, presentation);
    write.then(() => publish ? publishLandingDocument(EVENT_LANDING_DEFAULT_KEY) : Promise.resolve())
      .then(() => loadLandingDocument(EVENT_LANDING_DEFAULT_KEY))
      .then((doc) => {
        this.landingTabSaving = false;
        this.landingDocBaseline = doc;
        this.setState((prev) => ({
          dirty: prev.s === snapshotS && prev.ct === snapshotCt ? false : prev.dirty,
          saved: { ...prev.saved, landing: { s: snapshotS, ct: snapshotCt, at: new Date() } },
          published: { ...prev.published, landing: doc.isPublished },
          saveNote: publish ? "published" : "saved", saveError: "",
        }));
      })
      .then(() => this.saveToast(true, publish))
      .catch((err) => { this.landingTabSaving = false; this.failSave(err, publish); });
  }

  /**
   * Point the wizard at a catalogue. Assigning the module bindings keeps every
   * existing reader unchanged; what has gone is the old behaviour of leaving a
   * baked Lakeview row on screen when a read came back empty.
   */
  adoptCatalog(catalog) {
    SERVICES = catalog.services;
    LOCATIONS = catalog.locations;
    STAFF = catalog.staff;
    DURATIONS = catalog.durations;
    this.catalogSource = "public";
    this.setState((prev) => ({
      svc: SERVICES.filter((x) => x.id === prev.svc)[0] ? prev.svc : ((SERVICES[0] && SERVICES[0].id) || ""),
      loc: LOCATIONS.filter((x) => x.id === prev.loc)[0] ? prev.loc : ((LOCATIONS[0] && LOCATIONS[0].id) || ""),
      staff: STAFF.filter((x) => x.id === prev.staff)[0] ? prev.staff : ((STAFF[0] && STAFF[0].id) || ""),
      dur: DURATIONS.filter((x) => x.id === prev.dur)[0] ? prev.dur : ((DURATIONS[0] && DURATIONS[0].id) || ""),
      cat: prev.journey === "appointment" ? (serviceCategoryOptions(SERVICES).some((option) => option.id === serviceFilterId(prev.cat)) ? serviceFilterId(prev.cat) : "all") : prev.cat,
    }));
  }

  /**
   * Load the real slots for the selected service. Both modes call this: the
   * designer preview used to draw slots generated from the calendar date, which
   * is fabricated availability shown to whoever is configuring the journey.
   */
  refreshAvailability() {
    const slug = this.bookingSlug();
    const serviceId = this.state.svc;
    if (!slug || !serviceId) { this.setState({ avail: EMPTY_AVAILABILITY, availState: "idle" }); return; }
    const staffId = this.state.s.staffSelect ? this.state.staff : "";
    this.setState({ availState: "loading", availError: "" });
    loadAvailability({ slug, serviceId, staffId, daysAhead: this.bookWindow() }).then((window) => {
      if (this._gone) return;
      if (this.isPublic() && window.staff.length && !STAFF.length) STAFF = window.staff;
      this.setState({ avail: window, availState: "ready", availError: "" }, () => this.initDay());
    }).catch((err) => {
      if (this._gone) return;
      // Never a silent empty calendar: an unreadable window is reported, so a
      // day with no slots cannot be confused with a read that failed.
      this.setState({ avail: EMPTY_AVAILABILITY, availState: "error", availError: String((err && err.message) || err) });
    });
  }

  /** The selected service's booking questions, as the catalogue resolved them
   *  (seed review step 06). Empty when the service has none or the read is old. */
  serviceQuestions() {
    const svc = SERVICES.filter((x) => x.id === this.state.svc)[0];
    return (svc && Array.isArray(svc.questions)) ? svc.questions : [];
  }

  /** The real, configured questions for whichever journey's details step is
   *  actually rendering — the appointment's catalogue questions, or (events/
   *  landing) `landingRegistrationQuestions` from the event's own detail read.
   *  Same renderable shape (`id/label/type/help/required/options`) either
   *  way, so the one existing question renderer below serves both without a
   *  second implementation. */
  activeQuestions() {
    if (this.state.journey === "events" || this.state.journey === "landing") {
      return this.landingRegistrationQuestions || [];
    }
    return this.serviceQuestions();
  }
  answerEmpty(v) { return v === undefined || v === null || v === "" || v === false || (Array.isArray(v) && !v.length); }

  /** The real slots the backend offered for one day. Never generated. */
  slotsFor(isoStr) {
    const window = this.state.avail || EMPTY_AVAILABILITY;
    return (window.byDay && window.byDay[isoStr]) || [];
  }

  /** The whole slot object behind a displayed time, so submit carries its UTC. */
  slotObjFor(isoStr, label) {
    return this.slotsFor(isoStr).filter((slot) => slot.time === label)[0] || null;
  }

  initDay() {
    if (!this.state.slot) {
      const iso = this.state.s.defaultDate === "firstAvailable" ? this.firstAvailable(this.bookWindow()) : this.state.day;
      const d = this.fromIso(iso);
      this.setState({ day: iso, calMonth: { y: d.getFullYear(), m: d.getMonth() }, slot: this.firstSlotFor(iso) });
    }
  }

  /** Appointments tab save/publish — the contract write path (drop 1).
   *  Nothing here is swallowed: a refusal sets the note AND the per-control rows. */
  saveAppointments(alsoPublish) {
    this.setState({ saveNote: "saving", saveError: "", refusedFields: [], saveNotices: [] });
    // Read the live state at call time: the id/create path may re-baseline first.
    const run = (id) => {
      const st = this.state;
      return saveAppointmentSettings(id, st.s, st.ct, { baseline: st.apptBaseline, journey: st.apptJourney }).then((res) => {
        if (!res.ok) { this.reportRefusal(res.refusal); return null; }
        if (!alsoPublish) return res;
        // An empty diff wrote nothing — but Publish was still asked for, and a
        // saved draft with no further edits is exactly the case where a customer
        // is waiting on it. Only an already-published journey has nothing to do.
        if (res.noop && String((res.journey || {}).status || "") === "published") return res;
        return publishAppointmentJourney(id).then((pub) => {
          if (!pub.ok) { this.reportRefusal(pub.refusal); return null; }
          return pub;
        });
      }).then((res) => {
        if (!res) return;
        const noop = !!res.noop;
        const journey = res.journey || st.apptJourney;
        // Re-baseline from the server's answer, so the next save diffs against what
        // was actually stored rather than what this screen believes it sent.
        const h = journey ? designerFromJourney(journey) : { s: {}, ct: {} };
        const base = { s: { ...DEFAULTS, ...h.s }, ct: { ...h.ct } };
        const notices = noop ? [] : this.saveNotices(st);
        this.setState((prev) => ({
          dirty: false,
          apptJourney: journey, apptBaseline: base,
          saved: { ...prev.saved, appointment: { s: prev.s, ct: prev.ct, at: new Date() } },
          published: alsoPublish && !noop ? { ...prev.published, appointment: true } : prev.published,
          saveNote: noop ? "nochange" : alsoPublish ? "published" : "saved",
          saveError: "", refusedFields: [], saveNotices: notices,
        }));
        if (noop) this.noopToast(); else this.saveToast(true, alsoPublish);
      }).catch((err) => this.failSave(err, alsoPublish));
    };
    const id = this.state.apptJourneyId;
    if (id) { run(id); return; }
    resolveAppointmentJourney(String(this.state.s.slug || "")).then((row) => {
      if (row && this.ownsJourney(row)) {
        return loadAppointmentJourney(String(row.id)).then((journey) => {
          this.adoptJourney(journey, false, row);
          return run(String(row.id));
        });
      }
      // No appointment journey on this tenant: make one from the 'classic' starter
      // rather than refusing the save.
      return createAppointmentJourney((this.props.tenantName || "Appointments") + " booking").then((res) => {
        if (!res.ok) { this.reportRefusal(res.refusal); return; }
        this.adoptJourney(res.journey, false);
        return run(String(res.journey.id));
      });
    }).catch((err) => this.failSave(err, alsoPublish));
  }

  /** Adopt a stored journey as this tab's source of truth. `seedForm` also writes
   *  the hydrated values into the open form (mount); a save-time adopt only
   *  re-baselines, so edits made before the save are not thrown away. */
  adoptJourney(journey, seedForm, row) {
    // The detail read can come back without the address fields the list row
    // carries; the row fills those gaps rather than the tab inventing a host.
    if (row) {
      journey = {
        ...journey,
        domain: journey.domain || row.domain,
        slug: journey.slug || row.slug,
        status: journey.status || row.status,
        // The server-declared public address survives adoption: the admin row
        // carries `publicUrl`/`publicPath`, and `publicLinkFor` reads them.
        publicUrl: journey.publicUrl || row.publicUrl,
        publicPath: journey.publicPath || row.publicPath,
      };
    }
    const h = designerFromJourney(journey);
    const s = { ...DEFAULTS, ...h.s };
    const ct = { ...h.ct };
    // The journey carries the slug the availability endpoint is keyed on, so the
    // preview can only load real slots once this has landed. Without this the
    // designer would show an empty calendar rather than the tenant's own.
    if (!this.isPublic()) setTimeout(() => {
      if (this._gone) return;
      this.refreshPublicCatalog();
      this.refreshAvailability();
    }, 0);
    this.setState((prev) => {
      const saved = { ...prev.saved, appointment: { s, ct, at: new Date(String(journey.updatedAt || Date.now())) } };
      const published = { ...prev.published, appointment: journey.status === "published" };
      // The baseline is the MERGED object, not the hydrator's partial output —
      // otherwise every default diffs on the first save.
      this.rememberJourneyId(journey.id);
      const next = { apptJourneyId: String(journey.id), apptJourney: journey, apptBaseline: { s, ct }, saved, published };
      if (!seedForm) return next;
      if (prev.journey === "appointment") return { ...next, s, ct, dirty: false };
      return { ...next, stash: { ...prev.stash, appointment: { s, ct } } };
    });
  }

  /* ── Walk-Ins seam ───────────────────────────────────────────────────
   *
   * The Walk-Ins tab writes through the SAME admin journey door the
   * Appointments tab does — `type: "queue"` instead of `"appointment"`, and a
   * `walkIn` presentation block instead of `steps`. Everything below mirrors
   * the appointment methods above so the two tabs cannot drift about what a
   * save, a publish or a refusal means.
   *
   * Nothing here writes on LOAD. Hydration is three reads (the journey row,
   * the journey, the tenant's queues) and a fourth for the public projection
   * of whichever queues are already attached; a tenant that has never opened
   * this tab ends it with exactly the rows it started with. */
  hydrateWalkIns() {
    if (this.isPublic()) return;
    // The tenant's real queues, for the author's explicit pick. A tenant with
    // none gets an empty picker and a stated reason — never an invented desk.
    listWalkInQueueOptions()
      .then((options) => { if (!this._gone) this.setState({ wiQueueOptions: options }); })
      .catch(() => { /* the picker says "could not be loaded" on its own */ });

    resolveWalkInJourney(String(this.state.s.slug || WALKIN_DEFAULT_SLUG))
      .then((row) => (row && this.ownsJourney(row)
        ? loadWalkInJourney(String(row.id)).then((journey) => ({ row, journey }))
        : null))
      .then((got) => { if (got) this.adoptWalkInJourney(got.journey, true, got.row); })
      .catch((err) => this.failSave(err, false));
  }

  /** Adopt a stored queue journey as this tab's source of truth. `seedForm`
   *  also writes the hydrated values into the open form (mount); a save-time
   *  adopt only re-baselines, so edits made before the save survive. */
  adoptWalkInJourney(journey, seedForm, row) {
    if (row) {
      journey = {
        ...journey,
        domain: journey.domain || row.domain,
        slug: journey.slug || row.slug,
        status: journey.status || row.status,
        // The server-declared public address survives adoption: the admin row
        // carries `publicUrl`/`publicPath`, and `publicLinkFor` reads them.
        publicUrl: journey.publicUrl || row.publicUrl,
        publicPath: journey.publicPath || row.publicPath,
      };
    }
    const h = designerFromWalkInJourney(journey);
    const s = { ...QUEUE_DEFAULTS, ...h.s };
    const ct = { ...h.ct };
    const queueIds = h.queueIds;
    this.setState((prev) => {
      const saved = { ...prev.saved, queue: { s, ct, at: new Date(String(journey.updatedAt || Date.now())) } };
      const published = { ...prev.published, queue: journey.status === "published" };
      const next = {
        wiJourneyId: String(journey.id), wiJourney: journey,
        wiBaseline: { s, ct, queueIds }, wiQueueIds: queueIds, saved, published,
      };
      if (!seedForm) return next;
      if (prev.journey === "queue") return { ...next, s, ct, dirty: false };
      return { ...next, stash: { ...prev.stash, queue: { s, ct } } };
    }, () => this.refreshWalkInQueues());
  }

  /**
   * The canonical public read of every attached queue — the SAME projection
   * the customer page reads, so the preview's service list, service points,
   * status and waits are the tenant's own and cannot disagree with what a
   * visitor is shown.
   */
  refreshWalkInQueues() {
    const ids = this.state.wiQueueIds || [];
    if (!ids.length) { this.setState({ wiQueues: [], wiQueuesLoading: false }); return; }
    this.setState({ wiQueuesLoading: true });
    loadWalkInQueues(ids)
      .then((reads) => { if (!this._gone) this.setState({ wiQueues: reads, wiQueuesLoading: false }); })
      .catch(() => { if (!this._gone) this.setState({ wiQueuesLoading: false }); });
  }

  /** Attach / detach one of the tenant's real queues. Marks the tab dirty; the
   *  selection only reaches the server on Save, like every other setting. */
  toggleWalkInQueue(id) {
    this.setState((prev) => {
      const list = (prev.wiQueueIds || []).slice();
      const at = list.indexOf(id);
      if (at > -1) list.splice(at, 1); else list.push(id);
      return { wiQueueIds: list, dirty: true };
    }, () => this.refreshWalkInQueues());
  }

  saveWalkIns(alsoPublish) {
    this.setState({ saveNote: "saving", saveError: "", refusedFields: [], saveNotices: [] });
    const run = (id) => {
      const st = this.state;
      return saveWalkInSettings(id, st.s, st.ct, st.wiQueueIds || [], { baseline: st.wiBaseline, journey: st.wiJourney })
        .then((res) => {
          if (!res.ok) { this.reportRefusal(res.refusal); return null; }
          if (!alsoPublish) return res;
          if (res.noop && String((res.journey || {}).status || "") === "published") return res;
          return publishWalkInJourney(id).then((pub) => {
            if (!pub.ok) { this.reportRefusal(pub.refusal); return null; }
            return pub;
          });
        })
        .then((res) => {
          // A refusal already reported itself; the draft stays DIRTY so the
          // operator does not lose what they typed and can retry.
          if (!res) return;
          const noop = !!res.noop;
          const journey = res.journey || this.state.wiJourney;
          const h = journey ? designerFromWalkInJourney(journey) : { s: {}, ct: {}, queueIds: [] };
          const base = { s: { ...QUEUE_DEFAULTS, ...h.s }, ct: { ...h.ct }, queueIds: h.queueIds };
          this.setState((prev) => ({
            dirty: false,
            wiJourney: journey, wiBaseline: base, wiQueueIds: h.queueIds.length ? h.queueIds : prev.wiQueueIds,
            saved: { ...prev.saved, queue: { s: prev.s, ct: prev.ct, at: new Date() } },
            // The pill reflects the SERVER's answer, not the button that was
            // pressed: a publish that no-op'd on an already-published journey
            // is still published, and a save is never reported as one.
            published: { ...prev.published, queue: String((journey || {}).status || "") === "published" },
            saveNote: noop ? "nochange" : alsoPublish ? "published" : "saved",
            saveError: "", refusedFields: [], saveNotices: [],
          }));
          if (noop) this.noopToast(); else this.saveToast(true, alsoPublish);
        })
        .catch((err) => this.failSave(err, alsoPublish));
    };
    const id = this.state.wiJourneyId;
    if (id) { run(id); return; }
    resolveWalkInJourney(String(this.state.s.slug || WALKIN_DEFAULT_SLUG)).then((row) => {
      if (row && this.ownsJourney(row)) {
        return loadWalkInJourney(String(row.id)).then((journey) => {
          this.adoptWalkInJourney(journey, false, row);
          return run(String(row.id));
        });
      }
      return createWalkInJourney((this.props.tenantName || "Walk-ins") + " line").then((res) => {
        if (!res.ok) { this.reportRefusal(res.refusal); return; }
        this.adoptWalkInJourney(res.journey, false);
        return run(String(res.journey.id));
      });
    }).catch((err) => this.failSave(err, alsoPublish));
  }

  /** The admin list op is not tenant-scoped here, so a row belonging to another
   *  tenant must never hydrate or be patched by this tab. Rows carry a short
   *  tenant key; the shell carries a served deployment slug. */
  apptKey() { return "apd.apptJourney." + String(this.props.tenant || "default"); }
  rememberedJourneyId() {
    try { return localStorage.getItem(this.apptKey()) || ""; } catch (e) { return ""; }
  }
  rememberJourneyId(id) {
    try { localStorage.setItem(this.apptKey(), String(id)); } catch (e) {}
  }

  ownsJourney(row) {
    // A journey this tab created under this tenant is ours whatever the row says.
    if (row && this.rememberedJourneyId() && String(row.id) === this.rememberedJourneyId()) return true;
    const theirs = String((row && row.tenant) || "").toLowerCase().replace(/[^a-z]/g, "");
    const mine = String(this.props.tenant || "").toLowerCase().replace(/[^a-z]/g, "");
    if (!theirs || !mine) return true;
    return mine.indexOf(theirs) > -1 || theirs.indexOf(mine) > -1;
  }

  /** What a successful save could NOT put on the visitor's page, said out loud. */
  saveNotices(st) {
    const out = [];
    const unmapped = unmappedKeys(st.s, st.apptBaseline && st.apptBaseline.s);
    const notLive = notYetLiveKeys(st.s);
    const inert = inertTextKeys(st.ct);
    if (unmapped.length) out.push("Not carried by the booking contract, so not stored: " + unmapped.join(", ") + ".");
    if (notLive.length) out.push("Stored, but the public page does not render it yet: " + notLive.join(", ") + ".");
    if (inert.length) out.push("Custom text the public page does not read yet: " + inert.join(", ") + ".");
    return out;
  }

  /** The origin the public link, embed snippet and QR codes point at: the
   *  appointment journey's own domain when the server knows one. */

  /** Attend is its own DOMAIN, so its ROOT is the event list and `/{eventId}`
   *  is one event — the `/events` prefix the first cut composed was a path
   *  segment invented by this screen, not something the server ever declared.
   *  `/events` and `/events/{id}` stay live as compatibility aliases (the
   *  attend worker owns that redirect/serve decision, and
   *  `public-events.tsx`'s parser accepts both), so a link already in the
   *  wild keeps working — but nothing here COMPOSES one any more.
   *
   *  A visitor who arrived through the alias keeps browsing inside it: this
   *  returns the base the current location is actually mounted under, so a
   *  pushState from `/events/a` goes to `/events/b` rather than jumping the
   *  visitor onto the canonical root mid-session. */
  publicRouteBase(): string {
    try {
      const first = String(window.location.pathname || "").split("/").filter(Boolean)[0] || "";
      if (decodeURIComponent(first) === EVENTS_PAGE_SLUG) return "/" + EVENTS_PAGE_SLUG;
    } catch {}
    return "";
  }

  pushPublicEventsHistory(eventId: string) {
    // A placeholder marker: `skipped` is only authoritative once the detail
    // has resolved, at which point `selectPublicEvent` replaces it in place.
    try { window.history.pushState({ attendEventJourney: { fromList: this.publicEventFromList, skipped: false } }, "", `${this.publicRouteBase()}/${encodeURIComponent(eventId)}`); } catch {}
  }

  pushPublicEventsListHistory() {
    try { window.history.pushState({}, "", this.publicRouteBase() || "/"); } catch {}
  }

  /** Restores state to match a URL the browser already navigated to (back/
   *  forward) — never pushes a new entry itself, that would duplicate the
   *  one the browser just created. Malformed/extra-segment paths land on the
   *  same honest not-found state the initial mount would show. */
  handlePublicEventsPopState() {
    if (this._gone || !this.isPublic()) return;
    let pathname = "";
    try { pathname = window.location.pathname; } catch { return; }
    // The SAME parser the standalone mount used on first load
    // (`parsePublicEventsPath`), so a root-mounted `/{eventId}` resolves
    // identically whether the visitor typed it or reached it with the back
    // button. It read `parts[1]` before, which under the canonical root
    // scheme is never the id: `/{eventId}` would have been mistaken for the
    // list, silently dropping the visitor's back-navigation on the wrong page.
    const route = parsePublicEventsPath(pathname);
    if (route.invalid) { this.setState({ previewStep: "linkError" }); return; }
    if (!route.eventId) {
      // Same fix as `backToEvents`: an actual journey switch, not just a
      // step flip, so `isEvents()`/`usingServerEvents` are true and the
      // real page-1 config + rows load rather than falling back to
      // whatever detail/related rows are still in `eventsRows`.
      // Same invalidation as `backToEvents` — a back-navigation to the list
      // outruns a detail request that would otherwise settle on top of it.
      this._evtDetailRequestId = (this._evtDetailRequestId || 0) + 1;
      this.publicLandingStepSkipped = false;
      this.publicEventFromList = false;
      this.setState({ journey: "events", previewStep: "events", section: "events" }, () => this.bootstrapPublicEventsList());
      return;
    }
    this.setState({ journey: "landing" }, () => this.selectPublicEvent(route.eventId, { fromPopstate: true }));
  }

  publicOrigin() {
    if (this.state.journey === "events" || this.state.journey === "landing") {
      if (this.isPublic()) {
        // The anonymous mount HAS no tenant (`tenant=""`, no config hook) —
        // asking `publicHost()` here just drives an authenticated Studio
        // settings fetch that can only fail for this mount and falls back
        // to an invented `attend.your-workspace`. The browser's own origin
        // IS the canonical one already; this mount is served from it.
        try { return window.location.origin; } catch { return ""; }
      }
      // Studio preview: the real, resolved attend config only. `publicHost`
      // returns a same-shaped fallback host before the config has loaded,
      // so callers that must not act on an invented host gate on
      // `attendConfigResolved` (set once `ensurePublicConfig()` settles)
      // rather than trusting this return value alone.
      //
      // The configured authority is taken WHOLE — port included. `attendHost`
      // is the authority the deployment declared (`localhost:18811` locally,
      // the tenant's real Attend domain live); this composes around it and
      // never rewrites, truncates or substitutes a hostname of its own.
      // If it already declares a scheme, that scheme wins, because a config
      // that says `http://` is stating something Studio's own protocol cannot
      // tell us. Otherwise the SCHEME follows Studio's origin: a local
      // generator maps the canonical attend host to a plain-HTTP local port,
      // and hardcoding `https://` there fails TLS against it.
      const authority = splitPublicAuthority(publicHost("attend", this.props.tenant));
      if (!authority.host) return "";
      let scheme = "https:";
      try { if (window.location.protocol === "http:" || window.location.protocol === "https:") scheme = window.location.protocol; } catch {}
      return (authority.scheme || scheme) + "//" + authority.host;
    }
    // Walk-Ins lives on the JOIN host. Deriving it from `publicLink()` keeps
    // the origin and the link from ever disagreeing, and keeps the `book`
    // fallback below off a walk-in embed snippet or QR.
    if (this.state.journey === "queue") {
      const walkInLink = this.publicLink();
      return walkInLink ? walkInLink.replace(/^([a-z]+:\/\/[^/]+).*$/, "$1") : "";
    }
    const link = this.state.journey === "appointment" ? publicLinkFor(this.state.apptJourney) : "";
    if (link) return link.replace(/^(https?:\/\/[^/]+).*$/, "$1");
    /* No vanity-domain literal. `publicHost("book")` resolves the tenant's own
       public config and degrades to a bare `book.<slug>` when it has not
       loaded — never another tenant's real domain (2026-09-08). */
    return "https://" + (this.props.publicHost || publicHost("book", this.props.tenant));
  }

  /** The journey's public address, or "" when the server has not given it one. */
  publicLink() {
    if (this.state.journey === "appointment") return publicLinkFor(this.state.apptJourney);
    if (this.state.journey === "landing") {
      // The per-event path the server itself declared (`row.public_path`) —
      // never a hand-composed `/{slug}` guess, and never the appointment host.
      // `canonicalEventPath` only strips the legacy `/events` alias prefix off
      // the front, so the printed link is the canonical `/{eventId}` while the
      // server keeps deciding WHICH event. A row that already carries `/{id}`
      // passes through untouched.
      const row = this.eventsRows.find((e) => e.id === this.state.evt);
      const origin = this.publicOrigin();
      if (!origin) return "";
      const path = canonicalEventPath(row?.publicPath || "");
      if (path) return origin + path;
      return "";
    }
    // Attend's ROOT is the event list — it is a dedicated domain, so there is
    // no `/events` segment to compose. `/events` remains served as a
    // compatibility alias; it is simply no longer what we hand anyone.
    if (this.state.journey === "events") {
      const origin = this.publicOrigin();
      return origin ? origin + "/" : "";
    }
    // Walk-Ins is served on the tenant's JOIN host. `publicHost("join")` now
    // returns the SERVED authority (`joinHost`, resolved by the canonical
    // `publicBaseUrlFor(queue)`) or "" — it no longer composes
    // `join.<tenant>.<rootDomain>`, which was a hostname that resolved to
    // nothing while the real surface was on a local `localhost:<port>`.
    //
    // The authority is used WHOLE. There is no shape check on it: an earlier
    // cut required a dot in the hostname to weed out the composed fallback,
    // and that heuristic rejected the canonical local `localhost:18812`
    // outright. With the fallback gone there is nothing to weed out — a
    // non-empty value here IS the configured authority, whatever it looks
    // like, and "" is the only "no link yet".
    //
    // NEVER the `book` host: that is the appointment surface and serves no
    // queue page at all.
    if (this.state.journey === "queue") {
      if (this.isPublic()) { try { return window.location.origin + "/" + String(this.state.s.slug || WALKIN_DEFAULT_SLUG); } catch { return ""; } }
      // The server's own `publicUrl`/`publicPath` on the published journey
      // wins; the join-authority composition below is the fallback for a row
      // the server has not yet given an address.
      if (this.state.wiJourney) { const served = publicLinkFor(this.state.wiJourney); if (served) return served; }
      const joinAuthority = splitPublicAuthority(publicHost("join", this.props.tenant));
      if (!joinAuthority.host) return "";
      // Scheme: what the config declared, else Studio's own — so a local
      // plain-HTTP join surface is not linked over https and failed on TLS.
      let joinScheme = "https:";
      try { if (window.location.protocol === "http:" || window.location.protocol === "https:") joinScheme = window.location.protocol; } catch {}
      return (joinAuthority.scheme || joinScheme) + "//" + joinAuthority.host + "/" + String(this.state.s.slug || WALKIN_DEFAULT_SLUG);
    }
    return "https://" + (this.props.publicHost || publicHost("book", this.props.tenant)) + "/" + String(this.state.s.slug || this.state.journey);
  }

  /** One success/failure announcement for every save path (toast + the inline note). */
  saveToast(ok, published) {
    try {
      toast(published
        ? { tone: "success", title: "Published", subtitle: "This journey is live for customers now.", autoDismissMs: 4000 }
        : { tone: "success", title: "Saved", subtitle: "Your changes are stored.", autoDismissMs: 4000 });
    } catch (e) {}
  }

  noopToast() {
    try {
      toast({ tone: "info", title: "No changes to save", subtitle: "Nothing here differs from what is already live.", autoDismissMs: 4000 });
    } catch (e) {}
  }

  errorToast(message, published) {
    try {
      toast({ tone: "error", title: published ? "Couldn't publish" : "Couldn't save", subtitle: message, autoDismissMs: 9000 });
    } catch (e) {}
  }

  failSave(err, published) {
    const message = String((err && err.message) || err || "Something went wrong.");
    this.setState({ saveNote: "error", saveError: message, refusedFields: [] });
    this.errorToast(message, published);
  }

  /** One place that turns a typed refusal into what the operator reads. */
  reportRefusal(refusal) {
    /* 2026-09-08 (Nick): the server sometimes sends the WHOLE zod issue array as
       one message string, which rendered as a wall of JSON across the header.
       Expand it into one row per issue with the path as the label. */
    const expand = (f) => {
      const raw = String(f.message || "").trim();
      if (raw.charAt(0) === "[") {
        try {
          const issues = JSON.parse(raw);
          if (Array.isArray(issues)) return issues.map((is) => ({
            path: Array.isArray(is.path) ? is.path.join(".") : String(is.path || f.path),
            label: Array.isArray(is.path) ? is.path.filter((p) => typeof p === "string").join(" › ") : f.path,
            panel: (f.control && f.control.panel) || "",
            message: is.message === "Required" ? "This field is required." : String(is.message || "The server refused this value."),
          }));
        } catch (err) { /* fall through to the single row */ }
      }
      return [{
        path: f.path,
        label: (f.control && f.control.label) || f.path,
        panel: (f.control && f.control.panel) || "",
        message: f.message || "The server refused this value.",
      }];
    };
    const rows = (refusal.fields || []).flatMap(expand);
    const headline = refusal.kind === "config-invalid"
      ? (rows.length ? "The server refused " + rows.length + (rows.length === 1 ? " setting." : " settings.") : refusal.message)
      : refusal.kind === "envelope-invalid"
        ? "This save was refused as malformed: " + refusal.message
        : refusal.kind === "not-found"
          ? "That journey no longer exists on this tenant."
          : refusal.message;
    this.setState({ saveNote: "error", saveError: headline, refusedFields: rows });
    this.errorToast(headline, false);
  }

  today() { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); }

  iso(d) { return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); }

  fromIso(s) { const p = (s || "").split("-"); return new Date(+p[0], +p[1] - 1, +p[2]); }

  parsePhone(raw, pickedIso) {
    const typed = String(raw || "");
    const digits = typed.replace(/\D/g, "");
    const intl = typed.trim().charAt(0) === "+";
    const picked = DIAL.filter((c) => c.iso === pickedIso)[0];
    let country = picked || DIAL[0], national = digits;
    if (intl) {
      const hit = DIAL.slice().sort((a, b) => b.dial.length - a.dial.length)
        .filter((c) => digits.indexOf(c.dial) === 0)[0];
      if (hit) { country = hit; national = digits.slice(hit.dial.length); }
    }
    const len = national.length;
    return { country, national, len, intl, valid: len >= country.min && len <= country.max };
  }

  ianaFor(zone) {
    return ({ eastern: "America/New_York", central: "America/Chicago", mountain: "America/Denver", pacific: "America/Los_Angeles", alaska: "America/Anchorage", hawaii: "Pacific/Honolulu" })[zone] || "America/Chicago";
  }

  liveZones() {
    const seen = {};
    LOCATIONS.forEach((x) => { if (x.tz) seen[x.tz] = true; });
    return ZONE_ORDER.filter((z) => seen[z]);
  }

  zoneFor(locId) {
    const l = LOCATIONS.filter((x) => x.id === locId)[0];
    return (l && l.tz) || this.liveZones()[0] || "central";
  }

  hoursUntilAppt() {
    const d = this.fromIso(this.state.day);
    const m = /(\d+):(\d+)\s*(AM|PM)/i.exec(this.state.slot || "");
    if (m) {
      let h = parseInt(m[1], 10) % 12;
      if (/pm/i.test(m[3])) h += 12;
      d.setHours(h, parseInt(m[2], 10), 0, 0);
    }
    return (d - new Date()) / 3600000;
  }

  ordinal(n) {
    const s2 = ["th", "st", "nd", "rd"], v = n % 100;
    return n + (s2[(v - 20) % 10] || s2[v] || s2[0]);
  }

  bookWindow() {
    const svc = SERVICES.filter((x) => x.id === this.state.svc)[0];
    return (svc && svc.window) || 60;
  }

  /**
   * How many real slots the backend offered for a day.
   *
   * This used to be arithmetic on the calendar date — closed Sundays, alternate
   * Saturdays, one "fully booked" day a week, and 3 to 11 slots from
   * `3 + ((date * 7) % 9)`. It looked plausible and matched nothing, on the
   * designer preview as much as on the customer page.
   */
  slotCount(d, daysAhead) {
    return this.slotsFor(this.iso(d)).length;
  }

  durMinutes() {
    const st = this.state;
    const cur = SERVICES.filter((x) => x.id === st.svc)[0] || SERVICES[0] || EMPTY_SERVICE;
    const src = cur.dur === "multi"
      ? (DURATIONS.filter((d) => d.id === st.dur)[0] || DURATIONS[0] || EMPTY_DURATION).label
      : cur.dur;
    let mins = 0;
    const hr = /(\d+)\s*(hour|hr)/i.exec(src);
    const mn = /(\d+)\s*(minute|min)/i.exec(src);
    if (hr) mins += parseInt(hr[1], 10) * 60;
    if (mn) mins += parseInt(mn[1], 10);
    return mins || 30;
  }

  endTime(slot, mins) {
    const m = /(\d+):(\d+)\s*(AM|PM)/i.exec(slot || "");
    if (!m) return "";
    let h = parseInt(m[1], 10) % 12;
    if (/pm/i.test(m[3])) h += 12;
    const total = h * 60 + parseInt(m[2], 10) + mins;
    const eh = Math.floor(total / 60) % 24;
    const em = total % 60;
    const ampm = eh >= 12 ? "pm" : "am";
    const h12 = eh % 12 === 0 ? 12 : eh % 12;
    return h12 + ":" + String(em).padStart(2, "0") + ampm;
  }

  firstSlotFor(isoStr) {
    const first = this.slotsFor(isoStr)[0];
    return first ? first.time : "";
  }

  firstAvailable(daysAhead) {
    const t = this.today();
    for (let i = 0; i <= (daysAhead || 60); i++) {
      const d = new Date(t.getFullYear(), t.getMonth(), t.getDate() + i);
      if (this.slotsFor(this.iso(d)).length > 0) return this.iso(d);
    }
    return this.iso(t);
  }

  fmtDay(isoStr, long) {
    const d = this.fromIso(isoStr);
    const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
    const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()];
    return dow + ", " + mon + " " + d.getDate() + (long ? ", " + d.getFullYear() : "");
  }

  /** Whether a catalogue row may be opened from the search shortcut at all.
   *  Same authoritative fields the cards use (`bookable`/`registrationOpen`/
   *  `soldOut`/`waitlistOpen`, and `bookable_reason: "completed"` for a past
   *  event) — so a shortcut can never reach registration or the event
   *  detail step for something the list itself renders as closed. Rows with
   *  no projection yet keep the previous permissive behaviour. */
  eventRowSelectable(e) {
    const av = e && e.availability;
    if (!av) return true;
    if (av.bookableReason === "completed") return false;
    if (av.bookable === true && av.registrationOpen === true && av.soldOut === false) return true;
    // Authoritative in BOTH the Studio preview and the public page: the
    // preview's rows come from the same `getPublicEvents` read, so a row that
    // carries a projection is judged on `waitlistOpen`, never on the author's
    // switch alone. The switch is the fallback only when no projection exists.
    return Boolean(this.state.s.waitlist) && av.soldOut === true && av.waitlistOpen === true;
  }

  ctv(group, field, def) {
    const v = this.state.ct[group + "." + field];
    return v && v.trim() ? v : def;
  }

  isQueue() { return this.state.journey === "queue"; }
  isKiosk() { return this.state.journey === "kiosk"; }
  journeyOptions() { return [{ value: "appointment", label: "Appointments" }, { value: "queue", label: "Walk-Ins" }, { value: "events", label: "Event List Page" }, { value: "landing", label: "Event Landing Page" }, { value: "kiosk", label: "Kiosk" }, { value: "onestop", label: "OneStop" }]; }
  isEvents() { return this.state.journey === "events"; }
  isLanding() { return this.state.journey === "landing"; }
  defaults() { return this.isQueue() ? QUEUE_DEFAULTS : this.isKiosk() ? KIOSK_DEFAULTS : this.isEvents() ? EVENT_DEFAULTS : this.isLanding() ? LANDING_DEFAULTS : DEFAULTS; }
  sectionKeys() { return this.isQueue() ? QUEUE_SECTION_KEYS : this.isKiosk() ? KIOSK_SECTION_KEYS : this.isEvents() ? EVENT_SECTION_KEYS : this.isLanding() ? LANDING_SECTION_KEYS : SECTION_KEYS; }
  panels() {
    if (this.isQueue()) return Object.assign({}, PANELS, QUEUE_PANELS);
    if (this.isKiosk()) return Object.assign({}, PANELS, KIOSK_PANELS);
    if (this.isEvents()) return Object.assign({}, PANELS, EVENT_PANELS);
    if (this.isLanding()) return Object.assign({}, PANELS, LANDING_PANELS);
    return PANELS;
  }
  ctGroupDefs() { return this.isQueue() ? QUEUE_CT_GROUPS : this.isKiosk() ? KIOSK_CT_GROUPS : this.isEvents() ? EVENT_CT_GROUPS : CT_GROUPS; }

  locsForService(svcId) {
    const svc = SERVICES.filter((x) => x.id === svcId)[0];
    const ids = svc && svc.locs ? svc.locs : LOCATIONS.map((x) => x.id);
    return LOCATIONS.filter((x) => ids.indexOf(x.id) > -1);
  }

  steps() {
    const s = this.state.s;
    const pinned = !!this.state.pinnedLoc;
    if (this.isKiosk()) return ["kioskEntry", "kioskCheckin", "kioskDone"];
    // The landing step is a REAL step of the events rail, not decoration: the
    // Studio sidebar, the preview progress row and the public list all read
    // this one list, so "Show event details step" changes the count in every
    // one of them (4 on, 3 off).
    if (this.isLanding()) {
      // A direct event link (or a related pick/popstate) always keeps its
      // Event page; only the list shortcut, once the authoritative detail
      // confirmed it, drops the step for THIS selection. The non-public
      // Event Landing Page tab keeps its own three-step rail unchanged.
      // Only a list-origin selection owns the `events` step; a direct URL
      // (or a related pick made from one) is the plain three-step rail.
      if (this.isPublic() && this.publicEventFromList) return ["events", ...(this.publicLandingStepSkipped ? [] : ["landing"]), "details", "confirmation"];
      return ["landing", "details", "confirmation"];
    }
    if (this.isEvents()) {
      // Off = 3 steps, on = 4. The one exception: a preview selection that
      // was NOT eligible for the shortcut still shows the Event page, so
      // while that is the previewed step the rail keeps it rather than
      // leaving the active pane pointing at a step no longer in the list.
      const keepLanding = !s.skipSingleEventSlot || this.state.previewStep === "landing";
      return keepLanding
        ? ["events", "landing", "details", "confirmation"]
        : ["events", "details", "confirmation"];
    }
    if (this.isQueue()) {
      const q = ["service"];
      if (s.queueLocationStep && !pinned) q.push("location");
      q.push("details", "confirmation");
      return q;
    }
    // The kiosk already decided where you are, so there is nothing to choose and no map to open.
    if (pinned) {
      let k = ["service"];
      if (s.skipService && SERVICES.length === 1) k = [];
      return k.concat(["datetime", "details", "confirmation"]);
    }
    let arr = s.firstStep === "service" ? ["service", "location"] : ["location", "service"];
    if (s.firstStep === "map") arr = ["location", "service"];
    // A step with only one thing to choose is auto-selected and dropped, whichever
    // order the journey runs in — so location-first skips the service step too.
    if (s.skipService && SERVICES.length === 1) arr = arr.filter((x) => x !== "service");
    // A service offered at exactly one location needs no location step.
    const svcLocs = this.locsForService(this.state.svc);
    const oneLoc = s.firstStep === "service" ? svcLocs.length === 1 : LOCATIONS.length === 1;
    if (s.skipLocation && oneLoc && s.firstStep !== "map") arr = arr.filter((x) => x !== "location");
    return arr.concat(["datetime", "details", "confirmation"]);
  }

  /** Where a Studio (non-public) events-rail selection lands: the Event page
   *  when the step is shown, and otherwise `details` only for a row whose own
   *  raw record is genuinely bookable with exactly one valid one-off
   *  schedule. Never submits — it only moves the previewed step. */
  eventsRailStepForSelection(e: any) {
    const raw = (e as { raw?: any }).raw;
    const canSkip = this.state.s.skipSingleEventSlot
      && !e.full
      && raw?.bookable === true
      && raw?.registrationOpen === true
      && raw?.soldOut === false
      && hasSingleOneOffSchedule(raw);
    const step = canSkip ? "details" : "landing";
    return { evt: e.id, previewStep: step, section: this.sectionForStep(step) };
  }

  stepLabel(id) {
    const q = this.isQueue();
    switch (id) {
      case "kioskEntry": return "Entry screen";
      case "kioskCheckin": return "Check in";
      case "kioskDone": return "Checked in";
      case "landing": return this.ctv("progress", "svc", "Event page");
      case "events": return this.ctv("progress", "svc", "Events");
      case "service": return this.ctv("progress", "svc", "What");
      case "location": return this.ctv("progress", "loc", "Where");
      case "datetime": return this.ctv("progress", "dt", "When");
      case "booking": return "Your booking";
      case "details": return this.ctv("progress", "pd", "Your info");
      default: return q ? this.ctv("progress", "conf", "Your place in line") : this.ctv("progress", "conf", "Confirmation");
    }
  }

  sectionForStep(id) {
    if (id === "kioskEntry") return "kioskentry";
    if (id === "kioskCheckin" || id === "kioskDone") return "kioskcheckin";
    // The events rail's Event page step is previewed under the Events
    // settings section — that journey has no `landing` panel of its own.
    if (id === "landing" && this.isEvents()) return "events";
    return id === "booking" ? "details" : id;
  }

  /** The categories this tenant's catalogue actually returns, for the toggle's
   *  description — a fixed list would contradict the chips the step renders. */
  categoryHint() {
    const labels = serviceCategoryOptions(SERVICES).filter((c) => c.id !== "all").map((c) => c.label);
    if (!labels.length) return "No service categories are set on this catalogue yet.";
    const shown = labels.slice(0, 3).join(", ");
    return labels.length > 3 ? shown + " and " + (labels.length - 3) + " more." : shown + ".";
  }

  invalidAppointmentServiceSelection() {
    return this.state.journey === "appointment"
      && this.state.previewStep === "service"
      && !SERVICES.some((service) => service.id === this.state.svc
        && (!this.state.s.showCategories || serviceMatchesCategory(service, this.state.cat)));
  }

  continueBlocked() {
    return this.invalidAppointmentServiceSelection()
      || (this.state.previewStep === "datetime" && !this.state.slot);
  }

  summaryGo(stepId) {
    const order = this.steps();
    const target = order.indexOf(stepId);
    const dt = order.indexOf("datetime");
    const next = { previewStep: stepId };
    if (dt > -1 && target <= dt) {
      // anything chosen after this step can no longer be trusted — the customer re-picks a time
      next.slot = "";
      next.reconfirmFrom = stepId === "service" ? "service" : stepId === "location" ? "location" : "date";
    }
    this.setState(next);
  }

  longNext(next) {
    const FULL = { Sun: "Sunday", Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday" };
    return String(next || "").replace(/^([A-Za-z]{3}),/, (m, a) => (FULL[a] || a) + ",");
  }

  weekFor(hours) {
    const ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const m = String(hours || "").match(/^([A-Za-z]{3})[\u2013-]([A-Za-z]{3})\s+(.+?)\s+[\u2013-]\s+(.+)$/);
    if (!m) return FULL.map((day) => ({ key: day, day, label: hours || "Closed" }));
    const a = ABBR.indexOf(m[1]), b = ABBR.indexOf(m[2]);
    return FULL.map((day, i) => ({ key: day, day, label: i >= a && i <= b ? m[3] + " \u2013 " + m[4] : "Closed" }));
  }

  embedTitle() {
    const j = this.state.journey;
    if (j === "landing") return "Event details";
    if (j === "events") return (this.props.tenantName || "This workspace") + " events";
    if (j === "queue") return "Join the walk-in line";
    return "Book an appointment";
  }

  embedSnippet(s, title) {
    const auto = s.embedResize === "auto";
    const perms = "geolocation; payment; clipboard-write; camera";
    const w = s.embedWidthUnit === "px" ? String(s.embedWidth || 960) : "100%";
    const src = this.publicLink();
    if (!src) return "";
    const L = ['<iframe', '  src="' + src + '"', '  width="' + w + '"'];
    if (auto) L.push('  data-jrni-autoresize="true"', '  style="border:0;width:100%"');
    else L.push('  height="' + (s.embedHeight || 760) + '"', '  style="border:0"');
    L.push('  title="' + title + ' \u2014 ' + (this.props.tenantName || 'This workspace') + '"');
    L.push('  allow="' + perms + '"');
    L.push('  referrerpolicy="strict-origin-when-cross-origin"');
    L.push('  sandbox="allow-scripts allow-forms allow-popups allow-same-origin allow-top-navigation-by-user-activation"');
    L.push('  loading="lazy">', '</iframe>');
    if (auto) L.push('<script src="' + this.publicOrigin() + '/embed/resize.js" defer><\/script>');
    return L.join("\n");
  }

  journeyDefaults(j) {
    if (j === "kiosk") return { s: { ...KIOSK_DEFAULTS }, ct: { ...KIOSK_CT_PREFILL } };
    if (j === "landing") return { s: { ...LANDING_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } };
    if (j === "queue") return { s: { ...QUEUE_DEFAULTS }, ct: { ...QUEUE_CT_PREFILL } };
    if (j === "events") return { s: { ...EVENT_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } };
    return { s: { ...DEFAULTS }, ct: {} };
  }

  goSection(id) {
    const next = { section: id, formOpen: true };
    if (["service", "location", "datetime", "details", "confirmation"].indexOf(id) > -1) next.previewStep = id;
    this.setState(next);
  }

  moveStep(dir) {
    // Walk-Ins renders through WalkInJourney, which keeps its own step. The
    // preview toolbar's Back/Next drive THAT step, not the appointment
    // wizard's `previewStep` — otherwise the buttons moved a value nothing on
    // screen was reading.
    if (this.isQueue()) {
      const wl = ["service"]
        .concat(this.state.s.queueLocationStep ? ["location"] : [])
        .concat(["details", "ticket"]);
      const wi = Math.max(0, Math.min(wl.length - 1, wl.indexOf(this.state.wiPreviewStep || "service") + dir));
      this.setState({ wiPreviewStep: wl[wi] });
      return;
    }
    const l = this.steps();
    const i = Math.max(0, Math.min(l.length - 1, l.indexOf(this.state.previewStep) + dir));
    // On the public rail the events step belongs to the LIST journey, so
    // stepping back onto it has to switch journeys exactly like the progress
    // row's own Events tab — flipping `previewStep` alone would leave
    // `isEvents()` false and render the list off stale detail rows.
    if (l[i] === "events" && this.isPublic() && this.isLanding()) { this.actions.backToEvents(); return; }
    this.setState({ previewStep: l[i] });
  }


  icon(name, size) {
    const C = NS()[name];
    return C ? React.createElement(C, { size: size || 16 }) : null;
  }

  cardCover(name, hue, aspect) {
    const H = NS().HeroTile;
    if (!H) return null;
    const initials = name.split(" ").filter((w) => /^[A-Za-z]/.test(w)).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
    return React.createElement(H, { initials, hue, aspect: aspect || 1.9, radius: 0, alt: name, style: { width: "100%" } });
  }

  hero(e) {
    const H = NS().HeroTile;
    if (!H) return null;
    const initials = e.name.split(" ").filter((w) => /^[A-Za-z]/.test(w)).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
    return React.createElement(H, { initials, hue: e.hue, size: 56, radius: 8, alt: e.name });
  }

  heroWide(e) {
    const H = NS().HeroTile;
    if (!H) return null;
    const initials = e.name.split(" ").filter((w) => /^[A-Za-z]/.test(w)).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
    return React.createElement(H, { initials, hue: e.hue, aspect: 1.9, radius: 8, alt: e.name, style: { width: "100%" } });
  }

  tile(iconName, tone) {
    const T = NS().IconTile;
    return T ? React.createElement(T, { icon: this.icon(iconName, 14), tone: tone || "primary", size: "md" }) : null;
  }

  renderVals() {
    const st = this.state;
    const persistenceUnavailable = !!PD_STUB_ONLY[st.journey];
    const isPublished = !persistenceUnavailable && (st.journey === "events" || st.journey === "landing"
      ? Boolean(st.published?.[st.journey])
      : st.apptJourney?.status === "published");
    // The events rail's Event page step previews the tenant's REAL saved
    // landing settings (the Event Landing Page tab's draft, already stashed
    // by the journey loader) layered over the list's own `s`, so the
    // existing landing renderer and templates work unchanged. This is a
    // render-time view only — `V.s`, which every settings control writes
    // through, stays the saved list settings and is never overwritten.
    const landingPreviewOnEventsRail = this.isEvents() && st.previewStep === "landing";
    const s = landingPreviewOnEventsRail
      ? { ...st.s, ...((st.stash.landing && st.stash.landing.s) || LANDING_DEFAULTS) }
      : st.s;
    const W: TenantWords = (this.props.vocab as TenantWords) || VOCAB_DEFAULTS;
    const stepList = this.steps();
    const stepIndex = Math.max(0, stepList.indexOf(st.previewStep));
    // The record's own brand colour wins; the prototype's indigo default gives way to the tenant's brand (2026-09-04).
    const brand = (s.brandColor && s.brandColor !== DEFAULTS.brandColor ? s.brandColor : null) || this.props.brandColor || DEFAULTS.brandColor;
    const radius = (s.radiusScale || 0) + "px";

    const landing = this.isLanding();
    const kiosk = this.isKiosk();
    const setupIds = kiosk ? ["general", "kioskentry", "kioskcheckin", "customtext", "launch"]
      : landing ? ["general", "customtext", "launch"] : ["general", "progress", "customtext", "launch"];
    const setupIcons = { general: "IconSettings", progress: "IconLayers", kioskentry: "IconBrowser", kioskcheckin: "IconQr", customtext: "IconMessage", launch: "IconLink" };
    const queue = this.isQueue();
    const appointment = st.journey === "appointment";
    const events = this.isEvents();
    const P = this.panels();
    const setupItems = setupIds.map((id) => {
      return { id, label: P[id][0], icon: this.icon(setupIcons[id], 15) };
    });

    const journeyItems = stepList.map((id, i) => {
      const sec = this.sectionForStep(id);
      const selected = st.previewStep === id && !st.cancelled && !st.leftQueue && st.previewStep !== "linkError";
      return {
        id, num: String(i + 1), label: this.stepLabel(id), selected,
        onClick: () => this.setState({ previewStep: id, section: sec, formOpen: true, cancelled: false, leftQueue: false,
          dwellLeft: id === "kioskDone" ? (this.state.s.ckDoneDwell || 6) : null }),
        bg: selected ? "var(--jrni-color-surface-card)" : "transparent",
        border: selected ? "var(--jrni-color-surface-border)" : "transparent",
        shadow: selected ? "var(--jrni-shadow-sm)" : "none",
        fg: selected ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: selected ? "var(--jrni-color-primary-1)" : "var(--jrni-color-neutral-6)",
        numFg: selected ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      };
    });

    // Outcome screens hang off the end of the rail so they can be previewed directly.
    const railExtras = [];
    if (kiosk) {
      // Five failure states, each reachable directly — they are the reason the design holds up.
      const probs = [
        ["noMatch", "Not recognised", "var(--jrni-color-semantic-orange-1)"],
        ["already", "Already checked in", "var(--jrni-color-semantic-orange-1)"],
        ["cancelled", "Cancelled booking", "var(--jrni-color-semantic-red-1)"],
        ["noCamera", "Reader down", "var(--jrni-color-semantic-red-1)"],
        ["offline", "Offline", "var(--jrni-color-semantic-red-1)"],
      ];
      probs.forEach((p) => {
        const on = st.previewStep === "kioskCheckin" && st.ckProblem === p[0];
        railExtras.push({
          id: p[0], num: "!", label: p[1], selected: on,
          onClick: () => this.actions.kioskProblem(p[0]),
          bg: on ? "var(--jrni-color-surface-card)" : "transparent",
          border: on ? "var(--jrni-color-surface-border)" : "transparent",
          shadow: on ? "var(--jrni-shadow-sm)" : "none",
          fg: on ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-body)",
          opacity: 1,
          numBg: on ? p[2] : "var(--jrni-color-neutral-6)",
          numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
        });
      });
    }
    if (!queue && !events && !landing && !kiosk && s.allowCancel) {
      const on = st.previewStep === "confirmation" && st.cancelled;
      railExtras.push({
        id: "cancelled", num: "\u00d7", label: "Cancelled", selected: on,
        onClick: () => this.setState({ previewStep: "confirmation", section: "confirmation", cancelled: true, formOpen: true }),
        bg: on ? "var(--jrni-color-surface-card)" : "transparent",
        border: on ? "var(--jrni-color-surface-border)" : "transparent",
        shadow: on ? "var(--jrni-shadow-sm)" : "none",
        fg: on ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: on ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-neutral-6)",
        numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      });
    }
    if (!queue && !events && !landing && !kiosk) {
      const on = st.previewStep === "linkError";
      railExtras.push({
        id: "linkError", num: "!", label: "Link error", selected: on,
        onClick: () => this.setState({ previewStep: "linkError", section: "general", formOpen: true, cancelled: false }),
        bg: on ? "var(--jrni-color-surface-card)" : "transparent",
        border: on ? "var(--jrni-color-surface-border)" : "transparent",
        shadow: on ? "var(--jrni-shadow-sm)" : "none",
        fg: on ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: on ? "var(--jrni-color-semantic-orange-1)" : "var(--jrni-color-neutral-6)",
        numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      });
    }
    if ((events || landing) && s.allowCancelReg) {
      const on = st.previewStep === "confirmation" && st.cancelled;
      railExtras.push({
        id: "regCancelled", num: "\u00d7", label: "Cancelled", selected: on,
        onClick: () => this.setState({ previewStep: "confirmation", section: "confirmation", cancelled: true, formOpen: true }),
        bg: on ? "var(--jrni-color-surface-card)" : "transparent",
        border: on ? "var(--jrni-color-surface-border)" : "transparent",
        shadow: on ? "var(--jrni-shadow-sm)" : "none",
        fg: on ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: on ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-neutral-6)",
        numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      });
    }
    if (queue && s.allowLeaveQueue) {
      const on = st.previewStep === "confirmation" && st.leftQueue;
      railExtras.push({
        id: "leftQueue", num: "\u00d7", label: "Left the line", selected: on,
        onClick: () => this.setState({ previewStep: "confirmation", section: "confirmation", leftQueue: true, formOpen: true }),
        bg: on ? "var(--jrni-color-surface-card)" : "transparent",
        border: on ? "var(--jrni-color-surface-border)" : "transparent",
        shadow: on ? "var(--jrni-shadow-sm)" : "none",
        fg: on ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: on ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-neutral-6)",
        numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      });
    }

    const panelMeta = P[st.section] || P.general;

    const svcOf = (id) => SERVICES.filter((x) => x.id === id)[0] || SERVICES[0] || EMPTY_SERVICE;
    const locOf = (id) => LOCATIONS.filter((x) => x.id === id)[0] || LOCATIONS[0] || EMPTY_LOCATION;
    const cur = svcOf(st.svc);
    const curLoc = locOf(st.loc);
    const curStaff = ((STAFF.filter((x) => x.id === st.staff)[0] || STAFF[0] || EMPTY_STAFF) || {}).name || "";

    const priceOf = (x) => {
      if (x.dur === "multi") return "Multiple durations";
      const bits = [];
      if (s.showDuration) bits.push(x.dur);
      if (s.showPrice && x.price) bits.push(x.price);
      return bits.join(" · ");
    };

    const waitPrefix = this.ctv("service", "wait", "Wait about");
    const SVC_HUES = { advising: "#5142C7", aid: "#2D60EB", career: "#C18FE8", tour: "#75ADFA", registrar: "#5AC8AD", wellness: "#87D23B" };
    const visibleServices = SERVICES.filter((x) => appointment
      ? (!s.showCategories || serviceMatchesCategory(x, st.cat))
      : (st.cat === "all" || !s.showCategories || x.cat === st.cat))
      // A kiosk shows only what you can actually get in the building you are standing in.
      .filter((x) => !st.pinnedLoc || !x.locs || x.locs.indexOf(st.pinnedLoc) > -1)
      .map((x) => {
      const on = st.svc === x.id;
      const meta = queue
        ? (s.liveWaitPerService ? waitPrefix + " " + (SERVICE_WAITS[x.id] || "—") : "")
        : priceOf(x);
      return {
        name: x.name,
        sub: s.serviceDisplay === "compact"
          ? (queue ? "" : priceOf(x))
          : (queue ? x.desc : [priceOf(x), x.desc].filter(Boolean).join(" — ")),
        desc: x.desc, meta, hasMeta: !!meta,
        cover: this.cardCover(x.name, SVC_HUES[x.id] || brand, 2.1),
        border: on ? brand : "var(--jrni-color-surface-border)",
        shadow: on ? "var(--jrni-shadow-md)" : "var(--jrni-shadow-chrome)",
        leading: s.showServiceImages ? this.tile(queue ? "IconUserFriends" : "IconCalendar", "primary") : null,
        trailing: queue && s.liveWaitPerService ? waitPrefix + " " + (SERVICE_WAITS[x.id] || "—") : null,
        selected: on,
        onSelect: () => {
          const only = this.locsForService(x.id);
          this.setState(only.length === 1 ? { svc: x.id, loc: only[0].id } : { svc: x.id });
        },
      };
    });

    // Walk-Ins render through WalkInJourney (real queue reads), so the wizard has no desk list of its own.
    const locSource = queue ? [] : (s.firstStep === "service" ? this.locsForService(st.svc) : LOCATIONS);
    const locCount = !queue && s.loadMore ? Math.max(1, st.locShown || s.initialCount || 1) : locSource.length;
    // Typeahead over name, street, town and ZIP \u2014 whatever the customer types.
    const q = (st.locQuery || "").trim().toLowerCase();
    const locSuggestions = (!queue && q.length > 1 && q !== "current location")
      ? LOCATIONS.filter((x) => (x.name + " " + x.addr).toLowerCase().indexOf(q) > -1).slice(0, 6).map((x) => ({
          key: x.id, name: x.name, meta: x.addr + (x.dist === "\u2014" ? "" : " \u00b7 " + x.dist),
          selected: st.loc === x.id,
          bg: st.loc === x.id ? "var(--jrni-color-primary-4)" : "transparent",
          onPick: () => this.setState({ loc: x.id, locQuery: x.name }),
        }))
      : [];
    const nearFirst = !queue && st.geoState === "done";
    const ordered = nearFirst
      ? locSource.slice().sort((a, b) => (parseFloat(a.dist) || 999) - (parseFloat(b.dist) || 999))
      : locSource;
    const visibleLocations = ordered.slice(0, locCount).map((x) => {
      const row = (key, kids) => React.createElement("span", { key, style: { display: "block" } }, kids);
      const kids = [row("a", x.addr)];
      if (!queue && s.nextAvailable) {
        kids.push(row("n", [React.createElement("strong", { key: "b", style: { fontWeight: 700 } }, "Next available"), " " + x.next]));
      }
      if (!queue && s.extraLocationInfo) kids.push(row("h", x.hours + " · " + x.phone));
      const qb = !queue && s.quickbook && s.nextAvailable;
      const on = (queue ? st.desk : st.loc) === x.id;
      return {
        name: x.name, addr: x.addr, phone: x.phone, dist: x.dist,
        sub: React.createElement("span", { style: { display: "flex", flexDirection: "column", gap: "2px" } }, kids),
        leading: this.tile(queue ? "IconConciergeBell" : "IconPin", "neutral"),
        trailing: x.dist,
        showPhone: !queue && s.extraLocationInfo,
        showNext: !queue && s.nextAvailable,
        nextLabel: this.longNext(x.next),
        showHours: !queue && s.extraLocationInfo,
        hoursOpen: !!st.openHours[x.id],
        hoursCaret: st.openHours[x.id] ? "rotate(-90deg)" : "rotate(90deg)",
        onToggleHours: (e) => { if (e && e.stopPropagation) e.stopPropagation(); this.setState((prev) => ({ openHours: { ...prev.openHours, [x.id]: !prev.openHours[x.id] } })); },
        week: this.weekFor(x.hours),
        tzLabel: "Local time \u2014 " + (ZONES[x.tz] || ZONES.central).label,
        border: on ? brand : "var(--jrni-color-surface-border)",
        shadow: on ? "var(--jrni-shadow-md)" : "var(--jrni-shadow-chrome)",
        quickbook: qb, quickbookLabel: "Quickbook",
        selected: on,
        onSelect: () => this.setState(queue ? { desk: x.id } : { loc: x.id }),
        onQuickbook: () => {
          const parts = String(x.next || "").split(", ");
          const time = parts[parts.length - 1];
          const datePart = parts.length > 1 ? parts.slice(0, -1).join(", ") : "";
          const t = this.today();
          let d = t;
          if (datePart && !/today/i.test(datePart)) {
            const md = datePart.replace(/^[A-Za-z]{3},?\s*/, "");
            const parsed = new Date(md + " " + t.getFullYear() + " 00:00:00");
            if (!isNaN(parsed.getTime())) d = parsed;
          }
          const iso = this.iso(d);
          this.setState({
            loc: x.id, day: iso, calMonth: { y: d.getFullYear(), m: d.getMonth() },
            slot: time, previewStep: "details", reconfirmFrom: null,
          });
        },
      };
    });

    const slotOpts = (arr) => arr.map((t) => ({ value: t, label: t }));
    const groupDefs = [["Morning", SLOTS.morning], ["Afternoon", SLOTS.afternoon], ["Evening", SLOTS.evening]];

    const dayStart = new Date(this.today().getFullYear(), this.today().getMonth(), this.today().getDate());
    const evtDate = (e) => { const d = new Date(e.date); return isNaN(d.getTime()) ? dayStart : d; };
    const eq = (st.evtQuery || "").trim().toLowerCase();
    const evtHay = (e) => (e.name + " " + e.desc + " " + e.venue + " " + e.addr + " " + e.date + " " + e.cat).toLowerCase();
    const remainingLabel = this.ctv("events", "remaining", "spaces left");
    const soldOutLabel = this.ctv("events", "soldout", "Fully booked");
    // "Fully booked" is only true of a sold-out/full event. An event that is
    // simply not open for registration (registrationOpen false, or bookable
    // false for a non-capacity reason) is closed, not full — saying "Fully
    // booked" there invents a capacity fact the server never stated.
    const regClosedLabel = this.ctv("events", "regclosed", "Registration closed");
    // Public: the server already filtered, sorted and paged these rows \u2014
    // rendered exactly once, never re-sliced/re-sorted/re-filtered here.
    // Totals/pages/facets come from the same server response
    // (`refreshPublicEventsList`'s `evtPagination`/`evtFacets`), not derived
    // from this (partial, one-page) array.
    const usingServerEvents = this.isPublic() && events;
    let evtShown, evtPages, evtPage, evtMatches, evtFilteredCount;
    if (usingServerEvents) {
      evtShown = this.eventsRows;
      const pagination = st.evtPagination;
      evtPages = pagination ? Math.max(1, pagination.total_pages) : 1;
      evtPage = pagination ? pagination.page : (st.evtPage || 1);
      evtMatches = (!st.evtPicked && eq.length > 1)
        ? this.eventsRows.filter((e) => this.eventRowSelectable(e)).slice(0, 5).map((e) => ({
            key: e.id, name: e.name, meta: [e.when, e.venue].filter(Boolean).join(" \u00b7 "),
            onPick: () => this.actions.pickEvent(e.id, e.name),
          }))
        : [];
    } else {
      const evtSorted = this.eventsRows.slice()
        .filter((e) => s.showPastEvents || evtDate(e) >= dayStart)
        .sort((a, b) => {
          if (s.sortOrder === "name") return a.name.localeCompare(b.name);
          if (s.sortOrder === "popularity") return (b.cap - b.left) - (a.cap - a.left);
          return evtDate(a) - evtDate(b);
        });
      // Events are matched to the kiosk's campus on the venue's street address.
      const pinnedAddr = st.pinnedLoc ? ((LOCATIONS.filter((x) => x.id === st.pinnedLoc)[0] || {}).addr || "").split(",")[0].trim().toLowerCase() : "";
      const evtFiltered = evtSorted.filter((e) => st.evtCat === "all" || !s.eventFilters || e.cat === st.evtCat)
        .filter((e) => !pinnedAddr || (e.addr || "").toLowerCase().indexOf(pinnedAddr) > -1)
        // Preview rows are real catalogue rows, so the picked location id is
        // matched against the row's own authoritative location id.
        .filter((e) => !st.evtLocationFilter || (e.raw && (e.raw.locationId || e.raw.location_id)) === st.evtLocationFilter)
        .filter((e) => s.showSoldOut || e.left > 0)
        .filter((e) => !eq || evtHay(e).indexOf(eq) > -1)
        .filter((e) => {
          if (!st.evtFrom && !st.evtTo) return true;
          const d = new Date(e.date);
          if (isNaN(d.getTime())) return true;
          const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          const bound = (v) => { const b = new Date(String(v).slice(0, 10)); return isNaN(b.getTime()) ? null : new Date(b.getFullYear(), b.getMonth(), b.getDate()); };
          const from = st.evtFrom ? bound(st.evtFrom) : null;
          const to = st.evtTo ? bound(st.evtTo) : null;
          if (from && day < from) return false;
          if (to && day > to) return false;
          return true;
        });
      evtMatches = (!st.evtPicked && eq.length > 1)
        ? this.eventsRows.filter((e) => evtHay(e).indexOf(eq) > -1 && this.eventRowSelectable(e)).slice(0, 5).map((e) => ({
            key: e.id, name: e.name, meta: [e.when, e.venue].filter(Boolean).join(" \u00b7 "),
            onPick: () => this.actions.pickEvent(e.id, e.name),
          }))
        : [];
      const perPage = Math.max(1, s.eventsPerPage || 1);
      evtPages = Math.max(1, Math.ceil(evtFiltered.length / perPage));
      evtPage = Math.min(st.evtPage, evtPages);
      evtShown = s.eventPaging === "pages"
        ? evtFiltered.slice((evtPage - 1) * perPage, evtPage * perPage)
        : evtFiltered.slice(0, perPage * evtPage);
      evtFilteredCount = evtFiltered.length;
    }
    // Unified across both branches: the real server total/hasMore in public
    // mode (never derived by re-measuring the one page already on screen),
    // the locally-filtered count in admin preview.
    const evtTotalCount = usingServerEvents ? (st.evtPagination?.total ?? evtShown.length) : undefined;
    const evtHasMore = usingServerEvents ? Boolean(st.evtPagination?.hasMore) : undefined;
    // Category chips: the real facet catalogue the server returned for this
    // scope — on the public path AND in the Studio preview, which now reads
    // the same `getPublicEvents` facets (`refreshPublicEventsPreview` stores
    // them in `evtFacets`). The invented admissions/academic/careers/wellness
    // list is gone: it never matched the tenant's own categories, so filtering
    // a server-backed preview by it could only ever return nothing. With no
    // facets yet, the categories actually present on the loaded rows are
    // offered instead of a fixture. "All" is always first, so clear/all
    // behaviour is unchanged.
    const evtFacetCategories = (st.evtFacets?.categories || []).filter((c) => typeof c === "string" && c.trim());
    const evtCategoryValues = evtFacetCategories.length
      ? evtFacetCategories
      : Array.from(new Set(this.eventsRows.map((e) => e.cat).filter((c) => typeof c === "string" && c.trim())));
    const evtCategoryOptions = [{ value: "all", label: "All" }]
      .concat(evtCategoryValues.map((c) => ({ value: c, label: c })));
    // Location filter: the server's own location facet ({id, name}) off the
    // same `getPublicEvents` response the rows came from — never a fixture
    // list and never an invented id. With no facet there is nothing honest to
    // offer, so the control renders empty and disabled rather than pretending.
    const evtLocationFacet = (st.evtFacets?.locations || []).filter((l) => l && l.id && l.name);
    const evtLocationOptions = [{ value: "", label: "All locations" }]
      .concat(evtLocationFacet.map((l) => ({ value: l.id, label: l.name })));
    const curEvt = this.eventsRows.filter((e) => e.id === st.evt)[0] || this.eventsRows[0] || EMPTY_EVENT;
    // Multiple places is Coming soon on both event journeys, so the effective
    // count is always one there — regardless of a saved `ticketQuantity: true`
    // or a stale `st.tickets` from an earlier session. Appointments and queue
    // keep their own party/ticket counts untouched.
    const effectiveTickets = (events || landing) ? 1 : st.tickets;
    // Authoritative event host only: the public projection's staffName /
    // staff_name off this row's raw record. Never the appointment journey's
    // `curStaff`, and never a stand-in — blank hides the section.
    const eventHostName = String((curEvt.raw && (curEvt.raw.staffName || curEvt.raw.staff_name)) || "").trim();
    // The Landing/Event detail's availability, read off the same authoritative
    // projection the cards use (`bookable` / `bookableReason` / `soldOut` /
    // `registrationOpen` / `waitlistOpen`) in preview and in public alike.
    const lpAv = curEvt.availability || null;
    const lpEnded = lpAv ? lpAv.bookableReason === "completed" : false;
    const lpSoldOut = lpAv ? (lpAv.soldOut === true || !!st.soldOutPreview) : !!st.soldOutPreview;
    // Registration is offered only on the contract's literal booleans, never
    // on `!soldOut` — which would re-open a completed or closed event.
    const lpRegOpen = lpEnded ? false
      : lpAv ? (lpAv.bookable === true && lpAv.registrationOpen === true && lpAv.soldOut === false && !st.soldOutPreview)
      : !st.soldOutPreview;
    const relatedEvents = (() => {
      const pool = this.eventsRows.filter((e) => e.id !== curEvt.id);
      if (s.relatedRule === "manual") return (s.relatedManual || []).map((id) => pool.filter((e) => e.id === id)[0]).filter(Boolean);
      const n = Math.max(1, s.relatedCount || 3);
      const rank = (e) => {
        if (s.relatedRule === "venue") return e.venue === curEvt.venue ? 0 : 1;
        if (s.relatedRule === "date") return 0;
        return e.cat === curEvt.cat ? 0 : 1;
      };
      return pool.slice().sort((a, b) => rank(a) - rank(b) || (new Date(a.date) - new Date(b.date))).slice(0, n);
    })();

    const endedLabel = this.ctv("events", "ended", "Event ended");
    const eventCards = evtShown.map((e) => {
      const full = e.left === 0;
      // Authoritative availability, straight off the real public projection
      // (`projectAvailability`: bookable / bookable_reason / registrationOpen
      // / soldOut / waitlistOpen). Never a client-side date comparison and
      // never capacity alone — "this event has ended" is a server fact
      // (`bookable_reason: "completed"`), not something the browser's clock
      // gets to decide. Rows loaded before a projection exists fall back to
      // the previous capacity-only reading, so nothing regresses.
      const av = e.availability || null;
      const ended = av ? av.bookableReason === "completed" : false;
      const soldOut = av ? av.soldOut === true : full;
      // Waitlist availability follows the same shape the Landing detail uses
      // (`lpShowWaitlist`): the server's own `waitlistOpen` decides on BOTH
      // paths, with the author's switch as the fallback only for a row that
      // carries no projection at all.
      const waitlistOffered = s.waitlist && soldOut && !ended
        && (av ? av.waitlistOpen === true : true);
      // Registration is offered only on the contract's literal booleans —
      // never `!soldOut`, which would re-open a completed or closed event.
      const canRegister = ended ? false
        : av ? (av.bookable === true && av.registrationOpen === true && av.soldOut === false)
        : !full;
      const cardClosed = !canRegister && !waitlistOffered;
      // Ended wins, then genuine sold-out; anything else closed is closed for
      // a registration reason and says so.
      const closedStatus = ended ? endedLabel : soldOut ? soldOutLabel : regClosedLabel;
      const bits = [];
      if (s.showEventDate) bits.push(e.when);
      if (s.showEventVenue) bits.push(e.venue);
      if (s.showEventPrice) bits.push(e.price);
      return {
        id: e.id, name: e.name, sub: bits.join(" · "),
        desc: s.showEventDesc ? e.desc : "",
        showDesc: s.showEventDesc,
        addr: e.addr, date: e.date,
        time: s.showTimezone ? e.time : e.time.replace(/\s*\([A-Z]+\)/, ""),
        showAddr: s.showEventVenue, showDate: s.showEventDate, showTime: s.showEventTime,
        priceLine: s.showEventPrice ? e.price : "",
        showPrice: s.showEventPrice,
        when: e.when, venue: e.venue, price: e.price,
        cover: s.eventImages ? this.hero(e) : null,
        wide: s.eventImages ? this.heroWide(e) : null,
        left: e.left, cap: e.cap,
        ended,
        spaces: cardClosed ? closedStatus : (soldOut ? soldOutLabel : (e.left == null ? "" : e.left + " " + remainingLabel)),
        meterTone: ended ? "neutral" : soldOut ? "danger" : (e.left == null ? "success" : e.left / e.cap < 0.2 ? "warning" : "success"),
        showMeter: s.showRemaining && !ended,
        full, selected: st.evt === e.id,
        interactive: !cardClosed,
        onSelect: (clickEvent?: { stopPropagation?: () => void }) => {
          // The card's own container and the nested Register/waitlist button
          // share this one handler, so a button click bubbles to the card and
          // would select twice — pushing a duplicate history entry. Stopping
          // propagation here keeps it to exactly one; card, keyboard and
          // direct calls pass no event and are unaffected.
          clickEvent?.stopPropagation?.();
          // A closed card (ended, or full with no waitlist) offers nothing to
          // open — the shortcut must not be a back door into registration
          // or the event detail step.
          if (cardClosed) return;
          if (this.isPublic()) this.selectPublicEvent(e.id, { fromList: true });
          // Studio preview: with the Event page step shown, a card opens it —
          // the same order the public rail runs. With it hidden, the shortcut
          // may only fire for a row that really carries an authoritative
          // one-off schedule and is bookable, the same facts the public
          // journey decides on; anything else still shows the Event page.
          // Nothing is submitted here — this only moves the previewed step.
          else this.setState(this.eventsRailStepForSelection(e));
        },
        cta: waitlistOffered ? this.ctv("events", "waitlist", "Join the waiting list") : "",
        taken: e.left == null ? null : e.cap - e.left,
        dim: ended ? 0.6 : cardClosed ? 0.72 : 1,
        bookable: canRegister,
        showWaitlist: waitlistOffered,
        closed: cardClosed,
        spacesPill: cardClosed ? closedStatus : (s.showRemaining ? (soldOut ? soldOutLabel : (e.left == null ? null : e.left + " " + remainingLabel)) : null),
      };
    });

    const ctGroups = this.ctGroupDefs().map((g) => {
      const fields = g.fields.map((f) => {
        const key = g.id + "." + f.id;
        const value = st.ct[key] || "";
        return {
          label: f.label, def: f.def, value, changed: !!value.trim(),
          onChange: (e) => {
            const v = e && e.target ? e.target.value : e;
            this.setState((prev) => ({ ct: { ...prev.ct, [key]: v }, dirty: true }));
          },
          onRevert: () => this.setState((prev) => { const ct = { ...prev.ct }; delete ct[key]; return { ct, dirty: true }; }),
        };
      });
      const custom = fields.filter((f) => f.changed).length;
      const shown = st.ctFilter === "custom" ? fields.filter((f) => f.changed) : fields;
      const open = !!st.openGroups[g.id];
      return {
        id: g.id, label: g.label, open, fields: shown, hasCustom: custom > 0,
        countLabel: custom ? custom + " of " + fields.length + " customised" : fields.length + " fields",
        caret: open ? "rotate(90deg)" : "rotate(0deg)",
        onToggle: () => this.setState((prev) => ({ openGroups: { ...prev.openGroups, [g.id]: !prev.openGroups[g.id] } })),
      };
    }).filter((g) => st.ctFilter !== "custom" || g.hasCustom);

    // Letters (including accents), spaces, hyphens and apostrophes only \u2014 no digits, no symbols.
    const nameOk = (v) => /^[\p{L}][\p{L}\u2019' -]{1,}$/u.test(String(v || "").trim());
    const nameMsg = (v) => {
      const s2 = String(v || "").trim();
      if (!s2) return "This field is required.";
      if (s2.length < 2) return "Names need at least two letters.";
      return "Use letters, hyphens and apostrophes only.";
    };
    const firstBad = !s.readOnly && st.firstTouched && !nameOk(st.first);
    const lastBad = !s.readOnly && st.lastTouched && !nameOk(st.last);
    // Place lookup: matches the house number, street, town or ZIP as it is typed.
    const aq = (st.addr || "").trim().toLowerCase();
    const addrMatches = (!st.addrPicked && aq.length > 1)
      ? PLACES.filter((x) => (x.main + " " + x.secondary).toLowerCase().indexOf(aq) > -1).slice(0, 5).map((x) => ({
          key: x.main, main: x.main, secondary: x.secondary,
          onPick: () => this.actions.pickAddr(x.main + ", " + x.secondary),
        }))
      : [];
    const ph = this.parsePhone(st.phone, st.phoneCountry);
    const phoneBad = !s.readOnly && st.phoneTouched && !ph.valid && !(s.phoneOptional && ph.len === 0);
    const outsideJourney = st.previewStep === "linkError" || !!st.cancelled || !!st.leftQueue;
    const emailOk = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test((st.email || "").trim());
    const emailBad = !s.readOnly && st.emailTouched && !emailOk && !(s.emailOptional && !(st.email || "").trim());
    const frameWidth = st.previewOnly ? "100%" : st.device === "mobile" ? "390px" : st.device === "tablet" ? "min(100%, 768px)" : "100%";

    /* ── Kiosk preview ────────────────────────────────────────────────────────
       Every destination tile, the live strip, the three lookup methods and the
       five failure states. Sizes step up when larger text is on, because a wall
       tablet is read standing up and from further away than a phone. */
    const kioskBooking = st.ckBooking || null;
    const bigT = kiosk && s.kioskBigText && st.bigText;
    const phone = st.device === "mobile";
    const ckMethods = [];
    if (s.ckQr || s.ckWallet || s.ckNfc) ckMethods.push({ value: "scan", label: "Scan or tap" });
    if (s.ckRef) ckMethods.push({ value: "ref", label: "Reference" });
    if (s.ckName) ckMethods.push({ value: "name", label: "Name" });
    const ckMethodActive = ckMethods.filter((m) => m.value === st.ckMethod).length ? st.ckMethod : (ckMethods[0] || {}).value;
    const refTyped = (st.ckRefInput || "").trim().toUpperCase();
    const refMatch = kioskBooking && refTyped && st.ckFound === refTyped ? [kioskBooking] : [];
    const nameTyped = (st.ckNameInput || "").trim().toLowerCase();
    // The check-in service looks up by code only; there is no name search to list results from.
    const nameMatch = [];
    const kioskProblem = st.ckProblem || "";
    // Nouns only — these get joined into "Hold your ___ up to the reader", so a verb like
    // "tap" cannot go in the list. NFC is expressed in ckWalletHint underneath instead.
    const scanLabelBits = [];
    if (s.ckQr) scanLabelBits.push("QR code");
    if (s.ckWallet) scanLabelBits.push("wallet pass");
    const compactTiles = s.kioskTileSize === "compact";
    const tileIcon = compactTiles ? (bigT ? 22 : 19) : (bigT ? 34 : 28);
    const kioskTiles = [];
    if (s.kioskAppts) kioskTiles.push({
      id: "appts", title: this.ctv("entry", "appts", "Book an appointment"),
      desc: this.ctv("entry", "apptsSub", "Advising, financial aid, careers and more"),
      icon: this.icon("IconCalendar", tileIcon), hue: "var(--jrni-color-primary-1)",
      // No availability read backs the tile, so it states nothing it cannot know.
      meta: "",
      onClick: () => this.actions.kioskGo("appts"),
    });
    if (s.kioskWalkin) kioskTiles.push({
      id: "walkin", title: this.ctv("entry", "walkin", "See someone today"),
      desc: this.ctv("entry", "walkinSub", "Join the walk-in line at this campus"),
      icon: this.icon("IconUserFriends", tileIcon), hue: "var(--jrni-color-semantic-green-1)",
      meta: "",
      onClick: () => this.actions.kioskGo("walkin"),
    });
    if (s.kioskEvents) kioskTiles.push({
      id: "events", title: this.ctv("entry", "events", "What's on at Lakeview"),
      desc: this.ctv("entry", "eventsSub", "Orientation, fairs and workshops"),
      icon: this.icon("IconTicket", tileIcon), hue: "var(--jrni-color-semantic-orange-1)",
      meta: "",
      onClick: () => this.actions.kioskGo("events"),
    });
    if (s.kioskCheckin) kioskTiles.push({
      id: "checkin", title: this.ctv("entry", "ck", "I'm here for my appointment"),
      desc: this.ctv("entry", "ckSub", "Scan your pass or find your booking"),
      icon: this.icon("IconQr", tileIcon), hue: "var(--jrni-color-primary-2)",
      meta: "", onClick: () => this.actions.kioskGo("checkin"),
    });
    const hour = 9;
    const kioskPv = {
      isKioskEntry: kiosk && st.previewStep === "kioskEntry",
      isKioskCheckin: kiosk && st.previewStep === "kioskCheckin" && !kioskProblem,
      isKioskDone: kiosk && st.previewStep === "kioskDone",
      isKioskProblem: kiosk && st.previewStep === "kioskCheckin" && !!kioskProblem,
      kioskGreeting: s.kioskGreeting === "none" ? "" : s.kioskGreeting === "time"
        ? this.ctv("entry", "greet", hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening")
        : this.ctv("entry", "greet", "Welcome to Lakeview"),
      kioskShowGreeting: s.kioskGreeting !== "none",
      kioskSub: this.ctv("entry", "sub", "What do you need today?"),
      kioskTiles,
      kioskTileCols: st.device === "mobile" ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
      kioskTileCompact: s.kioskTileSize === "compact",
      kioskTileRoomy: s.kioskTileSize !== "compact",
      kioskTileMinH: phone ? "auto" : s.kioskTileSize === "compact" ? "auto" : (bigT ? "184px" : "158px"),
      kioskTilePad: s.kioskTileSize === "compact" ? "14px 16px" : "22px 24px",
      kioskTileGap: s.kioskTileSize === "compact" ? "12px" : "12px",
      kioskTileDir: s.kioskTileSize === "compact" ? "row" : "column",
      kioskTileAlign: s.kioskTileSize === "compact" ? "center" : "flex-start",
      kioskIconBox: s.kioskTileSize === "compact" ? (bigT ? "44px" : "38px") : "56px",
      kioskTitleSize: bigT ? (phone ? "30px" : "40px") : (phone ? "25px" : "32px"),
      kioskSubSize: bigT ? (phone ? "19px" : "24px") : (phone ? "16px" : "19px"),
      kioskTileTitleSize: bigT ? (phone ? "19px" : "24px") : (phone ? "17px" : "20px"),
      kioskTileDescSize: bigT ? (phone ? "15px" : "18px") : (phone ? "14px" : "15px"),
      kioskBodySize: bigT ? (phone ? "17px" : "20px") : (phone ? "15px" : "16px"),
      kioskShowStrip: s.kioskWaitTime || s.kioskNextSlot || s.kioskHours,
      kioskWait: s.kioskWaitTime, kioskNextSlot: s.kioskNextSlot, kioskHours: s.kioskHours,
      kioskBigTextToggle: s.kioskBigText,
      kioskBigTextOn: !!st.bigText,
      pinnedLocName: (LOCATIONS.filter((x) => x.id === st.pinnedLoc)[0] || LOCATIONS[0] || EMPTY_LOCATION).name,
      pinnedStepWord: this.stepLabel("location"),
      ckHeader: this.ctv("checkin", "hdr", "Let's find your booking"),
      ckScanLine: this.ctv("checkin", "scan", scanLabelBits.length
        ? "Hold your " + scanLabelBits.join(" or ") + " up to the reader"
        : "Tap your wallet pass against the reader"),
      ckRefHeader: this.ctv("checkin", "refHdr", "Enter your booking reference"),
      ckNameHeader: this.ctv("checkin", "nameHdr", "Find your booking by name"),
      ckMethods, ckMethodActive,
      ckScanMode: ckMethodActive === "scan", ckRefMode: ckMethodActive === "ref", ckNameMode: ckMethodActive === "name",
      ckAnyMethod: ckMethods.length > 0,
      ckShowWalletHint: s.ckWallet || s.ckNfc,
      ckWalletHint: s.ckNfc && s.ckWallet ? "Apple and Google wallet passes work by scan or by tapping the reader."
        : s.ckNfc ? "Tap your pass against the reader." : "Apple and Google wallet passes work here too.",
      ckRefValue: st.ckRefInput, ckNameValue: st.ckNameInput,
      ckRefMatches: refMatch.map((b) => ({ key: b.ref, ref: b.ref, name: b.name, svcWhen: [b.svc, b.when].filter(Boolean).join(" · "), onPick: () => this.actions.kioskPick(b.ref) })),
      ckNameMatches: nameMatch.map((b) => ({ key: b.ref, ref: b.ref, name: b.name, svcWhen: [b.svc, b.when].filter(Boolean).join(" · "), onPick: () => this.actions.kioskPick(b.ref) })),
      ckHasRefMatches: refMatch.length > 0, ckHasNameMatches: nameMatch.length > 0,
      ckNoRefMatch: !!refTyped && st.ckLookedUp === refTyped && !st.ckLooking && refMatch.length === 0,
      ckNoNameMatch: nameTyped.length > 2,
      ckLooking: !!st.ckLooking,
      ckCanLookUp: refTyped.length > 0 && !st.ckLooking,
      // A reference is digits behind a fixed LV- prefix, so it gets a keypad; a name gets QWERTY.
      ckKeysOpen: !!st.ckKeys && !phone,
      ckOwnKeyboard: !phone,
      ckNativeKeyboardNote: phone ? "On a phone or tablet the device's own keyboard opens — the field sets the right layout for it." : "",
      ckQwertyMode: true,
      ckNumberRow: ckMethodActive !== "name",
      ckSpaceKey: ckMethodActive === "name",
      ckKeyboardLabel: "On-screen keyboard",
      ckInputMode: "text",
      ckDigitKeys: "1234567890".split("").concat(["-"]).map((c) => ({ key: c, ch: c, onTap: () => this.actions.keyPress(c) })),
      ckQwertyRows: [
        { key: "q1", keys: "QWERTYUIOP".split("").map((c) => ({ key: c, ch: c, onTap: () => this.actions.keyPress(c) })) },
        { key: "q2", keys: "ASDFGHJKL".split("").map((c) => ({ key: c, ch: c, onTap: () => this.actions.keyPress(c) })) },
        { key: "q3", keys: "ZXCVBNM".split("").map((c) => ({ key: c, ch: c, onTap: () => this.actions.keyPress(c) })) },
      ],
      ckHasBooking: !!kioskBooking,
      ckPickedName: kioskBooking ? kioskBooking.name : "", ckPickedSvc: kioskBooking ? kioskBooking.svc : "",
      ckPickedRef: kioskBooking ? kioskBooking.ref : "",
      ckPickedSvcWhen: kioskBooking ? [kioskBooking.svc, kioskBooking.when].filter(Boolean).join(" · ") : "",
      ckPickedWhenStaff: kioskBooking ? [kioskBooking.when, kioskBooking.staff].filter(Boolean).join(" · ") : "",
      ckDoneHeader: this.ctv("checkin", "done", "You're checked in"),
      ckDoneSub: this.ctv("checkin", "doneSub", "Please take a seat — someone will call your name."),
      // Only a walk-in holds a place in a line; an appointment has a time instead.
      ckShowQueuePos: !!kioskBooking && s.ckShowQueuePos && kioskBooking.kind === "walkin" && kioskBooking.pos > 0,
      ckShowSlot: !kioskBooking || kioskBooking.kind !== "walkin",
      ckQueuePos: !kioskBooking ? "" : kioskBooking.pos === 1 ? "You're next" : String(kioskBooking.pos) + " people ahead of you",
      ckDwellLeft: st.dwellLeft == null ? (s.ckDoneDwell || 6) : st.dwellLeft,
      ckDwellNote: "Back to the entry screen in " + (st.dwellLeft == null ? (s.ckDoneDwell || 6) : st.dwellLeft) + (((st.dwellLeft == null ? (s.ckDoneDwell || 6) : st.dwellLeft) === 1) ? " second" : " seconds") + ".",
      // Failure states — the wording and the way out differ per cause.
      ckProblemIs: kioskProblem,
      ckProbNoMatch: kioskProblem === "noMatch", ckProbAlready: kioskProblem === "already",
      ckProbCancelled: kioskProblem === "cancelled", ckProbNoCamera: kioskProblem === "noCamera",
      ckProbOffline: kioskProblem === "offline",
      ckProbTitle: kioskProblem === "noMatch" ? this.ctv("problems", "noMatch", "We couldn't read that pass")
        : kioskProblem === "already" ? this.ctv("problems", "already", "You're already checked in")
        : kioskProblem === "cancelled" ? this.ctv("problems", "cancelled", "This booking was cancelled")
        : kioskProblem === "noCamera" ? this.ctv("problems", "noCamera", "The reader isn't working")
        : this.ctv("problems", "offline", "This kiosk is offline"),
      ckProbBody: kioskProblem === "noMatch" ? this.ctv("problems", "noMatchSub", "Try holding it flatter and a little closer, or find your booking by name instead.")
        : kioskProblem === "already" ? "This booking is already checked in. " + (s.ckShowQueuePos ? "You're still in the line — please wait to be called." : "Please wait to be called.")
        : kioskProblem === "cancelled" ? "Nothing is booked for this reference any more. Staff at the welcome desk can rebook it for you."
        : kioskProblem === "noCamera" ? "Enter your booking reference or find yourself by name instead — both work without the reader."
        : this.ctv("problems", "offlineSub", "Please check in at the welcome desk. Your appointment is still booked."),
      // Offline and a dead reader are the two the visitor cannot solve on the screen.
      ckProbRecoverable: kioskProblem === "noMatch" || kioskProblem === "noCamera",
      ckNotScanMode: ckMethodActive !== "scan",
      // The booking is only worth showing back when we know which one it was.
      ckProbShowBooking: !!kioskBooking && (kioskProblem === "already" || kioskProblem === "cancelled"),
      bigTextLabel: st.bigText ? "Normal text" : "Larger text",
      ckProbTone: kioskProblem === "cancelled" || kioskProblem === "offline" ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-semantic-orange-1)",
      ckProbTint: kioskProblem === "cancelled" || kioskProblem === "offline" ? "var(--jrni-color-semantic-red-2)" : "var(--jrni-color-semantic-orange-2)",
    };
    const dayLabel = this.fmtDay(st.day);

    const todayIso = this.iso(this.today());
    const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const cm = st.calMonth;
    const firstOfMonth = new Date(cm.y, cm.m, 1);
    const lead = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(cm.y, cm.m + 1, 0).getDate();
    const calCells = [];
    for (let i = 0; i < lead; i++) calCells.push({ key: "b" + i, blank: true, disabled: true, label: "", bg: "transparent", fg: "transparent", border: "transparent", cursor: "default", weight: "400" });
    for (let dNum = 1; dNum <= daysInMonth; dNum++) {
      const d = new Date(cm.y, cm.m, dNum);
      const isoStr = this.iso(d);
      const avail = this.slotCount(d, this.bookWindow()) > 0;
      const sel = st.day === isoStr;
      const isToday = isoStr === todayIso;
      calCells.push({
        key: isoStr, blank: false, label: String(dNum), iso: isoStr, avail, selected: sel, isToday,
        bg: sel ? brand : "transparent",
        fg: sel ? "#FFFFFF" : avail ? "var(--jrni-color-text-strong)" : "var(--jrni-color-neutral-3)",
        border: sel ? brand : isToday ? "var(--jrni-color-neutral-3)" : "transparent",
        cursor: avail ? "pointer" : "not-allowed",
        weight: sel || isToday ? "700" : "500",
        disabled: !avail,
        onSelect: avail ? () => this.setState({ day: isoStr, slot: this.firstSlotFor(isoStr) }) : () => {},
      });
    }
    const dayStripDays = [];
    const stripOffset = (st.stripWeek || 0) * 7;
    for (let i = stripOffset; i < stripOffset + 7; i++) {
      const d = new Date(this.today().getFullYear(), this.today().getMonth(), this.today().getDate() + i);
      const isoStr = this.iso(d);
      const avail = this.slotCount(d, this.bookWindow()) > 0;
      const sel = st.day === isoStr && avail;
      dayStripDays.push({
        key: isoStr, value: isoStr, avail, selected: sel, disabled: !avail,
        label: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()] + " " + d.getDate(),
        sublabel: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()],
        bg: sel ? brand : "var(--jrni-color-base-white)",
        fg: sel ? "#FFFFFF" : avail ? "var(--jrni-color-text-strong)" : "var(--jrni-color-neutral-3)",
        subFg: sel ? "#FFFFFF" : "var(--jrni-color-text-soft)",
        border: sel || avail ? brand : "var(--jrni-color-neutral-4)",
        opacity: avail ? "1" : "0.5",
        cursor: avail ? "pointer" : "not-allowed",
        onSelect: () => { if (avail) this.actions.setDay(isoStr); },
      });
    }

    // The day's REAL slots, grouped by the part of day each one falls in. The
    // old sampling decimated a fixed list of twelve invented times to hit a
    // fabricated count.
    const daySlots = this.slotsFor(st.day);
    const dayCount = daySlots.length;
    const groupOf = (name) => daySlots
      .filter((slot) => partOfDay(slot) === name)
      .map((slot) => ({ value: slot.time, label: slot.time }));

    // Two hints below state a fact about the tenant's own catalogue while an
    // operator decides whether a rule fires, so they compose from the live
    // arrays the same way the skipService hint does — and correct themselves
    // once loadPreviewCatalog replaces the placeholder rows.
    const singleLocSvcs = SERVICES.filter((x) => (x.locs || []).length === 1);
    const paidEvents = this.eventsRows.filter((x) => x.price && x.price !== "Free");

    return { // ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: inspector view-model, not data)
      s: st.s, set: this.set, actions: this.actions,
      journey: st.journey,
      ui: { formOpen: st.formOpen, device: st.device, ctFilter: st.ctFilter },
      icons: {
        close: this.icon("IconX", 14), external: this.icon("IconExternal", 13), refresh: this.icon("IconRefresh", 13),
        left: this.icon("IconLeft", 13), right: this.icon("IconRight", 13), panel: this.icon("IconList", 15),
        chevron: this.icon("IconRight", 13), lock: this.icon("IconLock", 14),
        share: this.icon("IconExternal", 13),
        search: this.icon("IconSearch", 15), pin: this.icon("IconPin", 14), calendar: this.icon("IconCalendar", 14),
        pinSm: this.icon("IconPin", 13), calSm: this.icon("IconCalendar", 13), clockSm: this.icon("IconClock", 13),
        locate: this.icon("IconLocation", 15), embed: this.icon("IconBrowser", 14), copy: this.icon("IconCopy", 13),
        qrLarge: this.icon("IconQr", 40), warnLarge: this.icon("IconWarning", 22), tickLarge: this.icon("IconCheck", 38),
      },
      style: {
        field320: { width: 320, maxWidth: "100%" },
        field240: { width: 240, maxWidth: "100%" },
        field120: { width: "120px" },
        field180: { width: "180px" },
        field140: { width: 140 },
        field100: { width: 100 },
        fieldFull: { width: "100%" },
      },
      hints: {
        skipService: "The single service is selected for the customer and the " + this.stepLabel("service") + " step is dropped \u2014 in location-first journeys too. " + (SERVICES.length === 0
          ? "No services are currently offered, so the customer cannot continue."
          : SERVICES.length === 1
            ? (s.skipService ? "One service is offered, so this step is skipped." : "One service is offered. Enable this option to skip its selection step.")
            : SERVICES.length + " services are offered, so the step still shows."),
        skipLocation: st.pinnedLoc
          ? "No effect while you arrive from the kiosk — the location is fixed by the unit, so there is no " + this.stepLabel("location") + " step to skip. Still applies to web and mobile visitors."
          : "When the chosen service runs at one location only, that location is selected and the " + this.stepLabel("location") + " step is dropped. " + (singleLocSvcs.length === 0
              ? "No service runs at a single location today, so the step still shows."
              : singleLocSvcs.length === 1
                ? singleLocSvcs[0].name + " is the only single-location service today."
                : singleLocSvcs.length + " of " + SERVICES.length + " services run at a single location today."),
        perPage: this.eventsRows.length + " events are live · " + evtPages + (evtPages === 1 ? " page" : " pages"),
        firstAvail: "Skips fully-booked days — today that is " + this.fmtDay(this.firstAvailable(this.bookWindow()), true) + ".",
        map: s.firstStep === "map" ? "Always on while the journey opens on the map." : "A pin per location beside the list.",
        payment: events ? "Card fields appear only for paid events — " + (paidEvents.length === 0
          ? "none of the " + this.eventsRows.length + " live events has a price today."
          : paidEvents.length + " of " + this.eventsRows.length + (paidEvents.length === 1 ? " has" : " have") + " a price today.") : "Card fields appear only when the chosen service has a price.",
      },
      setupItems, setupValue: setupIds.indexOf(st.section) > -1 ? st.section : "",
      journeyItems: journeyItems.concat(railExtras),
      journeyCountLabel: stepList.length + " steps",
      panel: {
        title: panelMeta[0], desc: panelMeta[1],
      },
      is: {
        pinnedFromKiosk: !!st.pinnedLoc,
        landingJourney: this.isLanding(), general: st.section === "general", progress: st.section === "progress", service: st.section === "service",
        kiosk, kioskEntry: st.section === "kioskentry", kioskCheckin: st.section === "kioskcheckin",
        location: st.section === "location", datetime: st.section === "datetime", details: st.section === "details",
        hub: st.section === "hub",
        landing: st.section === "landing", tplBanner: s.landingTemplate === "banner", tplHero: s.landingTemplate === "hero",
        tplSplit: s.landingTemplate === "split", tplLong: s.landingTemplate === "long",
        soldWaitlist: s.soldOutMode === "waitlist", soldRelated: s.soldOutMode === "related", soldBoth: s.soldOutMode === "both",
        confirmation: st.section === "confirmation", customtext: st.section === "customtext",
        launch: st.section === "launch",
        serviceFirst: s.firstStep === "service", locationFirst: s.firstStep === "location", mapFirst: s.firstStep === "map",
        pTitles: s.progressType === "titles", pNumbers: s.progressType === "numbers",
        pBar: s.progressType === "bar", pNone: s.progressType === "none",
        dayStrip: s.calendarLayout === "daystrip", monthCal: s.calendarLayout === "month",
        dateToday: s.defaultDate === "today", dateFirst: s.defaultDate === "firstAvailable",
        grouped: s.slotGrouping === "partofday", flatSlots: s.slotGrouping === "flat",
        quickbookLocked: !s.nextAvailable,
        appt: !queue && !events && !landing && !kiosk, queue: queue, events: events, docs: !queue && !kiosk,
        // Shared "What you ask for" controls that are authored but not yet
        // delivered on the two event journeys. Appointments, Walk-Ins and
        // Kiosk are untouched — they still author these normally.
        /* Walk-Ins joins this set (2026-09-10): the canonical queue join
           carries name / phone / email / service / party / answers and
           nothing else, so a document checklist, a payment step or
           fill-and-lock for signed-in customers would be a control that
           changes nothing a visitor experiences. Disabled and labelled
           rather than offered and quietly inert. */
        detailsComingSoon: events || landing || queue,
        // The Events and Landing journeys save ONE shared presentation record
        // (both `confirmation` section key lists are identical: showQr /
        // addToCalendar / allowCancelReg / showResource / showStaff), so the
        // controls that author it must render on both tabs. Appointments,
        // Walk-Ins and Kiosk keep their own guards untouched.
        eventOrLanding: events || landing,
        eventList: st.section === "events",
        evList: s.eventDisplay === "list", evGrid: s.eventDisplay === "grid",
        cols2: s.cardCols === 2, cols3: s.cardCols === 3, cols4: s.cardCols === 4,
        pagePages: s.eventPaging === "pages", pageLoadMore: s.eventPaging === "loadmore", pageScroll: s.eventPaging === "scroll",
        soldOutHidden: !s.showSoldOut,
        waitExact: s.waitDisplay === "exact", waitRange: s.waitDisplay === "range", waitPosition: s.waitDisplay === "position",
      },
      opts: {
        reschedule: [{ value: "same", label: "Reschedule with the same staff member" }, { value: "any", label: "Reschedule with any available staff" }],
        serviceDisplay: [{ value: "list", label: "List" }, { value: "grid", label: "Cards" }, { value: "compact", label: "Compact list" }],
        radiusUnit: [{ value: "auto", label: "Auto (local)" }, { value: "mi", label: "Miles" }, { value: "km", label: "Kilometres" }],
        notif: [{ value: "both", label: "Both text & email" }, { value: "email", label: "Email only" }, { value: "sms", label: "Text message only" }],
        languages: [{ value: "en-US", label: "English (United States)" }, { value: "es-US", label: "Spanish (United States)" }, { value: "en-GB", label: "English (United Kingdom)" }],
        countries: [{ value: "US", label: "United States" }, { value: "MX", label: "Mexico" }, { value: "CA", label: "Canada" }, { value: "IN", label: "India" }, { value: "CN", label: "China" }, { value: "NG", label: "Nigeria" }],
        embedResize: [{ value: "fixed", label: "Fixed height" }, { value: "auto", label: "Auto-resize" }],
        dialCodes: (() => {
          const top = (s.customCountries || []);
          const ordered = DIAL.slice().sort((a, b) => {
            const ai = top.indexOf(a.iso), bi = top.indexOf(b.iso);
            if (ai > -1 || bi > -1) return (ai > -1 ? ai : 99) - (bi > -1 ? bi : 99);
            return a.name.localeCompare(b.name);
          });
          return ordered.map((c) => ({ value: c.iso, label: (s.countryFlags ? c.flag + " " : "") + "+" + c.dial + " " + c.iso }));
        })(),
        shareChannels: SHARE_CHANNELS.map((c) => ({ value: c.id, label: c.label })),
        sponsorTier: SPONSOR_TIERS.map((x) => ({ value: x, label: x })),
        relatedRule: [
          { value: "category", label: "Same type of event" },
          { value: "date", label: "Nearest in date" },
          { value: "venue", label: "Same venue" },
          { value: "manual", label: "Chosen by hand" },
        ],
        relatedCount: [{ value: 2, label: "2" }, { value: 3, label: "3" }, { value: 4, label: "4" }], // ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: static designer option list, not data)
        heroHeight: [{ value: 200, label: "Short" }, { value: 280, label: "Medium" }, { value: 380, label: "Tall" }], // ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: static designer option list, not data)
        heroTreatment: [{ value: "image", label: "Photo" }, { value: "tint", label: "Photo with brand tint" }, { value: "solid", label: "Brand colour only" }],
        refund: [
          { value: "full", label: "Refund in full" },
          { value: "minusFee", label: "Refund minus the cancellation fee" },
          { value: "none", label: "No refund" },
        ],
        ctFilter: [{ value: "all", label: "All fields" }, { value: "custom", label: "Customised only" }],
        kioskGreeting: [{ value: "time", label: "By time of day" }, { value: "fixed", label: "Always the same" }, { value: "none", label: "No greeting" }],
        kioskTileSize: [{ value: "large", label: "Large" }, { value: "compact", label: "Compact" }],
        kioskLocations: LOCATIONS.map((l) => ({ value: l.id, label: l.name })),
        ckDefault: [{ value: "scan", label: "Scan" }, { value: "ref", label: "Reference" }, { value: "name", label: "Name" }],
        journeys: [{ value: "appointment", label: "Appointments" }, { value: "queue", label: "Walk-Ins" }, { value: "events", label: "Event List Page" }, { value: "landing", label: "Event Landing Page" }, { value: "kiosk", label: "Kiosk" }, { value: "onestop", label: "OneStop" }],
        sortOrder: [{ value: "date", label: "Soonest first" }, { value: "popularity", label: "Most booked first" }, { value: "name", label: "Alphabetical" }],
        sizeUnit: [{ value: "auto", label: "Auto" }, { value: "px", label: "px" }],
        attendeeFields: [{ value: "first", label: "First name" }, { value: "last", label: "Last name" }, { value: "email", label: "Email" }, { value: "phone", label: "Phone" }],
        cardCols: [{ value: 2, label: "2" }, { value: 3, label: "3" }, { value: 4, label: "4" }], // ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: static designer option list, not data)
        eventFilterChips: [{ value: "all", label: "All events" }, { value: "admissions", label: "Admissions" }, { value: "academic", label: "Academic" }, { value: "careers", label: "Careers" }, { value: "wellness", label: "Wellness" }],
      },
      ct: { groups: ctGroups },
      pv: {
        brand, radius,
        /* ── Walk-Ins preview inputs ──────────────────────────────────
         * Projected here rather than inside the component so the preview
         * takes the DRAFT under edit, while the standalone page takes the
         * PUBLISHED config for its slug — the one and only difference
         * between the two mounts. `walkInQueues` is the canonical
         * `getPublicQueue` read of the attached queues, so the preview's
         * services, service points, status and waits are the tenant's own. */
        walkInConfig: walkInConfigFromDesigner(s, st.wiQueueIds || []),
        walkInText: st.ct || {},
        walkInPresentation: {
          // The JOURNEY's own persisted brand, through the same resolver the
          // standalone page uses (`walkInBrandFrom`) — not `brand` above and
          // not `props.tenantName`. Those are the Studio shell's tenant colour
          // and tenant name: an authenticated read a visitor cannot make, so
          // preferring them here is what made the preview show the tenant's
          // own name and colour while the published page showed the journey's
          // own. An unbranded journey resolves to "" and the renderer
          // supplies the DS token and the neutral wording, identically.
          ...walkInBrandFrom(st.wiJourney),
          showHeader: s.showHeader !== false,
          showFooter: !!s.showFooter,
          hideJrni: s.hideJrni ?? ((st.wiJourney && st.wiJourney.advanced && st.wiJourney.advanced.hideJrni) === true),
          footer: (st.wiJourney && st.wiJourney.footer) || {},
          progressType: s.progressType === "none" ? "none" : s.progressType === "bar" ? "bar" : s.progressType === "numbers" ? "numbers" : "titles",
          progressShowCount: !!((st.wiJourney && st.wiJourney.progress && st.wiJourney.progress.showCount)),
        },
        walkInQueues: st.wiQueues || [],
        walkInQueuesLoading: !!st.wiQueuesLoading,
        // An honest reason, never an empty page that looks configured.
        walkInError: (st.wiQueueIds || []).length
          ? ""
          : "No service point is attached to this page yet. Pick at least one queue in General before publishing.",
        walkInStep: st.wiPreviewStep || "service",
        walkInQueueRows: (st.wiQueues || []).map((read) => {
          const view = (read && read.view) || null;
          const q = (view && view.queue) || null;
          const option = (st.wiQueueOptions || []).filter((o) => o.id === read.id)[0] || null;
          if (!q) {
            return { id: read.id, name: (option && option.name) || read.id, sub: read.error || "Could not be loaded", meta: "" };
          }
          // The resolved catalog NAMES, not the queue's raw refs: a queue that
          // stores service ids listed them as ids in the panel.
          const serviceNames = serviceOptionsOf(read.view).map((o) => o.name);
          // `canAcceptEntries` from the queue's live status read is the
          // authority when it is available — a queue can be `open` and still
          // be outside its hours.
          const live = read.status;
          return {
            id: read.id,
            name: String(q.name || read.id),
            sub: [q.org, serviceNames.join(", ")].filter(Boolean).join(" · "),
            meta: live && !live.canAcceptEntries
              ? (live.storeOpen ? "Not taking anyone" : (live.nextOpenTime ? "Closed · opens " + live.nextOpenTime : "Closed"))
              : q.status === "open"
                ? (q.waiting === 1 ? "1 waiting" : q.waiting + " waiting")
                : q.status === "paused" ? "Paused" : "Closed",
          };
        }),
        walkInQueueOptions: (st.wiQueueOptions || []).map((q) => ({
          id: q.id,
          name: q.name || q.id,
          selected: (st.wiQueueIds || []).indexOf(q.id) > -1,
          // The queue's OWN location and status, as the list op reports them.
          // A field the worker does not enrich stays out of the line rather
          // than being filled with something plausible.
          meta: [q.locationName, q.status === "open" ? "Open" : q.status === "paused" ? "Paused" : "Closed"].filter(Boolean).join(" · "),
        })),
        chrome: !st.previewOnly,
        formPane: st.formOpen && !st.previewOnly,
        saveDisabled: persistenceUnavailable || (!st.dirty && isPublished) || st.saveNote === "saving",
        saveLabel: st.dirty ? "Save" : "Saved",
        publishLabel: st.published[st.journey] ? "Publish changes" : "Publish",
        published: isPublished,
        // One action saves and publishes; the status still comes from the server.
        dirty: !!st.dirty,
        showSavePublish: persistenceUnavailable || st.dirty || !isPublished,
        savePublishLabel: persistenceUnavailable ? "Save unavailable" : st.saveNote === "saving" ? "Saving\u2026" : "Save & publish",
        statusLabel: persistenceUnavailable ? "Save unavailable" : st.dirty ? "Unsaved changes" : isPublished ? "Published" : "Not published",
        statusEdge: isPublished && !st.dirty ? "var(--jrni-color-semantic-green-1)" : "var(--jrni-color-semantic-orange-1)",
        statusFill: isPublished && !st.dirty ? "var(--jrni-color-semantic-green-2)" : "var(--jrni-color-semantic-orange-2)",
        statusInk: isPublished && !st.dirty ? "var(--jrni-color-semantic-green-1)" : "var(--jrni-color-semantic-orange-1)",
        hasSaveNote: persistenceUnavailable || !!st.saveNote,
        // A typed refusal points at controls, not at JSON paths — the same treatment
        // a slug conflict gets (ask 3 of the editor-save entry, 2026-09-04).
        refusedFields: st.refusedFields || [],
        hasRefusals: !!(st.refusedFields && st.refusedFields.length),
        saveDotBg: st.saveNote === "published" ? "var(--jrni-color-semantic-green-1)" : st.saveNote === "error" ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-neutral-3)",
        saveNote: persistenceUnavailable ? PD_STUB_REASON : st.saveNote === "error"
          ? (st.saveError || "Couldn't save.")
          : st.saveNote === "nochange"
          ? "No changes to save \u2014 nothing here differs from what is already live."
          : st.saveNote === "published"
          ? "Published \u2014 this journey is live for customers at"
          : "All changes saved. Publish when you want customers to see them.",
        saveNotices: st.saveNotices || [],
        hasSaveNotices: !!(st.saveNotices && st.saveNotices.length),
        liveUrl: this.publicLink(),
        // Walk-Ins steps through WalkInJourney's own list, so the toolbar's
        // bounds are read from THAT list rather than the appointment wizard's.
        atFirstStep: queue ? (st.wiPreviewStep || "service") === "service" : stepIndex <= 0,
        atLastStep: queue ? (st.wiPreviewStep || "service") === "ticket" : stepIndex >= stepList.length - 1,
        progressPct: Math.round(((stepIndex + 1) / Math.max(1, stepList.length)) * 100) + "%",
        pageTitle: kiosk
          ? this.ctv("entry", "page", "Welcome")
          : this.ctv("progress", "page", queue ? "Join the queue" : landing ? "Event details" : events ? "Campus events" : "Book an appointment"),
        showPageTitle: true,
        blockRows: (s.blockOrder || []).map((id, i) => {
          const def = LANDING_BLOCKS.filter((b) => b.id === id)[0] || { label: id };
          const on = (s.blocksOn || []).indexOf(id) > -1;
          const DERIVED = {
            spaces: "The meter reads the event's capacity and remaining places, so there is nothing to write here.",
            map: "The pin and address come from the event's venue.",
          };
          const counts = {
            desc: (s.lpAgendaRows || []).length + " agenda lines",
            speakers: (s.lpSpeakerRows || []).length + " people",
            included: (s.lpIncludedRows || []).length + " lines",
            tickets: this.eventTicketRows(curEvt).length + " from the event",
            faq: (s.lpFaqRows || []).length + " questions",
            sponsors: (s.lpSponsorRows || []).length + " logos",
            share: (s.lpShareChannels || []).length + " channels",
          };
          const derived = !!DERIVED[id];
          const open = !!st.blockOpen[id];
          return {
            key: id, label: def.label, on, open,
            meta: counts[id] || "",
            fg: on ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-soft)",
            caret: open ? "rotate(90deg)" : "rotate(0deg)",
            caretGlyph: "›",
            cursor: "pointer",
            noEditor: false,
            isDesc: id === "desc", isSpeakers: id === "speakers", isIncluded: id === "included",
            isTickets: id === "tickets", isFaq: id === "faq", isSponsors: id === "sponsors",
            isShare: id === "share", isDerived: derived, derivedNote: DERIVED[id] || "",
            first: i === 0, last: i === (s.blockOrder || []).length - 1,
            onExpand: () => this.actions.expandBlock(id),
            onUp: () => this.actions.moveBlock(id, -1),
            onDown: () => this.actions.moveBlock(id, 1),
            onToggle: () => this.actions.toggleBlock(id),
          };
        }),
        agendaEdit: (s.lpAgendaRows || []).map((r, i) => ({
          key: "ag" + i, time: r.time, what: r.what,
          setTime: (v) => this.actions.editRow("lpAgendaRows", i, "time", v),
          setWhat: (v) => this.actions.editRow("lpAgendaRows", i, "what", v),
          onRemove: () => this.actions.removeRow("lpAgendaRows", i),
        })),
        speakerEdit: (s.lpSpeakerRows || []).map((r, i) => ({
          key: "spe" + i, name: r.name, role: r.role, photo: r.photo || "",
          setName: (v) => this.actions.editRow("lpSpeakerRows", i, "name", v),
          setRole: (v) => this.actions.editRow("lpSpeakerRows", i, "role", v),
          setPhoto: (v) => this.actions.editRow("lpSpeakerRows", i, "photo", v),
          onRemove: () => this.actions.removeRow("lpSpeakerRows", i),
        })),
        includedEdit: (s.lpIncludedRows || []).map((r, i) => ({
          key: "ine" + i, text: r,
          setText: (v) => this.actions.editRow("lpIncludedRows", i, null, v),
          onRemove: () => this.actions.removeRow("lpIncludedRows", i),
        })),
        ticketEdit: this.eventTicketRows(curEvt),
        ticketsEmpty: this.eventTicketRows(curEvt).length === 0,
        ticketsEmptyTitle: curEvt.id ? "No ticket types on this event" : "No event selected",
        ticketsEmptyBody: curEvt.id
          ? "This event sells no ticket types yet. Add them to the event in Studio and they appear here."
          : "Pick an event to see the ticket types it sells.",
        faqEdit: (s.lpFaqRows || []).map((r, i) => ({
          key: "fqe" + i, q: r.q, a: r.a,
          setQ: (v) => this.actions.editRow("lpFaqRows", i, "q", v),
          setA: (v) => this.actions.editRow("lpFaqRows", i, "a", v),
          onRemove: () => this.actions.removeRow("lpFaqRows", i),
        })),
        sponsorEdit: (s.lpSponsorRows || []).map((r, i) => ({
          key: "sre" + i, name: r.name, tier: r.tier || "Supporter", logo: r.logo || "",
          setName: (v) => this.actions.editRow("lpSponsorRows", i, "name", v),
          setTier: (v) => this.actions.editRow("lpSponsorRows", i, "tier", v),
          setLogo: (v) => this.actions.editRow("lpSponsorRows", i, "logo", v),
          onRemove: () => this.actions.removeRow("lpSponsorRows", i),
        })),
        soldOutPreview: !!st.soldOutPreview,
        isLandingPage: (landing || events) && st.previewStep === "landing",
        isLinkError: st.previewStep === "linkError",
        errTitle: this.ctv("error", "ttl", "This booking link is no longer available"),
        errBody: this.ctv("error", "body", "The adviser or service behind this link is not taking appointments at the moment. Nothing has been booked, and nothing has been charged."),
        errHasRoutes: s.errBookStaff || s.errOtherServices,
        errNoRoutes: !s.errBookStaff && !s.errOtherServices,
        errStaffTile: this.tile("IconUsers", "primary"),
        errSvcTile: this.tile("IconCalendar", "primary"),
        errStaffTitle: this.ctv("error", "staffttl", "Book with another adviser"),
        errStaffSub: this.ctv("error", "staffsub", "Same service, next available adviser \u2014 " + LOCATIONS.length + " locations to choose from."),
        errSvcTitle: this.ctv("error", "svcttl", "Explore other services"),
        errSvcSub: this.ctv("error", "svcsub", SERVICES.length + " services are open for booking today."),
        errStartLabel: this.ctv("error", "start", "Start again from the beginning"),
        tplBannerHero: s.landingTemplate === "banner",
        tplHeroHero: s.landingTemplate === "hero",
        tplSplitHero: s.landingTemplate === "split",
        tplLongHero: s.landingTemplate === "long",
        lpShowAside: s.landingTemplate === "banner",
        lpInlineCta: s.landingTemplate === "hero" || s.landingTemplate === "long",
        lpMainW: s.landingTemplate === "banner" ? "420px" : "100%",
        lpHeroH: (s.heroHeight || 280) + "px",
        lpHeroBg: (() => {
          if (s.heroTreatment === "solid") return brand;
          const img = s.coverImage ? 'url("' + s.coverImage + '")' : "linear-gradient(120deg, " + curEvt.hue + " 0%, #2B2350 100%)";
          return s.heroTreatment === "tint" ? "linear-gradient(0deg, " + brand + "66, " + brand + "66), " + img : img;
        })(),
        lpHeroScrim: s.landingTemplate === "hero" ? "linear-gradient(0deg, rgba(14, 18, 28, 0.72) 0%, rgba(14, 18, 28, 0.15) 70%)" : "transparent",
        lpTitle: curEvt.name,
        lpWhen: curEvt.when, lpVenue: curEvt.venue, lpAddr: curEvt.addr,
        lpPrice: curEvt.price === "Free" ? "Free" : "From " + curEvt.price,
        lpCta: this.ctv("details", "cta", "Register"),
        // An explicit authored empty description (a real landing document
        // whose field the author left blank) stays empty; only the ABSENCE
        // of any landing document at all falls back to the event's own
        // description.
        lpDesc: (this.isPublic() && this.landingIsConfigured) ? s.lpDescText : (s.lpDescText || curEvt.desc),
        lpAgenda: (s.lpAgendaRows || []).map((r, i) => ({ key: "a" + i, time: r.time, what: r.what })),
        lpSpeakers: (s.lpSpeakerRows || []).map((x, i) => ({
          key: "sp" + i, name: x.name, role: x.role,
          hasPhoto: !!x.photo, noPhoto: !x.photo,
          photoCss: x.photo ? 'url("' + x.photo + '")' : "none",
        })),
        lpIncluded: (s.lpIncludedRows || []).map((x, i) => ({ key: "in" + i, text: x })),
        // Public and admin alike: the event's OWN ticket tiers from its read.
        // With none (or no event selected) the designer says so; it never
        // invents sample tickets or prices.
        lpTickets: this.eventTicketRows(curEvt),
        lpTaken: Math.max(0, (curEvt.cap || 0) - (st.soldOutPreview ? 0 : curEvt.left || 0)),
        lpCap: curEvt.cap || 0,
        lpMeterTone: st.soldOutPreview ? "danger" : (curEvt.left / (curEvt.cap || 1)) < 0.2 ? "warning" : "success",
        // Reads the same resolved availability as the pill above it, so the
        // sticky bar cannot promise places on an event the server closed.
        lpSpacesLabel: lpEnded ? endedLabel : lpSoldOut ? "No places left" : curEvt.left + " of " + curEvt.cap + " places left",
        lpFaq: (s.lpFaqRows || []).map((x, i) => ({
          key: x.q, q: x.q, a: x.a, open: !!st.faqOpen[i],
          caret: st.faqOpen[i] ? "rotate(90deg)" : "rotate(0deg)",
          onToggle: () => this.actions.toggleFaq(i),
        })),
        relatedAuto: s.relatedRule !== "manual", relatedManualMode: s.relatedRule === "manual",
        eventOptions: this.eventsRows.filter((e) => e.id !== curEvt.id).map((e) => ({ value: e.id, label: e.name })),
        relatedNote: s.relatedRule === "manual"
          ? "Only the events you pick are shown, in the order you picked them."
          : s.relatedRule === "venue"
            ? "Events at " + curEvt.venue + " come first; the rest fill any gap."
            : s.relatedRule === "date"
              ? "The next events on the calendar, whatever their type."
              : "Events of the same type come first; the rest fill any gap.",
        lpRelated: relatedEvents.map((e) => ({
          key: e.id, name: e.name, meta: [e.when, e.venue].filter(Boolean).join(" \u00b7 "),
          onSelect: () => {
            this.setState({ faqOpen: {}, wlDone: false });
            if (this.isPublic()) this.selectPublicEvent(e.id);
            else this.setState({ evt: e.id });

          },
        })),
        lpSponsorGroups: (() => {
          const rows = (s.lpSponsorRows || []);
          const mk = (r, i) => {
            const big = (r.tier || "").indexOf("Gold") === 0;
            return {
              key: "sr" + i, name: r.name,
              logoCss: r.logo ? 'url("' + r.logo + '")' : "none",
              fallback: r.logo ? "" : r.name,
              border: r.logo ? "1px solid var(--jrni-color-surface-border)" : "1px dashed var(--jrni-color-neutral-4)",
              w: big ? "148px" : "112px", h: big ? "60px" : "44px",
            };
          };
          if (!s.sponsorTiers) return [{ key: "all", tier: "", showTier: false, items: rows.map(mk) }];
          return SPONSOR_TIERS.map((tier) => ({
            key: tier, tier, showTier: true,
            items: rows.filter((r) => (r.tier || "Supporter") === tier).map(mk),
          })).filter((g) => g.items.length > 0);
        })(),
        lpShare: SHARE_CHANNELS.filter((c) => (s.lpShareChannels || []).indexOf(c.id) > -1).map((c) => ({ key: c.id, label: c.label })),
        // Authoritative availability facts on BOTH paths. The Studio preview
        // loads its rows from the same `getPublicEvents` read as the public
        // page, so the projection decides here too — there is no isPublic-only
        // branch. `soldOutPreview` remains an authoring aid: it may force the
        // sold-out layout on, but it can never re-open an event the server
        // closed, and it cannot un-end an ended one.
        lpEnded: lpEnded,
        lpSoldOut: lpSoldOut,
        lpOpen: lpRegOpen,
        // Closed, but honest about why: ended, genuinely full, or simply not
        // open for registration.
        lpClosed: !lpRegOpen,
        lpClosedLabel: lpEnded ? endedLabel : lpSoldOut ? soldOutLabel : regClosedLabel,
        // An ended event has nothing to wait for, so the waiting list is never
        // offered on it; otherwise the server's own `waitlistOpen` decides,
        // with the author's switch as the fallback when no projection exists.
        lpShowWaitlist: s.soldOutMode !== "related" && lpSoldOut && !lpEnded
          && (lpAv ? lpAv.waitlistOpen === true : true),
        wlOpen: !st.wlDone, wlDone: !!st.wlDone, wlName: st.wlName, wlEmail: st.wlEmail,
        wlNote: (st.booking?.registration?.status === "waitlisted")
          ? "You're on the waitlist for " + curEvt.name + "."
          : "",
        wlSubmitting: !!st.submitting, wlError: st.bookingError || "",
        landingBlocks: (s.blockOrder || []).filter((id) => (s.blocksOn || []).indexOf(id) > -1)
          .filter((id) => !(st.soldOutPreview && id === "tickets"))
          .filter((id) => !(st.soldOutPreview && s.soldOutMode === "waitlist" && id === "related"))
          .map((id) => ({
            key: id,
            isDesc: id === "desc", isSpeakers: id === "speakers", isIncluded: id === "included",
            isTickets: id === "tickets", isSpaces: id === "spaces", isMap: id === "map",
            isFaq: id === "faq", isRelated: id === "related", isSponsors: id === "sponsors",
            isShare: id === "share",
          })),
        hasHero: s.landingTemplate === "hero" || s.landingTemplate === "split",
        noHero: !(s.landingTemplate === "hero" || s.landingTemplate === "split"),
        noHeroNote: s.landingTemplate === "banner"
          ? "Banner and sidebar uses a slim colour banner, so there is no cover image to set."
          : "Long-form has no hero. Pick Hero cover or Split panel to set a cover image, height and treatment.",
        frameWidth,
        brandHeader: s.showHeader,
        ...kioskPv,        ctaLabel: queue ? this.ctv("details", "cta", "Join the queue") : (events || landing) ? this.ctv("details", "cta", "Complete registration") : "Confirm appointment",
        paneLabel: st.formOpen ? "Hide the settings pane" : "Show the settings pane",
        ctaHeight: "44px",
        queueHeader: st.leftQueue ? this.ctv("queue", "lvhdr2", "You are no longer in the queue") : this.ctv("queue", "hdr", "You're in the queue"),
        posLabel: this.ctv("queue", "pos", "Position in line"),
        waitLabel: this.ctv("queue", "wait", "Estimated wait"),
        notifyNote: this.ctv("queue", "notify", "We'll text you when you're next to be seen."),
        leaveLabel: this.ctv("queue", "leave", "Leave the queue"),
        inQueue: !st.leftQueue, leftQueue: !!st.leftQueue,
        leaveAsking: !!st.leaveAsking,
        leaveTitle: this.ctv("queue", "lvttl", "Leave the line?"),
        leaveConsequence: "Your place goes to the next person, and rejoining puts you at the back of the line.",
        leaveConfirmLabel: this.ctv("queue", "leave", "Leave the queue"),
        leaveKeepLabel: this.ctv("queue", "lvkeep", "Stay in the line"),
        leftHeader: this.ctv("queue", "lvhdr", "You have left the line"),
        leftText: this.ctv("queue", "lvtxt", "Your ticket is no longer valid. You can join again or book a time instead."),
        rejoinLabel: this.ctv("queue", "lvagain", "Join the line again"),
        bookInsteadLabel: this.ctv("queue", "lvbook", "Book an appointment instead"),
        ticketNumber: "A-042",
        position: st.qPos <= 0 ? "Now serving" : st.qPos === 1 ? "You're next" : this.ordinal(st.qPos),
        waitText: (() => {
          const mins = Math.max(0, Math.round((st.qPos * QUEUE_AVG_MIN) / QUEUE_DESKS_ON_DUTY));
          if (st.qPos <= 0) return "Go to the desk";
          if (s.waitDisplay === "exact") return mins + (mins === 1 ? " minute" : " minutes");
          return mins + "\u2013" + (mins + 5) + " minutes";
        })(),
        staffLine: st.qPos <= 0
          ? "An adviser is ready for you now."
          : st.qFree + " of " + QUEUE_DESKS_ON_DUTY + (st.qFree === 1 ? " adviser is" : " advisers are") + " free right now, and the line is moving as they finish.",
        showWait: s.waitDisplay !== "position",
        notifyLine: s.notifyWhenClose ? "You'll get a text when " + s.notifyAhead + (s.notifyAhead === 1 ? " person is" : " people are") + " ahead of you." : "",
        queueLocationHeader: this.ctv("location", "hdr", "Choose a service point"),
        arrivalNote: s.remoteJoin ? "Arrive within " + s.arrivalWindow + " minutes of being called, or you'll be moved to the back of the line." : "",
        hasArrivalNote: queue && s.remoteJoin,
        hasLogo: !!s.logo, noLogo: !s.logo,
        logoCss: s.logo ? 'url("' + s.logo + '")' : "none",
        stepTitle: st.previewStep === "linkError"
          ? "Link error page"
          : st.cancelled
            ? (events || landing ? "Registration cancelled" : "Appointment cancelled")
            : st.leftQueue
              ? "Left the line"
              : this.stepLabel(st.previewStep) + " \u2014 step " + (stepIndex + 1) + " of " + stepList.length,
        stepMeta: st.device === "mobile" ? "390 px · mobile" : st.device === "tablet" ? "Up to 768 px · tablet" : "Full width · desktop",
        atFirst: stepIndex === 0, atLast: stepIndex === stepList.length - 1,
        stepIndex, percent: Math.round(((stepIndex + 1) / stepList.length) * 100),
        percentLabel: "Step " + (stepIndex + 1) + " of " + stepList.length,
        stepperSteps: stepList.map((id) => this.stepLabel(id)),
        steps: stepList.map((id, i) => ({
          label: this.stepLabel(id),
          // The events step is the list journey's own step: on the public
          // rail tapping it is a real journey switch, not a step flip.
          onClick: () => {
            if (id === "events" && this.isPublic() && this.isLanding()) { this.actions.backToEvents(); return; }
            this.setState({ previewStep: id });
          },
          underline: i === stepIndex ? brand : "transparent",
          fg: i === stepIndex ? "var(--jrni-color-text-strong)" : i < stepIndex ? brand : "var(--jrni-color-text-soft)",
          weight: i === stepIndex ? "700" : "500",
          // Numbered-steps variant only. These were MISSING, so that variant rendered a
          // 22px chip with no glyph and a transparent fill — visually identical to the
          // step-titles variant, which is why picking "Numbered steps" appeared to do
          // nothing. Done steps get the tick the option's description promises.
          num: i < stepIndex ? "\u2713" : String(i + 1),
          numBg: i === stepIndex ? brand : i < stepIndex ? "var(--jrni-color-primary-3)" : "var(--jrni-color-neutral-6)",
          numFg: i === stepIndex ? "#FFFFFF" : i < stepIndex ? brand : "var(--jrni-color-text-soft)",
          connector: i === stepList.length - 1 ? "" : i < stepIndex ? brand : "var(--jrni-color-neutral-5)",
        })),
        progressTitles: s.progressType === "titles" && !outsideJourney, progressNumbers: s.progressType === "numbers" && !outsideJourney, progressBar: s.progressType === "bar" && !outsideJourney,
        isService: st.previewStep === "service",
        isLocation: st.previewStep === "location" && !(!queue && !events && s.firstStep === "map"),
        isMapOnly: st.previewStep === "location" && !queue && !events && s.firstStep === "map",
        isDatetime: st.previewStep === "datetime",
        isBooking: st.previewStep === "booking", isDetails: st.previewStep === "details",
        isEvents: st.previewStep === "events",
        isConfirmation: st.previewStep === "confirmation" && !queue && !events && !landing,
        isTicket: st.previewStep === "confirmation" && queue,
        isRegistration: st.previewStep === "confirmation" && (events || landing),
        eventsHeader: this.ctv("events", "hdr", "Select an event"),
        eventCards, eventCount: usingServerEvents ? evtTotalCount : evtFilteredCount,
        eventShownLabel: usingServerEvents
          ? "Showing " + evtShown.length + " of " + evtTotalCount + " events"
          : "Showing " + evtShown.length + " of " + evtFilteredCount + " events",
        eventPages: evtPages, eventPage: evtPage,
        showPager: events && s.eventPaging === "pages" && evtPages > 1,
        showLoadMoreEvents: usingServerEvents
          ? (events && s.eventPaging === "loadmore" && evtHasMore)
          : (events && s.eventPaging !== "pages" && evtShown.length < evtFilteredCount),
        // "scroll" gets an actually-mounted sentinel (below) that fetches the
        // next server page on intersection, not the manual button — a
        // config that fetches nothing on scroll is the bug this replaces.
        showScrollSentinel: usingServerEvents && events && s.eventPaging === "scroll" && evtHasMore,
        eventFilterValue: st.evtCat,
        cardGrid: st.device === "mobile"
          ? "minmax(0, 1fr)"
          : (function (n) {
              return "repeat(auto-fit, minmax(min(100%, max(190px, calc((100% - " + ((n - 1) * 24) + "px) / " + n + "))), 1fr))";
            })(s.cardCols || 3),
        showFilterBar: events && s.filterBar,
        evtQuery: st.evtQuery, evtFrom: st.evtFrom, evtTo: st.evtTo,
        evtMatches, hasEvtMatches: evtMatches.length > 0,
        typeOptions: evtCategoryOptions,
        locationOptions: evtLocationOptions,
        hasLocationOptions: evtLocationFacet.length > 0,
        evtLocationValue: st.evtLocationFilter || "",
        eventHostName,
        eventEmpty: usingServerEvents ? evtShown.length === 0 : evtFilteredCount === 0,
        eventEmptyText: this.ctv("events", "empty", "There are no events to show right now."),
        ticketsLabel: this.ctv("details", "tickets", "How many places?"),
        paymentHeader: this.ctv("details", "phdr", "Payment"),
        paymentInfo: this.ctv("details", "pinfo", ""),
        hasPaymentInfo: !!(st.ct["details.pinfo"] || "").trim(),
        totalDue: (() => {
          if (events) {
            const m = /([0-9.]+)/.exec(curEvt.price || "");
            if (!m) return "No payment needed";
            return "$" + (parseFloat(m[1]) * effectiveTickets).toFixed(2);
          }
          return cur.price === "Free" || !cur.price ? "No payment needed" : cur.price;
        })(),
        attFirst: (s.attendeeFields || []).indexOf("first") > -1,
        attLast: (s.attendeeFields || []).indexOf("last") > -1,
        attEmail: (s.attendeeFields || []).indexOf("email") > -1,
        attPhone: (s.attendeeFields || []).indexOf("phone") > -1,
        addAttendeeLabel: (st.attRows || []).length > 0 ? "Add another attendee" : "Add attendees",
        canAddAttendee: s.attendees && (st.attRows || []).length < Math.max(0, (events ? (curEvt.availability && curEvt.availability.soldOut ? 0 : 1) : (s.maxParty || 1)) - 1),
        attendeeList: (st.attRows || []).map((r, i) => {
          const rp = this.parsePhone(r.phone, r.country);
          const eOk = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test((r.email || "").trim());
          const fBad = r.touched.first && !nameOk(r.first);
          const lBad = r.touched.last && !nameOk(r.last);
          const eBad = r.touched.email && !eOk && !(s.emailOptional && !(r.email || "").trim());
          const pBad = r.touched.phone && !rp.valid && !(s.phoneOptional && rp.len === 0);
          return {
            key: String(r.id), title: "Attendee " + (i + 1),
            first: r.first, last: r.last, email: r.email, phone: r.phone, dialIso: rp.country.iso,
            setFirst: (v) => this.actions.attSet(r.id, "first", v),
            setLast: (v) => this.actions.attSet(r.id, "last", v),
            setEmail: (v) => this.actions.attSet(r.id, "email", v),
            setPhone: (v) => this.actions.attSet(r.id, "phone", v),
            setCountry: (iso) => this.actions.attSet(r.id, "country", iso),
            blurFirst: () => this.actions.attBlur(r.id, "first"),
            blurLast: () => this.actions.attBlur(r.id, "last"),
            blurEmail: () => this.actions.attBlur(r.id, "email"),
            blurPhone: () => this.actions.attBlur(r.id, "phone"),
            firstError: fBad ? nameMsg(r.first) : undefined,
            lastError: lBad ? nameMsg(r.last) : undefined,
            emailError: eBad ? ((r.email || "").trim() ? "Enter a valid e-mail address, like you@example.com." : "An e-mail address is required.") : undefined,
            phoneError: pBad ? (rp.len === 0 ? "A phone number is required." : "That is not a valid " + rp.country.name + " number \u2014 " + (rp.country.min === rp.country.max ? rp.country.min + " digits" : rp.country.min + "\u2013" + rp.country.max + " digits") + " expected.") : undefined,
            firstState: fBad ? "error" : "default",
            lastState: lBad ? "error" : "default",
            emailState: eBad ? "error" : "default",
            phoneState: pBad ? "error" : "default",
            onRemove: () => this.actions.attRemove(r.id),
          };
        }),
        showParty: queue && s.partySize,
        party: st.party, partyHint: "Up to " + s.maxParty + " people on one ticket",
        showEventSummary: events || landing,
        // Coming soon on both event journeys: a registration is always for a
        // single place, so the quantity picker never renders here.
        showTickets: (events || landing) ? false : s.ticketQuantity,
        ticketsHint: "Up to " + (curEvt.availability && curEvt.availability.soldOut ? 0 : 1) + " per registration for this event",
        evtSummaryLine: [curEvt.when, curEvt.venue].concat(s.showEventPrice ? [curEvt.price] : []).join(" · "),
        tickets: effectiveTickets, maxTickets: (curEvt.availability && curEvt.availability.soldOut ? 0 : 1),
        // Real ticket tiers from the event's own detail read — never a
        // fixture list, and never auto-picked when there is more than one:
        // `submitEventRegistration` refuses to submit without an explicit
        // choice in that case.
        ticketChoices: ((curEvt.raw && curEvt.raw.ticketTiers) || []).map((t) => ({
          value: t.id, label: t.name + " — " + formatEventPrice({ amountMinor: t.priceMinor, currency: t.currency }),
        })),
        selectedTicketId: st.selectedTicketId || "",
        ticketChoiceError: st.bookingError === "Choose a ticket type." ? st.bookingError : undefined,
        evtName: curEvt.name, evtWhen: curEvt.when, evtVenue: curEvt.venue, evtPrice: curEvt.price,
        evtCover: this.heroWide(curEvt),
        // Real per-registration QR only — the credential a genuine registration
        // issued (`st.booking.ticketLinks[0].qrUrl`). Empty (never a fabricated
        // path) until a real booking with an issued credential exists; credential
        // issuance can fail on an otherwise-successful registration, and that is
        // exactly the case this renders as "no QR", not a made-up one.
        qrValue: (st.booking && Array.isArray(st.booking.ticketLinks) && st.booking.ticketLinks[0]?.qrUrl) || "",
        // Empty until a booking exists. The QR used to encode a hard-coded
        // reference, so every confirmation carried the same one.
        apptQrValue: (st.booking && st.booking.orderRef)
          ? this.publicOrigin() + "/a/" + encodeURIComponent(String(st.booking.orderRef))
          : "",
        bookingReference: (st.booking && st.booking.orderRef) ? String(st.booking.orderRef) : "",
        bookingRoom: (st.booking && st.booking.booking && st.booking.booking.resourceName)
          ? String(st.booking.booking.resourceName) : "",
        bookingError: st.bookingError || "",
        submitting: !!st.submitting,
        rootMode: this.isPublic() ? "public" : "preview",
        queueQrValue: this.publicOrigin() + "/q/A-042",
        placeCapNote: "Each event carries its own limit and its own remaining places, so the picker never offers more than the event allows \u2014 " + this.eventsRows.map((e) => e.name.split(" \u2014 ")[0] + " allows " + e.maxPer).slice(0, 2).join(", ") + ".",
        placesLine: effectiveTickets + (effectiveTickets === 1 ? " place" : " places") + (!s.showEventPrice ? "" : curEvt.price === "Free" ? " · Free" : " · " + curEvt.price + " each"),
        attendeeRows: effectiveTickets > 1,
        attendeeCount: effectiveTickets - 1,
        serviceHeader: this.ctv("service", "hdr", queue ? "What do you need help with?" : "Select a service"),
        serviceCatalogEmpty: SERVICES.length === 0,
        serviceCatalogEmptyText: "No services are available to book right now.",
        durationHeader: this.ctv("service", "dur", "Select a duration"),
        locationHeader: this.ctv("location", "hdr", queue ? "Choose a service point" : "Select location"),
        dateHeader: this.ctv("datetime", "date", "Select a date"),
        timeHeader: this.ctv("datetime", "time", "Select a time"),
        detailsHeader: this.ctv("details", "hdr", "Enter your details"),
        uploadHeader: this.ctv("details", "upl", "Document upload"),
        uploadHint: this.ctv("details", "uplin", "PNG, JPG, DOC, PDF and Google Docs supported. Max file size 20 MB.")
          + " " + (st.upFiles || []).length + " of " + s.maxAttachments + " added"
          + ((st.upFiles || []).length >= s.maxAttachments ? " \u2014 remove one to add another." : ", one at a time or several at once.")
          + (st.upError ? " " + st.upError : ""),
        uploadAccept: ".png,.jpg,.jpeg,.doc,.docx,.pdf,.gdoc,application/vnd.google-apps.document",
        uploadFull: (st.upFiles || []).length >= s.maxAttachments,
        consentHeader: this.ctv("details", "chdr", "Data protection policy"),
        consentText: this.ctv("details", "ctext", "I consent to my data being used for the purposes of this booking."),
        confirmMsg: st.cancelled
          ? ((events || landing) ? this.ctv("confirmation", "cnclmsg", "This registration is cancelled") : this.ctv("cancel", "msg", "This appointment is cancelled"))
          : (events || landing)
            ? this.ctv("confirmation", "msg", (st.booking?.registration?.status === "waitlisted")
                ? `Thanks${st.first ? " " + st.first : ""}! You're on the waitlist`
                : `Thanks${st.first ? " " + st.first : ""}! You're registered`)
            : this.ctv("confirmation", "msg", "Thanks Jordan! Your appointment is confirmed"),
        flagHeader: st.cancelled
          ? ((events || landing) ? this.ctv("confirmation", "cnclhdr", "Registration cancelled") : this.ctv("cancel", "flaghdr", "Appointment cancelled"))
          : (events || landing)
            ? this.ctv("confirmation", "flaghdr", (st.booking?.registration?.status === "waitlisted") ? "You're on the waitlist" : "Registration confirmed")
            : this.ctv("confirmation", "flaghdr", "Appointment confirmed"),
        flagText: st.cancelled
          ? ((events || landing) ? this.ctv("confirmation", "cncltxt", "Your place has been released.") : this.ctv("cancel", "flagtxt", "A cancellation e-mail is on its way. Nothing else is needed from you."))
          : (events || landing)
            ? this.ctv("confirmation", "flagtxt", (st.booking?.registration?.status === "waitlisted") ? "You'll be notified if a place opens." : "Your ticket is below.")
            : this.ctv("confirmation", "flagtxt", "You will receive a confirmation e-mail shortly and may close this window"),
        services: SERVICES.map((x) => ({ name: x.name, desc: x.desc, meta: x.dur === "multi" ? "2 durations" : x.dur, leading: this.tile("IconCalendar", "neutral") })),
        visibleServices,
        serviceCols: st.device === "mobile" ? "minmax(0, 1fr)" : "repeat(auto-fit, minmax(230px, 1fr))",
        serviceGrid: s.serviceDisplay === "grid",
        serviceRows: s.serviceDisplay !== "grid",
        categories: (appointment ? serviceCategoryOptions(SERVICES) : [{ id: "all", label: "All" }, { id: "academic", label: "Academic" }, { id: "support", label: "Student support" }, { id: "admissions", label: "Admissions" }]).map((c) => ({
          label: c.label, selected: appointment ? serviceFilterId(st.cat) === c.id : st.cat === c.id, onClick: () => appointment ? this.actions.setServiceCategory(c.id) : this.setState({ cat: c.id }),
        })),
        showDurations: cur.dur === "multi",
        durations: DURATIONS.map((d) => ({ label: d.label, sub: s.showPrice ? d.sub : "", selected: st.dur === d.id, onSelect: () => this.setState({ dur: d.id }) })),
        locQuery: st.locQuery,
        suggestions: locSuggestions,
        hasSuggestions: locSuggestions.length > 0,
        locating: st.geoState === "locating",
        geoLabel: st.geoState === "locating" ? "Finding you\u2026" : "Use my location",
        geoDenied: st.geoState === "denied" || st.geoState === "unsupported",
        geoNote: st.geoState === "unsupported"
          ? "This browser cannot share a location. Type a town or ZIP code instead."
          : "Location access was blocked. Type a town or ZIP code instead.",
        visibleLocations, deskList: queue, branchList: !queue,
        showLoadMore: !queue && s.loadMore && locCount < locSource.length,
        loadMoreLabel: "Load more locations (" + (locSource.length - locCount) + " more)",
        showMapNow: !queue && s.showMap,
        pinName: curLoc.name, pinMeta: [curLoc.dist, curLoc.next ? "next available " + curLoc.next : ""].filter(Boolean).join(" \u00b7 "),
        summaryHeader: this.ctv("progress", "sum", "Your appointment"),
        bodyDir: st.device === "mobile" ? "column-reverse" : "row",
        asideW: st.device === "mobile" ? "100%" : "268px",
        asideMax: st.device === "mobile" ? "100%" : "min(100%, 268px)",
        dtDir: st.device !== "mobile" && s.calendarLayout === "month" ? "row" : "column",
        dtCalFlex: st.device !== "mobile" && s.calendarLayout === "month" ? "0 1 316px" : "1 1 100%",
        showSummary: !queue && !events && !landing && !kiosk && stepIndex > 0 && st.previewStep !== "confirmation",
        sumWhen: this.fmtDay(st.day, true).replace(/^[A-Za-z]{3}, /, "") + (st.slot ? ", " + st.slot.toLowerCase().replace(" ", "") : ""),
        sumEnds: st.slot ? "Ends at " + this.endTime(st.slot, this.durMinutes()) : "No time picked yet",
        sumShowWhen: stepList.slice(0, stepIndex).indexOf("datetime") > -1,
        sumGoWhen: () => this.summaryGo("datetime"),
        canChange: true,
        reconfirmNote: st.reconfirmFrom
          ? "You changed your " + st.reconfirmFrom + ", so the times have been re-checked — pick a time again."
          : "",
        showReconfirm: !!st.reconfirmFrom,
        summaryRows: (() => {
          const done = stepList.slice(0, stepIndex);
          const rows = [];
          const go = (id) => () => this.summaryGo(id);
          if (done.indexOf("location") > -1) {
            rows.push({ title: curLoc.name, sub: curLoc.addr, onChange: go("location") });
          }
          if (done.indexOf("service") > -1) {
            const dur = cur.dur === "multi"
              ? (DURATIONS.filter((d) => d.id === st.dur)[0] || DURATIONS[0] || EMPTY_DURATION).label
              : cur.dur;
            rows.push({ title: cur.name, sub: s.showDuration ? dur : "", onChange: go("service") });
          }
          if (done.indexOf("datetime") > -1 && s.staffSelect) {
            rows.push({ title: "Adviser", sub: curStaff, onChange: go("datetime") });
          }
          if (done.indexOf("location") > -1) {
            rows.push({ title: "Method", sub: curLoc.dist === "—" ? "Video appointment" : "In-person", onChange: go("location") });
          }
          return rows;
        })(),
        showContinue: ["service", "location", "datetime"].indexOf(st.previewStep) > -1 && !(st.previewStep === "location" && !queue && !events && s.firstStep === "map"),
        continueBlocked: this.continueBlocked(),
        continueBg: this.continueBlocked() ? "var(--jrni-color-neutral-4)" : brand,
        continueCursor: this.continueBlocked() ? "not-allowed" : "pointer",
        showCustomerBack: stepIndex > 0,
        continueLabel: this.ctv("progress", "cta", "Continue"),
        publicUrl: this.publicLink(),
        pxWidth: s.embedWidthUnit === "px",
        copyLabel: st.copiedEmbed ? "Copied" : "Copy code",
        embedCode: this.embedSnippet(s, this.embedTitle()),
        autoResize: s.embedResize === "auto", fixedHeight: s.embedResize !== "auto",
        resizeHint: s.embedResize === "auto"
          ? "The frame grows and shrinks with the step. Adds one script tag to your page."
          : "One height for every step, so short steps leave dead space.",
        mapHeight: (s.mapHeight || 190) + "px",
        mapHeightLabel: (s.mapHeight || 190) + " px",
        resizingMap: !!st.resizingMap,
        selectedAddr: curLoc.addr + " · " + curLoc.dist,
        selectedNext: curLoc.next, showNextLine: s.nextAvailable,
        selectedHours: curLoc.hours + " · " + curLoc.phone,
        hasVirtual: LOCATIONS.some((x) => x.dist === "—"),
        virtualLabel: "Rather meet online? Book a video appointment instead.",
        onVirtual: () => this.setState({ loc: "l4" }),
        mapPins: LOCATIONS.filter((x) => x.dist !== "—").map((x, i) => {
          const on = st.loc === x.id;
          return {
            name: x.name, short: x.name.split(" — ")[0],
            top: [46, 24, 70, 58, 34, 80, 18, 62][i % 8] + "%",
            left: [34, 66, 22, 56, 84, 46, 40, 76][i % 8] + "%",
            bg: on ? brand : "var(--jrni-color-base-white)",
            fg: on ? "#FFFFFF" : "var(--jrni-color-text-strong)",
            border: on ? brand : "var(--jrni-color-surface-border)",
            onSelect: () => this.setState({ loc: x.id }),
          };
        }),
        mapLocations: LOCATIONS.filter((x) => x.dist !== "—").map((x) => ({
          id: x.id, name: x.name, latitude: x.latitude, longitude: x.longitude,
          selected: st.loc === x.id, onSelect: () => this.setState({ loc: x.id }),
        })),
        days: dayStripDays, day: st.day, slot: st.slot,
        stripPrevDisabled: (st.stripWeek || 0) === 0,
        stripNextDisabled: stripOffset + 7 > (this.bookWindow() || 60),
        stripLabel: this.fmtDay(dayStripDays[0].value, false) + " \u2013 " + this.fmtDay(dayStripDays[dayStripDays.length - 1].value, false),
        calCells, calTitle: MONTHS[cm.m] + " " + cm.y,
        calPrevDisabled: cm.y < this.today().getFullYear() || (cm.y === this.today().getFullYear() && cm.m <= this.today().getMonth()),
        custTz: st.custTz || this.ianaFor(this.zoneFor(st.loc)),
        tzLabelNode: (() => {
          const z = st.custTz || this.ianaFor(this.zoneFor(st.loc));
          let off = "";
          try {
            const parts = new Intl.DateTimeFormat("en-US", { timeZone: z, timeZoneName: "longOffset" }).formatToParts(new Date());
            const found = parts.filter((x) => x.type === "timeZoneName")[0];
            off = found ? found.value : "";
          } catch (err) {}
          return React.createElement("span", { style: { display: "flex", gap: "8px", alignItems: "baseline", flexWrap: "wrap" } }, [
            React.createElement("strong", { key: "l", style: { fontWeight: 700 } }, "Time zone"),
            React.createElement("span", { key: "v", style: { color: "var(--jrni-color-text-soft)", fontSize: "13px" } }, (off ? "(" + off + ") " : "") + z),
          ]);
        })(),
        selectedDayLabel: this.fmtDay(st.day, true),
        noSlotsToday: this.slotCount(this.fromIso(st.day), this.bookWindow()) === 0,
        // 2026-09-08 (Nick): `pickedSlots` was the fabricated list that `daySlots`
        // replaced above; this one reference survived and threw on every render
        // (ReferenceError). The flat list is the same real day's slots, ungrouped.
        allSlots: daySlots.map((slot) => ({ value: slot.time, label: slot.time })),
        slotGroups: groupDefs.map((g) => ({ label: g[0], slots: groupOf(g[0]) })).filter((g) => g.slots.length > 0),
        hasSlots: dayCount > 0,
        noSlots: dayCount === 0,
        groupedSlots: dayCount > 0 && s.slotGrouping === "partofday",
        flatSlots: dayCount > 0 && s.slotGrouping === "flat",
        noSlotsTitle: "No times left on " + this.fmtDay(st.day),
        noSlotsBody: "Pick another date — the calendar greys out days that are fully booked or outside the booking window.",
        dateValue: dayLabel,
        staff: STAFF.map((p) => ({ name: p.name, leading: null, selected: st.staff === p.id, onClick: () => this.setState({ staff: p.id }) })),
        bookingLeading: this.tile("IconCalendar", "primary"),
        bookingTitle: cur.name, bookingSub: dayLabel + " · " + st.slot + " · " + curLoc.name,
        emailLabel: "E-mail" + (s.emailOptional ? " (optional)" : ""), emailRequired: !s.emailOptional,
        // Events/landing public registration always requires email at the API level, so the
        // authoring controls for it are locked rather than left editable-but-ignored.
        emailApiRequired: this.isEvents() || this.isLanding(),
        emailSettingTitle: (this.isEvents() || this.isLanding()) ? "Email address (required)" : "Email address",
        emailSettingDescription: (this.isEvents() || this.isLanding())
          ? "Public registration always submits an email address, so it cannot be turned off or made optional."
          : undefined,
        phoneLabel: "Phone" + (s.phoneOptional ? " (optional)" : ""), phoneRequired: !s.phoneOptional,
        dialIso: ph.country.iso,
        countryError: st.countryMax ? "Ten is the maximum \u2014 remove one before adding another." : undefined,
        phoneValue: s.readOnly ? "(512) 555-0142" : st.phone,
        phoneError: phoneBad
          ? (ph.len === 0 ? "A phone number is required." : "That is not a valid " + ph.country.name + " number \u2014 " + (ph.country.min === ph.country.max ? ph.country.min + " digits" : ph.country.min + "\u2013" + ph.country.max + " digits") + " expected.")
          : undefined,
        phoneHint: phoneBad ? undefined : "Start with + and a country code to use a number outside the US.",
        phoneState: s.readOnly ? "readonly" : phoneBad ? "error" : "default",
        lockState: s.readOnly ? "readonly" : "default",
        signedIn: !!s.readOnly,
        signedInLabel: "Signed in as Jordan Alvarez \u00b7 j.alvarez@example.com. These details come from your account and cannot be changed here.",
        addressLabel: "Address" + (s.addressOptional ? " (optional)" : ""),
        addressRequired: !s.addressOptional,
        addrValue: s.readOnly ? "48 Warwick St, Lakeview, TX 78701, USA" : st.addr,
        addrState: s.readOnly ? "readonly" : "default",
        addrMatches: addrMatches,
        hasAddrMatches: !s.readOnly && addrMatches.length > 0,
        // Booking questions for the chosen service (appointment), or the
        // event's own configured registration questions (events/landing) —
        // `activeQuestions()` picks the real source for whichever journey
        // this is; either way, `false` is a legitimate answer to a yes/no
        // question and must not be treated as "no answer".
        bookingQuestions: (events || landing ? this.activeQuestions() : this.serviceQuestions()).map((q) => {
          const v = (st.answers || {})[q.id];
          // `false` is a legitimate yes/no answer for events/landing — never
          // "no answer" the way `answerEmpty` treats it for the appointment
          // shape this helper otherwise still governs unchanged.
          const missing = q.required && st.answersTouched && ((events || landing) ? v === undefined : this.answerEmpty(v));
          const checks = Array.isArray(v) ? v : [];
          const opts = q.options || [];
          return {
            id: q.id, label: q.label, help: q.help || undefined, required: q.required, error: missing ? "This answer is required." : undefined,
            isShort: q.type === "short" || q.type === "long", isLong: q.type === "long", isSelect: q.type === "select" || q.type === "radio", isYesNo: q.type === "yesno", isChecks: q.type === "checks" || q.type === "multi", isStepper: q.type === "stepper", isFile: q.type === "file",
            value: v === undefined || v === null ? "" : (Array.isArray(v) ? "" : String(v)),
            options: opts.map((o) => ({ value: o.id, label: o.label })),
            checkRows: opts.map((o) => ({ id: o.id, label: o.label, checked: checks.indexOf(o.id) > -1, onToggle: () => this.actions.setAnswer(q.id, checks.indexOf(o.id) > -1 ? checks.filter((x) => x !== o.id) : checks.concat([o.id])) })),
            yesNoValue: v === true ? "yes" : v === false ? "no" : "",
            onChange: (e) => this.actions.setAnswer(q.id, e),
            onSelect: (val) => this.actions.setAnswer(q.id, val),
            onYes: () => this.actions.setAnswer(q.id, true), onNo: () => this.actions.setAnswer(q.id, false),
            groupName: q.groupName,
          };
        }),
        hasBookingQuestions: (events || landing ? this.activeQuestions() : this.serviceQuestions()).length > 0,
        bookingQuestionsHeading: (events || landing
          ? "A few questions before you register"
          : (this.serviceQuestions()[0] && this.serviceQuestions()[0].groupName) || "A few questions before your visit"),
        // Events/landing ALWAYS require first/last/email regardless of the
        // authored optional toggles (the current JSON registration contract
        // enforces this independent of any Designer setting), and a
        // read-only PREVIEW of that journey must never substitute the
        // appointment fixture's "Jordan Alvarez" identity for it.
        // Required first/last/email must stay EDITABLE for events/landing
        // regardless of the authored `readOnly` toggle — that toggle has no
        // supported meaning for a mandatory registration field; capability
        // belongs disabled in authoring, not on this required visitor input.
        firstValue: (events || landing) ? st.first : (s.readOnly ? "Jordan" : st.first),
        lastValue: (events || landing) ? st.last : (s.readOnly ? "Alvarez" : st.last),
        firstError: firstBad ? nameMsg(st.first) : undefined,
        lastError: lastBad ? nameMsg(st.last) : undefined,
        firstState: (events || landing) ? (firstBad ? "error" : "default") : (s.readOnly ? "readonly" : firstBad ? "error" : "default"),
        lastState: (events || landing) ? (lastBad ? "error" : "default") : (s.readOnly ? "readonly" : lastBad ? "error" : "default"),
        emailValue: (events || landing) ? st.email : (s.readOnly ? "j.alvarez@example.com" : st.email),
        emailError: emailBad ? (st.email.trim() ? "Enter a valid e-mail address, like you@example.com." : "An e-mail address is required.") : undefined,
        emailState: (events || landing) ? (emailBad ? "error" : "default") : (s.readOnly ? "readonly" : emailBad ? "error" : "default"),
        notifEmail: s.notifDefault !== "sms", notifSms: s.notifDefault !== "email",
        cancelled: !!st.cancelled, notCancelled: !st.cancelled,
        regActionsVis: st.cancelled ? "hidden" : "visible",
        cancelRegLabel: this.ctv("confirmation", "cnclreg", "Cancel registration"),
        cancelRegKeepLabel: this.ctv("confirmation", "keepreg", "Keep my place"),
        cancelRegTitle: this.ctv("confirmation", "cnclttl", "Cancel this registration?"),
        cancelRegConsequence: "Your place is released to anyone on the waiting list, and the event may fill before you can register again.",
        backToEventsLabel: this.ctv("confirmation", "backev", "Back to the events list"),
        notQueue: !queue,
        notifSectionTitle: queue ? "Notifications" : "Notifications and attendees",
        cancelAsking: !!st.cancelAsking,
        statusBg: st.cancelled ? "var(--jrni-color-semantic-red-2)" : "var(--jrni-color-semantic-green-2)",
        statusFg: st.cancelled ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-semantic-green-1)",
        statusGlyph: st.cancelled ? "\u00d7" : "\u2713",
        flagTone: st.cancelled ? "danger" : "success",
        strike: "none",
        detailFg: st.cancelled ? "var(--jrni-color-text-soft)" : "var(--jrni-color-text-body)",
        againLabel: this.ctv("cancel", "again", "Book a new appointment"),
        cancelTitle: this.ctv("cancel", "dlgttl", "Cancel this appointment?"),
        cancelConsequence: (() => {
          const late = this.hoursUntilAppt() < CANCEL_CUTOFF_HRS;
          if (!late) return "You are outside the " + CANCEL_CUTOFF_HRS + "-hour window, so there is nothing to pay.";
          const money = s.takePayment
            ? (s.refundMode === "full" ? " Your payment is refunded in full." : s.refundMode === "none" ? " Your payment is not refunded." : " Your payment is refunded minus the " + CANCEL_FEE + " fee.")
            : "";
          return "This is inside the " + CANCEL_CUTOFF_HRS + "-hour window, so a " + CANCEL_FEE + " cancellation fee applies." + money;
        })(),
        cancelConfirmLabel: this.ctv("cancel", "confirm", "Cancel appointment"),
        cancelKeepLabel: this.ctv("cancel", "keep", "Keep appointment"),
        calMenuOpen: !!st.calMenu,
        calTargets: [
          { key: "outlook", label: "Outlook", meta: "Opens the desktop app with an .ics file" },
          { key: "owa", label: "Outlook on the web", meta: "Adds it to outlook.office.com" },
          { key: "google", label: "Google Calendar", meta: "Opens calendar.google.com" },
          { key: "ics", label: "Download .ics file", meta: "Apple Calendar and everything else" },
        ].map((x) => ({ key: x.key, label: x.label, meta: x.meta, onPick: () => this.actions.pickCal(x.key) })),
        files: st.upFiles || [], consent: st.consent,
        confirmWhen: this.fmtDay(st.day, true) + (st.slot ? " \u00b7 " + st.slot : ""),
        confirmDuration: cur.dur === "multi" ? (DURATIONS.filter((d) => d.id === st.dur)[0] || DURATIONS[0] || EMPTY_DURATION).label : cur.dur,
        confirmService: cur.name, confirmLocation: curLoc.name, confirmAddress: curLoc.addr,
        staffName: curStaff,
      },
    };
  }


  /**
   * The Preview dialog — CD harness only (see `previewInDialog`).
   *
   * The frame holds the SAME component the deployed page mounts: `Designer` in
   * `mode="public"` against this journey's slug, so it reads the published
   * settings, the public catalogue and the same availability endpoint. Nothing
   * about the render is a stand-in — only the window it sits in is, and the
   * device toggle is here rather than in the page because a real browser gets
   * its width from the browser.
   */
  previewDialog() {
    const slug = this.bookingSlug();
    const url = this.publicLink();
    const device = this.state.pvDevice;
    const w = resolveDeviceWidths()[device];
    const close = this.actions.closePreviewDialog;
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Customer page preview"
        onClick={close}
        style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(20,22,30,0.68)", backdropFilter: "blur(3px)", display: "flex", flexDirection: "column", padding: "18px clamp(10px, 4vw, 48px)", fontFamily: "var(--jrni-font-family-sans)" }}
      >
        <div onClick={(e) => e.stopPropagation()} style={{ margin: "0 auto", width: "100%", maxWidth: "1180px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <span style={{ color: "#fff", fontSize: "13.5px", fontWeight: 700 }}>Customer page</span>
            {url ? (
              <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "12.5px" }}>{url}</span>
            ) : slug ? (
              <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "12.5px" }}>/{slug} · the server has not returned a public address yet</span>
            ) : (
              <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "12.5px" }}>Not published yet</span>
            )}
            <div style={{ flex: "1 1 auto" }}></div>
            <DevicePreviewToggle value={device} onChange={this.actions.setPvDevice} size="sm" ariaLabel="Preview width" />
            {url ? (
              <Button variant="ghost" size="sm" iconRight={this.icon("IconExternal", 13)} onClick={this.actions.openPreviewTab}>Open in a tab</Button>
            ) : null}
            <Button hierarchy="primary" size="sm" onClick={close}>Close</Button>
          </div>
          <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", justifyContent: "center", alignItems: "flex-start", overflow: "auto" }}>
            <div style={{ width: "100%", maxWidth: device === "desktop" ? "100%" : w + "px", minHeight: "100%", background: "var(--jrni-color-surface-canvas)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "10px", overflow: "hidden", boxShadow: "0 18px 48px rgba(0,0,0,0.32)" }}>
              {slug ? (
                <ErrorBoundary scope="alljourneys:preview-dialog" label="Customer page" variant="pane" resetKey={slug + ":" + device}>
                  <Designer mode="public" slug={slug} tenant="" tenantName={this.props.tenantName} brandColor={this.props.brandColor} device={device} />
                </ErrorBoundary>
              ) : (
                <div style={{ padding: "48px 24px", textAlign: "center", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>
                  This journey has no slug yet. Publish it and the customer page will load here.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.journey === "onestop") {
      return (
        <>
          <style>{APD_CSS}</style>
          <OneStopTab tenant={this.props.tenant} tenantName={this.props.tenantName} brandColor={this.props.brandColor} publicHost={this.props.publicHost} journeyOptions={this.journeyOptions()} onSwitchJourney={(j) => this.actions.setJourney(j)} />
        </>
      );
    }
    const publicStillLoading = this.isPublic() && this.state.previewStep !== "linkError" && (
      this.state.journey === "events" ? !["ready", "error", "empty"].includes(this.state.evtListState)
        : this.state.journey === "landing" ? !["ready", "error"].includes(this.state.evtDetailState)
        : !this.state.apptJourney
    );
    if (publicStillLoading) {
      return (
        <>
          <style>{APD_CSS}</style>
          <div role="status" aria-live="polite" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--jrni-color-text-soft)", fontFamily: "var(--jrni-font-family-sans)" }}>Loading…</div>
        </>
      );
    }
    if (this.isPublic() && this.state.journey === "events" && this.state.evtListState === "error") {
      return (
        <>
          <style>{APD_CSS}</style>
          <div role="alert" style={{ minHeight: "60vh", display: "flex", flexDirection: "column", gap: 12, alignItems: "center", justifyContent: "center", color: "var(--jrni-color-text-soft)", fontFamily: "var(--jrni-font-family-sans)" }}>
            <span>Events could not be loaded{this.state.evtListError ? `: ${this.state.evtListError}` : "."}</span>
            <button type="button" onClick={() => this.refreshPublicEventsList(this.state.evtPage || 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid currentColor", background: "transparent", cursor: "pointer" }}>Retry</button>
          </div>
        </>
      );
    }
    if (this.isPublic() && this.state.journey === "events" && this.state.evtScopeUnsupported) {
      return (
        <>
          <style>{APD_CSS}</style>
          <div role="alert" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--jrni-color-text-soft)", fontFamily: "var(--jrni-font-family-sans)", textAlign: "center", padding: 24 }}>
            This event list's configuration isn't supported by the current public catalogue read yet.
          </div>
        </>
      );
    }
    const V: any = this.renderVals();
    return (
      <>
        <style>{APD_CSS}</style>
        {this.state.pvOpen ? this.previewDialog() : null}
        <div style={{ height: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--jrni-color-surface-canvas)", fontFamily: "var(--jrni-font-family-sans)", color: "var(--jrni-color-text-strong)" }}>
          {V.pv.chrome ? (<>
            <header style={{ flex: "0 0 auto", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "14px", rowGap: "8px", padding: "10px 16px", background: "var(--jrni-color-surface-card)", borderBottom: "1px solid var(--jrni-color-surface-border)", boxShadow: "var(--jrni-shadow-chrome)", zIndex: "5" }}>
              <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "10px", whiteSpace: "nowrap" }}>
                <SegmentedControl options={V.opts.journeys} value={V.journey} onChange={V.actions.setJourney} size="sm" aria-label="Journey type" />
                <span style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: "6px", padding: "3px 9px", borderRadius: "999px", fontSize: "12px", fontWeight: "600", border: `1px solid ${V.pv.statusEdge}`, background: V.pv.statusFill, color: V.pv.statusInk }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "999px", background: V.pv.statusInk }}></span>{V.pv.statusLabel}
                </span>
              </div>
              <div style={{ flex: "1 1 auto" }}></div>
              <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap" }}>
                <Button variant="ghost" size="sm" iconRight={V.icons.external} onClick={V.actions.openPreview}>Preview
                </Button>
                {V.pv.showSavePublish ? (
                  <Button hierarchy="primary" size="sm" onClick={V.actions.publish} disabled={V.pv.saveDisabled}>{V.pv.savePublishLabel}
                  </Button>
                ) : null}
              </div>
              {V.pv.hasSaveNote ? (<>
                <div style={{ flex: "0 0 100%", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "999px", background: V.pv.saveDotBg }}></span>
                  <span>{V.pv.saveNote}</span>
                  {V.pv.published && V.pv.liveUrl ? (<>
                    <Button variant="link" size="sm" onClick={V.actions.openPreview}>{V.pv.liveUrl}
                    </Button>
                  </>) : null}
                </div>
              </>) : null}
              {V.pv.hasSaveNotices ? (<>
                <div style={{ flex: "0 0 100%", display: "flex", flexDirection: "column", gap: "4px", fontSize: "12.5px", color: "var(--jrni-color-text-soft)", paddingLeft: "14px" }}>
                  {V.pv.saveNotices.map((n) => (<div key={n}>{n}</div>))}
                </div>
              </>) : null}
              {V.pv.hasRefusals ? (<>
                <div style={{ flex: "0 0 100%", display: "flex", flexDirection: "column", gap: "4px", fontSize: "12.5px", color: "var(--jrni-color-semantic-red-1)", paddingLeft: "14px" }}>
                  {V.pv.refusedFields.map((f) => (
                    <div key={f.path}>
                      <strong style={{ fontWeight: 600 }}>{f.label}</strong>{" \u2014 "}{f.message}
                    </div>
                  ))}
                </div>
              </>) : null}
            </header>
          </>) : null}
          <div className="apd-scroll" style={{ flex: "1 1 auto", display: "flex", minHeight: "0", overflowX: "auto" }}>
            {V.pv.chrome ? (<>
              <nav className="apd-scroll" style={{ flex: "0 0 236px", width: "236px", minWidth: "236px", boxSizing: "border-box", overflowY: "auto", overflowX: "hidden", background: "var(--jrni-color-surface-card)", borderRight: "1px solid var(--jrni-color-surface-border)", padding: "16px 13px 20px", display: "flex", flexDirection: "column", gap: "6px" }}>
                <div style={{ padding: "0 13px 2px" }}>
                  <SectionLabel>Setup
                  </SectionLabel>
                </div>
                {/* 210 = the rail's 236px minus its 13px side padding: the setup rows must end on
                    the same right edge as the journey-step rows below, which are width:100%. */}
                <EditorSectionNav items={V.setupItems} value={V.setupValue} onChange={V.actions.goSection} width={210} />
                <div style={{ padding: "14px 13px 2px" }}>
                  <SectionLabel>Customer journey
                  </SectionLabel>
                </div>
                <div role="tablist" aria-label="Journey steps" style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                  {(V.journeyItems || []).map((j, __i0) => (<React.Fragment key={j.key ?? j.id ?? __i0}>
                    <button type="button" role="tab" aria-selected={j.selected} onClick={j.onClick} style={{ display: "flex", alignItems: "center", gap: "6px", width: "100%", boxSizing: "border-box", textAlign: "left", padding: "8px 12px", borderRadius: "var(--jrni-radius-control)", border: "1px solid transparent", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", background: j.bg, borderColor: j.border, boxShadow: j.shadow, color: j.fg, opacity: j.opacity }}>
                      <span style={{ flex: "0 0 22px", height: "22px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", background: j.numBg, color: j.numFg }}>{j.num}</span>
                      <span style={{ flex: "1 1 auto", fontWeight: "500", minWidth: "0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.label}</span>
                      {j.hidden ? (<>
                        <span style={{ flex: "0 0 auto", fontSize: "11px", letterSpacing: "0.02em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Off</span>
                      </>) : null}
                    </button>
                  </React.Fragment>))}
                </div>
                <div style={{ flex: "1 1 auto", minHeight: "16px" }}></div>
              </nav>
            </>) : null}
            {V.pv.formPane ? (<>
              <div className="apd-scroll" style={{ flex: "1 1 520px", minWidth: "340px", overflow: "auto", padding: "24px 28px 56px", background: "var(--jrni-color-surface-canvas)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", marginBottom: "20px", maxWidth: "880px" }}>
                  <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                    <h1 style={{ margin: "0", fontSize: "22px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.panel.title}</h1>
                    <p style={{ margin: "6px 0 0", fontSize: "14px", lineHeight: "1.5", color: "var(--jrni-color-text-body)", maxWidth: "62ch", textWrap: "pretty" }}>{V.panel.desc}</p>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: "880px" }}>
                  {V.is.general ? (<>
                    {V.is.kiosk ? (<>
                      <Section title="Where this kiosk is" description="A kiosk is fixed to one wall, so the location is a property of the unit rather than something the visitor picks. Everything it opens inherits it: no location step, and only this campus's services, slots and events.">
                        <Field label="Installed at">
                          <Dropdown options={V.opts.kioskLocations} value={V.s.kioskLocation} onChange={V.set.kioskLocation} style={{  }} />
                        </Field>
                      </Section>
                      <Section title="Between visitors" description="The entry screen is the resting state — there is no attract loop — so anything personal has to be cleared on a timer.">
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                          <Field label="Clear the screen after">
                            <Input type="number" value={V.s.kioskResetAfter} onChange={V.set.kioskResetAfter} trailing="sec" style={{  }} />
                          </Field>
                          <Field label="Greeting">
                            <SegmentedControl options={V.opts.kioskGreeting} value={V.s.kioskGreeting} onChange={V.set.kioskGreeting} size="sm" />
                          </Field>
                        </div>
                      </Section>
                      <Section title="Everyone who walks up to it" description="A shared screen in a public lobby has no settings of its own to inherit.">
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <ToggleRow title="Larger text button" description="Steps every size up one notch. Wall-mounted screens are read standing and from further away than a phone." checked={V.s.kioskBigText} onChange={V.set.kioskBigText} />
                          <ToggleRow title="Show the university header" checked={V.s.showHeader} onChange={V.set.showHeader} />
                        </div>
                      </Section>
                    </>) : null}
                    {V.is.appt ? (<>
                      {V.is.pinnedFromKiosk ? (<>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 16px", border: "1px solid var(--jrni-color-primary-3)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-primary-4)" }}>
                          <span style={{ flex: "0 0 auto", color: "var(--jrni-color-primary-1)", paddingTop: "1px" }}>{V.icons.info}</span>
                          <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "13px", lineHeight: "1.55", color: "var(--jrni-color-text-strong)", textWrap: "pretty" }}>You opened this from the kiosk at 
                            <b>{V.pv.pinnedLocName}</b>. A kiosk is fixed to one place, so the location is already decided — the {V.pv.pinnedStepWord} step is dropped and every service, slot and event is limited to that campus. The settings below still apply to web and mobile visitors.
                          </span>
                        </div>
                      </>) : null}
                      <Section title="Which step comes first">
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <SelectableTile showIndicator={false} title="Service" description="Recommended — lets you skip the location step for virtual appointments and show next available slots per location." selected={V.is.serviceFirst} onSelect={V.actions.serviceFirst} full={true} />
                          <SelectableTile showIndicator={false} disabled trailing={<Pill tone="neutral">Coming soon</Pill>} title="Location" description="Recommended when similar in-person services run across many locations." selected={V.is.locationFirst} onSelect={V.actions.locationFirst} full={true} />
                          <SelectableTile showIndicator={false} disabled trailing={<Pill tone="neutral">Coming soon</Pill>} title="Map only" description="Opens on a full map with a pin per location — no list. Best when people choose by where they are, not by name." selected={V.is.mapFirst} onSelect={V.actions.mapFirst} full={true} />
                        </div>
                      </Section>
                      <Section title="Skipping steps">
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <Checkbox checked={V.s.skipService} onChange={V.set.skipService} label="Skip the service step if only one service is available" supportingText={V.hints.skipService} />
                          <Checkbox checked={V.s.skipLocation} onChange={V.set.skipLocation} label="Skip the location step if only one location is available" supportingText={V.hints.skipLocation} />
                        </div>
                      </Section>
                    </>) : null}
                    {V.is.events ? (<>
                      <Section title="What the list includes" description="The first page is a list of everything on sale.">
                        <div style={{ paddingBottom: "12px" }}>
                          <Field label="Sort events by">
                            <Dropdown options={V.opts.sortOrder} value={V.s.sortOrder} onChange={V.set.sortOrder} style={{  }} />
                          </Field>
                        </div>
                        <ToggleRow title="Keep past events on the list" description="Greyed out, no registration. Useful for series that repeat." checked={V.s.showPastEvents} onChange={V.actions.setShowPastEvents} />
                      </Section>
                      <Section title="Skipping steps">
                        <ToggleRow title="Show event details step" description="Show Events, Event page, Your info and Confirmation. Turn off to skip Event page when the selected event has one bookable date/time. Direct event links still show Event page." checked={!V.s.skipSingleEventSlot} onChange={V.actions.setShowEventDetailsStep} />
                      </Section>
                    </>) : null}
                    {V.is.queue ? (<>
                      {/* The author's EXPLICIT pick from the tenant's real
                          queues (`listQueues`, followed across pages). Nothing
                          is attached automatically and nothing is written on
                          load — the selection reaches the server on Save, like
                          every other setting on this page. The public join is
                          bound to exactly these queues: the join handler
                          refuses a queue outside the published `queueIds`. */}
                      <Section title="Which lines this page joins" description="Pick the queues a visitor can join here. A page with none cannot be published as a walk-in page.">
                        {V.pv.walkInQueueOptions.length ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {V.pv.walkInQueueOptions.map((q) => (
                              <Checkbox
                                key={q.id}
                                checked={q.selected}
                                onChange={() => V.actions.toggleWalkInQueue(q.id)}
                                label={q.name}
                                supportingText={q.meta}
                              />
                            ))}
                          </div>
                        </>) : (<>
                          <InlineEmptyState
                            title="No queues on this workspace yet"
                            description="Create a queue in Queues first; this page can only offer lines that already exist."
                          />
                        </>)}
                      </Section>
                    </>) : null}
                    {V.is.landingJourney ? (<>
                      <Section title="Template">
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                          <SelectableTile showIndicator={false} title="Banner and sidebar" description="Slim colour banner, details left, a sticky registration card right." selected={V.is.tplBanner} onSelect={V.actions.tplBanner} full={true} />
                          <SelectableTile showIndicator={false} title="Hero cover" description="Full-bleed image with the title over it, content below." selected={V.is.tplHero} onSelect={V.actions.tplHero} full={true} />
                          <SelectableTile showIndicator={false} title="Split panel" description="Image on one half, title and registration on the other." selected={V.is.tplSplit} onSelect={V.actions.tplSplit} full={true} />
                          <SelectableTile showIndicator={false} title="Long-form" description="No hero. Title, then every block stacked full width." selected={V.is.tplLong} onSelect={V.actions.tplLong} full={true} />
                        </div>
                      </Section>
                      <Section title="Hero">
                        {V.pv.hasHero ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                            <Field label="Height">
                              <SegmentedControl options={V.opts.heroHeight} value={V.s.heroHeight} onChange={V.actions.setHeroHeight} />
                            </Field>
                            <Field label="Treatment">
                              <SegmentedControl options={V.opts.heroTreatment} value={V.s.heroTreatment} onChange={V.actions.setHeroTreatment} />
                            </Field>
                            <Field label="Cover image for this event">
                              <ImageDropField value={V.s.coverImage} onChange={V.actions.setCoverImage} aspect={2} maxWidth={320} placeholder="Drop a cover image, or use a URL" />
                            </Field>
                          </div>
                        </>) : null}
                        {V.pv.noHero ? (<>
                          <Banner tone="neutral" variant="explainer" size="compact" title="This template has no hero">{V.pv.noHeroNote}
                          </Banner>
                        </>) : null}
                      </Section>
                      <Section title="Registration bar">
                        <ToggleRow title="Sticky registration bar" description="Follows the scroll with the price and a Register button, so the call to action is never off screen." checked={V.s.stickyBar} onChange={V.actions.setStickyBar} />
                      </Section>
                    </>) : null}
                    <Section title="Header and footer">
                      <ToggleRow title="Show custom header" description="Logo and title bar above the journey." checked={V.s.showHeader} onChange={V.set.showHeader} />
                      <ToggleRow title="Show custom footer" description="Contact links and legal text below the journey." checked={V.s.showFooter} onChange={V.set.showFooter} />
                    </Section>
                    {V.is.appt ? (<>
                      <Section title="Staff">
                        <ToggleRow title="Let customers choose a staff member" description="Adds a staff picker to the date & time step. Recommended for small teams." checked={V.s.staffSelect} onChange={V.set.staffSelect} />
                        <div style={{ paddingTop: "12px" }}>
                          <Field label="Reschedule behaviour" hint="Personal booking links always reschedule with the same staff member.">
                            <Dropdown options={V.opts.reschedule} value={V.s.reschedule} onChange={V.set.reschedule} style={{  }} />
                          </Field>
                        </div>
                      </Section>
                    </>) : null}
                    {V.is.appt ? (<>
                      <Section title="Error page redirect" density="dense">
                        <ToggleRow title="Offer to book with another staff member" checked={V.s.errBookStaff} onChange={V.set.errBookStaff} />
                        <ToggleRow title="Offer to explore other services" checked={V.s.errOtherServices} onChange={V.set.errOtherServices} />
                      </Section>
                    </>) : null}
                  </>) : null}
                  {V.is.progress ? (<>
                    <Section title="Progress indicator" description="How customers see where they are in the journey.">
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                        <SelectableTile showIndicator={false} title="Step titles" description="What · Where · When · Your info" selected={V.is.pTitles} onSelect={V.actions.pTitles} full={true} />
                        <SelectableTile showIndicator={false} title="Numbered steps" description="Numbered track with tick marks" selected={V.is.pNumbers} onSelect={V.actions.pNumbers} full={true} />
                        <SelectableTile showIndicator={false} title="Bar only" description="Percentage complete, no labels" selected={V.is.pBar} onSelect={V.actions.pBar} full={true} />
                        <SelectableTile showIndicator={false} title="Hidden" description="No progress indicator" selected={V.is.pNone} onSelect={V.actions.pNone} full={true} />
                      </div>
                    </Section>
                  </>) : null}
                  {V.is.eventList ? (<>
                    <Section title="How many events on the first page" description="The rest are reached by the control you pick below.">
                      <div style={{ display: "flex", alignItems: "flex-end", gap: "16px", flexWrap: "wrap", paddingBottom: "16px" }}>
                        <Field label="Events per page" hint={V.hints.perPage}>
                          <Input type="number" value={V.s.eventsPerPage} onChange={V.set.eventsPerPage} style={{  }} />
                        </Field>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <SelectableTile showIndicator={false} title="Numbered pages" description="A pager under the list — best when people scan for a date." selected={V.is.pagePages} onSelect={V.actions.pagePages} full={true} />
                        <SelectableTile showIndicator={false} title="Load more button" description="Appends the next batch, keeping everything on one page." selected={V.is.pageLoadMore} onSelect={V.actions.pageLoadMore} full={true} />
                        <SelectableTile showIndicator={false} title="Infinite scroll" description="Loads as the visitor scrolls. Avoid when the list is long." selected={V.is.pageScroll} onSelect={V.actions.pageScroll} full={true} />
                      </div>
                    </Section>
                    <Section title="Layout">
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                        <SelectableTile showIndicator={false} title="Cards" description="Cover image on top, details underneath" selected={V.is.evGrid} onSelect={V.actions.evGrid} full={true} />
                        <SelectableTile showIndicator={false} title="List" description="One event per row, dense" selected={V.is.evList} onSelect={V.actions.evList} full={true} />
                      </div>
                      {V.is.evGrid ? (<>
                        <div style={{ paddingTop: "16px" }}>
                          <Field label="Cards per row" hint="Cards stack to one column on phones whatever you pick.">
                            <SegmentedControl options={V.opts.cardCols} value={V.s.cardCols} onChange={V.set.cardCols} size="sm" />
                          </Field>
                        </div>
                      </>) : null}
                    </Section>
                    <Section title="Search bar" description="Sits above the list, so visitors can narrow a long calendar.">
                      <ToggleRow title="Show the search bar" checked={V.s.filterBar} onChange={V.set.filterBar} />
                      {V.s.filterBar ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "12px 0 0 2px" }}>
                          <Checkbox checked={V.s.filterLocation} onChange={V.set.filterLocation} label="Location search" supportingText="With a use-my-location button" />
                          <Checkbox checked={V.s.filterType} onChange={V.set.filterType} label="Event type" />
                          <Checkbox checked={V.s.filterDates} onChange={V.set.filterDates} label="Start and end date" />
                        </div>
                      </>) : null}
                      <div style={{ paddingTop: "14px" }}>
                        <ToggleRow title="Show category chips instead" description="A quicker filter when you have four or five clear categories." checked={V.s.eventFilters} onChange={V.set.eventFilters} />
                      </div>
                    </Section>
                    <Section title="What each event shows">
                      <ToggleRow title="Cover image" description="Uploaded on the event in Studio. Falls back to a coloured tile." checked={V.s.eventImages} onChange={V.set.eventImages} />
                      <ToggleRow title="Description" description="Two or three lines under the title." checked={V.s.showEventDesc} onChange={V.set.showEventDesc} />
                      <ToggleRow title="Date" checked={V.s.showEventDate} onChange={V.set.showEventDate} />
                      <ToggleRow title="Start and end time" checked={V.s.showEventTime} onChange={V.set.showEventTime} />
                      <ToggleRow title="Time zone" description="Worth keeping for online events booked from other states." checked={V.s.showTimezone} onChange={V.set.showTimezone} />
                      <ToggleRow title="Address" checked={V.s.showEventVenue} onChange={V.set.showEventVenue} />
                      <ToggleRow title="Price" checked={V.s.showEventPrice} onChange={V.set.showEventPrice} />
                      <ToggleRow title="Spaces remaining" description="A capacity meter and a count. Creates urgency, and pressure." checked={V.s.showRemaining} onChange={V.set.showRemaining} />
                    </Section>
                    <Section title="When an event is full">
                      <ToggleRow title="Keep full events on the list" description="Off removes them entirely, so nobody sees what they missed." checked={V.s.showSoldOut} onChange={V.set.showSoldOut} />
                      <ToggleRow title="Offer a waiting list" checked={V.s.waitlist} onChange={V.set.waitlist} disabled={V.is.soldOutHidden} />
                    </Section>
                  </>) : null}
                  {V.is.service ? (<>
                    <Section title="Layout" description="With more than twelve services, the list view reads best.">
                      <Field label="Display of services">
                        <Dropdown options={V.opts.serviceDisplay} value={V.s.serviceDisplay} onChange={V.set.serviceDisplay} style={{  }} />
                      </Field>
                    </Section>
                    <Section title="What each service shows">
                      <ToggleRow title="Group services into categories" description={this.categoryHint()} checked={V.s.showCategories} onChange={V.set.showCategories} />
                      <ToggleRow title="Show service images" description="Uploaded on the service profiles in Studio." checked={V.s.showServiceImages} onChange={V.set.showServiceImages} />
                      {V.is.appt ? (<>
                        <ToggleRow title="Show duration" checked={V.s.showDuration} onChange={V.set.showDuration} />
                        <ToggleRow title="Show price" checked={V.s.showPrice} onChange={V.set.showPrice} />
                      </>) : null}
                      {V.is.queue ? (<>
                        <ToggleRow title="Ask which service point" description="Adds a step when the building runs several desks or windows. Off routes visitors automatically." checked={V.s.queueLocationStep} onChange={V.set.queueLocationStep} />
                        <ToggleRow title="Show the live wait per service" description="Visitors see how long each desk is running before they choose." checked={V.s.liveWaitPerService} onChange={V.set.liveWaitPerService} />
                      </>) : null}
                    </Section>
                  </>) : null}
                  {V.is.location ? (<>
                    {V.is.queue ? (<>
                      {/* The attached queues, read back from the queue service
                          itself — not the designer's old DESKS fixture, which
                          named three advising desks that belong to no tenant.
                          A queue that could not be read says so instead of
                          disappearing from the list. */}
                      <Section title="Service points" description="The queues this page joins. Attach or detach them in General; their names, locations and waits come from Queues.">
                        <ToggleRow
                          title={<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Name the desk on the ticket <Pill tone="neutral">Coming soon</Pill></span>}
                          description="The public queue projection carries the queue's name but no desk assignment, so there is nothing true to print here yet."
                          checked={false}
                          disabled={true}
                        />
                        <div style={{ display: "flex", flexDirection: "column", paddingTop: "8px" }}>
                          {(V.pv.walkInQueueRows || []).map((dk) => (
                            <MediaRow key={dk.id} title={dk.name} subtitle={dk.sub} meta={dk.meta} as="div" />
                          ))}
                        </div>
                        {!(V.pv.walkInQueueRows || []).length ? (<>
                          <InlineEmptyState title="No lines attached" description="Pick at least one queue in General." />
                        </>) : null}
                      </Section>
                    </>) : null}
                    {V.is.appt ? (<>
                      <Section title="Availability on the list" description="Only applies to service-first journeys.">
                        <ToggleRow title="Show next available slot per location" checked={V.s.nextAvailable} onChange={V.set.nextAvailable} />
                        <ToggleRow nested title="Enable Quickbook" description="Books the next available slot directly, skipping the date & time step." checked={V.s.quickbook} onChange={V.set.quickbook} disabled={V.is.quickbookLocked} />
                        <ToggleRow title="Show opening hours and phone number" checked={V.s.extraLocationInfo} onChange={V.set.extraLocationInfo} />
                        <ToggleRow title="Show map" description={V.hints.map} checked={V.s.showMap} onChange={V.set.showMap} disabled={V.is.mapFirst} />
                        {V.s.showMap ? (<>
                          <div style={{ paddingTop: "12px" }}>
                            <Field label="Map height" hint="Or drag the handle under the map in the preview.">
                              <Input type="number" value={V.s.mapHeight} onChange={V.set.mapHeight} trailing="px" style={{  }} />
                            </Field>
                          </div>
                        </>) : null}
                      </Section>
                      {V.is.mapFirst ? (<>
                        <Banner tone="primary" variant="explainer" size="compact" title="This journey opens on the map">Map only is set in General, so the list is replaced by pins and a selected-location card. Load more and the result count do not apply.
                        </Banner>
                      </>) : null}
                      <Section title="How many results">
                        <ToggleRow title="Use a Load more button" description="Off means infinite scroll." checked={V.s.loadMore} onChange={V.set.loadMore} />
                        <div style={{ display: "flex", gap: "16px", paddingTop: "12px", flexWrap: "wrap" }}>
                          <Field label="Locations loaded initially">
                            <Input type="number" value={V.s.initialCount} onChange={V.set.initialCount} style={{  }} />
                          </Field>
                          <Field label="Maximum search radius" hint="0 means no limit.">
                            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                              <Input type="number" value={V.s.radius} onChange={V.set.radius} style={{  }} />
                              <Dropdown options={V.opts.radiusUnit} value={V.s.radiusUnit} onChange={V.set.radiusUnit} style={{  }} />
                            </div>
                          </Field>
                        </div>
                      </Section>
                    </>) : null}
                  </>) : null}
                  {V.is.datetime ? (<>
                    <Section title="Calendar layout">
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                        <SelectableTile showIndicator={false} title="Month calendar" description="Full month, unavailable dates greyed out" selected={V.is.monthCal} onSelect={V.actions.monthCal} full={true} />
                        <SelectableTile showIndicator={false} title="Day strip" description="Seven days at a time, slots underneath" selected={V.is.dayStrip} onSelect={V.actions.dayStrip} full={true} />
                      </div>
                      <div style={{ paddingTop: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
                        <SectionLabel tone="soft">Which date is selected when the step opens
                        </SectionLabel>
                        <SelectableTile showIndicator={false} title="Today" description="Customers land on today, even when it is fully booked." selected={V.is.dateToday} onSelect={V.actions.dateToday} full={true} />
                        <SelectableTile showIndicator={false} title="First date with availability" description={V.hints.firstAvail} selected={V.is.dateFirst} onSelect={V.actions.dateFirstAvail} full={true} />
                      </div>
                    </Section>
                    <Section title="Time slots">
                      <ToggleRow title="Group slots by part of day" description="Morning, afternoon, evening headings." checked={V.is.grouped} onChange={V.actions.setGrouped} />
                      <ToggleRow title="Show the time zone" description="Reassures customers booking virtual appointments from another state." checked={V.s.showTimezone} onChange={V.set.showTimezone} />
                    </Section>
                    {V.s.staffSelect ? (<>
                      <Banner tone="primary" variant="explainer" size="compact" title="Staff picker is on">Customers pick an adviser above the calendar. Turn it off in General › Staff.
                      </Banner>
                    </>) : null}
                  </>) : null}
                  {V.is.details ? (<>
                    {V.is.queue ? (<>
                      {/* The join is validated against the UNION of the
                          queue's own `settings.joinForm` and what this page
                          asks for, so making a field optional here cannot
                          make it optional at a desk that demands it. Said out
                          loud rather than left for a customer to discover as
                          a refusal on a form that told them it was optional. */}
                      <Banner tone="neutral" variant="explainer" size="compact" title="Queues can ask for more than this page does">
                        A queue with its own required contact field still demands it. These settings can add a question or make one required — they cannot relax what a queue insists on.
                      </Banner>
                    </>) : null}
                    <Section title="What you ask for">
                      {/* Each ask-for field is one compact group: the enable ToggleRow with its
                          dependent controls left-aligned directly underneath at a modest
                          tokenized gap, wrapping instead of stranding a lone control at the
                          far right of the panel. */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-1, 4px)" }}>
                        <ToggleRow title={V.pv.emailSettingTitle} description={V.pv.emailSettingDescription} checked={V.pv.emailApiRequired ? true : V.s.askEmail} onChange={V.pv.emailApiRequired ? () => {} : V.set.askEmail} disabled={V.pv.emailApiRequired} />
                        {!V.pv.emailApiRequired && V.s.askEmail ? (<>
                          <div style={{ padding: "0 0 var(--jrni-space-3, 12px) 2px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--jrni-space-2, 8px) var(--jrni-space-4, 16px)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2, 8px)" }}>
                              <Checkbox checked={V.s.emailOptional} onChange={V.set.emailOptional} aria-label="Mark as optional" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Mark as optional</span>
                            </div>
                          </div>
                        </>) : null}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-1, 4px)" }}>
                        <ToggleRow title="Phone number" checked={V.s.askPhone} onChange={V.set.askPhone} />
                        {V.s.askPhone ? (<>
                          <div style={{ padding: "0 0 var(--jrni-space-3, 12px) 2px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--jrni-space-2, 8px) var(--jrni-space-4, 16px)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2, 8px)" }}>
                              <Checkbox checked={V.s.phoneOptional} onChange={V.set.phoneOptional} aria-label="Mark as optional" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Mark as optional</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2, 8px)" }}>
                              <Checkbox checked={V.s.countryFlags} onChange={V.set.countryFlags} aria-label="Show country flags" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Show country flags</span>
                            </div>
                          </div>
                          {/* The country list is a phone-only dependent control, so it belongs
                              inside the phone group, under its own field — not stranded below
                              the postal-address group where it read as unrelated. */}
                          <div style={{ padding: "0 0 var(--jrni-space-3, 12px) 2px" }}>
                            <Field label="Countries suggested at the top" error={V.pv.countryError}>
                              <MultiSelect options={V.opts.countries} values={V.s.customCountries} onChange={V.actions.setCountries} width={360} />
                            </Field>
                          </div>
                        </>) : null}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-1, 4px)" }}>
                        {/* The canonical queue join carries name, phone,
                            email, service, party and answers — there is no
                            address field on it, so on Walk-Ins this asks for
                            something no queue would ever receive. */}
                        <ToggleRow
                          title={V.is.queue ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Postal address <Pill tone="neutral">Coming soon</Pill></span>) : "Postal address"}
                          description={V.is.queue ? "The queue join does not carry an address, so nothing entered here would reach the desk." : undefined}
                          checked={V.is.queue ? false : V.s.askAddress}
                          onChange={V.is.queue ? undefined : V.set.askAddress}
                          disabled={V.is.queue}
                        />
                        {V.s.askAddress && !V.is.queue ? (<>
                          <div style={{ padding: "0 0 var(--jrni-space-3, 12px) 2px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--jrni-space-2, 8px) var(--jrni-space-4, 16px)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2, 8px)" }}>
                              <Checkbox checked={V.s.addressOptional} onChange={V.set.addressOptional} aria-label="Mark as optional" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Mark as optional</span>
                            </div>
                          </div>
                        </>) : null}
                      </div>
                    </Section>
                    <Section title={V.pv.notifSectionTitle}>
                      {V.pv.notQueue ? (<>
                        <ToggleRow title="Let customers choose how they are notified" checked={V.s.notifPref} onChange={V.set.notifPref} />
                        {V.s.notifPref ? (<>
                          <div style={{ padding: "8px 0 12px 2px" }}>
                            <Field label="Default selection">
                              <Dropdown options={V.opts.notif} value={V.s.notifDefault} onChange={V.set.notifDefault} style={{  }} />
                            </Field>
                          </div>
                        </>) : null}
                      </>) : null}
                      {V.is.queue ? (<>
                        <ToggleRow title="Notify by text message" description="The only channel for walk-ins today, so it stays on." checked={true} disabled={true} />
                      </>) : null}
                      {V.is.appt ? (<>
                        <ToggleRow title="Allow additional attendees" description="A parent, guardian or interpreter joining the appointment." checked={V.s.attendees} onChange={V.set.attendees} />
                        {V.s.attendees ? (<>
                          <div style={{ padding: "12px 0 0 2px", display: "flex", flexDirection: "column", gap: "12px" }}>
                            <Field label="What you ask for each attendee" hint="At least one field is needed to identify them.">
                              <MultiSelect options={V.opts.attendeeFields} values={V.s.attendeeFields} onChange={V.set.attendeeFields} width={360} />
                            </Field>
                            <Field label="Maximum additional attendees" hint="Up to 20. The person booking is not counted.">
                              <Input type="number" min={1} max={20} value={V.s.maxParty} onChange={V.set.maxParty} />
                            </Field>
                          </div>
                        </>) : null}
                      </>) : null}
                    </Section>
                    {V.is.docs ? (<>
                      <Section title="Documents">
                        <ToggleRow title={V.is.detailsComingSoon ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Document checklist <Pill tone="neutral">Coming soon</Pill></span>) : "Document checklist"} description="Ask the customer to bring specific documents. The list is shown on the details step and repeated on the confirmation." checked={V.s.fileUpload} onChange={V.is.detailsComingSoon ? undefined : V.set.fileUpload} disabled={V.is.detailsComingSoon} />
                        {V.s.fileUpload ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "12px" }}>
                            {(V.s.requiredDocs || []).map((doc, i) => (
                              <div key={i} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                {/* Coming soon on Events/Landing: the saved list stays visible and
                                    stays in state, but nothing in it may be edited — the rows are
                                    author controls too, not just the parent toggle. */}
                                <Input value={doc} placeholder="Photo ID" disabled={V.is.detailsComingSoon} onChange={V.is.detailsComingSoon ? undefined : ((e) => V.actions.setDoc(i, e && e.target ? e.target.value : e))} />
                                <Button variant="ghost" size="sm" disabled={V.is.detailsComingSoon} onClick={V.is.detailsComingSoon ? undefined : (() => V.actions.removeDoc(i))}>Remove</Button>
                              </div>
                            ))}
                            <AddRowButton disabled={V.is.detailsComingSoon} onClick={V.is.detailsComingSoon ? undefined : V.actions.addDoc}>Add a document</AddRowButton>
                          </div>
                        </>) : null}
                      </Section>
                    </>) : null}
                    <Section title="Payment" description="Only appears to the customer when there is something to pay.">
                      <ToggleRow title={V.is.detailsComingSoon ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Take payment on this step <Pill tone="neutral">Coming soon</Pill></span>) : "Take payment on this step"} description={V.hints.payment} checked={V.s.takePayment} onChange={V.is.detailsComingSoon ? undefined : V.set.takePayment} disabled={V.is.detailsComingSoon} />
                    </Section>
                    <Section title="Known customers">
                      <ToggleRow title={V.is.detailsComingSoon ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Fill and lock details for signed-in customers <Pill tone="neutral">Coming soon</Pill></span>) : "Fill and lock details for signed-in customers"} description="When a sign-in integration is set up, the primary person's name, e-mail, phone and address come from their account and cannot be edited." checked={V.s.readOnly} onChange={V.is.detailsComingSoon ? undefined : V.set.readOnly} disabled={V.is.detailsComingSoon} />
                    </Section>
                    {V.is.queue ? (<>
                      <Section title="Party size">
                        <ToggleRow title="Ask how many people are waiting together" description="One ticket for the group, so a family is not split across two advisers." checked={V.s.partySize} onChange={V.set.partySize} />
                        {V.s.partySize ? (<>
                          <div style={{ paddingTop: "12px" }}>
                            <Field label="Maximum party size">
                              <Input type="number" value={V.s.maxParty} onChange={V.set.maxParty} style={{  }} />
                            </Field>
                          </div>
                        </>) : null}
                      </Section>
                    </>) : null}
                    {/* Places is an event-registration concern, so it belongs on BOTH event
                        journeys (Events list and Landing), not Events alone. */}
                    {V.is.eventOrLanding ? (<>
                      <Section title="Places">
                        <ToggleRow title={V.is.detailsComingSoon ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Let people book more than one place <Pill tone="neutral">Coming soon</Pill></span>) : "Let people book more than one place"} checked={V.is.detailsComingSoon ? false : V.s.ticketQuantity} onChange={V.is.detailsComingSoon ? undefined : V.set.ticketQuantity} disabled={V.is.detailsComingSoon} />
                        {V.s.ticketQuantity && !V.is.detailsComingSoon ? (<>
                          <div style={{ padding: "12px 0" }}>
                            <Banner tone="neutral" variant="explainer" size="compact" title="The ceiling comes from the event">{V.pv.placeCapNote}
                            </Banner>
                          </div>
                        </>) : null}
                      </Section>
                    </>) : null}
                  </>) : null}
                  {V.is.confirmation ? (<>
                    {V.is.eventOrLanding ? (<>
                      <Section title="The ticket" description="Registrations are checked in on the door with the scanner in Studio.">
                        <ToggleRow title="Show a QR ticket" description="Scannable on arrival, and included in the confirmation email." checked={V.s.showQr} onChange={V.set.showQr} />
                        <ToggleRow title="Name the room" checked={V.s.showResource} onChange={V.set.showResource} />
                        <ToggleRow title="Name the host" description="Who is running the session." checked={V.s.showStaff} onChange={V.set.showStaff} />
                      </Section>
                      <Section title="What registrants can do next">
                        <ToggleRow title="Add to calendar" checked={V.s.addToCalendar} onChange={V.set.addToCalendar} />
                        <ToggleRow title="Cancel their registration" description="Frees the place for the waiting list." checked={V.s.allowCancelReg} onChange={V.set.allowCancelReg} />
                      </Section>
                    </>) : null}
                    {V.is.queue ? (<>
                      <Section title="The ticket">
                        <ToggleRow title="Show a ticket number" description="Called out on the waiting-room screen, so visitors keep their place without giving a name." checked={V.s.showTicketNumber} onChange={V.set.showTicketNumber} />
                        <ToggleRow title="Show position in line" checked={V.s.showPosition} onChange={V.set.showPosition} />
                        <ToggleRow
                          title={<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Name the desk <Pill tone="neutral">Coming soon</Pill></span>}
                          description="The entry carries no desk assignment on the public projection, so this would print nothing."
                          checked={false}
                          disabled={true}
                        />
                        <ToggleRow title="Show a QR code" description="Encodes this visitor's own status page on this site, so they can check their place from another device." checked={V.s.showQr} onChange={V.set.showQr} />
                      </Section>
                      <Section title="How you express the wait" description="A range keeps its promise on a busy morning; an exact figure rarely does.">
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <SelectableTile showIndicator={false} title="A range" description="12–18 minutes" selected={V.is.waitRange} onSelect={V.actions.waitRange} full={true} />
                          <SelectableTile showIndicator={false} title="An exact estimate" description="15 minutes" selected={V.is.waitExact} onSelect={V.actions.waitExact} full={true} />
                          <SelectableTile showIndicator={false} title="Position only" description="No time promised at all" selected={V.is.waitPosition} onSelect={V.actions.waitPosition} full={true} />
                        </div>
                      </Section>
                      <Section title="While they wait">
                        {/* Proactive near-the-front SMS is a queue-side send.
                            There is no published-journey field for it and no
                            operation this page could call, so the toggle and
                            its child threshold are disabled rather than
                            stored as a promise nobody keeps. */}
                        <ToggleRow
                          title={<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Text them when they are near the front <Pill tone="neutral">Coming soon</Pill></span>}
                          description="Turn-approaching messages are sent by the queue itself; this page cannot switch them on yet."
                          checked={false}
                          disabled={true}
                        />
                        <div style={{ padding: "12px 0" }}>
                          <Field label="Send the text when this many are ahead">
                            <Input type="number" value={V.s.notifyAhead} disabled={true} style={{  }} />
                          </Field>
                        </div>
                        <ToggleRow title="Let them leave the queue" description="Posts a real leave, frees the slot, and shows the visitor that their ticket is finished." checked={V.s.allowLeaveQueue} onChange={V.set.allowLeaveQueue} />
                      </Section>
                    </>) : null}
                    {V.is.appt ? (<>
                      <Section title="What the confirmation shows">
                        <ToggleRow title="Staff member" description="Name and photo of the adviser." checked={V.s.showStaff} onChange={V.set.showStaff} />
                        <ToggleRow title="Resource name" description="Room, desk or booth." checked={V.s.showResource} onChange={V.set.showResource} />
                        <ToggleRow title="QR check-in code" description="Scanned at reception on arrival, and repeated in the confirmation email." checked={V.s.showQr} onChange={V.set.showQr} />
                      </Section>
                      <Section title="What customers can do next">
                        <ToggleRow title="Add to calendar" checked={V.s.addToCalendar} onChange={V.set.addToCalendar} />
                        <ToggleRow title="Reschedule" checked={V.s.allowReschedule} onChange={V.set.allowReschedule} />
                        <ToggleRow title="Cancel appointment" checked={V.s.allowCancel} onChange={V.set.allowCancel} />
                        {V.s.allowCancel ? (<>
                          <div style={{ padding: "12px 0 0", display: "flex", flexDirection: "column", gap: "16px" }}>
                            <Field label="Refund on a late cancellation">
                              <Dropdown options={V.opts.refund} value={V.s.refundMode} onChange={V.set.refundMode} style={{  }} />
                            </Field>
                          </div>
                        </>) : null}
                      </Section>
                    </>) : null}
                  </>) : null}
                  {V.is.landing ? (<>
                    <Section title="Blocks, order and content">
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {(V.pv.blockRows || []).map((bk, __i2) => (<React.Fragment key={bk.key ?? bk.id ?? __i2}>
                          <div style={{ borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" }}>
                              <button type="button" onClick={bk.onExpand} aria-expanded={bk.open} style={{ flex: "1 1 auto", display: "flex", alignItems: "center", gap: "8px", padding: "0", background: "none", border: "0", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                                <span style={{ flex: "0 0 12px", display: "flex", justifyContent: "center", color: "var(--jrni-color-text-soft)", transform: bk.caret }}>{bk.caretGlyph}</span>
                                <span style={{ flex: "1 1 auto", fontSize: "14px", color: bk.fg }}>{bk.label}</span>
                                <span style={{ flex: "0 0 auto", fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{bk.meta}</span>
                              </button>
                              <Button variant="ghost" size="sm" aria-label="Move up" onClick={bk.onUp} disabled={bk.first}>↑
                              </Button>
                              <Button variant="ghost" size="sm" aria-label="Move down" onClick={bk.onDown} disabled={bk.last}>↓
                              </Button>
                              <Toggle checked={bk.on} onChange={bk.onToggle} aria-label={bk.label} />
                            </div>
                            {bk.open ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "4px 0 16px 20px" }}>
                                {bk.isDesc ? (<>
                                  <Field label="Description">
                                    <Textarea value={V.s.lpDescText} onChange={V.actions.setDescText} rows={4} full={true} />
                                  </Field>
                                  <SectionLabel tone="soft">Agenda
                                  </SectionLabel>
                                  {(V.pv.agendaEdit || []).map((ag, __i3) => (<React.Fragment key={ag.key ?? ag.id ?? __i3}>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                      <Input value={ag.time} onChange={ag.setTime} placeholder="9:00 AM" style={{  }} />
                                      <Input value={ag.what} onChange={ag.setWhat} placeholder="What happens" full={true} />
                                      <Button variant="link" size="sm" danger={true} onClick={ag.onRemove}>Remove
                                      </Button>
                                    </div>
                                  </React.Fragment>))}
                                  <AddRowButton onClick={V.actions.addAgenda}>Add an agenda line
                                  </AddRowButton>
                                </>) : null}
                                {bk.isSpeakers ? (<>
                                  {(V.pv.speakerEdit || []).map((sp, __i4) => (<React.Fragment key={sp.key ?? sp.id ?? __i4}>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                      <Input value={sp.name} onChange={sp.setName} placeholder="Name" full={true} />
                                      <Input value={sp.role} onChange={sp.setRole} placeholder="Role" full={true} />
                                      <Button variant="link" size="sm" danger={true} onClick={sp.onRemove}>Remove
                                      </Button>
                                    </div>
                                  </React.Fragment>))}
                                  <AddRowButton onClick={V.actions.addSpeaker}>Add a speaker
                                  </AddRowButton>
                                </>) : null}
                                {bk.isIncluded ? (<>
                                  {(V.pv.includedEdit || []).map((inc, __i5) => (<React.Fragment key={inc.key ?? inc.id ?? __i5}>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                      <Input value={inc.text} onChange={inc.setText} placeholder="What they get" full={true} />
                                      <Button variant="link" size="sm" danger={true} onClick={inc.onRemove}>Remove
                                      </Button>
                                    </div>
                                  </React.Fragment>))}
                                  <AddRowButton onClick={V.actions.addIncluded}>Add a line
                                  </AddRowButton>
                                </>) : null}
                                {bk.isTickets ? (<>
                                  <p style={{ margin: "0", fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-soft)" }}>Ticket types, prices and their small print come from this event's configuration in Studio. The page shows whatever the event sells.</p>
                                  <div style={{ display: "flex", flexDirection: "column" }}>
                                    {(V.pv.ticketEdit || []).map((tk, __i6) => (<React.Fragment key={tk.key ?? tk.id ?? __i6}>
                                      <div style={{ display: "flex", alignItems: "baseline", gap: "12px", padding: "8px 0", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                                        <span style={{ flex: "1 1 auto", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{tk.name}</span>
                                        <span style={{ flex: "0 0 auto", fontSize: "14px", fontWeight: "700" }}>{tk.price}</span>
                                      </div>
                                      <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)", paddingBottom: "4px" }}>{tk.note}</div>
                                    </React.Fragment>))}
                                  </div>
                                  {V.pv.ticketsEmpty ? (<>
                                    <InlineEmptyState title={V.pv.ticketsEmptyTitle} description={V.pv.ticketsEmptyBody} />
                                  </>) : null}
                                </>) : null}
                                {bk.isFaq ? (<>
                                  {(V.pv.faqEdit || []).map((fq, __i7) => (<React.Fragment key={fq.key ?? fq.id ?? __i7}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px", paddingBottom: "8px" }}>
                                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                        <Input value={fq.q} onChange={fq.setQ} placeholder="Question" full={true} />
                                        <Button variant="link" size="sm" danger={true} onClick={fq.onRemove}>Remove
                                        </Button>
                                      </div>
                                      <Textarea value={fq.a} onChange={fq.setA} rows={2} placeholder="Answer" full={true} />
                                    </div>
                                  </React.Fragment>))}
                                  <AddRowButton onClick={V.actions.addFaq}>Add a question
                                  </AddRowButton>
                                </>) : null}
                                {bk.isSponsors ? (<>
                                  <ToggleRow title="Group by sponsor tier" description="Gold first, then silver, then supporters, each under its own heading." checked={V.s.sponsorTiers} onChange={V.actions.setSponsorTiers} />
                                  {(V.pv.sponsorEdit || []).map((sr, __i8) => (<React.Fragment key={sr.key ?? sr.id ?? __i8}>
                                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", paddingBottom: "12px", borderBottom: "1px solid var(--jrni-color-neutral-6)" }}>
                                      <div style={{ flex: "0 0 120px" }}>
                                        <ImageDropField value={sr.logo} onChange={sr.setLogo} aspect={2.4} maxWidth={120} placeholder="Logo" />
                                      </div>
                                      <div style={{ flex: "1 1 auto", minWidth: "0", display: "flex", flexDirection: "column", gap: "8px" }}>
                                        <Input value={sr.name} onChange={sr.setName} placeholder="Sponsor name" full={true} />
                                        <Dropdown options={V.opts.sponsorTier} value={sr.tier} onChange={sr.setTier} aria-label="Sponsor tier" full={true} />
                                        <div>
                                          <Button variant="link" size="sm" danger={true} onClick={sr.onRemove}>Remove
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </React.Fragment>))}
                                  <AddRowButton onClick={V.actions.addSponsor}>Add a sponsor
                                  </AddRowButton>
                                </>) : null}
                                {bk.isShare ? (<>
                                  <Field label="Channels">
                                    <MultiSelect options={V.opts.shareChannels} values={V.s.lpShareChannels} onChange={V.actions.setShareChannels} width={360} />
                                  </Field>
                                </>) : null}
                                {bk.isRelated ? (<>
                                  <Field label="How related events are chosen">
                                    <Dropdown options={V.opts.relatedRule} value={V.s.relatedRule} onChange={V.actions.setRelatedRule} style={{  }} />
                                  </Field>
                                  {V.pv.relatedAuto ? (<>
                                    <Field label="How many to show">
                                      <SegmentedControl options={V.opts.relatedCount} value={V.s.relatedCount} onChange={V.actions.setRelatedCount} />
                                    </Field>
                                  </>) : null}
                                  {V.pv.relatedManualMode ? (<>
                                    <Field label="Pick the events">
                                      <MultiSelect options={V.pv.eventOptions} values={V.s.relatedManual} onChange={V.actions.setRelatedManual} width={360} />
                                    </Field>
                                  </>) : null}
                                  <p style={{ margin: "0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.relatedNote}</p>
                                </>) : null}
                                {bk.isDerived ? (<>
                                  <p style={{ margin: "0", fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-soft)" }}>{bk.derivedNote}</p>
                                </>) : null}
                              </div>
                            </>) : null}
                          </div>
                        </React.Fragment>))}
                      </div>
                    </Section>
                    <Section title="When the event is full">
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <SelectableTile showIndicator={false} title="Waiting-list form" description="Name and e-mail, and they are told their position on the list." selected={V.is.soldWaitlist} onSelect={V.actions.soldWaitlist} full={true} />
                        <SelectableTile showIndicator={false} title="Related events only" description="No list to join — send them to the next comparable event." selected={V.is.soldRelated} onSelect={V.actions.soldRelated} full={true} />
                        <SelectableTile showIndicator={false} title="Both" description="The waiting list first, related events underneath." selected={V.is.soldBoth} onSelect={V.actions.soldBoth} full={true} />
                      </div>
                      <div style={{ paddingTop: "12px" }}>
                        <ToggleRow title="Preview the fully-booked page" description="Shows what a visitor sees once the last place goes." checked={V.pv.soldOutPreview} onChange={V.actions.setSoldOutPreview} />
                      </div>
                    </Section>
                  </>) : null}
                  {V.is.kioskEntry ? (<>
                    <Section title="Where it can send people" description="Each tile hands off to the journey that owns it. Turn one off and the tile goes — the others grow to fill the space.">
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <ToggleRow title="Book an appointment" description="Opens the Appointments journey." checked={V.s.kioskAppts} onChange={V.set.kioskAppts} />
                        <ToggleRow title="Join the walk-in line" description="Opens the Walk-Ins journey." checked={V.s.kioskWalkin} onChange={V.set.kioskWalkin} />
                        <ToggleRow title="What's on" description="Opens the Event List Page journey." checked={V.s.kioskEvents} onChange={V.set.kioskEvents} />
                        <ToggleRow title="Check in" description="Stays on the kiosk — the only destination it handles itself." checked={V.s.kioskCheckin} onChange={V.set.kioskCheckin} />
                      </div>
                      <div style={{ paddingTop: "16px" }}>
                        <Field label="Tile size" hint="Large is right at arm's length on a wall. Compact only if you have five or more tiles.">
                          <SegmentedControl options={V.opts.kioskTileSize} value={V.s.kioskTileSize} onChange={V.set.kioskTileSize} size="sm" />
                        </Field>
                      </div>
                    </Section>
                    <Section title="Live information" description="Shown on the tiles and in the strip beneath them. All of it is read-only — nobody has to touch the screen to get the answer.">
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <ToggleRow title="Walk-in wait time" description="Minutes and how many people are ahead. The single most asked question in a lobby." checked={V.s.kioskWaitTime} onChange={V.set.kioskWaitTime} />
                        <ToggleRow title="Next available appointment" description="The soonest slot across every service at this campus." checked={V.s.kioskNextSlot} onChange={V.set.kioskNextSlot} />
                        <ToggleRow title="Hours and address" description="Answers the question people ask on their way back out." checked={V.s.kioskHours} onChange={V.set.kioskHours} />
                      </div>
                    </Section>
                  </>) : null}
                  {V.is.kioskCheckin ? (<>
                    <Section title="How someone identifies themselves" description="Keep at least one method that works without the reader — it is the thing most likely to fail.">
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <ToggleRow title="Scan a QR code" description="From the confirmation email or a printout." checked={V.s.ckQr} onChange={V.set.ckQr} />
                        <ToggleRow title="Scan a wallet pass" description="Apple Wallet and Google Wallet passes held up to the reader." checked={V.s.ckWallet} onChange={V.set.ckWallet} />
                        <ToggleRow title="Tap a wallet pass (NFC)" description="Needs an NFC reader in the enclosure. Faster than scanning and works in poor light." checked={V.s.ckNfc} onChange={V.set.ckNfc} />
                        <ToggleRow title="Type a booking reference" description="The fallback when the reader is down." checked={V.s.ckRef} onChange={V.set.ckRef} />
                        <ToggleRow title="Search by name" description="Shows only today's bookings at this campus, and never a full name list." checked={V.s.ckName} onChange={V.set.ckName} />
                      </div>
                      <div style={{ paddingTop: "16px" }}>
                        <Field label="Method shown first">
                          <SegmentedControl options={V.opts.ckDefault} value={V.s.ckDefault} onChange={V.set.ckDefault} size="sm" />
                        </Field>
                      </div>
                    </Section>
                    <Section title="After they check in" description="The screen returns to the entry page on its own — nothing of theirs is left up for the next person.">
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <ToggleRow title="Show their place in the line" description="Walk-ins only — someone with a booked appointment has a time, not a queue position, so this never shows for them." checked={V.s.ckShowQueuePos} onChange={V.set.ckShowQueuePos} />
                      </div>
                      <div style={{ paddingTop: "16px" }}>
                        <Field label="Hold the confirmation for" hint={V.pv.ckDwellNote}>
                          <Input type="number" value={V.s.ckDoneDwell} onChange={V.set.ckDoneDwell} trailing="sec" style={{  }} />
                        </Field>
                      </div>
                    </Section>
                  </>) : null}
                  {V.is.customtext ? (<>
                    <Section title="What to replace" description="Every heading and message this journey shows. Leave a field empty and the customer sees the wording on the left.">
                      <Field label="Show">
                        <SegmentedControl options={V.opts.ctFilter} value={V.ui.ctFilter} onChange={V.actions.setCtFilter} size="sm" aria-label="Which fields to show" />
                      </Field>
                    </Section>
                    {(V.ct.groups || []).map((grp, __i9) => (<React.Fragment key={grp.key ?? grp.id ?? __i9}>
                      <div style={{ border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", overflow: "hidden" }}>
                        <button type="button" onClick={grp.onToggle} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "13px 16px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left", color: "var(--jrni-color-text-strong)" }}>
                          <span style={{ flex: "0 0 auto", display: "inline-flex", width: "14px", color: "var(--jrni-color-text-soft)", transform: grp.caret, transition: "transform 120ms ease" }}>▸</span>
                          <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "14px", fontWeight: "600" }}>{grp.label}</span>
                          {grp.hasCustom ? (<>
                            <span style={{ flex: "0 0 auto", width: "7px", height: "7px", borderRadius: "999px", background: "var(--jrni-color-primary-1)" }}></span>
                          </>) : null}
                          <span style={{ flex: "0 0 auto", fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{grp.countLabel}</span>
                        </button>
                        {grp.open ? (<>
                          <div style={{ padding: "4px 16px 16px", display: "flex", flexDirection: "column", gap: "18px", borderTop: "1px solid var(--jrni-color-surface-border)" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "20px", paddingTop: "12px" }}>
                              <span style={{ fontSize: "11px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Current default</span>
                              <span style={{ fontSize: "11px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Your wording</span>
                            </div>
                            {(grp.fields || []).map((fld, __i10) => (<React.Fragment key={fld.key ?? fld.id ?? __i10}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                                  <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "13px", fontWeight: "600" }}>{fld.label}</span>
                                  {fld.changed ? (<>
                                    <Button variant="ghost" size="sm" onClick={fld.onRevert}>Revert
                                    </Button>
                                  </>) : null}
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "20px", alignItems: "start" }}>
                                  <Textarea value={fld.def} rows={2} autoGrow={true} style={{  }} aria-label="Current default" />
                                  <Textarea value={fld.value} onChange={fld.onChange} rows={2} autoGrow={true} placeholder="Leave empty to keep the default" style={{  }} aria-label={fld.label} />
                                </div>
                              </div>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                      </div>
                    </React.Fragment>))}
                  </>) : null}
                  {V.is.launch ? (<>
                    <Section title="Public link">
                      {V.pv.publicUrl ? (<>
                        <CopyableUrl url={V.pv.publicUrl} />
                      </>) : (<>
                        <Banner tone="neutral" variant="explainer" size="compact" title="No address yet">This journey has no public address on the server yet. Save it once and the link appears here.
                        </Banner>
                      </>)}
                    </Section>
                    <Section title="Embed on your own page">
                      <div style={{ display: "flex", flexDirection: "column", gap: "14px", padding: "4px 0 16px" }}>
                        <Field label="Width">
                          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <SegmentedControl options={V.opts.sizeUnit} value={V.s.embedWidthUnit} onChange={V.set.embedWidthUnit} size="sm" aria-label="Width unit" />
                            {V.pv.pxWidth ? (<>
                              <Input type="number" value={V.s.embedWidth} onChange={V.set.embedWidth} trailing="px" style={{  }} />
                            </>) : null}
                          </div>
                        </Field>
                        <Field label="Height" hint={V.pv.resizeHint}>
                          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <SegmentedControl options={V.opts.embedResize} value={V.s.embedResize} onChange={V.set.embedResize} size="sm" aria-label="Height mode" />
                            {V.pv.fixedHeight ? (<>
                              <Input type="number" value={V.s.embedHeight} onChange={V.set.embedHeight} trailing="px" style={{  }} />
                            </>) : null}
                          </div>
                        </Field>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <SectionLabel tone="soft">Paste this where the journey should appear
                          </SectionLabel>
                          <div style={{ flex: "1 1 auto" }}></div>
                          <Button variant="secondary" size="sm" iconLeft={V.icons.copy} onClick={V.actions.copyEmbed} disabled={!V.pv.publicUrl}>{V.pv.copyLabel}
                          </Button>
                        </div>
                        {V.pv.publicUrl ? (<>
                          <pre style={{ margin: "0", padding: "14px", borderRadius: "var(--jrni-radius-sm)", background: "var(--jrni-color-neutral-1)", color: "var(--jrni-color-base-white)", fontFamily: "var(--jrni-font-family-mono)", fontSize: "12px", lineHeight: "1.6", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{V.pv.embedCode}</pre>
                          <p style={{ margin: "0", fontSize: "12px", lineHeight: "1.5", color: "var(--jrni-color-text-soft)", textWrap: "pretty" }}>Includes every permission the journey can ask for and a strict sandbox allowlist. Delete any line your page does not need.</p>
                        </>) : (<>
                          <Banner tone="neutral" variant="explainer" size="compact" title="No address yet">This journey has no public address on the server yet, so there is no embed code to copy. Save it once and the code appears here.
                          </Banner>
                        </>)}
                      </div>
                    </Section>
                  </>) : null}
                </div>
              </div>
            </>) : null}
            <section style={{ flex: "1 1 620px", minWidth: this.isPublic() ? "0" : "440px", display: "flex", flexDirection: "column", minHeight: "0", background: "var(--jrni-color-neutral-6)" }}>
              {V.pv.chrome ? (<>
                <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "12px", rowGap: "8px", flexWrap: "wrap", padding: "10px 16px", borderBottom: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card)" }}>
                  <Button variant="ghost" size="sm" iconOnly={V.icons.panel} aria-label={V.pv.paneLabel} onClick={V.actions.togglePane} />
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{V.pv.stepTitle}</span>
                  <div style={{ flex: "1 1 auto" }}></div>
                  <Button variant="secondary" size="sm" onClick={V.actions.prevStep} disabled={V.pv.atFirstStep}>Back
                  </Button>
                  <Button hierarchy="primary" size="sm" onClick={V.actions.nextStep} disabled={V.pv.atLastStep}>Next
                  </Button>
                  <div style={{ width: "1px", height: "24px", background: "var(--jrni-color-surface-border)" }}></div>
                  <DevicePreviewToggle value={V.ui.device} onChange={V.actions.setDevice} size="sm" />
                </div>
              </>) : null}
              <div className="apd-scroll" style={{ flex: "1 1 auto", overflow: "auto", padding: "12px", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
                {/* Walk-Ins draws through the SAME component the standalone
                    join page mounts (`WalkInJourney`, also used by
                    public-walkins.tsx), so the designer preview and the
                    customer page are one implementation rather than two that
                    have to be kept looking alike. `preview` is passed
                    explicitly and is NOT `mode`: this preview mounts the
                    Designer in `mode: "public"` for the customer chrome, so a
                    component that read `mode` would treat every designer
                    preview as a real customer and post joins into the
                    tenant's own queue. */}
                {V.is.queue ? (
                  <WalkInJourney
                    preview={true}
                    embedded={true}
                    frameWidth={V.pv.frameWidth}
                    slug={String(V.s.slug || WALKIN_DEFAULT_SLUG)}
                    config={V.pv.walkInConfig}
                    customText={V.pv.walkInText}
                    presentation={V.pv.walkInPresentation}
                    queues={V.pv.walkInQueues}
                    loading={V.pv.walkInQueuesLoading}
                    error={V.pv.walkInError}
                    step={V.pv.walkInStep}
                    onStepChange={V.actions.setWalkInStep}
                  />
                ) : (
                <div data-testid="appointment-journey" data-mode={V.pv.rootMode} style={{ width: "100%", maxWidth: V.pv.frameWidth, minHeight: "100%", containerType: "inline-size", containerName: "journey-preview", display: "flex", flexDirection: "column", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-lg)", boxShadow: "var(--jrni-shadow-lg)", overflow: "hidden" }}>
                  {V.pv.brandHeader ? (<>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", rowGap: "8px", flexWrap: "wrap", padding: "14px 20px", borderBottom: "1px solid var(--jrni-color-surface-border)", background: V.pv.brand }}>
                      <span style={{ fontSize: "15px", fontWeight: "700", color: "#FFFFFF", letterSpacing: "-0.01em" }}>{this.props.tenantName || this.state.apptJourney?.brand?.logoText || "This workspace"}</span>
                      <div style={{ flex: "1 1 auto" }}></div>
                      {V.pv.showPageTitle ? (<>
                        <span style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.85)" }}>{V.pv.pageTitle}</span>
                      </>) : null}
                    </div>
                  </>) : null}
                  {V.pv.progressTitles ? (<>
                    <div className="apd-scroll" style={{ display: "flex", gap: "28px", padding: "16px 24px 0", overflowX: "auto", whiteSpace: "nowrap" }}>
                      {(V.pv.steps || []).map((st, __i11) => (<React.Fragment key={st.key ?? st.id ?? __i11}>
                        <button type="button" onClick={st.onClick} style={{ background: "none", border: "0", padding: "0 0 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "13px", textAlign: "left", borderBottom: `2px solid ${st.underline}`, color: st.fg, fontWeight: st.weight }}>{st.label}</button>
                      </React.Fragment>))}
                    </div>
                  </>) : null}
                  {V.pv.progressNumbers ? (<>
                    <div className="apd-scroll" style={{ display: "flex", alignItems: "center", gap: "10px", padding: "16px 24px 4px", overflowX: "auto", whiteSpace: "nowrap" }}>
                      {(V.pv.steps || []).map((st, __i12) => (<React.Fragment key={st.key ?? st.id ?? __i12}>
                        <button type="button" onClick={st.onClick} style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "0", padding: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "13px", color: st.fg, fontWeight: st.weight }}>
                          <span style={{ width: "22px", height: "22px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", background: st.numBg, color: st.numFg }}>{st.num}</span>{st.label}
                        </button>
                        {st.connector ? (<>
                          <span aria-hidden="true" style={{ flex: "0 0 24px", height: "2px", borderRadius: "999px", background: st.connector }}></span>
                        </>) : null}
                      </React.Fragment>))}
                    </div>
                  </>) : null}
                  {V.pv.progressBar ? (<>
                    <div style={{ padding: "16px 24px 4px" }}>
                      <div style={{ height: "6px", borderRadius: "999px", background: "var(--jrni-color-neutral-5)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: V.pv.progressPct, background: V.pv.brand }}></div>
                      </div>
                    </div>
                  </>) : null}
                  <div style={{ flex: "1 1 auto", display: "flex", alignItems: "flex-start", gap: "20px", padding: "22px 24px 28px", flexWrap: "wrap", flexDirection: V.pv.bodyDir }}>
                    <div style={{ flex: "1 1 620px", minWidth: "0", display: "flex", flexDirection: "column", gap: "18px" }}>
                      {V.pv.isKioskEntry ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                          {V.pv.kioskShowGreeting ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <h2 style={{ margin: "0", fontSize: V.pv.kioskTitleSize, fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.1" }}>{V.pv.kioskGreeting}</h2>
                              <p style={{ margin: "0", fontSize: V.pv.kioskSubSize, color: "var(--jrni-color-text-body)" }}>{V.pv.kioskSub}</p>
                            </div>
                          </>) : null}
                          <div style={{ display: "grid", gridTemplateColumns: V.pv.kioskTileCols, gap: "16px" }}>
                            {(V.pv.kioskTiles || []).map((tile, __i13) => (<React.Fragment key={tile.key ?? tile.id ?? __i13}>
                              <button type="button" onClick={tile.onClick} style={{ display: "flex", flexDirection: V.pv.kioskTileDir, alignItems: V.pv.kioskTileAlign, gap: V.pv.kioskTileGap, minHeight: V.pv.kioskTileMinH, padding: V.pv.kioskTilePad, border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-lg)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)", cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "var(--jrni-color-text-strong)" }}>
                                <span style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center", width: V.pv.kioskIconBox, height: V.pv.kioskIconBox, borderRadius: "var(--jrni-radius-md)", background: tile.hue, color: "#FFFFFF" }}>{tile.icon}</span>
                                {V.pv.kioskTileRoomy ? (<>
                                  <span style={{ fontSize: V.pv.kioskTileTitleSize, fontWeight: "700", lineHeight: "1.2" }}>{tile.title}</span>
                                  <span style={{ fontSize: V.pv.kioskTileDescSize, color: "var(--jrni-color-text-body)", lineHeight: "1.4" }}>{tile.desc}</span>
                                  {tile.meta ? (<>
                                    <span style={{ marginTop: "auto", fontSize: V.pv.kioskTileDescSize, fontWeight: "600", color: tile.hue }}>{tile.meta}</span>
                                  </>) : null}
                                </>) : null}
                                {V.pv.kioskTileCompact ? (<>
                                  <span style={{ flex: "1 1 auto", minWidth: "0", display: "flex", flexDirection: "column", gap: "2px" }}>
                                    <span style={{ fontSize: V.pv.kioskTileTitleSize, fontWeight: "700", lineHeight: "1.25" }}>{tile.title}</span>
                                    {tile.meta ? (<>
                                      <span style={{ fontSize: V.pv.kioskTileDescSize, fontWeight: "600", color: tile.hue }}>{tile.meta}</span>
                                    </>) : null}
                                  </span>
                                </>) : null}
                              </button>
                            </React.Fragment>))}
                          </div>
                          {V.pv.kioskShowStrip ? (<>
                            <div style={{ display: "flex", alignItems: "stretch", gap: "0", flexWrap: "wrap", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)", overflow: "hidden" }}>
                              {V.pv.kioskWait ? (<>
                                <div style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: "3px", padding: "14px 18px" }}>
                                  <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Walk-in wait</span>
                                  <span style={{ fontSize: V.pv.kioskBodySize, fontWeight: "700" }}>About 12 min · 5 ahead</span>
                                </div>
                              </>) : null}
                              {V.pv.kioskNextSlot ? (<>
                                <div style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: "3px", padding: "14px 18px", borderLeft: "1px solid var(--jrni-color-surface-border)" }}>
                                  <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Next appointment</span>
                                  <span style={{ fontSize: V.pv.kioskBodySize, fontWeight: "700" }}>Today, 2:30 PM</span>
                                </div>
                              </>) : null}
                              {V.pv.kioskHours ? (<>
                                <div style={{ flex: "1 1 240px", display: "flex", flexDirection: "column", gap: "3px", padding: "14px 18px", borderLeft: "1px solid var(--jrni-color-surface-border)" }}>
                                  <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Open today</span>
                                  <span style={{ fontSize: V.pv.kioskBodySize, fontWeight: "700" }}>8:00 AM – 6:00 PM</span>
                                  <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>48 Warwick St, Fairmount Hall</span>
                                </div>
                              </>) : null}
                            </div>
                          </>) : null}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "14px", flexWrap: "wrap", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-surface-border)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingTop: "12px" }}>
                              {V.pv.kioskBigTextToggle ? (<>
                                <Button variant="outline" size="md" onClick={V.actions.toggleBigText}>{V.pv.bigTextLabel}
                                </Button>
                              </>) : null}
                            </div>
                          </div>
                        </div>
                      </>) : null}
                      {V.pv.isKioskCheckin ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", flexWrap: "wrap" }}>
                            <div style={{ flex: "1 1 320px", minWidth: "0", display: "flex", flexDirection: "column", gap: "5px" }}>
                              <h2 style={{ margin: "0", fontSize: V.pv.kioskTitleSize, fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.15" }}>{V.pv.ckHeader}</h2>
                            </div>
                            <Button variant="ghost" size="md" onClick={V.actions.kioskReset}>Start over
                            </Button>
                          </div>
                          {V.pv.ckAnyMethod ? (<>
                            <SegmentedControl options={V.pv.ckMethods} value={V.pv.ckMethodActive} onChange={V.actions.setCkMethod} aria-label="How to check in" />
                          </>) : null}
                          {V.pv.ckScanMode ? (<>
                            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "stretch" }}>
                              <div style={{ flex: "1 1 300px", minWidth: "260px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "14px", minHeight: "260px", padding: "24px", border: "2px dashed var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-lg)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "84px", height: "84px", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", border: "1px solid var(--jrni-color-surface-border)", color: "var(--jrni-color-primary-1)" }}>{V.icons.qrLarge}</span>
                                <span style={{ fontSize: V.pv.kioskBodySize, fontWeight: "600", textAlign: "center", maxWidth: "24ch", textWrap: "pretty" }}>{V.pv.ckScanLine}</span>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>
                                  <span style={{ width: "8px", height: "8px", borderRadius: "999px", background: "var(--jrni-color-semantic-green-1)", animation: "apd-pulse 1.6s ease-in-out infinite" }}></span>Reader ready
                                </span>
                                {V.pv.ckShowWalletHint ? (<>
                                  <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)", textAlign: "center", maxWidth: "30ch", textWrap: "pretty" }}>{V.pv.ckWalletHint}</span>
                                </>) : null}
                              </div>
                              <div style={{ flex: "1 1 260px", minWidth: "240px", display: "flex", flexDirection: "column", gap: "12px", justifyContent: "center" }}>
                                <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Simulate a scan</span>
                                <Button hierarchy="primary" size="lg" onClick={V.actions.kioskCheckInNow}>Simulate a successful scan
                                </Button>
                                <Button variant="outline" size="md" onClick={V.actions.kioskScanFail}>Scan something unreadable
                                </Button>
                                <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)", textWrap: "pretty" }}>Preview only — the two buttons stand in for the reader so both outcomes can be seen.</span>
                              </div>
                            </div>
                          </>) : null}
                          {V.pv.ckRefMode ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "14px", maxWidth: "520px" }}>
                              <h3 style={{ margin: "0", fontSize: V.pv.kioskBodySize, fontWeight: "700" }}>{V.pv.ckRefHeader}</h3>
                              <Input value={V.pv.ckRefValue} onChange={V.actions.setCkRef} onFocus={V.actions.openKeys} inputmode={V.pv.ckInputMode} placeholder="Booking reference" size="lg" style={{  }} aria-label="Booking reference" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>It's at the top of your confirmation email.</span>
                              <div>
                                <Button variant="outline" size="md" onClick={V.actions.kioskLookupRef} disabled={!V.pv.ckCanLookUp}>{V.pv.ckLooking ? "Looking up…" : "Find my booking"}
                                </Button>
                              </div>
                              {V.pv.ckHasRefMatches ? (<>
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                  {(V.pv.ckRefMatches || []).map((m, __i14) => (<React.Fragment key={m.key ?? m.id ?? __i14}>
                                    <button type="button" onClick={m.onPick} style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "14px 16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "var(--jrni-color-text-strong)" }}>
                                      <span style={{ fontSize: "15px", fontWeight: "700" }}>{m.name} · {m.ref}</span>
                                      <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{m.svcWhen}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                              {V.pv.ckNoRefMatch ? (<>
                                <span style={{ fontSize: "14px", color: "var(--jrni-color-semantic-orange-1)", fontWeight: "600" }}>No booking was found for that reference. Check it against your email, or ask at the welcome desk.</span>
                              </>) : null}
                            </div>
                          </>) : null}
                          {V.pv.ckNameMode ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "14px", maxWidth: "520px" }}>
                              <h3 style={{ margin: "0", fontSize: V.pv.kioskBodySize, fontWeight: "700" }}>{V.pv.ckNameHeader}</h3>
                              <Input value={V.pv.ckNameValue} onChange={V.actions.setCkName} onFocus={V.actions.openKeys} inputmode={V.pv.ckInputMode} placeholder="Start typing your last name" leading={V.icons.search} size="lg" style={{  }} aria-label="Your name" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Only today's bookings at this campus are searched, and nothing is listed until you type.</span>
                              {V.pv.ckHasNameMatches ? (<>
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                  {(V.pv.ckNameMatches || []).map((m, __i15) => (<React.Fragment key={m.key ?? m.id ?? __i15}>
                                    <button type="button" onClick={m.onPick} style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "14px 16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "var(--jrni-color-text-strong)" }}>
                                      <span style={{ fontSize: "15px", fontWeight: "700" }}>{m.name}</span>
                                      <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{m.svcWhen}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                              {V.pv.ckNoNameMatch ? (<>
                                <span style={{ fontSize: "14px", color: "var(--jrni-color-semantic-orange-1)", fontWeight: "600" }}>Finding a booking by name isn't available on this kiosk yet. Use your booking reference, or ask at the welcome desk.</span>
                              </>) : null}
                            </div>
                          </>) : null}
                          {V.pv.ckNativeKeyboardNote ? (<>
                            <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)", textWrap: "pretty" }}>{V.pv.ckNativeKeyboardNote}</span>
                          </>) : null}
                          {V.pv.ckKeysOpen ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-lg)", background: "var(--jrni-color-surface-card-soft)", boxShadow: "var(--jrni-shadow-sm)" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>{V.pv.ckKeyboardLabel}</span>
                                <Button variant="ghost" size="sm" onClick={V.actions.closeKeys}>Close
                                </Button>
                              </div>
                              {V.pv.ckQwertyMode ? (<>
                                <div style={{ display: "flex", flexDirection: "column", gap: "7px", alignItems: "center" }}>
                                  {V.pv.ckNumberRow ? (<>
                                    <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                                      {(V.pv.ckDigitKeys || []).map((k, __i16) => (<React.Fragment key={k.key ?? k.id ?? __i16}>
                                        <button type="button" onClick={k.onTap} style={{ flex: "1 1 0", minWidth: "32px", maxWidth: "52px", height: "46px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "16px", fontWeight: "700", color: "var(--jrni-color-text-strong)", cursor: "pointer" }}>{k.ch}</button>
                                      </React.Fragment>))}
                                    </div>
                                  </>) : null}
                                  {(V.pv.ckQwertyRows || []).map((row, __i17) => (<React.Fragment key={row.key ?? row.id ?? __i17}>
                                    <div style={{ display: "flex", gap: "6px", justifyContent: "center", flexWrap: "nowrap" }}>
                                      {(row.keys || []).map((k, __i18) => (<React.Fragment key={k.key ?? k.id ?? __i18}>
                                        <button type="button" onClick={k.onTap} style={{ flex: "1 1 0", minWidth: "34px", maxWidth: "56px", height: "52px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "17px", fontWeight: "700", color: "var(--jrni-color-text-strong)", cursor: "pointer" }}>{k.ch}</button>
                                      </React.Fragment>))}
                                    </div>
                                  </React.Fragment>))}
                                  {V.pv.ckSpaceKey ? (<>
                                    <div style={{ display: "flex", gap: "6px", justifyContent: "center", width: "100%" }}>
                                      <button type="button" onClick={V.actions.keySpace} style={{ flex: "1 1 auto", maxWidth: "240px", height: "52px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-body)", cursor: "pointer" }}>space</button>
                                    </div>
                                  </>) : null}
                                </div>
                              </>) : null}
                              <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                <button type="button" onClick={V.actions.keyBack} style={{ height: "48px", padding: "0 22px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-body)", cursor: "pointer" }}>Delete</button>
                                <button type="button" onClick={V.actions.keyClear} style={{ height: "48px", padding: "0 22px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-body)", cursor: "pointer" }}>Clear</button>
                              </div>
                            </div>
                          </>) : null}
                          {V.pv.ckNotScanMode ? (<>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-surface-border)" }}>
                              <span style={{ paddingTop: "14px", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Found yourself above?</span>
                              <span style={{ paddingTop: "14px" }}>
                                <Button hierarchy="primary" size="md" onClick={V.actions.kioskCheckInNow} disabled={!V.pv.ckHasBooking}>{V.pv.ckHasBooking ? "Check in as " + V.pv.ckPickedName : "Check in"}
                                </Button>
                              </span>
                            </div>
                          </>) : null}
                        </div>
                      </>) : null}
                      {V.pv.isKioskProblem ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "20px", maxWidth: "620px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", padding: "22px 24px", borderRadius: "var(--jrni-radius-lg)", background: V.pv.ckProbTint, border: `1px solid ${V.pv.ckProbTone}` }}>
                            <span style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center", width: "46px", height: "46px", borderRadius: "999px", background: V.pv.ckProbTone, color: "#FFFFFF" }}>{V.icons.warnLarge}</span>
                            <div style={{ flex: "1 1 auto", minWidth: "0", display: "flex", flexDirection: "column", gap: "7px" }}>
                              <h2 style={{ margin: "0", fontSize: "24px", fontWeight: "700", letterSpacing: "-0.01em", lineHeight: "1.2" }}>{V.pv.ckProbTitle}</h2>
                              <p style={{ margin: "0", fontSize: V.pv.kioskBodySize, lineHeight: "1.5", color: "var(--jrni-color-text-strong)", textWrap: "pretty" }}>{V.pv.ckProbBody}</p>
                            </div>
                          </div>
                          {V.pv.ckProbShowBooking ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px", padding: "16px 18px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                              <span style={{ fontSize: "15px", fontWeight: "700" }}>{V.pv.ckPickedName} · {V.pv.ckPickedRef}</span>
                              <span style={{ fontSize: "14px", color: "var(--jrni-color-text-body)" }}>{V.pv.ckPickedSvcWhen}</span>
                            </div>
                          </>) : null}
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {V.pv.ckProbRecoverable ? (<>
                              <Button hierarchy="primary" size="lg" onClick={V.actions.kioskTryName}>Find my booking by name
                              </Button>
                            </>) : null}
                            <Button hierarchy="primary" size="lg" onClick={V.actions.kioskReset}>Back to the start
                            </Button>
                          </div>
                        </div>
                      </>) : null}
                      {V.pv.isKioskDone ? (<>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "22px", padding: "24px 0", textAlign: "center" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "84px", height: "84px", borderRadius: "999px", background: "var(--jrni-color-semantic-green-1)", color: "#FFFFFF" }}>{V.icons.tickLarge}</span>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <h2 style={{ margin: "0", fontSize: V.pv.kioskTitleSize, fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.15" }}>{V.pv.ckDoneHeader}</h2>
                            <p style={{ margin: "0", fontSize: V.pv.kioskSubSize, color: "var(--jrni-color-text-body)", maxWidth: "40ch", textWrap: "pretty" }}>{V.pv.ckDoneSub}</p>
                          </div>
                          {V.pv.ckHasBooking ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "0", width: "100%", maxWidth: "460px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-lg)", background: "var(--jrni-color-surface-card)", overflow: "hidden", textAlign: "left" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "16px 20px" }}>
                              <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Booking</span>
                              <span style={{ fontSize: "17px", fontWeight: "700" }}>{V.pv.ckPickedSvc}</span>
                              <span style={{ fontSize: "14px", color: "var(--jrni-color-text-body)" }}>{V.pv.ckPickedWhenStaff}</span>
                            </div>
                            {V.pv.ckShowQueuePos ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "16px 20px", borderTop: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>In the line</span>
                                <span style={{ fontSize: "17px", fontWeight: "700" }}>{V.pv.ckQueuePos}</span>
                              </div>
                            </>) : null}
                          </div>
                          </>) : (<>
                            <div style={{ width: "100%", maxWidth: "460px", textAlign: "left" }}>
                              <InlineEmptyState title="No booking looked up" description="Find a booking by its reference to see it here. The preview shows only what a real lookup returns." />
                            </div>
                          </>)}
                          <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Preview only — nobody is checked in from here.</span>
                          <span style={{ fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>{V.pv.ckDwellNote}</span>
                          <Button hierarchy="primary" size="md" onClick={V.actions.kioskReset}>Done
                          </Button>
                        </div>
                      </>) : null}
                      {V.pv.isLinkError ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "18px", maxWidth: "560px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ flex: "0 0 auto", width: "24px", height: "24px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-semantic-orange-2)", color: "var(--jrni-color-semantic-orange-1)", fontSize: "14px", fontWeight: "700" }}>!</span>
                            <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.errTitle}</h2>
                          </div>
                          <p style={{ margin: "0", fontSize: "14px", lineHeight: "1.6", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{V.pv.errBody}</p>
                          {V.pv.errHasRoutes ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                              {V.s.errBookStaff ? (<>
                                <div role="button" tabIndex="0" onClick={V.actions.errStaff} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-chrome)" }}>
                                  <span style={{ flex: "0 0 auto", display: "flex" }}>{V.pv.errStaffTile}</span>
                                  <span style={{ flex: "1 1 auto", minWidth: "0" }}>
                                    <span style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{V.pv.errStaffTitle}</span>
                                    <span style={{ display: "block", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.errStaffSub}</span>
                                  </span>
                                  <span style={{ flex: "0 0 auto", display: "flex", color: "var(--jrni-color-text-soft)" }}>{V.icons.chevron}</span>
                                </div>
                              </>) : null}
                              {V.s.errOtherServices ? (<>
                                <div role="button" tabIndex="0" onClick={V.actions.errServices} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-chrome)" }}>
                                  <span style={{ flex: "0 0 auto", display: "flex" }}>{V.pv.errSvcTile}</span>
                                  <span style={{ flex: "1 1 auto", minWidth: "0" }}>
                                    <span style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{V.pv.errSvcTitle}</span>
                                    <span style={{ display: "block", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.errSvcSub}</span>
                                  </span>
                                  <span style={{ flex: "0 0 auto", display: "flex", color: "var(--jrni-color-text-soft)" }}>{V.icons.chevron}</span>
                                </div>
                              </>) : null}
                            </div>
                          </>) : null}
                          {V.pv.errNoRoutes ? (<>
                            <Banner tone="neutral" variant="explainer" size="compact" title="Nothing to offer">Both recovery options are off, so this page is a dead end. Turn one on in General.
                            </Banner>
                          </>) : null}
                          <div>
                            <Button variant="secondary" onClick={V.actions.errStart}>{V.pv.errStartLabel}
                            </Button>
                          </div>
                        </div>
                      </>) : null}
                      {V.pv.isLandingPage ? (<>
                        {V.pv.tplBannerHero ? (<>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 18px", borderRadius: "var(--jrni-radius-md)", color: "#FFFFFF", background: V.pv.brand }}>
                            <span style={{ flex: "1 1 auto", fontSize: "17px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.lpTitle}</span>
                            <span style={{ flex: "0 0 auto", fontSize: "13px", opacity: "0.9" }}>{V.pv.lpWhen}</span>
                          </div>
                        </>) : null}
                        {V.pv.tplHeroHero ? (<>
                          <div style={{ position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: "6px", padding: "20px", height: V.pv.lpHeroH, borderRadius: "var(--jrni-radius-md)", overflow: "hidden", background: V.pv.lpHeroBg, backgroundSize: "cover", backgroundPosition: "center" }}>
                            <div style={{ position: "absolute", inset: "0", background: V.pv.lpHeroScrim }}></div>
                            <h2 style={{ position: "relative", margin: "0", fontSize: "26px", fontWeight: "700", letterSpacing: "-0.02em", color: "#FFFFFF", textWrap: "pretty" }}>{V.pv.lpTitle}</h2>
                            <div style={{ position: "relative", fontSize: "14px", color: "rgba(255, 255, 255, 0.9)" }}>{V.pv.lpWhen} · {V.pv.lpVenue}</div>
                          </div>
                        </>) : null}
                        {V.pv.tplSplitHero ? (<>
                          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "stretch" }}>
                            <div style={{ flex: "1 1 260px", minHeight: V.pv.lpHeroH, borderRadius: "var(--jrni-radius-md)", background: V.pv.lpHeroBg, backgroundSize: "cover", backgroundPosition: "center" }}></div>
                            <div style={{ flex: "1 1 260px", minWidth: "0", display: "flex", flexDirection: "column", gap: "12px", justifyContent: "center" }}>
                              <h2 style={{ margin: "0", fontSize: "24px", fontWeight: "700", letterSpacing: "-0.02em", textWrap: "pretty" }}>{V.pv.lpTitle}</h2>
                              <div style={{ fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpWhen} · {V.pv.lpVenue}</div>
                              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)" }}>
                                <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpWhen}</div>
                                <div style={{ fontSize: "22px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.lpPrice}</div>
                                {V.pv.lpOpen ? (<>
                                  <button type="button" onClick={V.actions.lpRegister} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", height: "44px", background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.lpCta}</button>
                                  <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpSpacesLabel}</div>
                                </>) : null}
                                {V.pv.lpClosed ? (<>
                                  <Pill tone="neutral">{V.pv.lpClosedLabel}
                                  </Pill>
                                  {V.pv.lpShowWaitlist ? (<>
                                    {V.pv.wlDone ? (<>
                                      <Banner tone="success" size="compact" title="You are on the list">{V.pv.wlNote}
                                      </Banner>
                                    </>) : null}
                                    {V.pv.wlOpen ? (<>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                        <div style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Join the waiting list and we will e-mail you the moment a place opens.</div>
                                        <Input value={V.pv.wlName} onChange={V.actions.setWlName} placeholder="Your name" full={true} />
                                        <Input type="email" value={V.pv.wlEmail} onChange={V.actions.setWlEmail} placeholder="you@example.com" full={true} />
                                        <Button hierarchy="primary" full={true} onClick={V.actions.joinWaitlist}>Join the waiting list
                                        </Button>
                                      </div>
                                    </>) : null}
                                  </>) : null}
                                </>) : null}
                              </div>
                            </div>
                          </div>
                        </>) : null}
                        {V.pv.tplLongHero ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            <h2 style={{ margin: "0", fontSize: "24px", fontWeight: "700", letterSpacing: "-0.02em", textWrap: "pretty" }}>{V.pv.lpTitle}</h2>
                            <div style={{ fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpWhen} · {V.pv.lpVenue}</div>
                          </div>
                        </>) : null}
                        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "flex-start" }}>
                          <div style={{ flex: `1 1 ${V.pv.lpMainW}`, minWidth: "0", display: "flex", flexDirection: "column", gap: "22px" }}>
                            {(V.pv.landingBlocks || []).map((bl, __i19) => (<React.Fragment key={bl.key ?? bl.id ?? __i19}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                {bl.isDesc ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>About this event</h3>
                                  <p style={{ margin: "0", fontSize: "14px", lineHeight: "1.6", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{V.pv.lpDesc}</p>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", paddingTop: "4px" }}>
                                    {(V.pv.lpAgenda || []).map((ag, __i20) => (<React.Fragment key={ag.key ?? ag.id ?? __i20}>
                                      <div style={{ display: "flex", gap: "12px", fontSize: "14px", padding: "6px 0", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                                        <span style={{ flex: "0 0 92px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{ag.time}</span>
                                        <span style={{ flex: "1 1 auto", color: "var(--jrni-color-text-body)" }}>{ag.what}</span>
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isSpeakers ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Who you will hear from</h3>
                                  <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                                    {(V.pv.lpSpeakers || []).map((sp, __i21) => (<React.Fragment key={sp.key ?? sp.id ?? __i21}>
                                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", textAlign: "center" }}>
                                        {sp.hasPhoto ? (<>
                                          <div role="img" aria-label={sp.name} style={{ width: "100%", aspectRatio: "1 / 1", borderRadius: "var(--jrni-radius-md)", backgroundImage: sp.photoCss, backgroundSize: "cover", backgroundPosition: "center" }}></div>
                                        </>) : null}
                                        {sp.noPhoto ? (<>
                                          <div style={{ width: "100%", aspectRatio: "1 / 1", borderRadius: "var(--jrni-radius-md)", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-neutral-6)" }}>
                                            <Avatar name={sp.name} size={56} />
                                          </div>
                                        </>) : null}
                                        <div style={{ minWidth: "0" }}>
                                          <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{sp.name}</div>
                                          <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{sp.role}</div>
                                        </div>
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isIncluded ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>What's included</h3>
                                  <div style={{ display: "grid", gap: "8px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                                    {(V.pv.lpIncluded || []).map((inc, __i22) => (<React.Fragment key={inc.key ?? inc.id ?? __i22}>
                                      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "14px", color: "var(--jrni-color-text-body)" }}>
                                        <span style={{ flex: "0 0 auto", color: "var(--jrni-color-semantic-green-1)", fontWeight: "700" }}>✓</span>
                                        <span>{inc.text}</span>
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isTickets ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Tickets</h3>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    {(V.pv.lpTickets || []).map((tk, __i23) => (<React.Fragment key={tk.key ?? tk.id ?? __i23}>
                                      <div style={{ display: "flex", alignItems: "baseline", gap: "12px", padding: "12px 14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                                        <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                                          <div style={{ fontSize: "14px", fontWeight: "600" }}>{tk.name}</div>
                                          <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{tk.note}</div>
                                        </div>
                                        <div style={{ flex: "0 0 auto", fontSize: "15px", fontWeight: "700" }}>{tk.price}</div>
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                  {V.pv.ticketsEmpty && !this.isPublic() ? (<>
                                    <InlineEmptyState title={V.pv.ticketsEmptyTitle} description={V.pv.ticketsEmptyBody} />
                                  </>) : null}
                                </>) : null}
                                {bl.isSpaces ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Availability</h3>
                                  <div style={{ maxWidth: "320px" }}>
                                    <MeterBar value={V.pv.lpTaken} max={V.pv.lpCap} tone={V.pv.lpMeterTone} label={V.pv.lpSpacesLabel} />
                                  </div>
                                </>) : null}
                                {bl.isMap ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Getting there</h3>
                                  <div style={{ height: "190px", borderRadius: "var(--jrni-radius-md)", border: "1px solid var(--jrni-color-surface-border)", backgroundColor: "#EAEEE6", backgroundImage: "linear-gradient(0deg, rgba(255, 255, 255, 0.65) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.65) 1px, transparent 1px), linear-gradient(120deg, #DFE7DA 0%, #EDF1E8 55%, #E3EAF2 100%)", backgroundSize: "34px 34px, 34px 34px, 100% 100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <div style={{ background: "#FFFFFF", borderRadius: "var(--jrni-radius-md)", boxShadow: "var(--jrni-shadow-md)", padding: "10px 12px", fontSize: "12px", lineHeight: "1.5", maxWidth: "240px" }}>
                                      <div style={{ fontWeight: "700", fontSize: "13px" }}>{V.pv.lpVenue}</div>
                                      <div style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.lpAddr}</div>
                                    </div>
                                  </div>
                                </>) : null}
                                {bl.isFaq ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Questions</h3>
                                  <div style={{ display: "flex", flexDirection: "column" }}>
                                    {(V.pv.lpFaq || []).map((fq, __i24) => (<React.Fragment key={fq.key ?? fq.id ?? __i24}>
                                      <div style={{ borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                                        <button type="button" onClick={fq.onToggle} aria-expanded={fq.open} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", textAlign: "left", padding: "12px 0", background: "none", border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>
                                          <span style={{ flex: "1 1 auto" }}>{fq.q}</span>
                                          <span style={{ flex: "0 0 auto", display: "flex", color: "var(--jrni-color-text-soft)", transform: fq.caret }}>{V.icons.chevron}</span>
                                        </button>
                                        {fq.open ? (<>
                                          <p style={{ margin: "0", padding: "0 0 12px", fontSize: "14px", lineHeight: "1.6", color: "var(--jrni-color-text-body)" }}>{fq.a}</p>
                                        </>) : null}
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isRelated ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>You might also like</h3>
                                  <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                                    {(V.pv.lpRelated || []).map((rl, __i25) => (<React.Fragment key={rl.key ?? rl.id ?? __i25}>
                                      <div role="button" tabIndex="0" onClick={rl.onSelect} style={{ cursor: "pointer", display: "flex", flexDirection: "column", gap: "4px", padding: "12px 14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                                        <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{rl.name}</span>
                                        <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{rl.meta}</span>
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isSponsors ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>With support from</h3>
                                  {(V.pv.lpSponsorGroups || []).map((sg, __i26) => (<React.Fragment key={sg.key ?? sg.id ?? __i26}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                      {sg.showTier ? (<>
                                        <SectionLabel tone="soft">{sg.tier}
                                        </SectionLabel>
                                      </>) : null}
                                      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                                        {(sg.items || []).map((sr, __i27) => (<React.Fragment key={sr.key ?? sr.id ?? __i27}>
                                          <div style={{ flex: "0 0 auto", width: sr.w, height: sr.h, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "var(--jrni-radius-sm)", border: sr.border, backgroundImage: sr.logoCss, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center", fontSize: "12px", fontWeight: "600", color: "var(--jrni-color-text-soft)" }}>{sr.fallback}</div>
                                        </React.Fragment>))}
                                      </div>
                                    </div>
                                  </React.Fragment>))}
                                </>) : null}
                                {bl.isShare ? (<>
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: "14px", fontWeight: "600" }}>Share</span>
                                    {(V.pv.lpShare || []).map((sh, __i28) => (<React.Fragment key={sh.key ?? sh.id ?? __i28}>
                                      <Button variant="secondary" size="sm">{sh.label}
                                      </Button>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                              </div>
                            </React.Fragment>))}
                          </div>
                          {V.pv.lpShowAside ? (<>
                            <aside style={{ flex: "1 1 260px", minWidth: "0", maxWidth: "320px", position: "sticky", top: "0" }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)" }}>
                                <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpWhen}</div>
                                <div style={{ fontSize: "22px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.lpPrice}</div>
                                {V.pv.lpOpen ? (<>
                                  <button type="button" onClick={V.actions.lpRegister} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", height: "44px", background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.lpCta}</button>
                                  <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpSpacesLabel}</div>
                                </>) : null}
                                {V.pv.lpClosed ? (<>
                                  <Pill tone="neutral">{V.pv.lpClosedLabel}
                                  </Pill>
                                  {V.pv.lpShowWaitlist ? (<>
                                    {V.pv.wlDone ? (<>
                                      <Banner tone="success" size="compact" title="You are on the list">{V.pv.wlNote}
                                      </Banner>
                                    </>) : null}
                                    {V.pv.wlOpen ? (<>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                        <div style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Join the waiting list and we will e-mail you the moment a place opens.</div>
                                        <Input value={V.pv.wlName} onChange={V.actions.setWlName} placeholder="Your name" full={true} />
                                        <Input type="email" value={V.pv.wlEmail} onChange={V.actions.setWlEmail} placeholder="you@example.com" full={true} />
                                        <Button hierarchy="primary" full={true} onClick={V.actions.joinWaitlist}>Join the waiting list
                                        </Button>
                                      </div>
                                    </>) : null}
                                  </>) : null}
                                </>) : null}
                              </div>
                            </aside>
                          </>) : null}
                        </div>
                        {V.pv.lpInlineCta ? (<>
                          <div style={{ maxWidth: "360px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)" }}>
                              <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpWhen}</div>
                              <div style={{ fontSize: "22px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.lpPrice}</div>
                              {V.pv.lpOpen ? (<>
                                <button type="button" onClick={V.actions.lpRegister} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", height: "44px", background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.lpCta}</button>
                                <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpSpacesLabel}</div>
                              </>) : null}
                              {V.pv.lpClosed ? (<>
                                <Pill tone="neutral">{V.pv.lpClosedLabel}
                                </Pill>
                                {V.pv.lpShowWaitlist ? (<>
                                  {V.pv.wlDone ? (<>
                                    <Banner tone="success" size="compact" title="You are on the list">{V.pv.wlNote}
                                    </Banner>
                                  </>) : null}
                                  {V.pv.wlOpen ? (<>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                      <div style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Join the waiting list and we will e-mail you the moment a place opens.</div>
                                      <Input value={V.pv.wlName} onChange={V.actions.setWlName} placeholder="Your name" full={true} />
                                      <Input type="email" value={V.pv.wlEmail} onChange={V.actions.setWlEmail} placeholder="you@example.com" full={true} />
                                      <Button hierarchy="primary" full={true} onClick={V.actions.joinWaitlist}>Join the waiting list
                                      </Button>
                                    </div>
                                  </>) : null}
                                </>) : null}
                              </>) : null}
                            </div>
                          </div>
                        </>) : null}
                        {V.s.stickyBar ? (<>
                          <div style={{ position: "sticky", bottom: "0", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", margin: "8px -24px -28px", padding: "12px 24px", borderTop: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-base-white)", boxShadow: "0 -6px 18px rgba(22, 34, 51, 0.08)" }}>
                            <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                              <div style={{ fontSize: "14px", fontWeight: "700", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{V.pv.lpTitle}</div>
                              <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpPrice} · {V.pv.lpSpacesLabel}</div>
                            </div>
                            {V.pv.lpOpen ? (<>
                              <button type="button" onClick={V.actions.lpRegister} style={{ flex: "0 0 auto", border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "#FFFFFF", padding: "0 22px", height: "40px", background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.lpCta}</button>
                            </>) : null}
                            {V.pv.lpClosed ? (<>
                              <Pill tone="neutral">{V.pv.lpClosedLabel}
                              </Pill>
                            </>) : null}
                          </div>
                        </>) : null}
                      </>) : null}
                      {V.pv.isEvents ? (<>
                        <div style={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap" }}>
                          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.eventsHeader}</h2>
                          <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.eventShownLabel}</span>
                        </div>
                        {V.pv.showFilterBar ? (<>
                          <div style={{ position: "relative" }}>
                            <Field label="Event name">
                              <Input leading={V.icons.search} value={V.pv.evtQuery} onChange={V.actions.setEvtQuery} placeholder="Start typing an event name" full={true} />
                            </Field>
                            {V.pv.hasEvtMatches ? (<>
                              <div role="listbox" aria-label="Matching events" style={{ marginTop: "4px", maxHeight: "232px", overflowY: "auto", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                {(V.pv.evtMatches || []).map((em, __i29) => (<React.Fragment key={em.key ?? em.id ?? __i29}>
                                  <button type="button" role="option" aria-selected={false} onClick={em.onPick} style={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", textAlign: "left", padding: "7px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                                    <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{em.name}</span>
                                    <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{em.meta}</span>
                                  </button>
                                </React.Fragment>))}
                              </div>
                            </>) : null}
                          </div>
                          <div className="apd-evt-filters">
                            {V.s.filterLocation ? (<>
                              <div style={{ minWidth: "0" }}>
                                {/* A real filter, not a search box: the options are the read
                                    door's own location facet ({id, name}) and the picked id goes
                                    straight through `setEvtLocationFilter` → the list refresh's
                                    `locationId` param. The "use my location" pin that used to sit
                                    beside it is gone: there is no geosearch endpoint on this door
                                    (no lat/lng/radius param anywhere in the public event reads
                                    contract), so it could only ever have been decorative. */}
                                <Field label="Location">
                                  <Dropdown aria-label="Location" options={V.pv.locationOptions} value={V.pv.evtLocationValue}
                                    onChange={V.actions.setEvtLocationFilter}
                                    placeholder={V.pv.hasLocationOptions ? "All locations" : "No locations"}
                                    disabled={!V.pv.hasLocationOptions} />
                                </Field>
                              </div>
                            </>) : null}
                            {V.s.filterType ? (<>
                              <div style={{ minWidth: "0" }}>
                                <Field label="Type">
                                  <Dropdown options={V.pv.typeOptions} value={V.pv.eventFilterValue} onChange={V.actions.setEvtCat} />
                                </Field>
                              </div>
                            </>) : null}
                            {V.s.filterDates ? (<>
                              <div style={{ minWidth: "0" }}>
                                <DatePicker label="Start date" value={V.pv.evtFrom} onChange={V.actions.setEvtFrom} showTime={false} allowPast={true} leading={V.icons.calSm} full={true} />
                              </div>
                            </>) : null}
                            {V.s.filterDates ? (<>
                              <div style={{ minWidth: "0" }}>
                                <DatePicker label="End date" value={V.pv.evtTo} onChange={V.actions.setEvtTo} showTime={false} allowPast={true} leading={V.icons.calSm} full={true} />
                              </div>
                            </>) : null}
                            {/* Was a raw <button>: no focus ring, no hover/active treatment, and
                                the tenant's PREVIEW radius (V.pv.radius) sitting beside four
                                design-system controls that all read --jrni-radius-default. The
                                shared Button carries the state layer (tokens.css paints
                                hover/active as `filter: brightness()`, so the brand surface below
                                survives both) and the canonical `:focus-visible` ring. The brand
                                FILL is kept — this is the customer-facing preview, where the row
                                must read as the tenant's — while the RADIUS follows the row,
                                because its four neighbours are DS controls that cannot take a
                                per-tenant radius at all. Height/padding are the row's shared
                                control geometry; no handler is added, this control was and stays
                                inert in the preview. */}
                            <Button type="button" hierarchy="primary" className="apd-evt-search"
                              style={{ height: "var(--jrni-control-height-md)", padding: "0 22px", background: V.pv.brand, borderRadius: "var(--jrni-radius-control)" }}>Search events</Button>
                          </div>
                        </>) : null}
                        {V.s.eventFilters ? (<>
                          <ChipToggleGroup options={V.opts.eventFilterChips} value={V.pv.eventFilterValue} onChange={V.actions.setEvtCat} size="sm" aria-label="Event categories" />
                        </>) : null}
                        {V.pv.eventEmpty ? (<>
                          <InlineEmptyState title={V.pv.eventEmptyText} description="Change the filters, or allow full events back onto the list." />
                        </>) : null}
                        {V.is.evList ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {(V.pv.eventCards || []).map((ev, __i30) => (<React.Fragment key={ev.key ?? ev.id ?? __i30}>
                              <div style={{ display: "flex", gap: "14px", alignItems: "center", padding: "12px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", opacity: ev.dim }}>
                                {V.s.eventImages ? (<>
                                  <div style={{ flex: "0 0 auto" }}>{ev.cover}</div>
                                </>) : null}
                                <div style={{ flex: "1 1 auto", minWidth: "0", display: "flex", flexDirection: "column", gap: "4px" }}>
                                  <div style={{ fontSize: "15px", fontWeight: "700", letterSpacing: "-0.01em" }}>{ev.name}</div>
                                  <div style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>{ev.sub}</div>
                                  {ev.showMeter ? (<>
                                    <div style={{ maxWidth: "220px", paddingTop: "2px" }}>
                                      <MeterBar value={ev.taken} max={ev.cap} tone={ev.meterTone} label={ev.spaces} size="sm" />
                                    </div>
                                  </>) : null}
                                </div>
                                <div style={{ flex: "0 0 auto" }}>
                                  {ev.bookable ? (<>
                                    <button type="button" onClick={ev.onSelect} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "#FFFFFF", padding: "0 18px", height: "40px", background: V.pv.brand, borderRadius: V.pv.radius }}>Register</button>
                                  </>) : null}
                                  {ev.showWaitlist ? (<>
                                    <Button variant="secondary" onClick={ev.onSelect}>{ev.cta}
                                    </Button>
                                  </>) : null}
                                  {ev.closed ? (<>
                                    <Pill tone="neutral">{ev.spaces}
                                    </Pill>
                                  </>) : null}
                                </div>
                              </div>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.is.evGrid ? (<>
                          <div style={{ display: "grid", gap: "28px 24px", gridTemplateColumns: V.pv.cardGrid }}>
                            {(V.pv.eventCards || []).map((ev, __i31) => (<React.Fragment key={ev.key ?? ev.id ?? __i31}>
                              <div role={ev.interactive ? "button" : undefined} tabIndex={ev.interactive ? 0 : undefined} aria-disabled={ev.interactive ? undefined : true} aria-pressed={ev.interactive ? ev.selected : undefined} onClick={ev.interactive ? ev.onSelect : undefined} style={{ display: "flex", flexDirection: "column", gap: "12px", minWidth: "0", height: "100%", cursor: ev.interactive ? "pointer" : "default", opacity: ev.dim }}>
                                {V.s.eventImages ? (<>
                                  <div style={{ width: "100%", overflow: "hidden", borderRadius: V.pv.radius }}>{ev.wide}</div>
                                </>) : null}
                                <h3 style={{ margin: "0", fontSize: "19px", fontWeight: "600", lineHeight: "1.28", letterSpacing: "-0.01em", textWrap: "pretty" }}>{ev.name}</h3>
                                {ev.showDesc ? (<>
                                  <p style={{ margin: "0", fontSize: "14px", lineHeight: "1.5", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{ev.desc}</p>
                                </>) : null}
                                <div style={{ display: "flex", flexDirection: "column", gap: "7px", fontSize: "13px", color: "var(--jrni-color-text-strong)" }}>
                                  {ev.showAddr ? (<>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                                      <span style={{ flex: "0 0 16px", display: "flex", paddingTop: "2px", color: "var(--jrni-color-text-soft)" }}>{V.icons.pinSm}</span>
                                      <span style={{ flex: "1 1 auto", minWidth: "0" }}>{ev.addr}</span>
                                    </div>
                                  </>) : null}
                                  {ev.showDate ? (<>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                                      <span style={{ flex: "0 0 16px", display: "flex", paddingTop: "2px", color: "var(--jrni-color-text-soft)" }}>{V.icons.calSm}</span>
                                      <span style={{ flex: "1 1 auto", minWidth: "0" }}>{ev.date}</span>
                                    </div>
                                  </>) : null}
                                  {ev.showTime ? (<>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                                      <span style={{ flex: "0 0 16px", display: "flex", paddingTop: "2px", color: "var(--jrni-color-text-soft)" }}>{V.icons.clockSm}</span>
                                      <span style={{ flex: "1 1 auto", minWidth: "0" }}>{ev.time}</span>
                                    </div>
                                  </>) : null}
                                  {ev.showPrice ? (<>
                                    <div style={{ fontWeight: "600" }}>{ev.priceLine}</div>
                                  </>) : null}
                                </div>
                                {ev.showMeter ? (<>
                                  <MeterBar value={ev.taken} max={ev.cap} tone={ev.meterTone} label={ev.spaces} size="sm" />
                                </>) : null}
                                {/* marginTop:auto pins the CTA to the card's bottom edge. Grid rows already
                                    stretch every card to the tallest in the row, but the blocks inside were
                                    top-stacked, so a two-line description left its Register button sitting
                                    higher than its neighbours'. With the card at height:100% the auto margin
                                    absorbs the slack, and the CTAs line up across the row. */}
                                <div style={{ paddingTop: "2px", marginTop: "auto" }}>
                                  {ev.bookable ? (<>
                                    <button type="button" onClick={ev.onSelect} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "#FFFFFF", padding: "0 26px", height: "42px", background: V.pv.brand, borderRadius: V.pv.radius }}>Register now</button>
                                  </>) : null}
                                  {ev.showWaitlist ? (<>
                                    <Button variant="secondary" onClick={ev.onSelect}>{ev.cta}
                                    </Button>
                                  </>) : null}
                                  {ev.closed ? (<>
                                    <Pill tone="neutral">{ev.spaces}
                                    </Pill>
                                  </>) : null}
                                </div>
                              </div>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.pv.showPager ? (<>
                          <div style={{ display: "flex", justifyContent: "center", paddingTop: "6px" }}>
                            <Pagination totalPages={V.pv.eventPages} currentPage={V.pv.eventPage} onPageChange={V.actions.setEvtPage} showStatus={true} size="sm" />
                          </div>
                        </>) : null}
                        {V.pv.showLoadMoreEvents ? (<>
                          <Button variant="secondary" full={true} onClick={V.actions.moreEvents}>Load more events
                          </Button>
                        </>) : null}
                        {V.pv.showScrollSentinel ? (<>
                          <div ref={(el) => this.attachScrollSentinel(el)} data-testid="events-scroll-sentinel" style={{ height: "1px" }} />
                        </>) : null}
                      </>) : null}
                      {V.pv.isService ? (<>
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.serviceHeader}</h2>
                        {V.pv.serviceCatalogEmpty ? (<>
                          <div role="status" style={{ border: "1px dashed var(--jrni-color-surface-border)", borderRadius: V.pv.radius, padding: "18px 16px", color: "var(--jrni-color-text-body)", background: "var(--jrni-color-surface-card)" }}>{V.pv.serviceCatalogEmptyText}</div>
                        </>) : null}
                        {V.s.showCategories ? (<>
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            {(V.pv.categories || []).map((c, __i32) => (<React.Fragment key={c.key ?? c.id ?? __i32}>
                              <Chip selected={c.selected} onClick={c.onClick}>{c.label}
                              </Chip>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.pv.serviceGrid ? (<>
                          <div style={{ display: "grid", gap: "16px", gridTemplateColumns: V.pv.serviceCols }}>
                            {(V.pv.visibleServices || []).map((sv, __i33) => (<React.Fragment key={sv.key ?? sv.id ?? __i33}>
                              <div role="button" tabIndex="0" aria-pressed={sv.selected} onClick={sv.onSelect} style={{ display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden", background: "var(--jrni-color-surface-card)", border: `1px solid ${sv.border}`, boxShadow: sv.shadow, borderRadius: V.pv.radius }}>
                                {V.s.showServiceImages ? (<>
                                  <div style={{ width: "100%" }}>{sv.cover}</div>
                                </>) : null}
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "14px 16px 16px" }}>
                                  <div style={{ fontSize: "15px", fontWeight: "700", lineHeight: "1.3", letterSpacing: "-0.01em" }}>{sv.name}</div>
                                  {sv.hasMeta ? (<>
                                    <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-primary-text)" }}>{sv.meta}</div>
                                  </>) : null}
                                  <div style={{ fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{sv.desc}</div>
                                </div>
                              </div>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.pv.serviceRows ? (<>
                          <div style={{ display: "grid", gap: "10px", gridTemplateColumns: "minmax(0, 1fr)" }}>
                            {(V.pv.visibleServices || []).map((sv, __i34) => (<React.Fragment key={sv.key ?? sv.id ?? __i34}>
                              <ChoiceTile showCheck={false} label={sv.name} sublabel={sv.sub} leading={sv.leading} trailing={sv.trailing} selected={sv.selected} onSelect={sv.onSelect} full={true} />
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.pv.showDurations ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.durationHeader}</h3>
                            <div style={{ display: "grid", gap: "10px", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                              {(V.pv.durations || []).map((d, __i35) => (<React.Fragment key={d.key ?? d.id ?? __i35}>
                                <ChoiceTile showCheck={false} label={d.label} sublabel={d.sub} selected={d.selected} onSelect={d.onSelect} full={true} />
                              </React.Fragment>))}
                            </div>
                          </div>
                        </>) : null}
                      </>) : null}
                      {V.pv.isMapOnly ? (<>
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.locationHeader}</h2>
                        <div style={{ position: "relative", height: V.pv.mapHeight, borderRadius: "var(--jrni-radius-md)", border: "1px solid var(--jrni-color-surface-border)", overflow: "hidden", backgroundColor: "#EAEEE6", backgroundImage: "linear-gradient(0deg, rgba(255, 255, 255, 0.65) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.65) 1px, transparent 1px), linear-gradient(120deg, #DFE7DA 0%, #EDF1E8 55%, #E3EAF2 100%)", backgroundSize: "34px 34px, 34px 34px, 100% 100%" }}>
                          <div style={{ position: "absolute", top: "12px", left: "12px", right: "12px", display: "flex", gap: "8px" }}>
                            <div style={{ flex: "1 1 auto", maxWidth: "340px", position: "relative" }}>
                              <Input leading={V.icons.search} placeholder="Search a campus, town or ZIP code" value={V.pv.locQuery} onChange={V.actions.setLocQuery} full={true} />
                              {V.pv.hasSuggestions ? (<>
                                <div role="listbox" aria-label="Matching locations" style={{ marginTop: "4px", maxHeight: "232px", overflowY: "auto", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                  {(V.pv.suggestions || []).map((sg, __i36) => (<React.Fragment key={sg.key ?? sg.id ?? __i36}>
                                    <button type="button" role="option" aria-selected={sg.selected} onClick={sg.onPick} style={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", textAlign: "left", padding: "7px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: sg.bg, cursor: "pointer", fontFamily: "inherit" }}>
                                      <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{sg.name}</span>
                                      <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{sg.meta}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                            </div>
                            <Button variant="secondary" iconOnly={V.icons.locate} aria-label="Use my location" />
                          </div>
                          {(V.pv.mapPins || []).map((pn, __i37) => (<React.Fragment key={pn.key ?? pn.id ?? __i37}>
                            <button type="button" onClick={pn.onSelect} aria-label={pn.name} style={{ position: "absolute", top: pn.top, left: pn.left, transform: "translate(-50%, -50%)", maxWidth: "84%", display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap", padding: "6px 10px 6px 7px", border: `1px solid ${pn.border}`, borderRadius: "999px", cursor: "pointer", fontFamily: "inherit", fontSize: "12px", fontWeight: "600", boxShadow: "var(--jrni-shadow-md)", background: pn.bg, color: pn.fg }}>
                              <span style={{ display: "flex" }}>{V.icons.pinSm}</span>
                              <span>{pn.short}</span>
                            </button>
                          </React.Fragment>))}
                        </div>
                        <div onpointerdown={V.actions.startMapResize} role="separator" aria-label="Drag to resize the map" title="Drag to resize the map" style={{ marginTop: "-10px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", cursor: "ns-resize", touchAction: "none" }}>
                          <span style={{ width: "40px", height: "4px", borderRadius: "999px", background: "var(--jrni-color-neutral-4)" }}></span>
                          {V.pv.resizingMap ? (<>
                            <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.mapHeightLabel}</span>
                          </>) : null}
                        </div>
                        <div style={{ display: "flex", gap: "14px", alignItems: "center", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)" }}>
                          <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                            <div style={{ fontSize: "15px", fontWeight: "700" }}>{V.pv.pinName}</div>
                            <div style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>{V.pv.selectedAddr}</div>
                            {V.pv.showNextLine ? (<>
                              <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Next available {V.pv.selectedNext}</div>
                            </>) : null}
                            {V.s.extraLocationInfo ? (<>
                              <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.selectedHours}</div>
                            </>) : null}
                          </div>
                          <button type="button" onClick={V.actions.nextStep} style={{ flex: "0 0 auto", border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "#FFFFFF", padding: "0 22px", height: "42px", background: V.pv.brand, borderRadius: V.pv.radius }}>Choose this location</button>
                        </div>
                        {V.pv.hasVirtual ? (<>
                          <Button variant="link" size="sm" onClick={V.pv.onVirtual}>{V.pv.virtualLabel}
                          </Button>
                        </>) : null}
                      </>) : null}
                      {V.pv.isLocation ? (<>
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.locationHeader}</h2>
                        {V.is.appt ? (<>
                          <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                            <div style={{ flex: "1 1 auto", minWidth: "0", position: "relative" }}>
                              <Input leading={V.icons.search} placeholder="Enter a campus, town or ZIP code" value={V.pv.locQuery} onChange={V.actions.setLocQuery} full={true} />
                              {V.pv.hasSuggestions ? (<>
                                <div role="listbox" aria-label="Matching locations" style={{ marginTop: "4px", maxHeight: "232px", overflowY: "auto", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                  {(V.pv.suggestions || []).map((sg, __i38) => (<React.Fragment key={sg.key ?? sg.id ?? __i38}>
                                    <button type="button" role="option" aria-selected={sg.selected} onClick={sg.onPick} style={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", textAlign: "left", padding: "7px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: sg.bg, cursor: "pointer", fontFamily: "inherit" }}>
                                      <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{sg.name}</span>
                                      <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{sg.meta}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                            </div>
                            <Button variant="secondary" iconLeft={V.icons.pin} onClick={V.actions.useMyLocation} disabled={V.pv.locating}>{V.pv.geoLabel}
                            </Button>
                          </div>
                          {V.pv.geoDenied ? (<>
                            <p style={{ margin: "-4px 0 0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.geoNote}</p>
                          </>) : null}
                        </>) : null}
                        {V.pv.showMapNow ? (<>
                          <LocationsMap locations={V.pv.mapLocations || []} height={Number.parseInt(V.pv.mapHeight, 10) || 190} />
                          <div onpointerdown={V.actions.startMapResize} role="separator" aria-label="Drag to resize the map" title="Drag to resize the map" style={{ marginTop: "-6px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", cursor: "ns-resize", touchAction: "none" }}>
                            <span style={{ width: "40px", height: "4px", borderRadius: "999px", background: "var(--jrni-color-neutral-4)" }}></span>
                            {V.pv.resizingMap ? (<>
                              <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.mapHeightLabel}</span>
                            </>) : null}
                          </div>
                        </>) : null}
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {V.pv.deskList ? (<>
                            {(V.pv.visibleLocations || []).map((lo, __i39) => (<React.Fragment key={lo.key ?? lo.id ?? __i39}>
                              <ChoiceTile showCheck={false} label={lo.name} sublabel={lo.addr} leading={lo.leading} trailing={lo.dist} selected={lo.selected} onSelect={lo.onSelect} full={true} />
                            </React.Fragment>))}
                          </>) : null}
                          {V.pv.branchList ? (<>
                            {(V.pv.visibleLocations || []).map((lo, __i40) => (<React.Fragment key={lo.key ?? lo.id ?? __i40}>
                              <div role="button" tabIndex="0" aria-pressed={lo.selected} onClick={lo.onSelect} style={{ cursor: "pointer", padding: "16px 18px", display: "flex", flexDirection: "column", gap: "4px", background: "var(--jrni-color-surface-card)", border: `1px solid ${lo.border}`, borderRadius: "var(--jrni-radius-md)", boxShadow: lo.shadow }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: "12px" }}>
                                  <span style={{ flex: "0 0 auto", display: "flex", alignSelf: "center", color: "var(--jrni-color-text-soft)" }}>{V.icons.pinSm}</span>
                                  <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "16px", fontWeight: "700", letterSpacing: "-0.01em", color: "var(--jrni-color-text-strong)" }}>{lo.name}</span>
                                  <span style={{ flex: "0 0 auto", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{lo.dist}</span>
                                </div>
                                <div style={{ fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>{lo.addr}</div>
                                {lo.showPhone ? (<>
                                  <div style={{ fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>{lo.phone}</div>
                                </>) : null}
                                {lo.showNext ? (<>
                                  <div style={{ fontSize: "14px", color: "var(--jrni-color-text-strong)" }}>
                                    <strong style={{ fontWeight: "700" }}>Next available:</strong> {lo.nextLabel}
                                  </div>
                                </>) : null}
                                {lo.quickbook ? (<>
                                  <div style={{ paddingTop: "8px" }}>
                                    <Button variant="secondary" onClick={lo.onQuickbook}>Quick book this appointment
                                    </Button>
                                  </div>
                                </>) : null}
                                {lo.showHours ? (<>
                                  <div style={{ paddingTop: "10px", display: "flex", flexDirection: "column", gap: "6px" }}>
                                    <button type="button" onClick={lo.onToggleHours} aria-expanded={lo.hoursOpen} style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: "6px", padding: "0", background: "none", border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "700", color: "var(--jrni-color-text-strong)" }}>Branch hours
                                      <span style={{ display: "flex", color: "var(--jrni-color-text-soft)", transform: lo.hoursCaret }}>{V.icons.chevron}</span>
                                    </button>
                                    {lo.hoursOpen ? (<>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                        <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)", paddingBottom: "4px" }}>{lo.tzLabel}</div>
                                        {(lo.week || []).map((wd, __i41) => (<React.Fragment key={wd.key ?? wd.id ?? __i41}>
                                          <div style={{ display: "flex", alignItems: "baseline", gap: "12px", fontSize: "13px", padding: "3px 0", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                                            <span style={{ flex: "0 0 96px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{wd.day}</span>
                                            <span style={{ flex: "1 1 auto", color: "var(--jrni-color-text-soft)" }}>{wd.label}</span>
                                          </div>
                                        </React.Fragment>))}
                                      </div>
                                    </>) : null}
                                  </div>
                                </>) : null}
                              </div>
                            </React.Fragment>))}
                          </>) : null}
                          {V.pv.showLoadMore ? (<>
                            <Button variant="secondary" full={true} onClick={V.actions.moreLocations}>{V.pv.loadMoreLabel}
                            </Button>
                          </>) : null}
                        </div>
                      </>) : null}
                      {V.pv.isDatetime ? (<>
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.dateHeader}</h2>
                        {V.s.staffSelect ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <SectionLabel tone="soft">Adviser
                            </SectionLabel>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {(V.pv.staff || []).map((p, __i42) => (<React.Fragment key={p.key ?? p.id ?? __i42}>
                                <Chip leading={p.leading} selected={p.selected} onClick={p.onClick}>{p.name}
                                </Chip>
                              </React.Fragment>))}
                            </div>
                          </div>
                        </>) : null}
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "24px", flexWrap: "wrap", flexDirection: V.pv.dtDir }}>
                          <div style={{ flex: V.pv.dtCalFlex, minWidth: "0", display: "flex", flexDirection: "column", gap: "14px" }}>
                            {V.is.dayStrip ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <Button variant="ghost" size="sm" iconOnly={V.icons.left} aria-label="Previous week" onClick={V.actions.stripPrev} disabled={V.pv.stripPrevDisabled} />
                                  <div style={{ flex: "1 1 auto", textAlign: "center", fontSize: "14px", fontWeight: "700" }}>{V.pv.stripLabel}</div>
                                  <Button variant="ghost" size="sm" iconOnly={V.icons.right} aria-label="Next week" onClick={V.actions.stripNext} disabled={V.pv.stripNextDisabled} />
                                </div>
                                <div role="tablist" aria-label="Pick a day" style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "4px" }}>
                                  {(V.pv.days || []).map((dy, __i43) => (<React.Fragment key={dy.key ?? dy.id ?? __i43}>
                                    <button type="button" role="tab" aria-selected={dy.selected} disabled={dy.disabled} onClick={dy.onSelect} style={{ flexShrink: "0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", minWidth: "64px", padding: "8px 10px", fontFamily: "inherit", borderRadius: "var(--jrni-radius-default)", border: `1px solid ${dy.border}`, background: dy.bg, opacity: dy.opacity, cursor: dy.cursor }}>
                                      <span style={{ fontSize: "13px", fontWeight: "600", color: dy.fg }}>{dy.label}</span>
                                      <span style={{ fontSize: "11px", color: dy.subFg }}>{dy.sublabel}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </div>
                            </>) : null}
                            {V.is.monthCal ? (<>
                              <div style={{ maxWidth: "420px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", padding: "14px 16px 16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingBottom: "10px" }}>
                                  <Button variant="ghost" size="sm" iconOnly={V.icons.left} aria-label="Previous month" onClick={V.actions.calPrev} disabled={V.pv.calPrevDisabled} />
                                  <div style={{ flex: "1 1 auto", textAlign: "center", fontSize: "14px", fontWeight: "700" }}>{V.pv.calTitle}</div>
                                  <Button variant="ghost" size="sm" iconOnly={V.icons.right} aria-label="Next month" onClick={V.actions.calNext} />
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "4px", paddingBottom: "6px" }}>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Mon</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Tue</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Wed</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Thu</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Fri</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Sat</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Sun</span>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "4px" }}>
                                  {(V.pv.calCells || []).map((c, __i44) => (<React.Fragment key={c.key ?? c.id ?? __i44}>
                                    <button type="button" onClick={c.onSelect} disabled={c.disabled} aria-pressed={c.selected} style={{ height: "38px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", fontSize: "13px", borderRadius: "999px", background: c.bg, color: c.fg, border: `1px solid ${c.border}`, cursor: c.cursor, fontWeight: c.weight }}>{c.label}</button>
                                  </React.Fragment>))}
                                </div>
                              </div>
                            </>) : null}
                          </div>
                          <div style={{ flex: "1 1 260px", minWidth: "0", display: "flex", flexDirection: "column", gap: "12px" }}>
                            <h3 style={{ margin: "6px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.timeHeader}</h3>
                            <p style={{ margin: "-6px 0 0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.selectedDayLabel}</p>
                            {V.pv.noSlots ? (<>
                              <InlineEmptyState title={V.pv.noSlotsTitle} description={V.pv.noSlotsBody} />
                            </>) : null}
                            {V.pv.groupedSlots ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                                {(V.pv.slotGroups || []).map((grp, __i45) => (<React.Fragment key={grp.key ?? grp.id ?? __i45}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <SectionLabel tone="soft">{grp.label}
                                    </SectionLabel>
                                    <SlotPicker slots={grp.slots} value={V.pv.slot} onChange={V.actions.setSlot} minColWidth={96} />
                                  </div>
                                </React.Fragment>))}
                              </div>
                            </>) : null}
                            {V.pv.flatSlots ? (<>
                              <SlotPicker slots={V.pv.allSlots} value={V.pv.slot} onChange={V.actions.setSlot} minColWidth={96} />
                            </>) : null}
                            {V.s.showTimezone ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxWidth: "320px", paddingTop: "4px" }}>
                                <TimezoneSelect label={V.pv.tzLabelNode} value={V.pv.custTz} onChange={V.actions.setCustTz} allowEmpty={false} searchable={true} searchPlaceholder="Search time zones" />
                              </div>
                            </>) : null}
                          </div>
                        </div>
                      </>) : null}
                      {V.pv.isDetails ? (<>
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.detailsHeader}</h2>
                        {V.pv.showEventSummary ? (<>
                          <div style={{ display: "flex", gap: "12px", alignItems: "center", padding: "12px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                            <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                              <div style={{ fontSize: "14px", fontWeight: "700" }}>{V.pv.evtName}</div>
                              <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.evtSummaryLine}</div>
                            </div>
                            <Button variant="link" size="sm" onClick={V.actions.prevStep}>Change
                            </Button>
                          </div>
                        </>) : null}
                        {V.pv.ticketChoices && V.pv.ticketChoices.length > 1 ? (<>
                          <Field label="Ticket type" required={true} error={V.pv.ticketChoiceError}>
                            <Dropdown options={V.pv.ticketChoices} value={V.pv.selectedTicketId} onChange={V.actions.setSelectedTicketId} placeholder="Choose a ticket type" full={true} />
                          </Field>
                        </>) : null}
                        {V.pv.showTickets ? (<>
                          <Field label={V.pv.ticketsLabel} hint={V.pv.ticketsHint}>
                            <Input type="number" value={V.pv.tickets} onChange={V.actions.setTickets} style={{  }} />
                          </Field>
                        </>) : null}
                        {V.pv.signedIn ? (<>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                            <span style={{ flex: "0 0 auto", display: "flex", color: "var(--jrni-color-text-soft)" }}>{V.icons.lock}</span>
                            <span style={{ flex: "1 1 auto", fontSize: "13px", color: "var(--jrni-color-text-body)" }}>{V.pv.signedInLabel}</span>
                          </div>
                        </>) : null}
                        <p style={{ margin: "0", fontSize: "13px", fontStyle: "italic", color: "var(--jrni-color-text-soft)" }}>* Indicates required fields</p>
                        <div style={{ display: "grid", gap: "14px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                          <Field label="First name" required={true} error={V.pv.firstError}>
                            <Input value={V.pv.firstValue} onChange={V.actions.setFirst} onBlur={V.actions.blurFirst} forceState={V.pv.firstState} full={true} />
                          </Field>
                          <Field label="Last name" required={true} error={V.pv.lastError}>
                            <Input value={V.pv.lastValue} onChange={V.actions.setLast} onBlur={V.actions.blurLast} forceState={V.pv.lastState} full={true} />
                          </Field>
                        </div>
                        {(this.isEvents() || this.isLanding()) || V.s.askEmail ? (<>
                          {/* Events/landing: the current JSON registration contract always
                              requires email, regardless of the authored `askEmail` toggle —
                              shown and required unconditionally rather than hideable into an
                              impossible submission. */}
                          <Field label={(this.isEvents() || this.isLanding()) ? "Email" : V.pv.emailLabel} required={(this.isEvents() || this.isLanding()) ? true : V.pv.emailRequired} error={V.pv.emailError}>
                            <Input type="email" value={V.pv.emailValue} onChange={V.actions.setEmail} onBlur={V.actions.blurEmail} forceState={V.pv.emailState} placeholder="you@example.com" full={true} />
                          </Field>
                        </>) : null}
                        {V.s.askPhone ? (<>
                          <Field label={V.pv.phoneLabel} required={V.pv.phoneRequired} error={V.pv.phoneError} hint={V.pv.phoneHint}>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <Dropdown options={V.opts.dialCodes} value={V.pv.dialIso} onChange={V.actions.setPhoneCountry} disabled={V.pv.signedIn} aria-label="Country code" style={{  }} />
                              <Input type="tel" value={V.pv.phoneValue} onChange={V.actions.setPhone} onBlur={V.actions.blurPhone} forceState={V.pv.phoneState} placeholder="(512) 555-0142" full={true} />
                            </div>
                          </Field>
                        </>) : null}
                        {V.pv.hasBookingQuestions ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "14px", paddingTop: "6px", borderTop: "1px solid var(--jrni-color-surface-border)" }}>
                            <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--jrni-color-text-strong)" }}>{V.pv.bookingQuestionsHeading}</span>
                            {(V.pv.bookingQuestions || []).map((bq, __iq) => (<React.Fragment key={bq.id ?? __iq}>
                              <Field label={bq.label} required={bq.required} hint={bq.help} error={bq.error}>
                                {bq.isSelect ? (<Dropdown options={bq.options} value={bq.value} onChange={bq.onSelect} aria-label={bq.label} style={{  }} />) : null}
                                {bq.isShort && !bq.isLong ? (<Input value={bq.value} onChange={bq.onChange} full={true} />) : null}
                                {bq.isLong ? (<Input value={bq.value} onChange={bq.onChange} full={true} />) : null}
                                {bq.isStepper ? (<Input type="number" value={bq.value} onChange={bq.onChange} style={{  }} />) : null}
                                {bq.isYesNo ? (<div style={{ display: "flex", gap: "8px" }}>
                                  <Button size="sm" variant={bq.yesNoValue === "yes" ? "primary" : "secondary"} onClick={bq.onYes}>Yes</Button>
                                  <Button size="sm" variant={bq.yesNoValue === "no" ? "primary" : "secondary"} onClick={bq.onNo}>No</Button>
                                </div>) : null}
                                {bq.isChecks ? (<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                  {(bq.checkRows || []).map((cr, __ic) => (<React.Fragment key={cr.id ?? __ic}><Checkbox checked={cr.checked} onChange={cr.onToggle} label={cr.label} /></React.Fragment>))}
                                </div>) : null}
                                {bq.isFile ? (<Input type="file" onChange={bq.onChange} full={true} />) : null}
                              </Field>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.s.askAddress ? (
                          (this.state.journey === "events" || this.state.journey === "landing") ? (<>
                            {/* The event registration API accepts no address field — collecting it
                                here would be discarded silently, so it is disabled with the true
                                reason instead of offered. */}
                            <Field label={V.pv.addressLabel}>
                              <Input leading={V.icons.search} value="" disabled placeholder="Not collected for event registrations" full={true} />
                            </Field>
                          </>) : (<>
                          <Field label={V.pv.addressLabel} required={V.pv.addressRequired}>
                            <div style={{ position: "relative" }}>
                              <Input leading={V.icons.search} value={V.pv.addrValue} onChange={V.actions.setAddr} forceState={V.pv.addrState} placeholder="Start typing an address" full={true} />
                              {V.pv.hasAddrMatches ? (<>
                                <div role="listbox" aria-label="Address matches" style={{ marginTop: "4px", maxHeight: "232px", overflowY: "auto", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                  {(V.pv.addrMatches || []).map((ad, __i46) => (<React.Fragment key={ad.key ?? ad.id ?? __i46}>
                                    <button type="button" role="option" aria-selected={false} onClick={ad.onPick} style={{ display: "flex", alignItems: "flex-start", gap: "8px", width: "100%", textAlign: "left", padding: "7px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                                      <span style={{ display: "flex", paddingTop: "2px", color: "var(--jrni-color-text-soft)" }}>{V.icons.pinSm}</span>
                                      <span style={{ display: "flex", flexDirection: "column", gap: "1px", minWidth: "0" }}>
                                        <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{ad.main}</span>
                                        <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{ad.secondary}</span>
                                      </span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                            </div>
                          </Field>
                          </>)
                        ) : null}
                        {V.s.notifPref ? (
                          (this.state.journey === "events" || this.state.journey === "landing") ? (<>
                            {/* The registration API accepts no notification-preference field — a
                                recorded choice here would be discarded silently. */}
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <span style={{ fontSize: "13px", fontWeight: "600" }}>Reminder preference</span>
                              <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>Not available to set for event registrations yet.</span>
                            </div>
                          </>) : (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <span style={{ fontSize: "13px", fontWeight: "600" }}>How would you like to be reminded about your appointment? *</span>
                            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                              <Checkbox checked={V.pv.notifEmail} label="Email" />
                              <Checkbox checked={V.pv.notifSms} label="Text message" />
                            </div>
                          </div>
                          </>)
                        ) : null}
                        {V.pv.showParty ? (<>
                          <Field label="How many of you are waiting together?" hint={V.pv.partyHint}>
                            <Input type="number" value={V.pv.party} onChange={V.actions.setParty} style={{  }} />
                          </Field>
                        </>) : null}
                        {V.s.attendees ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {(V.pv.attendeeList || []).map((at, __i47) => (<React.Fragment key={at.key ?? at.id ?? __i47}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                  <span style={{ flex: "1 1 auto", fontSize: "13px", fontWeight: "700" }}>{at.title}</span>
                                  <Button variant="link" size="sm" danger={true} onClick={at.onRemove}>Remove
                                  </Button>
                                </div>
                                <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                                  {V.pv.attFirst ? (<>
                                    <Field label="First name" required={true} error={at.firstError}>
                                      <Input value={at.first} onChange={at.setFirst} onBlur={at.blurFirst} forceState={at.firstState} full={true} />
                                    </Field>
                                  </>) : null}
                                  {V.pv.attLast ? (<>
                                    <Field label="Last name" required={true} error={at.lastError}>
                                      <Input value={at.last} onChange={at.setLast} onBlur={at.blurLast} forceState={at.lastState} full={true} />
                                    </Field>
                                  </>) : null}
                                  {V.pv.attEmail ? (<>
                                    <Field label={V.pv.emailLabel} required={V.pv.emailRequired} error={at.emailError}>
                                      <Input type="email" value={at.email} onChange={at.setEmail} onBlur={at.blurEmail} forceState={at.emailState} placeholder="you@example.com" full={true} />
                                    </Field>
                                  </>) : null}
                                  {V.pv.attPhone ? (<>
                                    <Field label={V.pv.phoneLabel} required={V.pv.phoneRequired} error={at.phoneError}>
                                      <div style={{ display: "flex", gap: "8px" }}>
                                        <Dropdown options={V.opts.dialCodes} value={at.dialIso} onChange={at.setCountry} aria-label="Country code" style={{  }} />
                                        <Input type="tel" value={at.phone} onChange={at.setPhone} onBlur={at.blurPhone} forceState={at.phoneState} placeholder="(512) 555-0142" full={true} />
                                      </div>
                                    </Field>
                                  </>) : null}
                                </div>
                              </div>
                            </React.Fragment>))}
                            {V.pv.canAddAttendee ? (<>
                              <AddRowButton onClick={V.actions.addAttendee}>{V.pv.addAttendeeLabel}
                              </AddRowButton>
                            </>) : null}
                          </div>
                        </>) : null}
                        {V.s.fileUpload ? (
                          (this.state.journey === "events" || this.state.journey === "landing") ? (<>
                            {/* No public file-upload authorization exists for event registrations —
                                accepting files here would be silently dropped, so this is disabled
                                with the true reason rather than a working-looking control. */}
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                              <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.uploadHeader}</h3>
                              <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>File attachments aren't available for event registrations yet.</span>
                            </div>
                          </>) : (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.uploadHeader}</h3>
                            <FileUpload files={V.pv.files} onFilesAdded={V.actions.addFiles} onFileRemoved={V.actions.removeFile} accept={V.pv.uploadAccept} disabled={V.pv.uploadFull} hint={V.pv.uploadHint} multiple={true} />
                          </div>
                          </>)
                        ) : null}
                        {V.s.takePayment && !((this.state.journey === "events" || this.state.journey === "landing") && !this.hasUsablePaymentProvider()) ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.paymentHeader}</h3>
                            {V.pv.hasPaymentInfo ? (<>
                              <p style={{ margin: "0", fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)" }}>{V.pv.paymentInfo}</p>
                            </>) : null}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "12px", padding: "10px 12px", borderRadius: "var(--jrni-radius-default)", background: "var(--jrni-color-surface-card-soft)", border: "1px solid var(--jrni-color-surface-border)" }}>
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Total due today</span>
                              <span style={{ fontSize: "15px", fontWeight: "700" }}>{V.pv.totalDue}</span>
                            </div>
                            <Field label="Card number" required={true}>
                              <Input placeholder="•••• •••• •••• 4242" full={true} />
                            </Field>
                            <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                              <Field label="Expiry" required={true}>
                                <Input placeholder="MM / YY" full={true} />
                              </Field>
                              <Field label="Security code" required={true}>
                                <Input placeholder="CVC" full={true} />
                              </Field>
                            </div>
                          </div>
                        </>) : null}
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                          <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.consentHeader}</h3>
                          <Checkbox checked={V.pv.consent} onChange={V.actions.setConsent} label={V.pv.consentText} />
                        </div>
                        {V.pv.bookingError ? (
                          <div role="alert" style={{ marginTop: "10px", padding: "10px 12px", borderRadius: V.pv.radius, background: "var(--jrni-color-danger-surface, #FDECEC)", color: "var(--jrni-color-danger-text, #8A1C1C)", fontSize: "13px", lineHeight: "1.4" }}>{V.pv.bookingError}</div>
                        ) : null}
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingTop: "6px" }}>
                          {V.pv.showCustomerBack ? (<>
                            <Button variant="ghost" iconLeft={V.icons.left} onClick={V.actions.prevStep}>Back
                            </Button>
                          </>) : null}
                          <div style={{ flex: "1 1 auto" }}></div>
                          <button type="button" onClick={V.actions.nextStep} disabled={V.pv.submitting} style={{ border: "0", cursor: V.pv.submitting ? "progress" : "pointer", opacity: V.pv.submitting ? "0.7" : "1", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", padding: "0 24px", height: V.pv.ctaHeight, background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.submitting ? "Confirming…" : V.pv.ctaLabel}</button>
                        </div>
                      </>) : null}
                      {V.pv.isRegistration ? (<>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          {V.pv.notCancelled ? (<>
                            <span style={{ flex: "0 0 auto", width: "24px", height: "24px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-semantic-green-2)", color: "var(--jrni-color-semantic-green-1)", fontSize: "13px", fontWeight: "700" }}>✓</span>
                          </>) : null}
                          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.confirmMsg}</h2>
                          {V.pv.cancelled ? (<>
                            <Pill tone="danger">Cancelled
                            </Pill>
                          </>) : null}
                        </div>
                        <Banner tone={V.pv.flagTone} size="compact" title={V.pv.flagHeader}>{V.pv.flagText}
                        </Banner>
                        {V.pv.cancelled ? (<>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px" }}>
                            <Button hierarchy="primary" onClick={V.actions.backToEvents}>{V.pv.backToEventsLabel}
                            </Button>
                          </div>
                        </>) : null}
                        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-start" }}>
                          <div style={{ flex: "1 1 220px", minWidth: "0", display: "flex", flexDirection: "column", gap: "14px", fontSize: "13px", lineHeight: "1.5" }}>
                            <div>
                              <SectionLabel tone="soft">Event
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.evtName}</div>
                              <div style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.evtWhen}</div>
                            </div>
                            <div>
                              <SectionLabel tone="soft">Where
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.evtVenue}</div>
                              {V.s.showResource ? (<>
                                <div style={{ color: "var(--jrni-color-text-soft)" }}>Kellner Auditorium</div>
                              </>) : null}
                            </div>
                            <div>
                              <SectionLabel tone="soft">Places
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.placesLine}</div>
                              {V.pv.bookingReference ? (<div style={{ color: "var(--jrni-color-text-soft)" }}>Reference {V.pv.bookingReference}</div>) : null}
                            </div>
                            {/* The host of an EVENT is the event's own staffName from the public
                                projection, never the appointment journey's selected adviser. With
                                no host on the record there is nothing authoritative to name, so
                                the whole section is hidden rather than filled with a stand-in. */}
                            {V.s.showStaff && V.pv.eventHostName ? (<>
                              <div>
                                <SectionLabel tone="soft">Host
                                </SectionLabel>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingTop: "6px" }}>
                                  <Avatar name={V.pv.eventHostName} size={24} />
                                  <span style={{ fontWeight: "600" }}>{V.pv.eventHostName}</span>
                                </div>
                              </div>
                            </>) : null}
                          </div>
                          {V.s.showQr && V.pv.qrValue ? (<>
                            <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                              <QrCode value={V.pv.qrValue} size={132} label="Event ticket" />
                              <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>Show this at the door</span>
                            </div>
                          </>) : V.s.showQr && this.isPublic() ? (<>
                            <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontSize: "12px", color: "var(--jrni-color-text-soft)", maxWidth: 160, textAlign: "center" }}>
                              A ticket credential wasn't issued for this registration.
                            </div>
                          </>) : null}
                        </div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)", marginTop: "4px", visibility: V.pv.regActionsVis }}>
                          {/* No signed calendar token is exposed by the current registration
                              response — `downloadPublicEventCalendar` requires one, and the
                              management-URL token is NOT it. Disabled with the true reason
                              rather than opening a menu whose entries cannot actually work. */}
                          {V.s.addToCalendar ? (<>
                            <Button variant="secondary" iconLeft={V.icons.calendar} disabled title="Calendar download isn't available for event registrations yet.">Add to calendar
                            </Button>
                          </>) : null}
                          {V.s.allowCancelReg ? (<>
                            {/* No public cancellation operation exists for event registrations —
                                disabled with a truthful reason rather than a clickable action that
                                would locally fake a cancelled state (REQ-cancel-honest). */}
                            <Button hierarchy="destructive" disabled title="Cancellation isn't available for event registrations yet.">{V.pv.cancelRegLabel}
                            </Button>
                          </>) : null}
                        </div>
                      </>) : null}
                      {V.pv.isTicket ? (<>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          {V.pv.inQueue ? (<>
                            <span style={{ flex: "0 0 auto", width: "24px", height: "24px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-semantic-green-2)", color: "var(--jrni-color-semantic-green-1)", fontSize: "13px", fontWeight: "700" }}>✓</span>
                          </>) : null}
                          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.queueHeader}</h2>
                          {V.pv.leftQueue ? (<>
                            <Pill tone="danger">Left the line
                            </Pill>
                          </>) : null}
                        </div>
                        {V.pv.leftQueue ? (<>
                          <Banner tone="danger" size="compact" title={V.pv.leftHeader}>{V.pv.leftText}
                          </Banner>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px" }}>
                            <Button hierarchy="primary" onClick={V.actions.rejoinQueue}>{V.pv.rejoinLabel}
                            </Button>
                            <Button variant="secondary" onClick={V.actions.bookInstead}>{V.pv.bookInsteadLabel}
                            </Button>
                          </div>
                        </>) : null}
                        {V.pv.inQueue ? (<>
                          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
                            {V.s.showTicketNumber ? (<>
                              <div style={{ flex: "1 1 150px", padding: "18px", textAlign: "center", borderRadius: "var(--jrni-radius-md)", color: "#FFFFFF", background: V.pv.brand }}>
                                <div style={{ fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", opacity: "0.85" }}>Ticket</div>
                                <div style={{ fontSize: "34px", fontWeight: "700", lineHeight: "1.15", letterSpacing: "-0.02em" }}>{V.pv.ticketNumber}</div>
                              </div>
                            </>) : null}
                            {V.s.showPosition ? (<>
                              <div style={{ flex: "1 1 150px", padding: "18px", textAlign: "center", borderRadius: "var(--jrni-radius-md)", border: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                                  <span style={{ width: "7px", height: "7px", borderRadius: "999px", background: "var(--jrni-color-semantic-green-1)", animation: "apd-pulse 1.8s ease-in-out infinite" }}></span>
                                  <span style={{ fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>{V.pv.posLabel}</span>
                                </div>
                                <div style={{ fontSize: "34px", fontWeight: "700", lineHeight: "1.15", letterSpacing: "-0.02em", paddingTop: "4px" }}>{V.pv.position}</div>
                              </div>
                            </>) : null}
                            {V.pv.showWait ? (<>
                              <div style={{ flex: "1 1 150px", padding: "18px", textAlign: "center", borderRadius: "var(--jrni-radius-md)", border: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <div style={{ fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>{V.pv.waitLabel}</div>
                                <div style={{ fontSize: "22px", fontWeight: "700", lineHeight: "1.4", paddingTop: "6px" }}>{V.pv.waitText}</div>
                              </div>
                            </>) : null}
                          </div>
                          <p style={{ margin: "0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.staffLine}</p>
                          {V.s.notifyWhenClose ? (<>
                            <Banner tone="info" size="compact" title="Stay nearby">{V.pv.notifyLine}
                            </Banner>
                          </>) : null}
                          <div style={{ display: "grid", gap: "20px", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", fontSize: "13px", lineHeight: "1.5" }}>
                            <div>
                              <SectionLabel tone="soft">Reason for visit
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.confirmService}</div>
                            </div>
                          </div>
                          {V.s.showQr ? (<>
                            <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                              <QrCode value={V.pv.queueQrValue} size={108} label="Queue ticket code" />
                              <div style={{ fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)" }}>
                                <div style={{ fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>Keep this code</div>
                                <div>Scanned at the desk when your ticket is called, so nobody loses their place.</div>
                              </div>
                            </div>
                          </>) : null}
                          {V.pv.hasArrivalNote ? (<>
                            <p style={{ margin: "0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.arrivalNote}</p>
                          </>) : null}
                          {V.s.allowLeaveQueue ? (<>
                            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)", marginTop: "4px" }}>
                              <Button hierarchy="destructive" onClick={V.actions.askLeave}>{V.pv.leaveLabel}
                              </Button>
                            </div>
                          </>) : null}
                        </>) : null}
                        <ConfirmDialog open={V.pv.leaveAsking} onCancel={V.actions.dismissLeave} onConfirm={V.actions.doLeave} tone="danger" title={V.pv.leaveTitle} consequence={V.pv.leaveConsequence} icon={false} confirmLabel={V.pv.leaveConfirmLabel} cancelLabel={V.pv.leaveKeepLabel} />
                      </>) : null}
                      {V.pv.isConfirmation ? (<>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          {V.pv.notCancelled ? (<>
                            <span style={{ flex: "0 0 auto", width: "24px", height: "24px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-semantic-green-2)", color: "var(--jrni-color-semantic-green-1)", fontSize: "13px", fontWeight: "700" }}>✓</span>
                          </>) : null}
                          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.confirmMsg}</h2>
                          {V.pv.cancelled ? (<>
                            <Pill tone="danger">Cancelled
                            </Pill>
                          </>) : null}
                        </div>
                        <Banner tone={V.pv.flagTone} size="compact" title={V.pv.flagHeader}>{V.pv.flagText}
                        </Banner>
                        <div style={{ display: "grid", gap: "20px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", fontSize: "13px", lineHeight: "1.5", textDecoration: V.pv.strike, color: V.pv.detailFg }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                            <div>
                              <SectionLabel tone="soft">When
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.confirmWhen}</div>
                              <div style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.confirmDuration}</div>
                            </div>
                            <div>
                              <SectionLabel tone="soft">Service
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.confirmService}</div>
                              {V.pv.bookingReference ? (<div style={{ color: "var(--jrni-color-text-soft)" }}>Reference {V.pv.bookingReference}</div>) : null}
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                            <div>
                              <SectionLabel tone="soft">Where
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.confirmLocation}</div>
                              <div style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.confirmAddress}</div>
                            </div>
                            {V.s.showStaff ? (<>
                              <div>
                                <SectionLabel tone="soft">Adviser
                                </SectionLabel>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingTop: "6px" }}>
                                  <Avatar name={V.pv.staffName} size={24} />
                                  <span style={{ fontWeight: "600" }}>{V.pv.staffName}</span>
                                </div>
                              </div>
                            </>) : null}
                            {V.s.showResource ? (<>
                              <div>
                                <SectionLabel tone="soft">Room
                                </SectionLabel>
                                <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.bookingRoom}</div>
                              </div>
                            </>) : null}
                          </div>
                        </div>
                        {V.s.showQr ? (<>
                          <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                            <QrCode value={V.pv.apptQrValue} size={108} label="Appointment check-in code" />
                            <div style={{ fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)" }}>
                              <div style={{ fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>Check in with this code</div>
                              <div>Show it at reception when you arrive. A copy is in your confirmation email.</div>
                            </div>
                          </div>
                        </>) : null}
                        {V.pv.cancelled ? (<>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)", marginTop: "4px" }}>
                            <Button hierarchy="primary" onClick={V.actions.bookAgain}>{V.pv.againLabel}
                            </Button>
                          </div>
                        </>) : null}
                        {V.pv.notCancelled ? (<>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)", marginTop: "4px" }}>
                            {V.s.addToCalendar ? (<>
                              <div style={{ position: "relative" }}>
                                <Button variant="secondary" iconLeft={V.icons.calendar} hasCaret={true} onClick={V.actions.toggleCal}>Add to calendar
                                </Button>
                                {V.pv.calMenuOpen ? (<>
                                  <div role="menu" aria-label="Add to calendar" style={{ position: "absolute", zIndex: "6", bottom: "calc(100% + 6px)", left: "0", minWidth: "232px", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                    {(V.pv.calTargets || []).map((ct, __i49) => (<React.Fragment key={ct.key ?? ct.id ?? __i49}>
                                      <button type="button" role="menuitem" onClick={ct.onPick} style={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", textAlign: "left", padding: "8px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                                        <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{ct.label}</span>
                                        <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{ct.meta}</span>
                                      </button>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                              </div>
                            </>) : null}
                            {V.s.allowReschedule ? (<>
                              <Button variant="secondary" onClick={V.actions.reschedule}>Reschedule
                              </Button>
                            </>) : null}
                            {V.s.allowCancel ? (<>
                              <Button hierarchy="destructive" onClick={V.actions.askCancel}>Cancel appointment
                              </Button>
                            </>) : null}
                          </div>
                        </>) : null}
                        <ConfirmDialog open={V.pv.cancelAsking} onCancel={V.actions.dismissCancel} onConfirm={V.actions.doCancel} tone="danger" title={V.pv.cancelTitle} consequence={V.pv.cancelConsequence} icon={false} confirmLabel={V.pv.cancelConfirmLabel} cancelLabel={V.pv.cancelKeepLabel} />
                      </>) : null}
                      {V.pv.showContinue ? (<>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingTop: "16px", marginTop: "2px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                          {V.pv.showCustomerBack ? (<>
                            <Button variant="ghost" iconLeft={V.icons.left} onClick={V.actions.prevStep}>Back
                            </Button>
                          </>) : null}
                          <div style={{ flex: "1 1 auto" }}></div>
                          <button type="button" onClick={V.actions.nextStep} disabled={V.pv.continueBlocked} style={{ border: "0", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", padding: "0 26px", height: V.pv.ctaHeight, background: V.pv.continueBg, cursor: V.pv.continueCursor, borderRadius: V.pv.radius }}>{V.pv.continueLabel}</button>
                        </div>
                      </>) : null}
                    </div>
                    {V.pv.showSummary ? (<>
                      <aside style={{ flex: `1 1 ${V.pv.asideW}`, minWidth: "0", maxWidth: V.pv.asideMax, position: "sticky", top: "0", display: "flex", flexDirection: "column", overflow: "hidden", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-chrome)" }}>
                        <div style={{ padding: "14px 16px 12px", fontSize: "13px", fontWeight: "700", textAlign: "center", borderBottom: "1px solid var(--jrni-color-surface-border)" }}>{V.pv.summaryHeader}</div>
                        {V.pv.sumShowWhen ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "center", textAlign: "center", padding: "20px 16px" }}>
                            <div style={{ fontSize: "16px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.sumWhen}</div>
                            <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.sumEnds}</div>
                            {V.pv.canChange ? (<>
                              <Button variant="link" size="sm" onClick={V.pv.sumGoWhen}>Change
                              </Button>
                            </>) : null}
                          </div>
                        </>) : null}
                        {(V.pv.summaryRows || []).map((sm, __i50) => (<React.Fragment key={sm.key ?? sm.id ?? __i50}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "16px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <div style={{ flex: "1 1 auto", minWidth: "0", display: "flex", flexDirection: "column", gap: "3px" }}>
                              <span style={{ fontSize: "14px", fontWeight: "700", lineHeight: "1.35", textWrap: "pretty" }}>{sm.title}</span>
                              <span style={{ fontSize: "13px", lineHeight: "1.45", color: "var(--jrni-color-text-soft)", textWrap: "pretty" }}>{sm.sub}</span>
                            </div>
                            {V.pv.canChange ? (<>
                              <Button variant="link" size="sm" onClick={sm.onChange}>Change
                              </Button>
                            </>) : null}
                          </div>
                        </React.Fragment>))}
                      </aside>
                    </>) : null}
                  </div>
                  {V.is.appt ? <AppointmentFooter footer={this.state.apptJourney?.footer} showCustom={V.s.showFooter} hideJrni={V.s.hideJrni ?? this.state.apptJourney?.advanced?.hideJrni} /> : V.s.showFooter ? (<>
                    <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap", padding: "14px 24px", borderTop: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-neutral-6)", fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>
                      <span>© 2026 {this.props.tenantName || "This workspace"}</span>
                      <a href="#privacy">Privacy</a>
                      <a href="#accessibility">Accessibility</a>
                      <div style={{ flex: "1 1 auto" }}></div>
                      <span>Advising Center · (512) 555-0100</span>
                    </div>
                  </>) : null}
                </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </>
    );
  }
}

/** Route entry: the designer fills the app frame; Screen lights the "All Journeys" rail item. */
export default function AllJourneys() {
  const tenant = useTenant() as any;
  const slug: string = tenant?.slug ?? "default";
  /* Both from served sources rather than slug-keyed tables (2026-09-08): the
     host from the tenant's public config, the nouns from its vocabulary. The
     hook re-renders when either arrives, so the first paint's generic words are
     replaced rather than frozen. */
  const vocabulary = useTenantVocab();
  const host = publicHost("book", slug);
  // key on tenant so a persona switch remounts with that tenant's records, never a stale stash
  return (
    <Screen rail="all-journeys">
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Designer key={slug} tenant={slug} tenantName={tenant?.name} brandColor={tenant?.brandColor} publicHost={host} vocab={wordsFromVocab(vocabulary)} />
      </div>
    </Screen>
  );
}
